/*!
 * Bronson-React UI Components Library
 * @version v10.2.0
 * @link https://react.bronson.vwfs.tools
 * @author Volkswagen Financial Services AG
 * @license LicenseRef-VWFS-Standard-License
 * Copyright ©2025 Volkswagen Financial Services AG. All rights reserved.
 */

import * as React from 'react';
import React__default, { useMemo, useCallback, useState, useEffect, useContext, useRef, isValidElement, forwardRef, Children, useImperativeHandle, createContext } from 'react';
import classNames from 'classnames';
import Tippy from '@tippyjs/react';
import ReactDOM, { createPortal } from 'react-dom';
import debounce from 'lodash.debounce';
import { Link } from 'react-router-dom';
import imagesloaded from 'imagesloaded';
import nouislider from 'nouislider';
import Flatpickr from 'react-flatpickr';
import pl from 'flatpickr/dist/l10n/pl';
import it from 'flatpickr/dist/l10n/it';
import de from 'flatpickr/dist/l10n/de';
import cs from 'flatpickr/dist/l10n/cs';
import es from 'flatpickr/dist/l10n/es';
import fr from 'flatpickr/dist/l10n/fr';
import nl from 'flatpickr/dist/l10n/nl';
import pt from 'flatpickr/dist/l10n/pt';
import ja from 'flatpickr/dist/l10n/ja';
import sk from 'flatpickr/dist/l10n/sk';
import { IMaskInput } from 'react-imask';
import IMask from 'imask';
import { usePopper } from 'react-popper';
import Plyr from 'plyr';

/**
 * Type hack to prevent {@link Tippy} from being imported as an object (ES module)
 * in production when it is imported within another component.
 * @see https://github.com/Hacker0x01/react-datepicker/issues/3834#issuecomment-1451662259
 */
const ReactTippy = Tippy.default ?? Tippy;
/**
 * Hide tooltips when they and their children lose focus.
 * @see https://atomiks.github.io/tippyjs/v6/plugins/#hideonpopperblur
 * @type {{defaultValue: boolean, name: string, fn(*=): {onCreate(): void}}}
 */
const hideOnPopperBlur = {
  name: 'hideOnPopperBlur',
  defaultValue: true,
  fn(instance) {
    return {
      onCreate() {
        instance.popper.addEventListener('focusout', event => {
          if (instance.props.hideOnPopperBlur && event.relatedTarget && !instance.popper.contains(event.relatedTarget)) {
            instance.hide();
          }
        });
      }
    };
  }
};
/**
 * Additional {@link Partial<PopperOptions>} options.
 */
const popperOptions = {
  // [2]
  modifiers: [{
    name: 'flip',
    options: {
      /** @TODO: Check position values with {@link TooltipProps.placement}. */
      fallbackPlacements: ['bottom', 'right']
    }
  }, {
    name: 'preventOverflow',
    options: {
      altAxis: true,
      tether: false
    }
  }]
};
/**
 * Bronson Tooltip component.
 * @desc Tooltips are user-triggered messages that provide additional information about a page element or feature.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tooltip.html
 * @constructor
 */
function Tooltip({
  alwaysVisible,
  appendToBody,
  children,
  className,
  content,
  placement = 'bottom',
  ...otherProps
}) {
  const buttonClassNameList = classNames({
    'c-tooltip': true
  }, className).trim();
  function renderChildren() {
    if (typeof children === 'string') {
      return /*#__PURE__*/React__default.createElement("button", {
        type: "button",
        className: buttonClassNameList
      }, children);
    }
    return children;
  }
  const triggerObj = useMemo(() => {
    if (alwaysVisible) {
      return {
        trigger: 'manual'
      };
    }
    return {};
  }, [alwaysVisible]);
  /**
   * Set the `z-index` of the parent element high enough and disable any potential
   * transitioning of it. `z-index: 2001` is inherited from the header setting in
   * {@see packages/bronson-default/src/scss/01-settings/_settings.z-layers.scss}
   * @param {Instance} instance - A tippy instance is an individual tippy object.
   */
  const onTrigger = useCallback(instance => {
    /**
     * Prevent changes to the tooltip when it was appended to the `document.body`.
     */
    if (!appendToBody) {
      instance?.reference?.parentElement?.style.setProperty('transition-duration', '0s');
      instance?.reference?.parentElement?.style.setProperty('z-index', '2001');
    }
  }, []);
  /**
   * Reset the `z-index` and the `transition-duration` from the {onTrigger} method.
   * @param {Instance} instance - A tippy instance is an individual tippy object.
   */
  const onHidden = useCallback(instance => {
    /**
     * Prevent changes to the tooltip when it was appended to the `document.body`.
     */
    if (!appendToBody) {
      instance?.reference?.parentElement?.style.removeProperty('z-index');
      instance?.reference?.parentElement?.style.removeProperty('transition-duration');
    }
  }, []);
  return /*#__PURE__*/React__default.createElement(ReactTippy, {
    allowHTML: true,
    animation: "default",
    appendTo: appendToBody ? () => document.body : undefined,
    aria: {
      content: 'describedby',
      expanded: 'auto'
    },
    arrow: true,
    content: content,
    hideOnClick: !alwaysVisible,
    interactive: true,
    onHidden: onHidden,
    onTrigger: onTrigger,
    placement: placement,
    plugins: [hideOnPopperBlur],
    popperOptions: popperOptions,
    showOnCreate: alwaysVisible,
    theme: "default",
    zIndex: appendToBody ? 2001 : undefined,
    ...triggerObj,
    ...otherProps
  }, renderChildren());
}

/*
 * Bronson InfoIcon component.
 */
function InfoIcon({
  children,
  className,
  element = 'button',
  icon,
  tippyProps,
  testId,
  ...otherProps
}) {
  const CustomElement = `${element}`;
  const buttonClassNameList = classNames('c-icon', 'c-info-icon', {
    [`c-icon--[${icon}]`]: icon
  }, className).trim();
  const typeProp = element === 'button' ? {
    type: 'button'
  } : {};
  return /*#__PURE__*/React__default.createElement(Tooltip, {
    content: children,
    ...tippyProps
  }, /*#__PURE__*/React__default.createElement(CustomElement, {
    ...otherProps,
    "data-testid": testId,
    ...typeProp,
    className: buttonClassNameList,
    "data-tippy-content": children,
    "data-tippy-placement": children && 'top'
  }));
}

/**
 * Hook used to apply conditional rendering on hidden elements.
 * Use in case performance issues occur, e.g. when hidden elements take a huge amount in the DOM or
 * have side effects like fetching data.
 * Internally it manages a rendered state for a list of items.
 *
 * Use isRendered to conditional render the element with hidden/aria-hidden.
 * Call setRendered when the element turns visible, typically hidden=false.
 *
 * @param {*} lazy when false, it will not check the rendered state but returns always true (-> eager rendering)
 * @returns object as { isRendered, setRendered } with isRendered returning the current rendered state and
 *                 setRendered to set the rendered state for an item
 */
const useLazyRender = (lazy = true) => {
  const [rendered, setRendered] = useState([]);
  return {
    isRendered: id => !lazy || rendered.indexOf(id) >= 0,
    setRendered: id => {
      if (rendered.findIndex(entry => entry === id) < 0) {
        setRendered([...rendered, id]);
      }
    }
  };
};

const AccordionContext$1 = /*#__PURE__*/React__default.createContext(null);
/**
 * Bronson Accordion component.
 *
 * https://bronson.vwfs.tools/default/components/detail/bronson-accordion.html
 *
 * @constructor
 */
function Accordion({
  children,
  className,
  headerSplit,
  itemKeyProp,
  lazyRender = false,
  multipleOpen,
  openItems,
  onChange,
  withBackground,
  testId,
  ...otherProps
}) {
  const isControlled = openItems !== undefined;
  /**
   * Construct a default state for the context provider.
   * @type {Array<Exclude<any, boolean | null | undefined>>}
   */
  const defaultOpen = isControlled ? openItems : React__default.Children.map(children, child => {
    if (child?.props?.defaultOpen) {
      return child?.props?.[child?.props?.itemKeyProp] ?? child?.props?.title;
    }
  }) ?? [];
  const [open, setOpen] = useState(multipleOpen ? defaultOpen : defaultOpen.slice(0, 1));
  useEffect(() => {
    if (isControlled) {
      setOpen(openItems);
    }
  }, [openItems]);
  const {
    isRendered,
    setRendered
  } = useLazyRender(lazyRender);
  const isOpen = itemKey => {
    return open.indexOf(itemKey) >= 0;
  };
  const setOpenAndNotify = items => {
    setOpen(items);
    onChange && onChange(items);
  };
  const updateOpen = (itemKey, shouldOpen) => {
    const index = open.findIndex(entry => entry === itemKey);
    if (multipleOpen) {
      // is not yet open + shouldOpen -> open
      if (index < 0 && shouldOpen) {
        open.push(itemKey);
        setOpenAndNotify([...open]);
      } else if (index >= 0 && !shouldOpen) {
        // is open + !shouldOpen -> close
        open.splice(index, 1);
        setOpenAndNotify([...open]);
      }
    } else if (shouldOpen) {
      // is not open -> open
      setOpenAndNotify([itemKey]);
    } else {
      // is open -> close
      setOpenAndNotify([]);
    }
    if (shouldOpen) {
      setRendered(itemKey);
    }
  };
  /*
   * Change open state for the given item (ref) without the need to know the current state.
   */
  const toggleOpen = itemKey => {
    updateOpen(itemKey, !isOpen(itemKey));
  };
  const divClassNameList = classNames('c-accordion', {
    // Main class modifier convenience prop extension.
    'c-accordion--bg-panel': withBackground,
    // Convenience prop from Bronson variants.
    'c-accordion--header-split': headerSplit // Convenience prop from Bronson variants.
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, /*#__PURE__*/React__default.createElement(AccordionContext$1.Provider, {
    value: {
      isOpen,
      toggleOpen,
      updateOpen,
      itemKeyProp,
      isRendered
    }
  }, children));
}
Accordion.displayName = 'Accordion';
/**
 * Bronson AccordionItem component.
 * @internal
 * @returns {JSX.Element} - The Accordion component.
 * @constructor
 */
function AccordionItem(props) {
  const {
    children,
    className,
    contentClassName,
    defaultOpen,
    icon,
    subtitle,
    title,
    titleLevel = 3,
    titleSplit,
    tooltip,
    testId,
    ...otherProps
  } = props;
  /**
   * Track onAnimationEnd expanded/collapsed state.
   */
  const [isExpanded, setIsExpanded] = useState(defaultOpen ?? false);
  const [isCollapsed, setIsCollapsed] = useState(!defaultOpen);
  const context = useContext(AccordionContext$1);
  const itemKey = context?.itemKeyProp ? props[context.itemKeyProp] : title;
  const HeadingElement = `h${titleLevel}`;
  const childDivClassNameList = classNames('c-accordion__header', {
    'is-active': context?.isOpen(itemKey)
  }, className).trim();
  const contentDivClassNameList = classNames('c-accordion__content', contentClassName).trim();
  const panelClassNameList = classNames('c-accordion__panel', {
    /**
     * Async because triggered via onTransitionEnd.
     * Make sure the async state classes are just set according
     * to the {@link context?.isOpen} state. `.is-expanded` is only applied
     * after expanding the panel, and is immediately removed at the beginning of collapsing.
     */
    'is-expanded': context?.isOpen(itemKey) && isExpanded,
    'is-collapsed': !context?.isOpen(itemKey) && isCollapsed
  }).trim();
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: childDivClassNameList,
    onClick: () => {
      context?.toggleOpen(itemKey);
      /**
       * Remove the expanded state directly onclick when the accordion is closing.
       */
      if (!context?.isOpen(itemKey)) {
        setIsExpanded(false);
      } else if (context?.isOpen(itemKey)) {
        setIsCollapsed(false);
      }
    },
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-icon"
  }), /*#__PURE__*/React__default.createElement(HeadingElement, {
    className: "c-accordion__title"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label",
    "aria-expanded": context?.isOpen(itemKey),
    role: "button",
    tabIndex: 0
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text"
  }, title, icon && (/*#__PURE__*/React__default.createElement(InfoIcon, {
    className: "c-accordion__title-info-icon",
    icon: icon
  }, tooltip))), titleSplit && /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text"
  }, titleSplit))), subtitle && /*#__PURE__*/React__default.createElement("p", {
    className: "c-accordion__subtitle"
  }, subtitle)), context?.isRendered(itemKey) && (
  /*#__PURE__*/
  /**
   * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
   * @see https://github.com/facebook/react/pull/24730
   * @TODO: Remove once React supports direct `[inert]` usage.
   * @see BRON-11871
   */
  React__default.createElement("div", {
    className: panelClassNameList,
    "aria-hidden": !context?.isOpen(itemKey),
    // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
    inert: context?.isOpen(itemKey) ? null : '',
    onTransitionEnd: event => {
      const panel = event?.target;
      /**
       * Add class when transition has finished as trigger that can
       * be used in CSS.
       */
      if (panel?.hasAttribute('aria-hidden')) {
        if (panel?.matches('[aria-hidden="false"]')) {
          setIsExpanded(true);
        } else {
          setIsCollapsed(true);
        }
      }
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: contentDivClassNameList
  }, children))));
}
Accordion.Item = AccordionItem;
AccordionItem.displayName = 'Accordion.Item';

const gridElementId = 'float-grid';
const gridElementClass = 'o-float-grid';
const gridAreaClass = 'o-float-grid__area';
const gridAreaDataAttribute = 'data-float-grid-area';
const gridAreaCssCustomProperty = '--float-grid-area';
/**
 * Available areas for placing elements.
 * @type {string[]}
 */
const floatGridAreas = ['top', 'bottom', 'top-left', 'top-center', 'top-right', 'center-left', 'center-center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'];
const getFloatGridCustomProp = ref => ref && getComputedStyle(ref).getPropertyValue(gridAreaCssCustomProperty).trim();
/**
 * Create the floatGrid root element.
 * @returns {HTMLElement} - The floatGrid root element with all areas appended.
 */
function createFloatGridRootElement(id = gridElementId) {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  rootContainer.className = gridElementClass;
  /**
   * Iterate over the {@link floatGridAreas} and create a child element for each.
   */
  floatGridAreas.forEach(area => {
    rootContainer.insertAdjacentHTML('afterbegin', `<div class="${gridAreaClass}" ${gridAreaDataAttribute}="${area}"></div>`);
  });
  return rootContainer;
}
/**
 * Appends element as the firstChild’s previous element sibling of body.
 * @param {HTMLElement} floatGridContainerElement - The float grid container element.
 */
function addFloatGridContainerElement(floatGridContainerElement) {
  document.body.insertBefore(floatGridContainerElement, document.body.firstElementChild);
}
/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SRR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * @param {String} area - The id of the target container, e.g. `modal` or `spotlight`.
 * @returns {HTMLElement} - The DOM node to use as the Portal target.
 * @example
 *      const target = useFloatGrid(id, [id]);
 *      return createPortal(children, target);
 */
function useFloatGrid(area) {
  const rootElemRef = useRef(null);
  useEffect(() => {
    /**
     * Bail out if no area was passed via hoop arguments
     */
    if (!area) {
      return;
    }
    // Look for existing target DOM element to append to.
    const existingParent = document.querySelector(`#${gridElementId}`);
    // Parent is either a new root or the existing DOM element.
    const parentElem = existingParent || createFloatGridRootElement(gridElementId);
    // If there is no existing DOM element, add a new one.
    if (!existingParent) {
      addFloatGridContainerElement(parentElem);
    }
    const childRef = rootElemRef.current;
    /**
     * If there is no firstRootChild and area is not explicitly set to the string `false`
     * (this is a Bronson setting to disable area placement), move the element to the respective area.
     */
    if (childRef && area && area !== 'false') {
      const floatGridAreaElement = parentElem.querySelector(`[${gridAreaDataAttribute}=${area}]`);
      if (floatGridAreaElement) {
        // Move the element to target Float Grid area.
        floatGridAreaElement.appendChild(childRef);
      } else {
        // Throw error if the area is not found.
        throw new TypeError(`Float Grid area ${floatGridAreaElement} does not exist.`);
      }
      if (!floatGridAreaElement?.childElementCount) {
        // eslint-disable-next-line consistent-return
        return floatGridAreaElement?.remove();
      }
    }
    // eslint-disable-next-line consistent-return
    return () => {
      childRef?.remove();
    };
  }, [area, rootElemRef]);
  /**
   * It’s important we evaluate this lazily:
   * - We need first render to contain the DOM element, so it shouldn’t happen
   *   in `useEffect`. We would normally put this in the `constructor()`.
   * - We can’t do `const rootElemRef = useRef(document.createElement('div))`,
   *   since this will run every single render (that’s a lot).
   * - We want the ref to consistently point to the same DOM element and only
   *   ever run once.
   * @see https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
   */
  function getRootElem() {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement('div');
      /**
       * This artificial wrap should not have any display semantics.
       */
      rootElemRef?.current?.style.setProperty('display', 'contents');
    }
    return rootElemRef.current;
  }
  return getRootElem();
}

/**
 * Make all semantic icons available via enum.
 * @see @vwfs-bronson/bronson-default/src/scss/01-settings/_settings.icon-mapping.scss
 */
var IconName;
(function (IconName) {
  IconName["ACCESSOIRES"] = "semantic-accessoires";
  IconName["ADD"] = "semantic-add";
  IconName["AIR_FILTER"] = "semantic-air-filter";
  IconName["ARROW_RIGHT"] = "semantic-arrow-right";
  IconName["BACK"] = "semantic-back";
  IconName["BENEFIT_FINANCE"] = "semantic-benefit-finance";
  IconName["BRAKE_FLUID"] = "semantic-brake-fluid";
  IconName["BRIEFCASE"] = "semantic-briefcase";
  IconName["BROCHURE"] = "semantic-brochure";
  IconName["CALCULATOR"] = "semantic-calculator";
  IconName["CALENDAR"] = "semantic-calendar";
  IconName["CAR_BODY_TYPE_CABRIO"] = "semantic-car-body-type-cabrio";
  IconName["CAR_BODY_TYPE_COMBI"] = "semantic-car-body-type-combi";
  IconName["CAR_BODY_TYPE_COUPE"] = "semantic-car-body-type-coupe";
  IconName["CAR_BODY_TYPE_CUV"] = "semantic-car-body-type-cuv";
  IconName["CAR_BODY_TYPE_HATCHBACK"] = "semantic-car-body-type-hatchback";
  IconName["CAR_BODY_TYPE_LIFTBACK"] = "semantic-car-body-type-liftback";
  IconName["CAR_BODY_TYPE_LIMOUSINE"] = "semantic-car-body-type-limousine";
  IconName["CAR_BODY_TYPE_MPV"] = "semantic-car-body-type-mpv";
  IconName["CAR_BODY_TYPE_SMALL_CAR"] = "semantic-car-body-type-small-car";
  IconName["CAR_BODY_TYPE_SUV"] = "semantic-car-body-type-suv";
  IconName["CAR_DOOR"] = "semantic-car-door";
  IconName["CAR_HANDOVER"] = "semantic-car-handover";
  IconName["CAR_RACK_BIKE"] = "semantic-car-rack-bike";
  IconName["CAR_RACK_ROOFTOP"] = "semantic-car-rack-rooftop";
  IconName["CAR_RETURN"] = "semantic-car-return";
  IconName["CAR_SEAT_BABY"] = "semantic-car-seat-baby";
  IconName["CAR_SEAT_CHILD"] = "semantic-car-seat-child";
  IconName["CAR_SEAT"] = "semantic-car-seat";
  IconName["CAR_SIDE"] = "semantic-car-side";
  IconName["CAR_TOW_BAR"] = "semantic-car-tow-bar";
  IconName["CAR_VALUE"] = "semantic-car-value";
  IconName["CAR"] = "semantic-car";
  IconName["CHAT"] = "semantic-chat";
  IconName["CHECKMARK"] = "semantic-checkmark";
  IconName["CLOCK"] = "semantic-clock";
  IconName["CLOSE"] = "semantic-close";
  IconName["COLLAPSE"] = "semantic-collapse";
  IconName["CONFIGURATION_DETAILS"] = "semantic-configuration-details";
  IconName["CONTACT"] = "semantic-contact";
  IconName["COOKIE_SETTINGS"] = "semantic-cookie-settings";
  IconName["CROSSED_OUT"] = "semantic-crossed-out";
  IconName["DEALER"] = "semantic-dealer";
  IconName["DELETE"] = "semantic-delete";
  IconName["DETAILS"] = "semantic-details";
  IconName["DIESEL"] = "semantic-diesel";
  IconName["DISLIKE"] = "semantic-dislike";
  IconName["DOWNLOAD"] = "semantic-download";
  IconName["EDIT"] = "semantic-edit";
  IconName["ELECTRIC"] = "semantic-electric";
  IconName["EMISSION"] = "semantic-emission";
  IconName["ENGINE_OIL"] = "semantic-engine-oil";
  IconName["ENGINE"] = "semantic-engine";
  IconName["ENTER_FULLSCREEN"] = "semantic-enter-fullscreen";
  IconName["EQUALSIGN"] = "semantic-equalsign";
  IconName["ERROR"] = "semantic-error";
  IconName["EXIT_FULLSCREEN"] = "semantic-exit-fullscreen";
  IconName["EXPAND"] = "semantic-expand";
  IconName["FAQ"] = "semantic-faq";
  IconName["FAVORITE"] = "semantic-favorite";
  IconName["FEEDBACK"] = "semantic-feedback";
  IconName["FILE"] = "semantic-file";
  IconName["FILTER"] = "semantic-filter";
  IconName["FORWARD"] = "semantic-forward";
  IconName["FOUR_WHEEL_DRIVE"] = "semantic-four-wheel-drive";
  IconName["FRONT_WHEEL_DRIVE"] = "semantic-front-wheel-drive";
  IconName["FS_PAYMENT"] = "semantic-fs-payment";
  IconName["FUEL_NATURAL_GAS"] = "semantic-fuel-natural-gas";
  IconName["FUEL"] = "semantic-fuel";
  IconName["GEAR_OIL"] = "semantic-gear-oil";
  IconName["SHARE_GENERIC"] = "semantic-share-generic";
  IconName["GRID"] = "semantic-grid";
  IconName["GUARANTEE"] = "semantic-guarantee";
  IconName["HIDE"] = "semantic-hide";
  IconName["HYBRID"] = "semantic-hybrid";
  IconName["INFO"] = "semantic-info";
  IconName["LIKE"] = "semantic-like";
  IconName["LIST"] = "semantic-list";
  IconName["LOCATION"] = "semantic-location";
  IconName["LOCATOR"] = "semantic-locator";
  IconName["LOCK"] = "semantic-lock";
  IconName["LOGIN"] = "semantic-login";
  IconName["MAIL"] = "semantic-mail";
  IconName["MENU"] = "semantic-menu";
  IconName["MINUS"] = "semantic-minus";
  IconName["MOBILITY"] = "semantic-mobility";
  IconName["MORE"] = "semantic-more";
  IconName["NAVIGATION"] = "semantic-navigation";
  IconName["NEWS"] = "semantic-news";
  IconName["NOTIFICATION"] = "semantic-notification";
  IconName["OFFERS"] = "semantic-offers";
  IconName["PARAGRAPH"] = "semantic-paragraph";
  IconName["PAUSE"] = "semantic-pause";
  IconName["PERFORMANCE"] = "semantic-performance";
  IconName["PETROL"] = "semantic-petrol";
  IconName["PHONE"] = "semantic-phone";
  IconName["PLANE"] = "semantic-plane";
  IconName["PLAY"] = "semantic-play";
  IconName["PLUS"] = "semantic-plus";
  IconName["PREMIUM_L"] = "semantic-premium-l";
  IconName["PREMIUM_M"] = "semantic-premium-m";
  IconName["PREMIUM_S"] = "semantic-premium-s";
  IconName["PRINT"] = "semantic-print";
  IconName["QUALITY"] = "semantic-quality";
  IconName["RELOAD"] = "semantic-reload";
  IconName["SAFETY_CHECK"] = "semantic-safety-check";
  IconName["SAVE_CLOUD"] = "semantic-save-cloud";
  IconName["SAVE_LOCAL"] = "semantic-save-local";
  IconName["SEARCH"] = "semantic-search";
  IconName["SERVICE"] = "semantic-service";
  IconName["SETTINGS"] = "semantic-settings";
  IconName["SHARE"] = "semantic-share";
  IconName["SHOW"] = "semantic-show";
  IconName["SIGNATURE_DIGITAL"] = "semantic-signature-digital";
  IconName["SMARTPHONE"] = "semantic-smartphone";
  IconName["SNOW_CHAINS"] = "semantic-snow-chains";
  IconName["SOCIAL_FACEBOOK"] = "semantic-social-facebook";
  IconName["SOCIAL_INSTAGRAM"] = "semantic-social-instagram";
  IconName["SOCIAL_LINKEDIN"] = "semantic-social-linkedin";
  IconName["SOCIAL_PINTEREST"] = "semantic-social-pinterest";
  IconName["SOCIAL_RSS"] = "semantic-social-rss";
  /**
   * @DEPRECATED: Remove in v11.
   * @type {IconName.SOCIAL_TWITTER}
   */
  IconName["SOCIAL_TWITTER"] = "semantic-social-twitter";
  IconName["SOCIAL_TIKTOK"] = "semantic-social-tiktok";
  IconName["SOCIAL_VIMEO"] = "semantic-social-vimeo";
  IconName["SOCIAL_WHATSAPP"] = "semantic-social-whatsapp";
  IconName["SOCIAL_X"] = "semantic-social-x";
  IconName["SOCIAL_XING"] = "semantic-social-xing";
  IconName["SOCIAL_YOUTUBE"] = "semantic-social-youtube";
  IconName["SORT_ASC"] = "semantic-sort-asc";
  IconName["SORT_DESC"] = "semantic-sort-desc";
  IconName["SPARK_PLUG"] = "semantic-spark-plug";
  IconName["STAR_FILLED"] = "semantic-star-filled";
  IconName["STAR_HALF"] = "semantic-star-half";
  IconName["STAR_OUTLINE"] = "semantic-star-outline";
  IconName["STEERING_WHEEL"] = "semantic-steering-wheel";
  IconName["STOP"] = "semantic-stop";
  IconName["SUCCESS"] = "semantic-success";
  IconName["SWITCH"] = "semantic-switch";
  IconName["TIRES_WINTER"] = "semantic-tires-winter";
  IconName["TRANSMISSION_AUTOMATIC"] = "semantic-transmission-automatic";
  IconName["TRANSMISSION_MANUAL"] = "semantic-transmission-manual";
  IconName["UP"] = "semantic-up";
  IconName["UPLOAD"] = "semantic-upload";
  IconName["USER"] = "semantic-user";
  IconName["VOLUME_DOWN"] = "semantic-volume-down";
  IconName["VOLUME_UP"] = "semantic-volume-up";
  IconName["VOLUME"] = "semantic-volume";
  IconName["WARNING"] = "semantic-warning";
  IconName["ZOOM_IN"] = "semantic-zoom-in";
  IconName["ZOOM_OUT"] = "semantic-zoom-out";
})(IconName || (IconName = {}));
var IconSize;
(function (IconSize) {
  IconSize["XXSMALL"] = "xxsmall";
  IconSize["XSMALL"] = "xsmall";
  IconSize["SMALL"] = "small";
  IconSize["MEDIUM"] = "medium";
  IconSize["LARGE"] = "large";
  IconSize["XLARGE"] = "xlarge";
  IconSize["XXLARGE"] = "xxlarge";
})(IconSize || (IconSize = {}));
/**
 * Bronson Icon component.
 * @constructor
 */
function Icon({
  className,
  name,
  size,
  viaCss,
  ...otherProps
}) {
  const classNameList = classNames(className, {
    'c-icon': !viaCss,
    [`c-icon--[${name}]`]: name && !viaCss,
    [`c-icon--${size}`]: size && !viaCss
  }).trim();
  return /*#__PURE__*/React__default.createElement("i", {
    className: classNameList,
    "aria-hidden": "true",
    role: "img",
    ...otherProps
  });
}

/**
 * Bronson button component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-buttons.html
 *
 * @constructor
 */
function Button({
  action,
  ariaExpanded,
  ariaLabel,
  children,
  className,
  disabled,
  element = 'button',
  full,
  hiddenLabel,
  icon,
  iconClassName,
  iconReversed,
  iconViaCss,
  incognito,
  link,
  onClick,
  round,
  secondary,
  simple,
  small,
  title,
  truncate,
  testId,
  ...otherProps
}) {
  /**
   * This assigns the `element` (`button-element` in Bronson) prop to a variable
   * which is in PascalCase, because custom component tags names must start with
   * an upperCase letter as lower-case tags are reserved for HTML.
   * @see https://reactjs.org/docs/jsx-in-depth.html#html-tags-vs.-react-components
   */
  // @ts-ignore
  const CustomElement = typeof element === 'string' ? `${element}` : element;
  const buttonClassNameList = classNames('c-btn', {
    'c-btn--icon': icon && !link,
    // Automatically assign the icon-only class when icon is set and no label is assigned.
    'c-btn--icon-only': icon && !link && (!children || hiddenLabel),
    // Convenience props from Bronson variants.
    'c-btn--secondary': secondary,
    'c-btn--action': action,
    'c-btn--full': full,
    'c-btn--incognito': incognito,
    'c-btn--link': link,
    'c-btn--small': small,
    'c-btn--round': round,
    'c-btn--simple': simple,
    'c-btn--truncate': truncate,
    // Do not apply reverse logic on links. They are reversed automatically.
    'c-btn--reversed': icon && !link && iconReversed
  }, className).trim();
  const iClassNameList = classNames('c-btn__icon', iconClassName).trim();
  /**
   * Throw an accessibility warning when an icon-only is used without a proper label.
   */
  if ((icon || iconViaCss) && !children && !ariaLabel) {
    console.warn('Bronson-React: <Button> with `icon` or `iconViaCSS` set requires the `ariaLabel` prop.');
  }
  function renderIfButtonIcon() {
    if (icon || iconViaCss) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon,
        viaCss: iconViaCss
      });
    }
    return null;
  }
  const spanClassNameList = classNames({
    'c-btn__text ': true,
    'u-visually-hidden ': hiddenLabel
  }).trim();
  function renderIfButtonLabel() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: spanClassNameList
      }, children);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement(CustomElement, {
    "aria-disabled": disabled && 'true',
    "aria-expanded": ariaExpanded,
    "aria-label": ariaLabel,
    className: buttonClassNameList,
    "data-testid": testId,
    disabled: disabled,
    onClick: onClick,
    title: title,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderIfButtonLabel(), renderIfButtonIcon()));
}

/**
 * Simple UUID v4 function to create unique identifier based on {@link window.crypto}.
 * Please refer to {@see https://developer.mozilla.org/en-US/docs/Glossary/UUID} regarding
 * information around collision safety and implementation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto
 *
 * @param {string} [prefix='bron-'] - Optional prefix to prepend the unique identifier. Defaults to 'bron-'.
 * @returns {string} - Returns a unique identifier.
 */
function uuidv4(prefix = 'bron-') {
  return window.crypto?.randomUUID ? `${prefix}${window.crypto?.randomUUID()}` : `${prefix}${window.crypto?.getRandomValues(new Uint32Array(1))}-${window.crypto?.getRandomValues(new Uint32Array(1))}`;
}

/**
 * Bronson Toggle object.
 * The `toggle` pattern is an accessible content-reveal object.
 * It can show/hide (or expand/collapse) a {@link targetRef} element.
 * It can be used standalone or placed within other components to reveal/hide
 * a target element. Each Toggle object has a 1-to-1 relationship to its target,
 * but a target can be controlled via multiple `toggle` patterns.
 * @see https://bronson.vwfs.tools/default/docs/objects/toggle.html
 * @constructor
 */
function Toggle$1({
  className,
  defaultHidden = true,
  hidden,
  id,
  labelHidden,
  labelUnhidden,
  onClick,
  targetId,
  targetRef,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('o-toggle', className).trim();
  const [isHidden, setIsHidden] = useState(defaultHidden);
  const [toggleId] = useState(id ?? uuidv4());
  /**
   * Handle side effects for the {@link targetRef}.
   */
  useEffect(() => {
    if (targetRef?.current) {
      targetRef?.current.setAttribute('aria-hidden', `${hidden ?? isHidden}`);
      if (hidden ?? isHidden) {
        targetRef?.current.setAttribute('inert', '');
      } else {
        targetRef?.current.removeAttribute('inert');
      }
    }
    /**
     * Cleanup function.
     */
    return () => {};
  }, [isHidden, targetRef, hidden]);
  useEffect(() => {
    if (targetRef?.current) {
      const ariaLabelledBy = targetRef?.current.getAttribute('aria-labelledby');
      const ariaLabelledByValue = ariaLabelledBy !== toggleId && ariaLabelledBy ? `${ariaLabelledBy} ${toggleId}` : toggleId;
      targetRef?.current.setAttribute('aria-labelledby', ariaLabelledByValue);
    }
    /**
     * Cleanup function.
     */
    return () => {};
  }, [targetRef, toggleId]);
  /**
   * Handle click events on the Toggle button.
   * @type {(function(*): void)|*}
   */
  const handleClick = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    /**
     * Only change internal state when in uncontrolled mode.
     */
    if (!hidden) {
      setIsHidden(!isHidden);
    }
    /**
     * Call custom callback.
     */
    onClick?.(hidden ?? isHidden);
  }, [isHidden, onClick]);
  return /*#__PURE__*/React__default.createElement(Button, {
    className: classNameList,
    id: toggleId,
    onClick: handleClick,
    ariaExpanded: `${!(hidden ?? isHidden)}`,
    "aria-controls": targetId,
    testId: testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "o-toggle__label o-toggle__label--target-hidden"
  }, labelHidden), /*#__PURE__*/React__default.createElement("span", {
    className: "o-toggle__label o-toggle__label--target-unhidden"
  }, labelUnhidden));
}

/**
 * Wrapper component to group action/incognito buttons.
 * The ActionButtonsContainer is a container component without specific styling that places action/incognito buttons in the FloatGrid area.
 * Place the container anywhere in your document and the contained buttons will be automatically moved to the FloatGrid.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-action-buttons-container.html
 * @constructor
 */
function ActionButtonsContainer({
  children,
  className,
  labelCollapsed,
  labelExpanded,
  testId,
  ...otherProps
}) {
  const [area, setArea] = useState(null);
  const [scrollCollapsed, setScrollCollapsed] = useState(false);
  const floatGrid = useFloatGrid(area);
  const ref = useRef(null);
  const itemsRef = useRef(null);
  useEffect(() => {
    if (!area) {
      setTimeout(() => {
        // Get custom prop from CSS.
        setArea(getFloatGridCustomProp(ref?.current));
      }, 0);
    }
  }, [ref, area]);
  useEffect(() => {
    const handleScroll = debounce(() => requestAnimationFrame(() => setScrollCollapsed(window.scrollY > 250)), 250);
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  });
  const classNameList = classNames('c-action-buttons-container', className).trim();
  const toggleClassNameList = classNames('c-action-buttons-container__toggle', {
    'is-collapsed': scrollCollapsed
  }).trim();
  const comp = /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ref: ref,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-action-buttons-container__items",
    id: "action-buttons-container-items",
    ref: itemsRef
  }, React__default.Children.map(children, child => {
    if (! /*#__PURE__*/isValidElement(child)) {
      return false;
    }
    return /*#__PURE__*/React__default.cloneElement(child, {
      className: 'c-action-buttons-container__item'
    });
  })), /*#__PURE__*/React__default.createElement(Toggle$1, {
    className: toggleClassNameList,
    defaultHidden: true,
    id: "action-buttons-container-items-toggle",
    labelHidden: labelCollapsed,
    labelUnhidden: labelExpanded,
    targetId: "action-buttons-container-items",
    targetRef: itemsRef,
    /**
     * Pass in {@link Button.propTypes}.
     */
    action: true,
    iconClassName: "c-action-buttons-container__toggle__icon",
    iconViaCss: true,
    incognito: true
  }));
  return area && floatGrid ? /*#__PURE__*/createPortal(comp, floatGrid) : comp;
}

/**
 * Bronson BackToTop component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-back-to-top.html
 *
 * @constructor
 */
function BackToTop({
  children,
  className,
  onClick,
  testId,
  ...otherProps
}) {
  /**
   * @type {string|(function(): void)}
   */
  const handleClick = onClick ?? (() => window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  }));
  const divClassNameList = classNames('c-back-to-top', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-back-to-top__pagewrap"
  }, /*#__PURE__*/React__default.createElement(Button, {
    iconClassName: "c-back-to-top__icon",
    iconViaCss: true,
    link: true,
    simple: true,
    small: true,
    onClick: handleClick
  }, children)));
}

var BadgeState;
(function (BadgeState) {
  BadgeState["ERROR"] = "error";
  BadgeState["NEGATIVE"] = "negative";
  BadgeState["NOTIFICATION"] = "notification";
  BadgeState["POSITIVE"] = "positive";
  BadgeState["SUCCESS"] = "success";
  BadgeState["WARNING"] = "warning";
})(BadgeState || (BadgeState = {}));
/*
 * Bronson Badge component.
 */
function Badge({
  children,
  className,
  icon,
  label,
  modifier,
  wrapperClass,
  testId,
  ...otherProps
}) {
  const wrapperClassNameList = classNames('c-badge__wrapper', {
    [`${wrapperClass}`]: wrapperClass
  }).trim();
  const badgeClassNameList = classNames({
    'c-badge': true,
    [`c-badge--${modifier}`]: modifier
  }, className).trim();
  const iClassNameList = classNames({
    'c-badge__icon': true
  }).trim();
  function renderIfBadgeIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon
      });
    }
    return null;
  }
  function renderIfBadgeLabel() {
    if (label) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-badge__label"
      }, label);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("span", {
    ...otherProps,
    "data-testid": testId,
    className: wrapperClassNameList
  }, /*#__PURE__*/React__default.createElement("span", {
    className: badgeClassNameList
  }, children || /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderIfBadgeIcon())), renderIfBadgeLabel());
}

/*
 * Bronson Blockquote component.
 */
function Blockquote({
  children,
  footerSource,
  footerCite,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("blockquote", {
    ...otherProps,
    "data-testid": testId,
    className: "c-blockquote"
  }, /*#__PURE__*/React__default.createElement("p", {
    className: "c-blockquote__text"
  }, children), /*#__PURE__*/React__default.createElement("footer", {
    className: "c-blockquote__footer"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-blockquote__source"
  }, footerSource), /*#__PURE__*/React__default.createElement("cite", {
    className: "c-blockquote__cite"
  }, footerCite)));
}

/**
 * Render a simple HTMLAnchorElement either as normal link or {@link Link} from `react-router-dom`.
 * @constructor
 * @internal
 */
function ReactLink({
  children,
  className,
  href,
  normalLink,
  onClick,
  target,
  to,
  testId,
  ...otherProps
}) {
  /**
   * Optional link attributes depending on {@link normalLink}.
   * If we have a normal anchor link we only apply the `[href]` attribute,
   * if on the other hand we have a React Router {@link Link}, we apply `[to]`.
   * @type {{}}
   */
  const commonProps = {
    className: className,
    onClick,
    target: target,
    'data-testid': testId,
    ...otherProps
  };
  return normalLink ? (/*#__PURE__*/React__default.createElement("a", {
    ...commonProps,
    href: href
  }, children)) : (
  /*#__PURE__*/
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  React__default.createElement(Link, {
    ...commonProps,
    to: to || href
  }, children));
}

/**
 * Bronson Breadcrumb component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-breadcrumb.html
 *
 * @constructor
 */
function Breadcrumb({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("nav", {
    ...otherProps,
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement("ul", {
    className: "c-breadcrumb"
  }, children));
}
Breadcrumb.displayName = 'Breadcrumb';
/**
 * Bronson BreadcrumbItem component (nested).
 * @internal
 * @constructor
 */
function BreadcrumbItem({
  children,
  current,
  disabled,
  href,
  normalLink,
  onClick,
  state,
  testId,
  ...otherProps
}) {
  const aClassNameList = classNames('c-breadcrumb__link', {
    'is-current': current,
    'is-disabled': disabled,
    [`${state}`]: state
  }).trim();
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-breadcrumb__item",
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement(ReactLink, {
    className: aClassNameList,
    normalLink: normalLink,
    onClick: onClick,
    href: href,
    "aria-current": current ? 'page' : undefined,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-breadcrumb__label"
  }, children)));
}
BreadcrumbItem.displayName = 'Breadcrumb.Item';
Breadcrumb.Item = BreadcrumbItem;

/**
 * Bronson ButtonContainer object (component).
 * @constructor
 */
function ButtonContainer({
  children,
  className,
  testId,
  center,
  left,
  right,
  stretch,
  nav,
  vertical,
  noWrap,
  wrapReverse,
  reverse,
  leftSmall,
  centerSmall,
  rightSmall,
  verticalSmall,
  reverseSmall,
  ...otherProps
}) {
  const divClassNameList = classNames({
    'o-button-container ': true,
    // main class modifier convenience prop extension
    'o-button-container--center ': center,
    // Convenience prop
    'o-button-container--left ': left,
    // Convenience prop
    'o-button-container--right ': right,
    // Convenience prop
    'o-button-container--nav ': nav,
    // Convenience prop
    'o-button-container--stretch ': stretch,
    // Convenience prop
    'o-button-container--vertical ': vertical,
    // Convenience prop
    'o-button-container--nowrap ': noWrap,
    // Convenience prop
    'o-button-container--wrap-reverse ': wrapReverse,
    // Convenience prop
    'o-button-container--reverse ': reverse,
    // Convenience prop
    /**
     * `o-button-container--center-wrap` was replaced in Bronson.
     * @see https://jira.tools.platform.vwfs.io/browse/BRON-5562
     */
    'o-button-container--left@s ': leftSmall,
    // Convenience prop
    'o-button-container--center@s ': centerSmall,
    // Convenience prop
    'o-button-container--right@s ': rightSmall,
    // Convenience prop
    'o-button-container--vertical@s ': verticalSmall,
    // Convenience prop
    'o-button-container--reverse@s ': reverseSmall // Convenience prop
  }, className).trim();
  function renderChildren() {
    return React__default.Children.map(children, child => {
      if (! /*#__PURE__*/React__default.isValidElement(child)) {
        return null;
      }
      const buttonClassName = `o-button-container__button ${child.props.className ? child.props.className : ''}`;
      return /*#__PURE__*/React__default.cloneElement(child, {
        className: buttonClassName
      });
    });
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderChildren());
}

const ButtonGroupContext = /*#__PURE__*/React__default.createContext({});
/**
 * Bronson ButtonGroup component.
 * @constructor
 */
function ButtonGroup({
  auto,
  buttonGroupName,
  buttonGroupType,
  children,
  className,
  componentDescription,
  errorMessage,
  small,
  testId,
  ...otherProps
}) {
  const contextValue = {
    buttonGroupType,
    buttonGroupName
  };
  /**
   * @DEPRECATED Remove in v11.
   */
  function renderIfComponentDescription() {
    if (componentDescription) {
      return /*#__PURE__*/React__default.createElement("p", null, componentDescription);
    }
    return null;
  }
  const divClassNameList = classNames('c-button-group', {
    'c-button-group--auto': auto,
    'c-button-group--small': small
  }, className).trim();
  /**
   * @DEPRECATED Remove in v11.
   */
  function renderIfButtonGroupErrorMessage() {
    if (errorMessage) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: "c-error-message"
      }, errorMessage);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderIfComponentDescription(), /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(ButtonGroupContext.Provider, {
    value: contextValue
  }, children, " ")), renderIfButtonGroupErrorMessage());
}
ButtonGroup.displayName = 'ButtonGroup';
/**
 * Bronson ButtonGroupItem component (nested).
 * @constructor
 */
function ButtonGroupItem({
  handle,
  index,
  buttonGroupItemIcon,
  children,
  testId,
  ...otherProps
}) {
  const context = useContext(ButtonGroupContext);
  // eslint-disable-next-line no-param-reassign
  handle = handle || context.buttonGroupName;
  const iClassNameList = classNames({
    'c-button-group__icon': true
  }).trim();
  function renderIfButtonGroupItemIcon() {
    if (buttonGroupItemIcon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: buttonGroupItemIcon
      });
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("label", {
    "data-testid": testId,
    className: "c-button-group__item",
    htmlFor: `button-group-item-${handle}-${index}`
  }, /*#__PURE__*/React__default.createElement("input", {
    className: "c-button-group__input",
    type: context.buttonGroupType,
    name: `button-group-item-${handle}`,
    id: `button-group-item-${handle}-${index}`,
    ...otherProps
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-button-group__label"
  }, renderIfButtonGroupItemIcon(), children));
}
ButtonGroupItem.displayName = 'ButtonGroup.Item';
ButtonGroup.Item = ButtonGroupItem;

/**
 * The Callout component displays a prominent message with a call to action.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-callout.html
 *
 * @constructor
 */
function Callout({
  children,
  className,
  elementType = 'article',
  headingLevel = 4,
  media,
  title,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-callout', className).trim();
  const ComponentElement = `${elementType}`;
  const HeadingElement = `h${headingLevel}`;
  const renderMedia = () => {
    if (media) {
      const imageClassName = classNames('c-callout__image', media?.className).trim();
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-callout__media"
      }, /*#__PURE__*/React__default.createElement("img", {
        ...media,
        className: imageClassName
      }));
    }
    return null;
  };
  return /*#__PURE__*/React__default.createElement(ComponentElement, {
    className: classNameList,
    "data-theme": "brand",
    "data-testid": testId,
    ...otherProps
  }, renderMedia(), /*#__PURE__*/React__default.createElement(HeadingElement, {
    className: "c-callout__title"
  }, title), children);
}

/**
 * Bronson Card component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-cards.html
 *
 * @constructor
 */
function Card({
  additionalInfo,
  buttons,
  children,
  className,
  contentLarge,
  contentShort,
  defaultExpanded,
  disabled,
  element,
  error,
  expandable,
  expandableTestId,
  footer,
  footerContent,
  icon,
  imageAlt,
  imageProps,
  imageSrc,
  inactive,
  mediaElement,
  responsiveCollapse,
  scrollable,
  small,
  subtitle,
  subtitleClassName,
  success,
  title,
  titleClassName,
  titleLevel = 4,
  warning,
  testId,
  ...otherProps
}) {
  /**
   * Track the expanded state of the {@link Card}.
   */
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [responsiveCollapsed, setResponsiveCollapsed] = useState(true);
  const CustomTagCardElement = `${element}`;
  const bodyId = uuidv4('bron-card-body-');
  const expandablePanelId = uuidv4('bron-card-expandable-panel-');
  const customTagClassNameList = classNames('c-card', {
    'c-card--image': imageSrc || imageProps,
    'c-card--button-group': buttons && buttons.length > 0,
    'c-card--states': success || warning || error,
    'c-card--states-success': success,
    'c-card--states-warning': warning,
    'c-card--states-error': error,
    'c-card--inactive': inactive,
    'c-card--scrollable': scrollable,
    'c-card--expandable': expandable,
    'c-card--responsive-collapse': responsiveCollapse,
    'c-card--small': small
  }, className).trim();
  const expandableClassNameList = classNames('c-card__expandable', {
    'is-open': expanded
  }).trim();
  const expandableTriggerClassNameList = classNames('c-btn--card', 'c-card__expandable-btn', 'js-card-expandable__trigger' // Used in Bronson CSS.
  ).trim();
  const expandablePanelClassNameList = classNames('c-card__expandable-panel', {
    'is-closed': !expanded
  }).trim();
  const bodyClassNameList = classNames('c-card__body', {
    'js-is-ready': responsiveCollapse,
    // Used in Bronson CSS.
    'is-closed': responsiveCollapse && responsiveCollapsed
  }).trim();
  const expandablePanelStyle = expanded ? {} : {
    height: '0px',
    overflowY: 'hidden',
    visibility: 'hidden'
  };
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-card__icon"
      }, /*#__PURE__*/React__default.createElement(Icon, {
        name: icon
      }));
    }
    return null;
  }
  function renderIfImage() {
    if (imageSrc || imageProps) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-card__image"
      }, /*#__PURE__*/React__default.createElement("img", {
        src: imageSrc,
        alt: imageAlt,
        ...imageProps
      }));
    }
    return null;
  }
  const renderIfMedia = () => {
    if (mediaElement) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-card__image"
      }, mediaElement);
    }
    return null;
  };
  const subtitleClassNameList = classNames('c-card__subtitle', subtitleClassName).trim();
  function renderIfSubtitle() {
    if (subtitle) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: subtitleClassNameList
      }, subtitle);
    }
    return null;
  }
  function renderIfResponsiveCollapseButton() {
    if (responsiveCollapse) {
      return /*#__PURE__*/React__default.createElement("button", {
        className: "c-card--responsive-collapse__toggle",
        type: "button",
        "aria-label": "Toggle Card",
        "aria-expanded": responsiveCollapsed ? 'false' : 'true',
        "aria-controls": bodyId,
        disabled: inactive ? true : undefined,
        onClick: () => {
          setResponsiveCollapsed(!responsiveCollapsed);
        }
      });
    }
    return null;
  }
  const titleClassNameList = classNames('c-card__title', titleClassName).trim();
  function renderIfTitle() {
    if (title) {
      const HeadingElement = `h${titleLevel}`;
      return /*#__PURE__*/React__default.createElement("header", {
        className: "c-card__header"
      }, expandable || responsiveCollapse ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
        className: "c-card__header-items"
      }, /*#__PURE__*/React__default.createElement(HeadingElement, {
        className: titleClassNameList
      }, title), expandable && contentShort, renderIfResponsiveCollapseButton()))) : (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(HeadingElement, {
        className: titleClassNameList
      }, title))), renderIfSubtitle());
    }
    return null;
  }
  function renderIfButtons() {
    if (buttons) {
      return Array.isArray(buttons) ? /*#__PURE__*/React__default.createElement(ButtonContainer, {
        center: true
      }, buttons) : buttons;
    }
    return null;
  }
  function renderIfContentLarge() {
    if (contentLarge) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, contentLarge);
    }
    return null;
  }
  function renderIfIsExpandable() {
    /**
     * Render the expandable only if there is also content to reveal.
     */
    if (expandable && contentLarge) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
        className: expandableClassNameList,
        "aria-disabled": disabled && 'true'
      }, /*#__PURE__*/React__default.createElement(Button, {
        "aria-controls": expandablePanelId,
        "aria-expanded": expanded ? 'true' : 'false',
        ariaLabel: "Toggle Card",
        className: expandableTriggerClassNameList,
        "data-testid": expandableTestId,
        icon: "semantic-expand",
        iconClassName: "c-btn--card-icon",
        type: "button",
        disabled: inactive ? true : undefined,
        onClick: () => {
          setExpanded(!expanded);
        }
      }), /*#__PURE__*/React__default.createElement("div", {
        className: expandablePanelClassNameList,
        style: expandablePanelStyle,
        "aria-hidden": expanded ? 'false' : 'true',
        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
        inert: expanded ? null : '',
        id: expandablePanelId
      }, renderIfContentLarge())));
    }
    return null;
  }
  function renderIfAdditionalInfo() {
    if (additionalInfo) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "u-text-center"
      }, additionalInfo);
    }
    return null;
  }
  function renderIfFooter() {
    if (footer) {
      return /*#__PURE__*/React__default.createElement("footer", {
        className: "c-card__footer"
      }, renderIfButtons(), footerContent);
    }
    return null;
  }
  /**
   * Render the card body element.
   */
  function renderBody() {
    const expandableContent = renderIfIsExpandable();
    /**
     * Check whether {@link children} and/or {@link expandable}
     * with {@link contentLarge} is set. Please note that checking
     * for the content of the expandable is as {@link renderIfExpandable}
     * already checks for its content.
     */
    const hasContent = !!(children || expandableContent);
    if (hasContent) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: bodyClassNameList,
        id: responsiveCollapse ? bodyId : undefined
      }, children, expandableContent);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement(CustomTagCardElement, {
    ...otherProps,
    "data-testid": testId,
    className: customTagClassNameList
  }, renderIfIcon(), renderIfImage(), renderIfMedia(), renderIfTitle(), renderBody(), renderIfAdditionalInfo(), renderIfFooter());
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var fullscreen = {exports: {}};

var js = {exports: {}};

var flickity = {exports: {}};

var evEmitter$1 = {exports: {}};

/**
 * EvEmitter v1.1.0
 * Lil' event emitter
 * MIT License
 */
var evEmitter = evEmitter$1.exports;

var hasRequiredEvEmitter;

function requireEvEmitter () {
	if (hasRequiredEvEmitter) return evEmitter$1.exports;
	hasRequiredEvEmitter = 1;
	(function (module) {
		/* jshint unused: true, undef: true, strict: true */

		( function( global, factory ) {
		  // universal module definition
		  /* jshint strict: false */ /* globals define, module, window */
		  if ( module.exports ) {
		    // CommonJS - Browserify, Webpack
		    module.exports = factory();
		  } else {
		    // Browser globals
		    global.EvEmitter = factory();
		  }

		}( typeof window != 'undefined' ? window : evEmitter, function() {

		function EvEmitter() {}

		var proto = EvEmitter.prototype;

		proto.on = function( eventName, listener ) {
		  if ( !eventName || !listener ) {
		    return;
		  }
		  // set events hash
		  var events = this._events = this._events || {};
		  // set listeners array
		  var listeners = events[ eventName ] = events[ eventName ] || [];
		  // only add once
		  if ( listeners.indexOf( listener ) == -1 ) {
		    listeners.push( listener );
		  }

		  return this;
		};

		proto.once = function( eventName, listener ) {
		  if ( !eventName || !listener ) {
		    return;
		  }
		  // add event
		  this.on( eventName, listener );
		  // set once flag
		  // set onceEvents hash
		  var onceEvents = this._onceEvents = this._onceEvents || {};
		  // set onceListeners object
		  var onceListeners = onceEvents[ eventName ] = onceEvents[ eventName ] || {};
		  // set flag
		  onceListeners[ listener ] = true;

		  return this;
		};

		proto.off = function( eventName, listener ) {
		  var listeners = this._events && this._events[ eventName ];
		  if ( !listeners || !listeners.length ) {
		    return;
		  }
		  var index = listeners.indexOf( listener );
		  if ( index != -1 ) {
		    listeners.splice( index, 1 );
		  }

		  return this;
		};

		proto.emitEvent = function( eventName, args ) {
		  var listeners = this._events && this._events[ eventName ];
		  if ( !listeners || !listeners.length ) {
		    return;
		  }
		  // copy over to avoid interference if .off() in listener
		  listeners = listeners.slice(0);
		  args = args || [];
		  // once stuff
		  var onceListeners = this._onceEvents && this._onceEvents[ eventName ];

		  for ( var i=0; i < listeners.length; i++ ) {
		    var listener = listeners[i];
		    var isOnce = onceListeners && onceListeners[ listener ];
		    if ( isOnce ) {
		      // remove listener
		      // remove before trigger to prevent recursion
		      this.off( eventName, listener );
		      // unset once flag
		      delete onceListeners[ listener ];
		    }
		    // trigger listener
		    listener.apply( this, args );
		  }

		  return this;
		};

		proto.allOff = function() {
		  delete this._events;
		  delete this._onceEvents;
		};

		return EvEmitter;

		})); 
	} (evEmitter$1));
	return evEmitter$1.exports;
}

var getSize = {exports: {}};

/*!
 * getSize v2.0.3
 * measure size of elements
 * MIT license
 */

var hasRequiredGetSize;

function requireGetSize () {
	if (hasRequiredGetSize) return getSize.exports;
	hasRequiredGetSize = 1;
	(function (module) {
		/* jshint browser: true, strict: true, undef: true, unused: true */
		/* globals console: false */

		( function( window, factory ) {
		  /* jshint strict: false */ /* globals define, module */
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory();
		  } else {
		    // browser global
		    window.getSize = factory();
		  }

		})( window, function factory() {

		// -------------------------- helpers -------------------------- //

		// get a number from a string, not a percentage
		function getStyleSize( value ) {
		  var num = parseFloat( value );
		  // not a percent like '100%', and a number
		  var isValid = value.indexOf('%') == -1 && !isNaN( num );
		  return isValid && num;
		}

		function noop() {}

		var logError = typeof console == 'undefined' ? noop :
		  function( message ) {
		    console.error( message );
		  };

		// -------------------------- measurements -------------------------- //

		var measurements = [
		  'paddingLeft',
		  'paddingRight',
		  'paddingTop',
		  'paddingBottom',
		  'marginLeft',
		  'marginRight',
		  'marginTop',
		  'marginBottom',
		  'borderLeftWidth',
		  'borderRightWidth',
		  'borderTopWidth',
		  'borderBottomWidth'
		];

		var measurementsLength = measurements.length;

		function getZeroSize() {
		  var size = {
		    width: 0,
		    height: 0,
		    innerWidth: 0,
		    innerHeight: 0,
		    outerWidth: 0,
		    outerHeight: 0
		  };
		  for ( var i=0; i < measurementsLength; i++ ) {
		    var measurement = measurements[i];
		    size[ measurement ] = 0;
		  }
		  return size;
		}

		// -------------------------- getStyle -------------------------- //

		/**
		 * getStyle, get style of element, check for Firefox bug
		 * https://bugzilla.mozilla.org/show_bug.cgi?id=548397
		 */
		function getStyle( elem ) {
		  var style = getComputedStyle( elem );
		  if ( !style ) {
		    logError( 'Style returned ' + style +
		      '. Are you running this code in a hidden iframe on Firefox? ' +
		      'See https://bit.ly/getsizebug1' );
		  }
		  return style;
		}

		// -------------------------- setup -------------------------- //

		var isSetup = false;

		var isBoxSizeOuter;

		/**
		 * setup
		 * check isBoxSizerOuter
		 * do on first getSize() rather than on page load for Firefox bug
		 */
		function setup() {
		  // setup once
		  if ( isSetup ) {
		    return;
		  }
		  isSetup = true;

		  // -------------------------- box sizing -------------------------- //

		  /**
		   * Chrome & Safari measure the outer-width on style.width on border-box elems
		   * IE11 & Firefox<29 measures the inner-width
		   */
		  var div = document.createElement('div');
		  div.style.width = '200px';
		  div.style.padding = '1px 2px 3px 4px';
		  div.style.borderStyle = 'solid';
		  div.style.borderWidth = '1px 2px 3px 4px';
		  div.style.boxSizing = 'border-box';

		  var body = document.body || document.documentElement;
		  body.appendChild( div );
		  var style = getStyle( div );
		  // round value for browser zoom. desandro/masonry#928
		  isBoxSizeOuter = Math.round( getStyleSize( style.width ) ) == 200;
		  getSize.isBoxSizeOuter = isBoxSizeOuter;

		  body.removeChild( div );
		}

		// -------------------------- getSize -------------------------- //

		function getSize( elem ) {
		  setup();

		  // use querySeletor if elem is string
		  if ( typeof elem == 'string' ) {
		    elem = document.querySelector( elem );
		  }

		  // do not proceed on non-objects
		  if ( !elem || typeof elem != 'object' || !elem.nodeType ) {
		    return;
		  }

		  var style = getStyle( elem );

		  // if hidden, everything is 0
		  if ( style.display == 'none' ) {
		    return getZeroSize();
		  }

		  var size = {};
		  size.width = elem.offsetWidth;
		  size.height = elem.offsetHeight;

		  var isBorderBox = size.isBorderBox = style.boxSizing == 'border-box';

		  // get all measurements
		  for ( var i=0; i < measurementsLength; i++ ) {
		    var measurement = measurements[i];
		    var value = style[ measurement ];
		    var num = parseFloat( value );
		    // any 'auto', 'medium' value will be 0
		    size[ measurement ] = !isNaN( num ) ? num : 0;
		  }

		  var paddingWidth = size.paddingLeft + size.paddingRight;
		  var paddingHeight = size.paddingTop + size.paddingBottom;
		  var marginWidth = size.marginLeft + size.marginRight;
		  var marginHeight = size.marginTop + size.marginBottom;
		  var borderWidth = size.borderLeftWidth + size.borderRightWidth;
		  var borderHeight = size.borderTopWidth + size.borderBottomWidth;

		  var isBorderBoxSizeOuter = isBorderBox && isBoxSizeOuter;

		  // overwrite width and height if we can get it from style
		  var styleWidth = getStyleSize( style.width );
		  if ( styleWidth !== false ) {
		    size.width = styleWidth +
		      // add padding and border unless it's already including it
		      ( isBorderBoxSizeOuter ? 0 : paddingWidth + borderWidth );
		  }

		  var styleHeight = getStyleSize( style.height );
		  if ( styleHeight !== false ) {
		    size.height = styleHeight +
		      // add padding and border unless it's already including it
		      ( isBorderBoxSizeOuter ? 0 : paddingHeight + borderHeight );
		  }

		  size.innerWidth = size.width - ( paddingWidth + borderWidth );
		  size.innerHeight = size.height - ( paddingHeight + borderHeight );

		  size.outerWidth = size.width + marginWidth;
		  size.outerHeight = size.height + marginHeight;

		  return size;
		}

		return getSize;

		}); 
	} (getSize));
	return getSize.exports;
}

var utils = {exports: {}};

var matchesSelector = {exports: {}};

/**
 * matchesSelector v2.0.2
 * matchesSelector( element, '.selector' )
 * MIT license
 */

var hasRequiredMatchesSelector;

function requireMatchesSelector () {
	if (hasRequiredMatchesSelector) return matchesSelector.exports;
	hasRequiredMatchesSelector = 1;
	(function (module) {
		/*jshint browser: true, strict: true, undef: true, unused: true */

		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory();
		  } else {
		    // browser global
		    window.matchesSelector = factory();
		  }

		}( window, function factory() {

		  var matchesMethod = ( function() {
		    var ElemProto = window.Element.prototype;
		    // check for the standard method name first
		    if ( ElemProto.matches ) {
		      return 'matches';
		    }
		    // check un-prefixed
		    if ( ElemProto.matchesSelector ) {
		      return 'matchesSelector';
		    }
		    // check vendor prefixes
		    var prefixes = [ 'webkit', 'moz', 'ms', 'o' ];

		    for ( var i=0; i < prefixes.length; i++ ) {
		      var prefix = prefixes[i];
		      var method = prefix + 'MatchesSelector';
		      if ( ElemProto[ method ] ) {
		        return method;
		      }
		    }
		  })();

		  return function matchesSelector( elem, selector ) {
		    return elem[ matchesMethod ]( selector );
		  };

		})); 
	} (matchesSelector));
	return matchesSelector.exports;
}

/**
 * Fizzy UI utils v2.0.7
 * MIT license
 */

var hasRequiredUtils;

function requireUtils () {
	if (hasRequiredUtils) return utils.exports;
	hasRequiredUtils = 1;
	(function (module) {
		/*jshint browser: true, undef: true, unused: true, strict: true */

		( function( window, factory ) {
		  // universal module definition
		  /*jshint strict: false */ /*globals define, module, require */

		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		      window,
		      requireMatchesSelector()
		    );
		  } else {
		    // browser global
		    window.fizzyUIUtils = factory(
		      window,
		      window.matchesSelector
		    );
		  }

		}( window, function factory( window, matchesSelector ) {

		var utils = {};

		// ----- extend ----- //

		// extends objects
		utils.extend = function( a, b ) {
		  for ( var prop in b ) {
		    a[ prop ] = b[ prop ];
		  }
		  return a;
		};

		// ----- modulo ----- //

		utils.modulo = function( num, div ) {
		  return ( ( num % div ) + div ) % div;
		};

		// ----- makeArray ----- //

		var arraySlice = Array.prototype.slice;

		// turn element or nodeList into an array
		utils.makeArray = function( obj ) {
		  if ( Array.isArray( obj ) ) {
		    // use object if already an array
		    return obj;
		  }
		  // return empty array if undefined or null. #6
		  if ( obj === null || obj === undefined ) {
		    return [];
		  }

		  var isArrayLike = typeof obj == 'object' && typeof obj.length == 'number';
		  if ( isArrayLike ) {
		    // convert nodeList to array
		    return arraySlice.call( obj );
		  }

		  // array of single index
		  return [ obj ];
		};

		// ----- removeFrom ----- //

		utils.removeFrom = function( ary, obj ) {
		  var index = ary.indexOf( obj );
		  if ( index != -1 ) {
		    ary.splice( index, 1 );
		  }
		};

		// ----- getParent ----- //

		utils.getParent = function( elem, selector ) {
		  while ( elem.parentNode && elem != document.body ) {
		    elem = elem.parentNode;
		    if ( matchesSelector( elem, selector ) ) {
		      return elem;
		    }
		  }
		};

		// ----- getQueryElement ----- //

		// use element as selector string
		utils.getQueryElement = function( elem ) {
		  if ( typeof elem == 'string' ) {
		    return document.querySelector( elem );
		  }
		  return elem;
		};

		// ----- handleEvent ----- //

		// enable .ontype to trigger from .addEventListener( elem, 'type' )
		utils.handleEvent = function( event ) {
		  var method = 'on' + event.type;
		  if ( this[ method ] ) {
		    this[ method ]( event );
		  }
		};

		// ----- filterFindElements ----- //

		utils.filterFindElements = function( elems, selector ) {
		  // make array of elems
		  elems = utils.makeArray( elems );
		  var ffElems = [];

		  elems.forEach( function( elem ) {
		    // check that elem is an actual element
		    if ( !( elem instanceof HTMLElement ) ) {
		      return;
		    }
		    // add elem if no selector
		    if ( !selector ) {
		      ffElems.push( elem );
		      return;
		    }
		    // filter & find items if we have a selector
		    // filter
		    if ( matchesSelector( elem, selector ) ) {
		      ffElems.push( elem );
		    }
		    // find children
		    var childElems = elem.querySelectorAll( selector );
		    // concat childElems to filterFound array
		    for ( var i=0; i < childElems.length; i++ ) {
		      ffElems.push( childElems[i] );
		    }
		  });

		  return ffElems;
		};

		// ----- debounceMethod ----- //

		utils.debounceMethod = function( _class, methodName, threshold ) {
		  threshold = threshold || 100;
		  // original method
		  var method = _class.prototype[ methodName ];
		  var timeoutName = methodName + 'Timeout';

		  _class.prototype[ methodName ] = function() {
		    var timeout = this[ timeoutName ];
		    clearTimeout( timeout );

		    var args = arguments;
		    var _this = this;
		    this[ timeoutName ] = setTimeout( function() {
		      method.apply( _this, args );
		      delete _this[ timeoutName ];
		    }, threshold );
		  };
		};

		// ----- docReady ----- //

		utils.docReady = function( callback ) {
		  var readyState = document.readyState;
		  if ( readyState == 'complete' || readyState == 'interactive' ) {
		    // do async to allow for other scripts to run. metafizzy/flickity#441
		    setTimeout( callback );
		  } else {
		    document.addEventListener( 'DOMContentLoaded', callback );
		  }
		};

		// ----- htmlInit ----- //

		// http://jamesroberts.name/blog/2010/02/22/string-functions-for-javascript-trim-to-camel-case-to-dashed-and-to-underscore/
		utils.toDashed = function( str ) {
		  return str.replace( /(.)([A-Z])/g, function( match, $1, $2 ) {
		    return $1 + '-' + $2;
		  }).toLowerCase();
		};

		var console = window.console;
		/**
		 * allow user to initialize classes via [data-namespace] or .js-namespace class
		 * htmlInit( Widget, 'widgetName' )
		 * options are parsed from data-namespace-options
		 */
		utils.htmlInit = function( WidgetClass, namespace ) {
		  utils.docReady( function() {
		    var dashedNamespace = utils.toDashed( namespace );
		    var dataAttr = 'data-' + dashedNamespace;
		    var dataAttrElems = document.querySelectorAll( '[' + dataAttr + ']' );
		    var jsDashElems = document.querySelectorAll( '.js-' + dashedNamespace );
		    var elems = utils.makeArray( dataAttrElems )
		      .concat( utils.makeArray( jsDashElems ) );
		    var dataOptionsAttr = dataAttr + '-options';
		    var jQuery = window.jQuery;

		    elems.forEach( function( elem ) {
		      var attr = elem.getAttribute( dataAttr ) ||
		        elem.getAttribute( dataOptionsAttr );
		      var options;
		      try {
		        options = attr && JSON.parse( attr );
		      } catch ( error ) {
		        // log error, do not initialize
		        if ( console ) {
		          console.error( 'Error parsing ' + dataAttr + ' on ' + elem.className +
		          ': ' + error );
		        }
		        return;
		      }
		      // initialize
		      var instance = new WidgetClass( elem, options );
		      // make available via $().data('namespace')
		      if ( jQuery ) {
		        jQuery.data( elem, namespace, instance );
		      }
		    });

		  });
		};

		// -----  ----- //

		return utils;

		})); 
	} (utils));
	return utils.exports;
}

var cell = {exports: {}};

var hasRequiredCell;

function requireCell () {
	if (hasRequiredCell) return cell.exports;
	hasRequiredCell = 1;
	(function (module) {
		// Flickity.Cell
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireGetSize()
		    );
		  } else {
		    // browser global
		    window.Flickity = window.Flickity || {};
		    window.Flickity.Cell = factory(
		        window,
		        window.getSize
		    );
		  }

		}( window, function factory( window, getSize ) {

		function Cell( elem, parent ) {
		  this.element = elem;
		  this.parent = parent;

		  this.create();
		}

		var proto = Cell.prototype;

		proto.create = function() {
		  this.element.style.position = 'absolute';
		  this.element.setAttribute( 'aria-hidden', 'true' );
		  this.x = 0;
		  this.shift = 0;
		};

		proto.destroy = function() {
		  // reset style
		  this.unselect();
		  this.element.style.position = '';
		  var side = this.parent.originSide;
		  this.element.style[ side ] = '';
		  this.element.removeAttribute('aria-hidden');
		};

		proto.getSize = function() {
		  this.size = getSize( this.element );
		};

		proto.setPosition = function( x ) {
		  this.x = x;
		  this.updateTarget();
		  this.renderPosition( x );
		};

		// setDefaultTarget v1 method, backwards compatibility, remove in v3
		proto.updateTarget = proto.setDefaultTarget = function() {
		  var marginProperty = this.parent.originSide == 'left' ? 'marginLeft' : 'marginRight';
		  this.target = this.x + this.size[ marginProperty ] +
		    this.size.width * this.parent.cellAlign;
		};

		proto.renderPosition = function( x ) {
		  // render position of cell with in slider
		  var side = this.parent.originSide;
		  this.element.style[ side ] = this.parent.getPositionValue( x );
		};

		proto.select = function() {
		  this.element.classList.add('is-selected');
		  this.element.removeAttribute('aria-hidden');
		};

		proto.unselect = function() {
		  this.element.classList.remove('is-selected');
		  this.element.setAttribute( 'aria-hidden', 'true' );
		};

		/**
		 * @param {Integer} shift - 0, 1, or -1
		 */
		proto.wrapShift = function( shift ) {
		  this.shift = shift;
		  this.renderPosition( this.x + this.parent.slideableWidth * shift );
		};

		proto.remove = function() {
		  this.element.parentNode.removeChild( this.element );
		};

		return Cell;

		} ) ); 
	} (cell));
	return cell.exports;
}

var slide = {exports: {}};

var hasRequiredSlide;

function requireSlide () {
	if (hasRequiredSlide) return slide.exports;
	hasRequiredSlide = 1;
	(function (module) {
		// slide
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory();
		  } else {
		    // browser global
		    window.Flickity = window.Flickity || {};
		    window.Flickity.Slide = factory();
		  }

		}( window, function factory() {

		function Slide( parent ) {
		  this.parent = parent;
		  this.isOriginLeft = parent.originSide == 'left';
		  this.cells = [];
		  this.outerWidth = 0;
		  this.height = 0;
		}

		var proto = Slide.prototype;

		proto.addCell = function( cell ) {
		  this.cells.push( cell );
		  this.outerWidth += cell.size.outerWidth;
		  this.height = Math.max( cell.size.outerHeight, this.height );
		  // first cell stuff
		  if ( this.cells.length == 1 ) {
		    this.x = cell.x; // x comes from first cell
		    var beginMargin = this.isOriginLeft ? 'marginLeft' : 'marginRight';
		    this.firstMargin = cell.size[ beginMargin ];
		  }
		};

		proto.updateTarget = function() {
		  var endMargin = this.isOriginLeft ? 'marginRight' : 'marginLeft';
		  var lastCell = this.getLastCell();
		  var lastMargin = lastCell ? lastCell.size[ endMargin ] : 0;
		  var slideWidth = this.outerWidth - ( this.firstMargin + lastMargin );
		  this.target = this.x + this.firstMargin + slideWidth * this.parent.cellAlign;
		};

		proto.getLastCell = function() {
		  return this.cells[ this.cells.length - 1 ];
		};

		proto.select = function() {
		  this.cells.forEach( function( cell ) {
		    cell.select();
		  } );
		};

		proto.unselect = function() {
		  this.cells.forEach( function( cell ) {
		    cell.unselect();
		  } );
		};

		proto.getCellElements = function() {
		  return this.cells.map( function( cell ) {
		    return cell.element;
		  } );
		};

		return Slide;

		} ) ); 
	} (slide));
	return slide.exports;
}

var animate = {exports: {}};

var hasRequiredAnimate;

function requireAnimate () {
	if (hasRequiredAnimate) return animate.exports;
	hasRequiredAnimate = 1;
	(function (module) {
		// animate
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireUtils()
		    );
		  } else {
		    // browser global
		    window.Flickity = window.Flickity || {};
		    window.Flickity.animatePrototype = factory(
		        window,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, utils ) {

		// -------------------------- animate -------------------------- //

		var proto = {};

		proto.startAnimation = function() {
		  if ( this.isAnimating ) {
		    return;
		  }

		  this.isAnimating = true;
		  this.restingFrames = 0;
		  this.animate();
		};

		proto.animate = function() {
		  this.applyDragForce();
		  this.applySelectedAttraction();

		  var previousX = this.x;

		  this.integratePhysics();
		  this.positionSlider();
		  this.settle( previousX );
		  // animate next frame
		  if ( this.isAnimating ) {
		    var _this = this;
		    requestAnimationFrame( function animateFrame() {
		      _this.animate();
		    } );
		  }
		};

		proto.positionSlider = function() {
		  var x = this.x;
		  // wrap position around
		  if ( this.options.wrapAround && this.cells.length > 1 ) {
		    x = utils.modulo( x, this.slideableWidth );
		    x -= this.slideableWidth;
		    this.shiftWrapCells( x );
		  }

		  this.setTranslateX( x, this.isAnimating );
		  this.dispatchScrollEvent();
		};

		proto.setTranslateX = function( x, is3d ) {
		  x += this.cursorPosition;
		  // reverse if right-to-left and using transform
		  x = this.options.rightToLeft ? -x : x;
		  var translateX = this.getPositionValue( x );
		  // use 3D transforms for hardware acceleration on iOS
		  // but use 2D when settled, for better font-rendering
		  this.slider.style.transform = is3d ?
		    'translate3d(' + translateX + ',0,0)' : 'translateX(' + translateX + ')';
		};

		proto.dispatchScrollEvent = function() {
		  var firstSlide = this.slides[0];
		  if ( !firstSlide ) {
		    return;
		  }
		  var positionX = -this.x - firstSlide.target;
		  var progress = positionX / this.slidesWidth;
		  this.dispatchEvent( 'scroll', null, [ progress, positionX ] );
		};

		proto.positionSliderAtSelected = function() {
		  if ( !this.cells.length ) {
		    return;
		  }
		  this.x = -this.selectedSlide.target;
		  this.velocity = 0; // stop wobble
		  this.positionSlider();
		};

		proto.getPositionValue = function( position ) {
		  if ( this.options.percentPosition ) {
		    // percent position, round to 2 digits, like 12.34%
		    return ( Math.round( ( position / this.size.innerWidth ) * 10000 ) * 0.01 ) + '%';
		  } else {
		    // pixel positioning
		    return Math.round( position ) + 'px';
		  }
		};

		proto.settle = function( previousX ) {
		  // keep track of frames where x hasn't moved
		  var isResting = !this.isPointerDown &&
		      Math.round( this.x * 100 ) == Math.round( previousX * 100 );
		  if ( isResting ) {
		    this.restingFrames++;
		  }
		  // stop animating if resting for 3 or more frames
		  if ( this.restingFrames > 2 ) {
		    this.isAnimating = false;
		    delete this.isFreeScrolling;
		    // render position with translateX when settled
		    this.positionSlider();
		    this.dispatchEvent( 'settle', null, [ this.selectedIndex ] );
		  }
		};

		proto.shiftWrapCells = function( x ) {
		  // shift before cells
		  var beforeGap = this.cursorPosition + x;
		  this._shiftCells( this.beforeShiftCells, beforeGap, -1 );
		  // shift after cells
		  var afterGap = this.size.innerWidth - ( x + this.slideableWidth + this.cursorPosition );
		  this._shiftCells( this.afterShiftCells, afterGap, 1 );
		};

		proto._shiftCells = function( cells, gap, shift ) {
		  for ( var i = 0; i < cells.length; i++ ) {
		    var cell = cells[i];
		    var cellShift = gap > 0 ? shift : 0;
		    cell.wrapShift( cellShift );
		    gap -= cell.size.outerWidth;
		  }
		};

		proto._unshiftCells = function( cells ) {
		  if ( !cells || !cells.length ) {
		    return;
		  }
		  for ( var i = 0; i < cells.length; i++ ) {
		    cells[i].wrapShift( 0 );
		  }
		};

		// -------------------------- physics -------------------------- //

		proto.integratePhysics = function() {
		  this.x += this.velocity;
		  this.velocity *= this.getFrictionFactor();
		};

		proto.applyForce = function( force ) {
		  this.velocity += force;
		};

		proto.getFrictionFactor = function() {
		  return 1 - this.options[ this.isFreeScrolling ? 'freeScrollFriction' : 'friction' ];
		};

		proto.getRestingPosition = function() {
		  // my thanks to Steven Wittens, who simplified this math greatly
		  return this.x + this.velocity / ( 1 - this.getFrictionFactor() );
		};

		proto.applyDragForce = function() {
		  if ( !this.isDraggable || !this.isPointerDown ) {
		    return;
		  }
		  // change the position to drag position by applying force
		  var dragVelocity = this.dragX - this.x;
		  var dragForce = dragVelocity - this.velocity;
		  this.applyForce( dragForce );
		};

		proto.applySelectedAttraction = function() {
		  // do not attract if pointer down or no slides
		  var dragDown = this.isDraggable && this.isPointerDown;
		  if ( dragDown || this.isFreeScrolling || !this.slides.length ) {
		    return;
		  }
		  var distance = this.selectedSlide.target * -1 - this.x;
		  var force = distance * this.options.selectedAttraction;
		  this.applyForce( force );
		};

		return proto;

		} ) ); 
	} (animate));
	return animate.exports;
}

var hasRequiredFlickity;

function requireFlickity () {
	if (hasRequiredFlickity) return flickity.exports;
	hasRequiredFlickity = 1;
	(function (module) {
		// Flickity main
		/* eslint-disable max-params */
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireEvEmitter(),
		        requireGetSize(),
		        requireUtils(),
		        requireCell(),
		        requireSlide(),
		        requireAnimate()
		    );
		  } else {
		    // browser global
		    var _Flickity = window.Flickity;

		    window.Flickity = factory(
		        window,
		        window.EvEmitter,
		        window.getSize,
		        window.fizzyUIUtils,
		        _Flickity.Cell,
		        _Flickity.Slide,
		        _Flickity.animatePrototype
		    );
		  }

		}( window, function factory( window, EvEmitter, getSize,
		    utils, Cell, Slide, animatePrototype ) {

		// vars
		var jQuery = window.jQuery;
		var getComputedStyle = window.getComputedStyle;
		var console = window.console;

		function moveElements( elems, toElem ) {
		  elems = utils.makeArray( elems );
		  while ( elems.length ) {
		    toElem.appendChild( elems.shift() );
		  }
		}

		// -------------------------- Flickity -------------------------- //

		// globally unique identifiers
		var GUID = 0;
		// internal store of all Flickity intances
		var instances = {};

		function Flickity( element, options ) {
		  var queryElement = utils.getQueryElement( element );
		  if ( !queryElement ) {
		    if ( console ) {
		      console.error( 'Bad element for Flickity: ' + ( queryElement || element ) );
		    }
		    return;
		  }
		  this.element = queryElement;
		  // do not initialize twice on same element
		  if ( this.element.flickityGUID ) {
		    var instance = instances[ this.element.flickityGUID ];
		    if ( instance ) instance.option( options );
		    return instance;
		  }

		  // add jQuery
		  if ( jQuery ) {
		    this.$element = jQuery( this.element );
		  }
		  // options
		  this.options = utils.extend( {}, this.constructor.defaults );
		  this.option( options );

		  // kick things off
		  this._create();
		}

		Flickity.defaults = {
		  accessibility: true,
		  // adaptiveHeight: false,
		  cellAlign: 'center',
		  // cellSelector: undefined,
		  // contain: false,
		  freeScrollFriction: 0.075, // friction when free-scrolling
		  friction: 0.28, // friction when selecting
		  namespaceJQueryEvents: true,
		  // initialIndex: 0,
		  percentPosition: true,
		  resize: true,
		  selectedAttraction: 0.025,
		  setGallerySize: true,
		  // watchCSS: false,
		  // wrapAround: false
		};

		// hash of methods triggered on _create()
		Flickity.createMethods = [];

		var proto = Flickity.prototype;
		// inherit EventEmitter
		utils.extend( proto, EvEmitter.prototype );

		proto._create = function() {
		  // add id for Flickity.data
		  var id = this.guid = ++GUID;
		  this.element.flickityGUID = id; // expando
		  instances[ id ] = this; // associate via id
		  // initial properties
		  this.selectedIndex = 0;
		  // how many frames slider has been in same position
		  this.restingFrames = 0;
		  // initial physics properties
		  this.x = 0;
		  this.velocity = 0;
		  this.originSide = this.options.rightToLeft ? 'right' : 'left';
		  // create viewport & slider
		  this.viewport = document.createElement('div');
		  this.viewport.className = 'flickity-viewport';
		  this._createSlider();

		  if ( this.options.resize || this.options.watchCSS ) {
		    window.addEventListener( 'resize', this );
		  }

		  // add listeners from on option
		  for ( var eventName in this.options.on ) {
		    var listener = this.options.on[ eventName ];
		    this.on( eventName, listener );
		  }

		  Flickity.createMethods.forEach( function( method ) {
		    this[ method ]();
		  }, this );

		  if ( this.options.watchCSS ) {
		    this.watchCSS();
		  } else {
		    this.activate();
		  }

		};

		/**
		 * set options
		 * @param {Object} opts - options to extend
		 */
		proto.option = function( opts ) {
		  utils.extend( this.options, opts );
		};

		proto.activate = function() {
		  if ( this.isActive ) {
		    return;
		  }
		  this.isActive = true;
		  this.element.classList.add('flickity-enabled');
		  if ( this.options.rightToLeft ) {
		    this.element.classList.add('flickity-rtl');
		  }

		  this.getSize();
		  // move initial cell elements so they can be loaded as cells
		  var cellElems = this._filterFindCellElements( this.element.children );
		  moveElements( cellElems, this.slider );
		  this.viewport.appendChild( this.slider );
		  this.element.appendChild( this.viewport );
		  // get cells from children
		  this.reloadCells();

		  if ( this.options.accessibility ) {
		    // allow element to focusable
		    this.element.tabIndex = 0;
		    // listen for key presses
		    this.element.addEventListener( 'keydown', this );
		  }

		  this.emitEvent('activate');
		  this.selectInitialIndex();
		  // flag for initial activation, for using initialIndex
		  this.isInitActivated = true;
		  // ready event. #493
		  this.dispatchEvent('ready');
		};

		// slider positions the cells
		proto._createSlider = function() {
		  // slider element does all the positioning
		  var slider = document.createElement('div');
		  slider.className = 'flickity-slider';
		  slider.style[ this.originSide ] = 0;
		  this.slider = slider;
		};

		proto._filterFindCellElements = function( elems ) {
		  return utils.filterFindElements( elems, this.options.cellSelector );
		};

		// goes through all children
		proto.reloadCells = function() {
		  // collection of item elements
		  this.cells = this._makeCells( this.slider.children );
		  this.positionCells();
		  this._getWrapShiftCells();
		  this.setGallerySize();
		};

		/**
		 * turn elements into Flickity.Cells
		 * @param {[Array, NodeList, HTMLElement]} elems - elements to make into cells
		 * @returns {Array} items - collection of new Flickity Cells
		 */
		proto._makeCells = function( elems ) {
		  var cellElems = this._filterFindCellElements( elems );

		  // create new Flickity for collection
		  var cells = cellElems.map( function( cellElem ) {
		    return new Cell( cellElem, this );
		  }, this );

		  return cells;
		};

		proto.getLastCell = function() {
		  return this.cells[ this.cells.length - 1 ];
		};

		proto.getLastSlide = function() {
		  return this.slides[ this.slides.length - 1 ];
		};

		// positions all cells
		proto.positionCells = function() {
		  // size all cells
		  this._sizeCells( this.cells );
		  // position all cells
		  this._positionCells( 0 );
		};

		/**
		 * position certain cells
		 * @param {Integer} index - which cell to start with
		 */
		proto._positionCells = function( index ) {
		  index = index || 0;
		  // also measure maxCellHeight
		  // start 0 if positioning all cells
		  this.maxCellHeight = index ? this.maxCellHeight || 0 : 0;
		  var cellX = 0;
		  // get cellX
		  if ( index > 0 ) {
		    var startCell = this.cells[ index - 1 ];
		    cellX = startCell.x + startCell.size.outerWidth;
		  }
		  var len = this.cells.length;
		  for ( var i = index; i < len; i++ ) {
		    var cell = this.cells[i];
		    cell.setPosition( cellX );
		    cellX += cell.size.outerWidth;
		    this.maxCellHeight = Math.max( cell.size.outerHeight, this.maxCellHeight );
		  }
		  // keep track of cellX for wrap-around
		  this.slideableWidth = cellX;
		  // slides
		  this.updateSlides();
		  // contain slides target
		  this._containSlides();
		  // update slidesWidth
		  this.slidesWidth = len ? this.getLastSlide().target - this.slides[0].target : 0;
		};

		/**
		 * cell.getSize() on multiple cells
		 * @param {Array} cells - cells to size
		 */
		proto._sizeCells = function( cells ) {
		  cells.forEach( function( cell ) {
		    cell.getSize();
		  } );
		};

		// --------------------------  -------------------------- //

		proto.updateSlides = function() {
		  this.slides = [];
		  if ( !this.cells.length ) {
		    return;
		  }

		  var slide = new Slide( this );
		  this.slides.push( slide );
		  var isOriginLeft = this.originSide == 'left';
		  var nextMargin = isOriginLeft ? 'marginRight' : 'marginLeft';

		  var canCellFit = this._getCanCellFit();

		  this.cells.forEach( function( cell, i ) {
		    // just add cell if first cell in slide
		    if ( !slide.cells.length ) {
		      slide.addCell( cell );
		      return;
		    }

		    var slideWidth = ( slide.outerWidth - slide.firstMargin ) +
		      ( cell.size.outerWidth - cell.size[ nextMargin ] );

		    if ( canCellFit.call( this, i, slideWidth ) ) {
		      slide.addCell( cell );
		    } else {
		      // doesn't fit, new slide
		      slide.updateTarget();

		      slide = new Slide( this );
		      this.slides.push( slide );
		      slide.addCell( cell );
		    }
		  }, this );
		  // last slide
		  slide.updateTarget();
		  // update .selectedSlide
		  this.updateSelectedSlide();
		};

		proto._getCanCellFit = function() {
		  var groupCells = this.options.groupCells;
		  if ( !groupCells ) {
		    return function() {
		      return false;
		    };
		  } else if ( typeof groupCells == 'number' ) {
		    // group by number. 3 -> [0,1,2], [3,4,5], ...
		    var number = parseInt( groupCells, 10 );
		    return function( i ) {
		      return ( i % number ) !== 0;
		    };
		  }
		  // default, group by width of slide
		  // parse '75%
		  var percentMatch = typeof groupCells == 'string' &&
		    groupCells.match( /^(\d+)%$/ );
		  var percent = percentMatch ? parseInt( percentMatch[1], 10 ) / 100 : 1;
		  return function( i, slideWidth ) {
		    /* eslint-disable-next-line no-invalid-this */
		    return slideWidth <= ( this.size.innerWidth + 1 ) * percent;
		  };
		};

		// alias _init for jQuery plugin .flickity()
		proto._init =
		proto.reposition = function() {
		  this.positionCells();
		  this.positionSliderAtSelected();
		};

		proto.getSize = function() {
		  this.size = getSize( this.element );
		  this.setCellAlign();
		  this.cursorPosition = this.size.innerWidth * this.cellAlign;
		};

		var cellAlignShorthands = {
		  // cell align, then based on origin side
		  center: {
		    left: 0.5,
		    right: 0.5,
		  },
		  left: {
		    left: 0,
		    right: 1,
		  },
		  right: {
		    right: 0,
		    left: 1,
		  },
		};

		proto.setCellAlign = function() {
		  var shorthand = cellAlignShorthands[ this.options.cellAlign ];
		  this.cellAlign = shorthand ? shorthand[ this.originSide ] : this.options.cellAlign;
		};

		proto.setGallerySize = function() {
		  if ( this.options.setGallerySize ) {
		    var height = this.options.adaptiveHeight && this.selectedSlide ?
		      this.selectedSlide.height : this.maxCellHeight;
		    this.viewport.style.height = height + 'px';
		  }
		};

		proto._getWrapShiftCells = function() {
		  // only for wrap-around
		  if ( !this.options.wrapAround ) {
		    return;
		  }
		  // unshift previous cells
		  this._unshiftCells( this.beforeShiftCells );
		  this._unshiftCells( this.afterShiftCells );
		  // get before cells
		  // initial gap
		  var gapX = this.cursorPosition;
		  var cellIndex = this.cells.length - 1;
		  this.beforeShiftCells = this._getGapCells( gapX, cellIndex, -1 );
		  // get after cells
		  // ending gap between last cell and end of gallery viewport
		  gapX = this.size.innerWidth - this.cursorPosition;
		  // start cloning at first cell, working forwards
		  this.afterShiftCells = this._getGapCells( gapX, 0, 1 );
		};

		proto._getGapCells = function( gapX, cellIndex, increment ) {
		  // keep adding cells until the cover the initial gap
		  var cells = [];
		  while ( gapX > 0 ) {
		    var cell = this.cells[ cellIndex ];
		    if ( !cell ) {
		      break;
		    }
		    cells.push( cell );
		    cellIndex += increment;
		    gapX -= cell.size.outerWidth;
		  }
		  return cells;
		};

		// ----- contain ----- //

		// contain cell targets so no excess sliding
		proto._containSlides = function() {
		  if ( !this.options.contain || this.options.wrapAround || !this.cells.length ) {
		    return;
		  }
		  var isRightToLeft = this.options.rightToLeft;
		  var beginMargin = isRightToLeft ? 'marginRight' : 'marginLeft';
		  var endMargin = isRightToLeft ? 'marginLeft' : 'marginRight';
		  var contentWidth = this.slideableWidth - this.getLastCell().size[ endMargin ];
		  // content is less than gallery size
		  var isContentSmaller = contentWidth < this.size.innerWidth;
		  // bounds
		  var beginBound = this.cursorPosition + this.cells[0].size[ beginMargin ];
		  var endBound = contentWidth - this.size.innerWidth * ( 1 - this.cellAlign );
		  // contain each cell target
		  this.slides.forEach( function( slide ) {
		    if ( isContentSmaller ) {
		      // all cells fit inside gallery
		      slide.target = contentWidth * this.cellAlign;
		    } else {
		      // contain to bounds
		      slide.target = Math.max( slide.target, beginBound );
		      slide.target = Math.min( slide.target, endBound );
		    }
		  }, this );
		};

		// -----  ----- //

		/**
		 * emits events via eventEmitter and jQuery events
		 * @param {String} type - name of event
		 * @param {Event} event - original event
		 * @param {Array} args - extra arguments
		 */
		proto.dispatchEvent = function( type, event, args ) {
		  var emitArgs = event ? [ event ].concat( args ) : args;
		  this.emitEvent( type, emitArgs );

		  if ( jQuery && this.$element ) {
		    // default trigger with type if no event
		    type += this.options.namespaceJQueryEvents ? '.flickity' : '';
		    var $event = type;
		    if ( event ) {
		      // create jQuery event
		      var jQEvent = new jQuery.Event( event );
		      jQEvent.type = type;
		      $event = jQEvent;
		    }
		    this.$element.trigger( $event, args );
		  }
		};

		// -------------------------- select -------------------------- //

		/**
		 * @param {Integer} index - index of the slide
		 * @param {Boolean} isWrap - will wrap-around to last/first if at the end
		 * @param {Boolean} isInstant - will immediately set position at selected cell
		 */
		proto.select = function( index, isWrap, isInstant ) {
		  if ( !this.isActive ) {
		    return;
		  }
		  index = parseInt( index, 10 );
		  this._wrapSelect( index );

		  if ( this.options.wrapAround || isWrap ) {
		    index = utils.modulo( index, this.slides.length );
		  }
		  // bail if invalid index
		  if ( !this.slides[ index ] ) {
		    return;
		  }
		  var prevIndex = this.selectedIndex;
		  this.selectedIndex = index;
		  this.updateSelectedSlide();
		  if ( isInstant ) {
		    this.positionSliderAtSelected();
		  } else {
		    this.startAnimation();
		  }
		  if ( this.options.adaptiveHeight ) {
		    this.setGallerySize();
		  }
		  // events
		  this.dispatchEvent( 'select', null, [ index ] );
		  // change event if new index
		  if ( index != prevIndex ) {
		    this.dispatchEvent( 'change', null, [ index ] );
		  }
		  // old v1 event name, remove in v3
		  this.dispatchEvent('cellSelect');
		};

		// wraps position for wrapAround, to move to closest slide. #113
		proto._wrapSelect = function( index ) {
		  var len = this.slides.length;
		  var isWrapping = this.options.wrapAround && len > 1;
		  if ( !isWrapping ) {
		    return index;
		  }
		  var wrapIndex = utils.modulo( index, len );
		  // go to shortest
		  var delta = Math.abs( wrapIndex - this.selectedIndex );
		  var backWrapDelta = Math.abs( ( wrapIndex + len ) - this.selectedIndex );
		  var forewardWrapDelta = Math.abs( ( wrapIndex - len ) - this.selectedIndex );
		  if ( !this.isDragSelect && backWrapDelta < delta ) {
		    index += len;
		  } else if ( !this.isDragSelect && forewardWrapDelta < delta ) {
		    index -= len;
		  }
		  // wrap position so slider is within normal area
		  if ( index < 0 ) {
		    this.x -= this.slideableWidth;
		  } else if ( index >= len ) {
		    this.x += this.slideableWidth;
		  }
		};

		proto.previous = function( isWrap, isInstant ) {
		  this.select( this.selectedIndex - 1, isWrap, isInstant );
		};

		proto.next = function( isWrap, isInstant ) {
		  this.select( this.selectedIndex + 1, isWrap, isInstant );
		};

		proto.updateSelectedSlide = function() {
		  var slide = this.slides[ this.selectedIndex ];
		  // selectedIndex could be outside of slides, if triggered before resize()
		  if ( !slide ) {
		    return;
		  }
		  // unselect previous selected slide
		  this.unselectSelectedSlide();
		  // update new selected slide
		  this.selectedSlide = slide;
		  slide.select();
		  this.selectedCells = slide.cells;
		  this.selectedElements = slide.getCellElements();
		  // HACK: selectedCell & selectedElement is first cell in slide, backwards compatibility
		  // Remove in v3?
		  this.selectedCell = slide.cells[0];
		  this.selectedElement = this.selectedElements[0];
		};

		proto.unselectSelectedSlide = function() {
		  if ( this.selectedSlide ) {
		    this.selectedSlide.unselect();
		  }
		};

		proto.selectInitialIndex = function() {
		  var initialIndex = this.options.initialIndex;
		  // already activated, select previous selectedIndex
		  if ( this.isInitActivated ) {
		    this.select( this.selectedIndex, false, true );
		    return;
		  }
		  // select with selector string
		  if ( initialIndex && typeof initialIndex == 'string' ) {
		    var cell = this.queryCell( initialIndex );
		    if ( cell ) {
		      this.selectCell( initialIndex, false, true );
		      return;
		    }
		  }

		  var index = 0;
		  // select with number
		  if ( initialIndex && this.slides[ initialIndex ] ) {
		    index = initialIndex;
		  }
		  // select instantly
		  this.select( index, false, true );
		};

		/**
		 * select slide from number or cell element
		 * @param {[Element, Number]} value - zero-based index or element to select
		 * @param {Boolean} isWrap - enables wrapping around for extra index
		 * @param {Boolean} isInstant - disables slide animation
		 */
		proto.selectCell = function( value, isWrap, isInstant ) {
		  // get cell
		  var cell = this.queryCell( value );
		  if ( !cell ) {
		    return;
		  }

		  var index = this.getCellSlideIndex( cell );
		  this.select( index, isWrap, isInstant );
		};

		proto.getCellSlideIndex = function( cell ) {
		  // get index of slides that has cell
		  for ( var i = 0; i < this.slides.length; i++ ) {
		    var slide = this.slides[i];
		    var index = slide.cells.indexOf( cell );
		    if ( index != -1 ) {
		      return i;
		    }
		  }
		};

		// -------------------------- get cells -------------------------- //

		/**
		 * get Flickity.Cell, given an Element
		 * @param {Element} elem - matching cell element
		 * @returns {Flickity.Cell} cell - matching cell
		 */
		proto.getCell = function( elem ) {
		  // loop through cells to get the one that matches
		  for ( var i = 0; i < this.cells.length; i++ ) {
		    var cell = this.cells[i];
		    if ( cell.element == elem ) {
		      return cell;
		    }
		  }
		};

		/**
		 * get collection of Flickity.Cells, given Elements
		 * @param {[Element, Array, NodeList]} elems - multiple elements
		 * @returns {Array} cells - Flickity.Cells
		 */
		proto.getCells = function( elems ) {
		  elems = utils.makeArray( elems );
		  var cells = [];
		  elems.forEach( function( elem ) {
		    var cell = this.getCell( elem );
		    if ( cell ) {
		      cells.push( cell );
		    }
		  }, this );
		  return cells;
		};

		/**
		 * get cell elements
		 * @returns {Array} cellElems
		 */
		proto.getCellElements = function() {
		  return this.cells.map( function( cell ) {
		    return cell.element;
		  } );
		};

		/**
		 * get parent cell from an element
		 * @param {Element} elem - child element
		 * @returns {Flickit.Cell} cell - parent cell
		 */
		proto.getParentCell = function( elem ) {
		  // first check if elem is cell
		  var cell = this.getCell( elem );
		  if ( cell ) {
		    return cell;
		  }
		  // try to get parent cell elem
		  elem = utils.getParent( elem, '.flickity-slider > *' );
		  return this.getCell( elem );
		};

		/**
		 * get cells adjacent to a slide
		 * @param {Integer} adjCount - number of adjacent slides
		 * @param {Integer} index - index of slide to start
		 * @returns {Array} cells - array of Flickity.Cells
		 */
		proto.getAdjacentCellElements = function( adjCount, index ) {
		  if ( !adjCount ) {
		    return this.selectedSlide.getCellElements();
		  }
		  index = index === undefined ? this.selectedIndex : index;

		  var len = this.slides.length;
		  if ( 1 + ( adjCount * 2 ) >= len ) {
		    return this.getCellElements();
		  }

		  var cellElems = [];
		  for ( var i = index - adjCount; i <= index + adjCount; i++ ) {
		    var slideIndex = this.options.wrapAround ? utils.modulo( i, len ) : i;
		    var slide = this.slides[ slideIndex ];
		    if ( slide ) {
		      cellElems = cellElems.concat( slide.getCellElements() );
		    }
		  }
		  return cellElems;
		};

		/**
		 * select slide from number or cell element
		 * @param {[Element, String, Number]} selector - element, selector string, or index
		 * @returns {Flickity.Cell} - matching cell
		 */
		proto.queryCell = function( selector ) {
		  if ( typeof selector == 'number' ) {
		    // use number as index
		    return this.cells[ selector ];
		  }
		  if ( typeof selector == 'string' ) {
		    // do not select invalid selectors from hash: #123, #/. #791
		    if ( selector.match( /^[#.]?[\d/]/ ) ) {
		      return;
		    }
		    // use string as selector, get element
		    selector = this.element.querySelector( selector );
		  }
		  // get cell from element
		  return this.getCell( selector );
		};

		// -------------------------- events -------------------------- //

		proto.uiChange = function() {
		  this.emitEvent('uiChange');
		};

		// keep focus on element when child UI elements are clicked
		proto.childUIPointerDown = function( event ) {
		  // HACK iOS does not allow touch events to bubble up?!
		  if ( event.type != 'touchstart' ) {
		    event.preventDefault();
		  }
		  this.focus();
		};

		// ----- resize ----- //

		proto.onresize = function() {
		  this.watchCSS();
		  this.resize();
		};

		utils.debounceMethod( Flickity, 'onresize', 150 );

		proto.resize = function() {
		  if ( !this.isActive ) {
		    return;
		  }
		  this.getSize();
		  // wrap values
		  if ( this.options.wrapAround ) {
		    this.x = utils.modulo( this.x, this.slideableWidth );
		  }
		  this.positionCells();
		  this._getWrapShiftCells();
		  this.setGallerySize();
		  this.emitEvent('resize');
		  // update selected index for group slides, instant
		  // TODO: position can be lost between groups of various numbers
		  var selectedElement = this.selectedElements && this.selectedElements[0];
		  this.selectCell( selectedElement, false, true );
		};

		// watches the :after property, activates/deactivates
		proto.watchCSS = function() {
		  var watchOption = this.options.watchCSS;
		  if ( !watchOption ) {
		    return;
		  }

		  var afterContent = getComputedStyle( this.element, ':after' ).content;
		  // activate if :after { content: 'flickity' }
		  if ( afterContent.indexOf('flickity') != -1 ) {
		    this.activate();
		  } else {
		    this.deactivate();
		  }
		};

		// ----- keydown ----- //

		// go previous/next if left/right keys pressed
		proto.onkeydown = function( event ) {
		  // only work if element is in focus
		  var isNotFocused = document.activeElement && document.activeElement != this.element;
		  if ( !this.options.accessibility || isNotFocused ) {
		    return;
		  }

		  var handler = Flickity.keyboardHandlers[ event.keyCode ];
		  if ( handler ) {
		    handler.call( this );
		  }
		};

		Flickity.keyboardHandlers = {
		  // left arrow
		  37: function() {
		    var leftMethod = this.options.rightToLeft ? 'next' : 'previous';
		    this.uiChange();
		    this[ leftMethod ]();
		  },
		  // right arrow
		  39: function() {
		    var rightMethod = this.options.rightToLeft ? 'previous' : 'next';
		    this.uiChange();
		    this[ rightMethod ]();
		  },
		};

		// ----- focus ----- //

		proto.focus = function() {
		  // TODO remove scrollTo once focus options gets more support
		  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/focus ...
		  //    #Browser_compatibility
		  var prevScrollY = window.pageYOffset;
		  this.element.focus({ preventScroll: true });
		  // hack to fix scroll jump after focus, #76
		  if ( window.pageYOffset != prevScrollY ) {
		    window.scrollTo( window.pageXOffset, prevScrollY );
		  }
		};

		// -------------------------- destroy -------------------------- //

		// deactivate all Flickity functionality, but keep stuff available
		proto.deactivate = function() {
		  if ( !this.isActive ) {
		    return;
		  }
		  this.element.classList.remove('flickity-enabled');
		  this.element.classList.remove('flickity-rtl');
		  this.unselectSelectedSlide();
		  // destroy cells
		  this.cells.forEach( function( cell ) {
		    cell.destroy();
		  } );
		  this.element.removeChild( this.viewport );
		  // move child elements back into element
		  moveElements( this.slider.children, this.element );
		  if ( this.options.accessibility ) {
		    this.element.removeAttribute('tabIndex');
		    this.element.removeEventListener( 'keydown', this );
		  }
		  // set flags
		  this.isActive = false;
		  this.emitEvent('deactivate');
		};

		proto.destroy = function() {
		  this.deactivate();
		  window.removeEventListener( 'resize', this );
		  this.allOff();
		  this.emitEvent('destroy');
		  if ( jQuery && this.$element ) {
		    jQuery.removeData( this.element, 'flickity' );
		  }
		  delete this.element.flickityGUID;
		  delete instances[ this.guid ];
		};

		// -------------------------- prototype -------------------------- //

		utils.extend( proto, animatePrototype );

		// -------------------------- extras -------------------------- //

		/**
		 * get Flickity instance from element
		 * @param {[Element, String]} elem - element or selector string
		 * @returns {Flickity} - Flickity instance
		 */
		Flickity.data = function( elem ) {
		  elem = utils.getQueryElement( elem );
		  var id = elem && elem.flickityGUID;
		  return id && instances[ id ];
		};

		utils.htmlInit( Flickity, 'flickity' );

		if ( jQuery && jQuery.bridget ) {
		  jQuery.bridget( 'flickity', Flickity );
		}

		// set internal jQuery, for Webpack + jQuery v3, #478
		Flickity.setJQuery = function( jq ) {
		  jQuery = jq;
		};

		Flickity.Cell = Cell;
		Flickity.Slide = Slide;

		return Flickity;

		} ) ); 
	} (flickity));
	return flickity.exports;
}

var drag = {exports: {}};

var unidragger = {exports: {}};

var unipointer = {exports: {}};

/*!
 * Unipointer v2.4.0
 * base class for doing one thing with pointer event
 * MIT license
 */

var hasRequiredUnipointer;

function requireUnipointer () {
	if (hasRequiredUnipointer) return unipointer.exports;
	hasRequiredUnipointer = 1;
	(function (module) {
		/*jshint browser: true, undef: true, unused: true, strict: true */

		( function( window, factory ) {
		  // universal module definition
		  /* jshint strict: false */ /*global define, module, require */
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		      window,
		      requireEvEmitter()
		    );
		  } else {
		    // browser global
		    window.Unipointer = factory(
		      window,
		      window.EvEmitter
		    );
		  }

		}( window, function factory( window, EvEmitter ) {

		function noop() {}

		function Unipointer() {}

		// inherit EvEmitter
		var proto = Unipointer.prototype = Object.create( EvEmitter.prototype );

		proto.bindStartEvent = function( elem ) {
		  this._bindStartEvent( elem, true );
		};

		proto.unbindStartEvent = function( elem ) {
		  this._bindStartEvent( elem, false );
		};

		/**
		 * Add or remove start event
		 * @param {Boolean} isAdd - remove if falsey
		 */
		proto._bindStartEvent = function( elem, isAdd ) {
		  // munge isAdd, default to true
		  isAdd = isAdd === undefined ? true : isAdd;
		  var bindMethod = isAdd ? 'addEventListener' : 'removeEventListener';

		  // default to mouse events
		  var startEvent = 'mousedown';
		  if ( 'ontouchstart' in window ) {
		    // HACK prefer Touch Events as you can preventDefault on touchstart to
		    // disable scroll in iOS & mobile Chrome metafizzy/flickity#1177
		    startEvent = 'touchstart';
		  } else if ( window.PointerEvent ) {
		    // Pointer Events
		    startEvent = 'pointerdown';
		  }
		  elem[ bindMethod ]( startEvent, this );
		};

		// trigger handler methods for events
		proto.handleEvent = function( event ) {
		  var method = 'on' + event.type;
		  if ( this[ method ] ) {
		    this[ method ]( event );
		  }
		};

		// returns the touch that we're keeping track of
		proto.getTouch = function( touches ) {
		  for ( var i=0; i < touches.length; i++ ) {
		    var touch = touches[i];
		    if ( touch.identifier == this.pointerIdentifier ) {
		      return touch;
		    }
		  }
		};

		// ----- start event ----- //

		proto.onmousedown = function( event ) {
		  // dismiss clicks from right or middle buttons
		  var button = event.button;
		  if ( button && ( button !== 0 && button !== 1 ) ) {
		    return;
		  }
		  this._pointerDown( event, event );
		};

		proto.ontouchstart = function( event ) {
		  this._pointerDown( event, event.changedTouches[0] );
		};

		proto.onpointerdown = function( event ) {
		  this._pointerDown( event, event );
		};

		/**
		 * pointer start
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 */
		proto._pointerDown = function( event, pointer ) {
		  // dismiss right click and other pointers
		  // button = 0 is okay, 1-4 not
		  if ( event.button || this.isPointerDown ) {
		    return;
		  }

		  this.isPointerDown = true;
		  // save pointer identifier to match up touch events
		  this.pointerIdentifier = pointer.pointerId !== undefined ?
		    // pointerId for pointer events, touch.indentifier for touch events
		    pointer.pointerId : pointer.identifier;

		  this.pointerDown( event, pointer );
		};

		proto.pointerDown = function( event, pointer ) {
		  this._bindPostStartEvents( event );
		  this.emitEvent( 'pointerDown', [ event, pointer ] );
		};

		// hash of events to be bound after start event
		var postStartEvents = {
		  mousedown: [ 'mousemove', 'mouseup' ],
		  touchstart: [ 'touchmove', 'touchend', 'touchcancel' ],
		  pointerdown: [ 'pointermove', 'pointerup', 'pointercancel' ],
		};

		proto._bindPostStartEvents = function( event ) {
		  if ( !event ) {
		    return;
		  }
		  // get proper events to match start event
		  var events = postStartEvents[ event.type ];
		  // bind events to node
		  events.forEach( function( eventName ) {
		    window.addEventListener( eventName, this );
		  }, this );
		  // save these arguments
		  this._boundPointerEvents = events;
		};

		proto._unbindPostStartEvents = function() {
		  // check for _boundEvents, in case dragEnd triggered twice (old IE8 bug)
		  if ( !this._boundPointerEvents ) {
		    return;
		  }
		  this._boundPointerEvents.forEach( function( eventName ) {
		    window.removeEventListener( eventName, this );
		  }, this );

		  delete this._boundPointerEvents;
		};

		// ----- move event ----- //

		proto.onmousemove = function( event ) {
		  this._pointerMove( event, event );
		};

		proto.onpointermove = function( event ) {
		  if ( event.pointerId == this.pointerIdentifier ) {
		    this._pointerMove( event, event );
		  }
		};

		proto.ontouchmove = function( event ) {
		  var touch = this.getTouch( event.changedTouches );
		  if ( touch ) {
		    this._pointerMove( event, touch );
		  }
		};

		/**
		 * pointer move
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 * @private
		 */
		proto._pointerMove = function( event, pointer ) {
		  this.pointerMove( event, pointer );
		};

		// public
		proto.pointerMove = function( event, pointer ) {
		  this.emitEvent( 'pointerMove', [ event, pointer ] );
		};

		// ----- end event ----- //


		proto.onmouseup = function( event ) {
		  this._pointerUp( event, event );
		};

		proto.onpointerup = function( event ) {
		  if ( event.pointerId == this.pointerIdentifier ) {
		    this._pointerUp( event, event );
		  }
		};

		proto.ontouchend = function( event ) {
		  var touch = this.getTouch( event.changedTouches );
		  if ( touch ) {
		    this._pointerUp( event, touch );
		  }
		};

		/**
		 * pointer up
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 * @private
		 */
		proto._pointerUp = function( event, pointer ) {
		  this._pointerDone();
		  this.pointerUp( event, pointer );
		};

		// public
		proto.pointerUp = function( event, pointer ) {
		  this.emitEvent( 'pointerUp', [ event, pointer ] );
		};

		// ----- pointer done ----- //

		// triggered on pointer up & pointer cancel
		proto._pointerDone = function() {
		  this._pointerReset();
		  this._unbindPostStartEvents();
		  this.pointerDone();
		};

		proto._pointerReset = function() {
		  // reset properties
		  this.isPointerDown = false;
		  delete this.pointerIdentifier;
		};

		proto.pointerDone = noop;

		// ----- pointer cancel ----- //

		proto.onpointercancel = function( event ) {
		  if ( event.pointerId == this.pointerIdentifier ) {
		    this._pointerCancel( event, event );
		  }
		};

		proto.ontouchcancel = function( event ) {
		  var touch = this.getTouch( event.changedTouches );
		  if ( touch ) {
		    this._pointerCancel( event, touch );
		  }
		};

		/**
		 * pointer cancel
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 * @private
		 */
		proto._pointerCancel = function( event, pointer ) {
		  this._pointerDone();
		  this.pointerCancel( event, pointer );
		};

		// public
		proto.pointerCancel = function( event, pointer ) {
		  this.emitEvent( 'pointerCancel', [ event, pointer ] );
		};

		// -----  ----- //

		// utility function for getting x/y coords from event
		Unipointer.getPointerPoint = function( pointer ) {
		  return {
		    x: pointer.pageX,
		    y: pointer.pageY
		  };
		};

		// -----  ----- //

		return Unipointer;

		})); 
	} (unipointer));
	return unipointer.exports;
}

/*!
 * Unidragger v2.4.0
 * Draggable base class
 * MIT license
 */

var hasRequiredUnidragger;

function requireUnidragger () {
	if (hasRequiredUnidragger) return unidragger.exports;
	hasRequiredUnidragger = 1;
	(function (module) {
		/*jshint browser: true, unused: true, undef: true, strict: true */

		( function( window, factory ) {
		  // universal module definition
		  /*jshint strict: false */ /*globals define, module, require */

		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		      window,
		      requireUnipointer()
		    );
		  } else {
		    // browser global
		    window.Unidragger = factory(
		      window,
		      window.Unipointer
		    );
		  }

		}( window, function factory( window, Unipointer ) {

		// -------------------------- Unidragger -------------------------- //

		function Unidragger() {}

		// inherit Unipointer & EvEmitter
		var proto = Unidragger.prototype = Object.create( Unipointer.prototype );

		// ----- bind start ----- //

		proto.bindHandles = function() {
		  this._bindHandles( true );
		};

		proto.unbindHandles = function() {
		  this._bindHandles( false );
		};

		/**
		 * Add or remove start event
		 * @param {Boolean} isAdd
		 */
		proto._bindHandles = function( isAdd ) {
		  // munge isAdd, default to true
		  isAdd = isAdd === undefined ? true : isAdd;
		  // bind each handle
		  var bindMethod = isAdd ? 'addEventListener' : 'removeEventListener';
		  var touchAction = isAdd ? this._touchActionValue : '';
		  for ( var i=0; i < this.handles.length; i++ ) {
		    var handle = this.handles[i];
		    this._bindStartEvent( handle, isAdd );
		    handle[ bindMethod ]( 'click', this );
		    // touch-action: none to override browser touch gestures. metafizzy/flickity#540
		    if ( window.PointerEvent ) {
		      handle.style.touchAction = touchAction;
		    }
		  }
		};

		// prototype so it can be overwriteable by Flickity
		proto._touchActionValue = 'none';

		// ----- start event ----- //

		/**
		 * pointer start
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 */
		proto.pointerDown = function( event, pointer ) {
		  var isOkay = this.okayPointerDown( event );
		  if ( !isOkay ) {
		    return;
		  }
		  // track start event position
		  // Safari 9 overrides pageX and pageY. These values needs to be copied. flickity#842
		  this.pointerDownPointer = {
		    pageX: pointer.pageX,
		    pageY: pointer.pageY,
		  };

		  event.preventDefault();
		  this.pointerDownBlur();
		  // bind move and end events
		  this._bindPostStartEvents( event );
		  this.emitEvent( 'pointerDown', [ event, pointer ] );
		};

		// nodes that have text fields
		var cursorNodes = {
		  TEXTAREA: true,
		  INPUT: true,
		  SELECT: true,
		  OPTION: true,
		};

		// input types that do not have text fields
		var clickTypes = {
		  radio: true,
		  checkbox: true,
		  button: true,
		  submit: true,
		  image: true,
		  file: true,
		};

		// dismiss inputs with text fields. flickity#403, flickity#404
		proto.okayPointerDown = function( event ) {
		  var isCursorNode = cursorNodes[ event.target.nodeName ];
		  var isClickType = clickTypes[ event.target.type ];
		  var isOkay = !isCursorNode || isClickType;
		  if ( !isOkay ) {
		    this._pointerReset();
		  }
		  return isOkay;
		};

		// kludge to blur previously focused input
		proto.pointerDownBlur = function() {
		  var focused = document.activeElement;
		  // do not blur body for IE10, metafizzy/flickity#117
		  var canBlur = focused && focused.blur && focused != document.body;
		  if ( canBlur ) {
		    focused.blur();
		  }
		};

		// ----- move event ----- //

		/**
		 * drag move
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 */
		proto.pointerMove = function( event, pointer ) {
		  var moveVector = this._dragPointerMove( event, pointer );
		  this.emitEvent( 'pointerMove', [ event, pointer, moveVector ] );
		  this._dragMove( event, pointer, moveVector );
		};

		// base pointer move logic
		proto._dragPointerMove = function( event, pointer ) {
		  var moveVector = {
		    x: pointer.pageX - this.pointerDownPointer.pageX,
		    y: pointer.pageY - this.pointerDownPointer.pageY
		  };
		  // start drag if pointer has moved far enough to start drag
		  if ( !this.isDragging && this.hasDragStarted( moveVector ) ) {
		    this._dragStart( event, pointer );
		  }
		  return moveVector;
		};

		// condition if pointer has moved far enough to start drag
		proto.hasDragStarted = function( moveVector ) {
		  return Math.abs( moveVector.x ) > 3 || Math.abs( moveVector.y ) > 3;
		};

		// ----- end event ----- //

		/**
		 * pointer up
		 * @param {Event} event
		 * @param {Event or Touch} pointer
		 */
		proto.pointerUp = function( event, pointer ) {
		  this.emitEvent( 'pointerUp', [ event, pointer ] );
		  this._dragPointerUp( event, pointer );
		};

		proto._dragPointerUp = function( event, pointer ) {
		  if ( this.isDragging ) {
		    this._dragEnd( event, pointer );
		  } else {
		    // pointer didn't move enough for drag to start
		    this._staticClick( event, pointer );
		  }
		};

		// -------------------------- drag -------------------------- //

		// dragStart
		proto._dragStart = function( event, pointer ) {
		  this.isDragging = true;
		  // prevent clicks
		  this.isPreventingClicks = true;
		  this.dragStart( event, pointer );
		};

		proto.dragStart = function( event, pointer ) {
		  this.emitEvent( 'dragStart', [ event, pointer ] );
		};

		// dragMove
		proto._dragMove = function( event, pointer, moveVector ) {
		  // do not drag if not dragging yet
		  if ( !this.isDragging ) {
		    return;
		  }

		  this.dragMove( event, pointer, moveVector );
		};

		proto.dragMove = function( event, pointer, moveVector ) {
		  event.preventDefault();
		  this.emitEvent( 'dragMove', [ event, pointer, moveVector ] );
		};

		// dragEnd
		proto._dragEnd = function( event, pointer ) {
		  // set flags
		  this.isDragging = false;
		  // re-enable clicking async
		  setTimeout( function() {
		    delete this.isPreventingClicks;
		  }.bind( this ) );

		  this.dragEnd( event, pointer );
		};

		proto.dragEnd = function( event, pointer ) {
		  this.emitEvent( 'dragEnd', [ event, pointer ] );
		};

		// ----- onclick ----- //

		// handle all clicks and prevent clicks when dragging
		proto.onclick = function( event ) {
		  if ( this.isPreventingClicks ) {
		    event.preventDefault();
		  }
		};

		// ----- staticClick ----- //

		// triggered after pointer down & up with no/tiny movement
		proto._staticClick = function( event, pointer ) {
		  // ignore emulated mouse up clicks
		  if ( this.isIgnoringMouseUp && event.type == 'mouseup' ) {
		    return;
		  }

		  this.staticClick( event, pointer );

		  // set flag for emulated clicks 300ms after touchend
		  if ( event.type != 'mouseup' ) {
		    this.isIgnoringMouseUp = true;
		    // reset flag after 300ms
		    setTimeout( function() {
		      delete this.isIgnoringMouseUp;
		    }.bind( this ), 400 );
		  }
		};

		proto.staticClick = function( event, pointer ) {
		  this.emitEvent( 'staticClick', [ event, pointer ] );
		};

		// ----- utils ----- //

		Unidragger.getPointerPoint = Unipointer.getPointerPoint;

		// -----  ----- //

		return Unidragger;

		})); 
	} (unidragger));
	return unidragger.exports;
}

var hasRequiredDrag;

function requireDrag () {
	if (hasRequiredDrag) return drag.exports;
	hasRequiredDrag = 1;
	(function (module) {
		// drag
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireFlickity(),
		        requireUnidragger(),
		        requireUtils()
		    );
		  } else {
		    // browser global
		    window.Flickity = factory(
		        window,
		        window.Flickity,
		        window.Unidragger,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, Flickity, Unidragger, utils ) {

		// ----- defaults ----- //

		utils.extend( Flickity.defaults, {
		  draggable: '>1',
		  dragThreshold: 3,
		} );

		// ----- create ----- //

		Flickity.createMethods.push('_createDrag');

		// -------------------------- drag prototype -------------------------- //

		var proto = Flickity.prototype;
		utils.extend( proto, Unidragger.prototype );
		proto._touchActionValue = 'pan-y';

		// --------------------------  -------------------------- //

		var isTouch = 'createTouch' in document;
		var isTouchmoveScrollCanceled = false;

		proto._createDrag = function() {
		  this.on( 'activate', this.onActivateDrag );
		  this.on( 'uiChange', this._uiChangeDrag );
		  this.on( 'deactivate', this.onDeactivateDrag );
		  this.on( 'cellChange', this.updateDraggable );
		  // TODO updateDraggable on resize? if groupCells & slides change
		  // HACK - add seemingly innocuous handler to fix iOS 10 scroll behavior
		  // #457, RubaXa/Sortable#973
		  if ( isTouch && !isTouchmoveScrollCanceled ) {
		    window.addEventListener( 'touchmove', function() {} );
		    isTouchmoveScrollCanceled = true;
		  }
		};

		proto.onActivateDrag = function() {
		  this.handles = [ this.viewport ];
		  this.bindHandles();
		  this.updateDraggable();
		};

		proto.onDeactivateDrag = function() {
		  this.unbindHandles();
		  this.element.classList.remove('is-draggable');
		};

		proto.updateDraggable = function() {
		  // disable dragging if less than 2 slides. #278
		  if ( this.options.draggable == '>1' ) {
		    this.isDraggable = this.slides.length > 1;
		  } else {
		    this.isDraggable = this.options.draggable;
		  }
		  if ( this.isDraggable ) {
		    this.element.classList.add('is-draggable');
		  } else {
		    this.element.classList.remove('is-draggable');
		  }
		};

		// backwards compatibility
		proto.bindDrag = function() {
		  this.options.draggable = true;
		  this.updateDraggable();
		};

		proto.unbindDrag = function() {
		  this.options.draggable = false;
		  this.updateDraggable();
		};

		proto._uiChangeDrag = function() {
		  delete this.isFreeScrolling;
		};

		// -------------------------- pointer events -------------------------- //

		proto.pointerDown = function( event, pointer ) {
		  if ( !this.isDraggable ) {
		    this._pointerDownDefault( event, pointer );
		    return;
		  }
		  var isOkay = this.okayPointerDown( event );
		  if ( !isOkay ) {
		    return;
		  }

		  this._pointerDownPreventDefault( event );
		  this.pointerDownFocus( event );
		  // blur
		  if ( document.activeElement != this.element ) {
		    // do not blur if already focused
		    this.pointerDownBlur();
		  }

		  // stop if it was moving
		  this.dragX = this.x;
		  this.viewport.classList.add('is-pointer-down');
		  // track scrolling
		  this.pointerDownScroll = getScrollPosition();
		  window.addEventListener( 'scroll', this );

		  this._pointerDownDefault( event, pointer );
		};

		// default pointerDown logic, used for staticClick
		proto._pointerDownDefault = function( event, pointer ) {
		  // track start event position
		  // Safari 9 overrides pageX and pageY. These values needs to be copied. #779
		  this.pointerDownPointer = {
		    pageX: pointer.pageX,
		    pageY: pointer.pageY,
		  };
		  // bind move and end events
		  this._bindPostStartEvents( event );
		  this.dispatchEvent( 'pointerDown', event, [ pointer ] );
		};

		var focusNodes = {
		  INPUT: true,
		  TEXTAREA: true,
		  SELECT: true,
		};

		proto.pointerDownFocus = function( event ) {
		  var isFocusNode = focusNodes[ event.target.nodeName ];
		  if ( !isFocusNode ) {
		    this.focus();
		  }
		};

		proto._pointerDownPreventDefault = function( event ) {
		  var isTouchStart = event.type == 'touchstart';
		  var isTouchPointer = event.pointerType == 'touch';
		  var isFocusNode = focusNodes[ event.target.nodeName ];
		  if ( !isTouchStart && !isTouchPointer && !isFocusNode ) {
		    event.preventDefault();
		  }
		};

		// ----- move ----- //

		proto.hasDragStarted = function( moveVector ) {
		  return Math.abs( moveVector.x ) > this.options.dragThreshold;
		};

		// ----- up ----- //

		proto.pointerUp = function( event, pointer ) {
		  delete this.isTouchScrolling;
		  this.viewport.classList.remove('is-pointer-down');
		  this.dispatchEvent( 'pointerUp', event, [ pointer ] );
		  this._dragPointerUp( event, pointer );
		};

		proto.pointerDone = function() {
		  window.removeEventListener( 'scroll', this );
		  delete this.pointerDownScroll;
		};

		// -------------------------- dragging -------------------------- //

		proto.dragStart = function( event, pointer ) {
		  if ( !this.isDraggable ) {
		    return;
		  }
		  this.dragStartPosition = this.x;
		  this.startAnimation();
		  window.removeEventListener( 'scroll', this );
		  this.dispatchEvent( 'dragStart', event, [ pointer ] );
		};

		proto.pointerMove = function( event, pointer ) {
		  var moveVector = this._dragPointerMove( event, pointer );
		  this.dispatchEvent( 'pointerMove', event, [ pointer, moveVector ] );
		  this._dragMove( event, pointer, moveVector );
		};

		proto.dragMove = function( event, pointer, moveVector ) {
		  if ( !this.isDraggable ) {
		    return;
		  }
		  event.preventDefault();

		  this.previousDragX = this.dragX;
		  // reverse if right-to-left
		  var direction = this.options.rightToLeft ? -1 : 1;
		  if ( this.options.wrapAround ) {
		    // wrap around move. #589
		    moveVector.x %= this.slideableWidth;
		  }
		  var dragX = this.dragStartPosition + moveVector.x * direction;

		  if ( !this.options.wrapAround && this.slides.length ) {
		    // slow drag
		    var originBound = Math.max( -this.slides[0].target, this.dragStartPosition );
		    dragX = dragX > originBound ? ( dragX + originBound ) * 0.5 : dragX;
		    var endBound = Math.min( -this.getLastSlide().target, this.dragStartPosition );
		    dragX = dragX < endBound ? ( dragX + endBound ) * 0.5 : dragX;
		  }

		  this.dragX = dragX;

		  this.dragMoveTime = new Date();
		  this.dispatchEvent( 'dragMove', event, [ pointer, moveVector ] );
		};

		proto.dragEnd = function( event, pointer ) {
		  if ( !this.isDraggable ) {
		    return;
		  }
		  if ( this.options.freeScroll ) {
		    this.isFreeScrolling = true;
		  }
		  // set selectedIndex based on where flick will end up
		  var index = this.dragEndRestingSelect();

		  if ( this.options.freeScroll && !this.options.wrapAround ) {
		    // if free-scroll & not wrap around
		    // do not free-scroll if going outside of bounding slides
		    // so bounding slides can attract slider, and keep it in bounds
		    var restingX = this.getRestingPosition();
		    this.isFreeScrolling = -restingX > this.slides[0].target &&
		      -restingX < this.getLastSlide().target;
		  } else if ( !this.options.freeScroll && index == this.selectedIndex ) {
		    // boost selection if selected index has not changed
		    index += this.dragEndBoostSelect();
		  }
		  delete this.previousDragX;
		  // apply selection
		  // TODO refactor this, selecting here feels weird
		  // HACK, set flag so dragging stays in correct direction
		  this.isDragSelect = this.options.wrapAround;
		  this.select( index );
		  delete this.isDragSelect;
		  this.dispatchEvent( 'dragEnd', event, [ pointer ] );
		};

		proto.dragEndRestingSelect = function() {
		  var restingX = this.getRestingPosition();
		  // how far away from selected slide
		  var distance = Math.abs( this.getSlideDistance( -restingX, this.selectedIndex ) );
		  // get closet resting going up and going down
		  var positiveResting = this._getClosestResting( restingX, distance, 1 );
		  var negativeResting = this._getClosestResting( restingX, distance, -1 );
		  // use closer resting for wrap-around
		  var index = positiveResting.distance < negativeResting.distance ?
		    positiveResting.index : negativeResting.index;
		  return index;
		};

		/**
		 * given resting X and distance to selected cell
		 * get the distance and index of the closest cell
		 * @param {Number} restingX - estimated post-flick resting position
		 * @param {Number} distance - distance to selected cell
		 * @param {Integer} increment - +1 or -1, going up or down
		 * @returns {Object} - { distance: {Number}, index: {Integer} }
		 */
		proto._getClosestResting = function( restingX, distance, increment ) {
		  var index = this.selectedIndex;
		  var minDistance = Infinity;
		  var condition = this.options.contain && !this.options.wrapAround ?
		    // if contain, keep going if distance is equal to minDistance
		    function( dist, minDist ) {
		      return dist <= minDist;
		    } : function( dist, minDist ) {
		      return dist < minDist;
		    };
		  while ( condition( distance, minDistance ) ) {
		    // measure distance to next cell
		    index += increment;
		    minDistance = distance;
		    distance = this.getSlideDistance( -restingX, index );
		    if ( distance === null ) {
		      break;
		    }
		    distance = Math.abs( distance );
		  }
		  return {
		    distance: minDistance,
		    // selected was previous index
		    index: index - increment,
		  };
		};

		/**
		 * measure distance between x and a slide target
		 * @param {Number} x - horizontal position
		 * @param {Integer} index - slide index
		 * @returns {Number} - slide distance
		 */
		proto.getSlideDistance = function( x, index ) {
		  var len = this.slides.length;
		  // wrap around if at least 2 slides
		  var isWrapAround = this.options.wrapAround && len > 1;
		  var slideIndex = isWrapAround ? utils.modulo( index, len ) : index;
		  var slide = this.slides[ slideIndex ];
		  if ( !slide ) {
		    return null;
		  }
		  // add distance for wrap-around slides
		  var wrap = isWrapAround ? this.slideableWidth * Math.floor( index/len ) : 0;
		  return x - ( slide.target + wrap );
		};

		proto.dragEndBoostSelect = function() {
		  // do not boost if no previousDragX or dragMoveTime
		  if ( this.previousDragX === undefined || !this.dragMoveTime ||
		    // or if drag was held for 100 ms
		    new Date() - this.dragMoveTime > 100 ) {
		    return 0;
		  }

		  var distance = this.getSlideDistance( -this.dragX, this.selectedIndex );
		  var delta = this.previousDragX - this.dragX;
		  if ( distance > 0 && delta > 0 ) {
		    // boost to next if moving towards the right, and positive velocity
		    return 1;
		  } else if ( distance < 0 && delta < 0 ) {
		    // boost to previous if moving towards the left, and negative velocity
		    return -1;
		  }
		  return 0;
		};

		// ----- staticClick ----- //

		proto.staticClick = function( event, pointer ) {
		  // get clickedCell, if cell was clicked
		  var clickedCell = this.getParentCell( event.target );
		  var cellElem = clickedCell && clickedCell.element;
		  var cellIndex = clickedCell && this.cells.indexOf( clickedCell );
		  this.dispatchEvent( 'staticClick', event, [ pointer, cellElem, cellIndex ] );
		};

		// ----- scroll ----- //

		proto.onscroll = function() {
		  var scroll = getScrollPosition();
		  var scrollMoveX = this.pointerDownScroll.x - scroll.x;
		  var scrollMoveY = this.pointerDownScroll.y - scroll.y;
		  // cancel click/tap if scroll is too much
		  if ( Math.abs( scrollMoveX ) > 3 || Math.abs( scrollMoveY ) > 3 ) {
		    this._pointerDone();
		  }
		};

		// ----- utils ----- //

		function getScrollPosition() {
		  return {
		    x: window.pageXOffset,
		    y: window.pageYOffset,
		  };
		}

		// -----  ----- //

		return Flickity;

		} ) ); 
	} (drag));
	return drag.exports;
}

var prevNextButton = {exports: {}};

var hasRequiredPrevNextButton;

function requirePrevNextButton () {
	if (hasRequiredPrevNextButton) return prevNextButton.exports;
	hasRequiredPrevNextButton = 1;
	(function (module) {
		// prev/next buttons
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireFlickity(),
		        requireUnipointer(),
		        requireUtils()
		    );
		  } else {
		    // browser global
		    factory(
		        window,
		        window.Flickity,
		        window.Unipointer,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, Flickity, Unipointer, utils ) {

		var svgURI = 'http://www.w3.org/2000/svg';

		// -------------------------- PrevNextButton -------------------------- //

		function PrevNextButton( direction, parent ) {
		  this.direction = direction;
		  this.parent = parent;
		  this._create();
		}

		PrevNextButton.prototype = Object.create( Unipointer.prototype );

		PrevNextButton.prototype._create = function() {
		  // properties
		  this.isEnabled = true;
		  this.isPrevious = this.direction == -1;
		  var leftDirection = this.parent.options.rightToLeft ? 1 : -1;
		  this.isLeft = this.direction == leftDirection;

		  var element = this.element = document.createElement('button');
		  element.className = 'flickity-button flickity-prev-next-button';
		  element.className += this.isPrevious ? ' previous' : ' next';
		  // prevent button from submitting form http://stackoverflow.com/a/10836076/182183
		  element.setAttribute( 'type', 'button' );
		  // init as disabled
		  this.disable();

		  element.setAttribute( 'aria-label', this.isPrevious ? 'Previous' : 'Next' );

		  // create arrow
		  var svg = this.createSVG();
		  element.appendChild( svg );
		  // events
		  this.parent.on( 'select', this.update.bind( this ) );
		  this.on( 'pointerDown', this.parent.childUIPointerDown.bind( this.parent ) );
		};

		PrevNextButton.prototype.activate = function() {
		  this.bindStartEvent( this.element );
		  this.element.addEventListener( 'click', this );
		  // add to DOM
		  this.parent.element.appendChild( this.element );
		};

		PrevNextButton.prototype.deactivate = function() {
		  // remove from DOM
		  this.parent.element.removeChild( this.element );
		  // click events
		  this.unbindStartEvent( this.element );
		  this.element.removeEventListener( 'click', this );
		};

		PrevNextButton.prototype.createSVG = function() {
		  var svg = document.createElementNS( svgURI, 'svg' );
		  svg.setAttribute( 'class', 'flickity-button-icon' );
		  svg.setAttribute( 'viewBox', '0 0 100 100' );
		  var path = document.createElementNS( svgURI, 'path' );
		  var pathMovements = getArrowMovements( this.parent.options.arrowShape );
		  path.setAttribute( 'd', pathMovements );
		  path.setAttribute( 'class', 'arrow' );
		  // rotate arrow
		  if ( !this.isLeft ) {
		    path.setAttribute( 'transform', 'translate(100, 100) rotate(180) ' );
		  }
		  svg.appendChild( path );
		  return svg;
		};

		// get SVG path movmement
		function getArrowMovements( shape ) {
		  // use shape as movement if string
		  if ( typeof shape == 'string' ) {
		    return shape;
		  }
		  // create movement string
		  return 'M ' + shape.x0 + ',50' +
		    ' L ' + shape.x1 + ',' + ( shape.y1 + 50 ) +
		    ' L ' + shape.x2 + ',' + ( shape.y2 + 50 ) +
		    ' L ' + shape.x3 + ',50 ' +
		    ' L ' + shape.x2 + ',' + ( 50 - shape.y2 ) +
		    ' L ' + shape.x1 + ',' + ( 50 - shape.y1 ) +
		    ' Z';
		}

		PrevNextButton.prototype.handleEvent = utils.handleEvent;

		PrevNextButton.prototype.onclick = function() {
		  if ( !this.isEnabled ) {
		    return;
		  }
		  this.parent.uiChange();
		  var method = this.isPrevious ? 'previous' : 'next';
		  this.parent[ method ]();
		};

		// -----  ----- //

		PrevNextButton.prototype.enable = function() {
		  if ( this.isEnabled ) {
		    return;
		  }
		  this.element.disabled = false;
		  this.isEnabled = true;
		};

		PrevNextButton.prototype.disable = function() {
		  if ( !this.isEnabled ) {
		    return;
		  }
		  this.element.disabled = true;
		  this.isEnabled = false;
		};

		PrevNextButton.prototype.update = function() {
		  // index of first or last slide, if previous or next
		  var slides = this.parent.slides;
		  // enable is wrapAround and at least 2 slides
		  if ( this.parent.options.wrapAround && slides.length > 1 ) {
		    this.enable();
		    return;
		  }
		  var lastIndex = slides.length ? slides.length - 1 : 0;
		  var boundIndex = this.isPrevious ? 0 : lastIndex;
		  var method = this.parent.selectedIndex == boundIndex ? 'disable' : 'enable';
		  this[ method ]();
		};

		PrevNextButton.prototype.destroy = function() {
		  this.deactivate();
		  this.allOff();
		};

		// -------------------------- Flickity prototype -------------------------- //

		utils.extend( Flickity.defaults, {
		  prevNextButtons: true,
		  arrowShape: {
		    x0: 10,
		    x1: 60, y1: 50,
		    x2: 70, y2: 40,
		    x3: 30,
		  },
		} );

		Flickity.createMethods.push('_createPrevNextButtons');
		var proto = Flickity.prototype;

		proto._createPrevNextButtons = function() {
		  if ( !this.options.prevNextButtons ) {
		    return;
		  }

		  this.prevButton = new PrevNextButton( -1, this );
		  this.nextButton = new PrevNextButton( 1, this );

		  this.on( 'activate', this.activatePrevNextButtons );
		};

		proto.activatePrevNextButtons = function() {
		  this.prevButton.activate();
		  this.nextButton.activate();
		  this.on( 'deactivate', this.deactivatePrevNextButtons );
		};

		proto.deactivatePrevNextButtons = function() {
		  this.prevButton.deactivate();
		  this.nextButton.deactivate();
		  this.off( 'deactivate', this.deactivatePrevNextButtons );
		};

		// --------------------------  -------------------------- //

		Flickity.PrevNextButton = PrevNextButton;

		return Flickity;

		} ) ); 
	} (prevNextButton));
	return prevNextButton.exports;
}

var pageDots = {exports: {}};

var hasRequiredPageDots;

function requirePageDots () {
	if (hasRequiredPageDots) return pageDots.exports;
	hasRequiredPageDots = 1;
	(function (module) {
		// page dots
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireFlickity(),
		        requireUnipointer(),
		        requireUtils()
		    );
		  } else {
		    // browser global
		    factory(
		        window,
		        window.Flickity,
		        window.Unipointer,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, Flickity, Unipointer, utils ) {

		function PageDots( parent ) {
		  this.parent = parent;
		  this._create();
		}

		PageDots.prototype = Object.create( Unipointer.prototype );

		PageDots.prototype._create = function() {
		  // create holder element
		  this.holder = document.createElement('ol');
		  this.holder.className = 'flickity-page-dots';
		  // create dots, array of elements
		  this.dots = [];
		  // events
		  this.handleClick = this.onClick.bind( this );
		  this.on( 'pointerDown', this.parent.childUIPointerDown.bind( this.parent ) );
		};

		PageDots.prototype.activate = function() {
		  this.setDots();
		  this.holder.addEventListener( 'click', this.handleClick );
		  this.bindStartEvent( this.holder );
		  // add to DOM
		  this.parent.element.appendChild( this.holder );
		};

		PageDots.prototype.deactivate = function() {
		  this.holder.removeEventListener( 'click', this.handleClick );
		  this.unbindStartEvent( this.holder );
		  // remove from DOM
		  this.parent.element.removeChild( this.holder );
		};

		PageDots.prototype.setDots = function() {
		  // get difference between number of slides and number of dots
		  var delta = this.parent.slides.length - this.dots.length;
		  if ( delta > 0 ) {
		    this.addDots( delta );
		  } else if ( delta < 0 ) {
		    this.removeDots( -delta );
		  }
		};

		PageDots.prototype.addDots = function( count ) {
		  var fragment = document.createDocumentFragment();
		  var newDots = [];
		  var length = this.dots.length;
		  var max = length + count;

		  for ( var i = length; i < max; i++ ) {
		    var dot = document.createElement('li');
		    dot.className = 'dot';
		    dot.setAttribute( 'aria-label', 'Page dot ' + ( i + 1 ) );
		    fragment.appendChild( dot );
		    newDots.push( dot );
		  }

		  this.holder.appendChild( fragment );
		  this.dots = this.dots.concat( newDots );
		};

		PageDots.prototype.removeDots = function( count ) {
		  // remove from this.dots collection
		  var removeDots = this.dots.splice( this.dots.length - count, count );
		  // remove from DOM
		  removeDots.forEach( function( dot ) {
		    this.holder.removeChild( dot );
		  }, this );
		};

		PageDots.prototype.updateSelected = function() {
		  // remove selected class on previous
		  if ( this.selectedDot ) {
		    this.selectedDot.className = 'dot';
		    this.selectedDot.removeAttribute('aria-current');
		  }
		  // don't proceed if no dots
		  if ( !this.dots.length ) {
		    return;
		  }
		  this.selectedDot = this.dots[ this.parent.selectedIndex ];
		  this.selectedDot.className = 'dot is-selected';
		  this.selectedDot.setAttribute( 'aria-current', 'step' );
		};

		PageDots.prototype.onTap = // old method name, backwards-compatible
		PageDots.prototype.onClick = function( event ) {
		  var target = event.target;
		  // only care about dot clicks
		  if ( target.nodeName != 'LI' ) {
		    return;
		  }

		  this.parent.uiChange();
		  var index = this.dots.indexOf( target );
		  this.parent.select( index );
		};

		PageDots.prototype.destroy = function() {
		  this.deactivate();
		  this.allOff();
		};

		Flickity.PageDots = PageDots;

		// -------------------------- Flickity -------------------------- //

		utils.extend( Flickity.defaults, {
		  pageDots: true,
		} );

		Flickity.createMethods.push('_createPageDots');

		var proto = Flickity.prototype;

		proto._createPageDots = function() {
		  if ( !this.options.pageDots ) {
		    return;
		  }
		  this.pageDots = new PageDots( this );
		  // events
		  this.on( 'activate', this.activatePageDots );
		  this.on( 'select', this.updateSelectedPageDots );
		  this.on( 'cellChange', this.updatePageDots );
		  this.on( 'resize', this.updatePageDots );
		  this.on( 'deactivate', this.deactivatePageDots );
		};

		proto.activatePageDots = function() {
		  this.pageDots.activate();
		};

		proto.updateSelectedPageDots = function() {
		  this.pageDots.updateSelected();
		};

		proto.updatePageDots = function() {
		  this.pageDots.setDots();
		};

		proto.deactivatePageDots = function() {
		  this.pageDots.deactivate();
		};

		// -----  ----- //

		Flickity.PageDots = PageDots;

		return Flickity;

		} ) ); 
	} (pageDots));
	return pageDots.exports;
}

var player = {exports: {}};

var hasRequiredPlayer;

function requirePlayer () {
	if (hasRequiredPlayer) return player.exports;
	hasRequiredPlayer = 1;
	(function (module) {
		// player & autoPlay
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        requireEvEmitter(),
		        requireUtils(),
		        requireFlickity()
		    );
		  } else {
		    // browser global
		    factory(
		        window.EvEmitter,
		        window.fizzyUIUtils,
		        window.Flickity
		    );
		  }

		}( window, function factory( EvEmitter, utils, Flickity ) {

		// -------------------------- Player -------------------------- //

		function Player( parent ) {
		  this.parent = parent;
		  this.state = 'stopped';
		  // visibility change event handler
		  this.onVisibilityChange = this.visibilityChange.bind( this );
		  this.onVisibilityPlay = this.visibilityPlay.bind( this );
		}

		Player.prototype = Object.create( EvEmitter.prototype );

		// start play
		Player.prototype.play = function() {
		  if ( this.state == 'playing' ) {
		    return;
		  }
		  // do not play if page is hidden, start playing when page is visible
		  var isPageHidden = document.hidden;
		  if ( isPageHidden ) {
		    document.addEventListener( 'visibilitychange', this.onVisibilityPlay );
		    return;
		  }

		  this.state = 'playing';
		  // listen to visibility change
		  document.addEventListener( 'visibilitychange', this.onVisibilityChange );
		  // start ticking
		  this.tick();
		};

		Player.prototype.tick = function() {
		  // do not tick if not playing
		  if ( this.state != 'playing' ) {
		    return;
		  }

		  var time = this.parent.options.autoPlay;
		  // default to 3 seconds
		  time = typeof time == 'number' ? time : 3000;
		  var _this = this;
		  // HACK: reset ticks if stopped and started within interval
		  this.clear();
		  this.timeout = setTimeout( function() {
		    _this.parent.next( true );
		    _this.tick();
		  }, time );
		};

		Player.prototype.stop = function() {
		  this.state = 'stopped';
		  this.clear();
		  // remove visibility change event
		  document.removeEventListener( 'visibilitychange', this.onVisibilityChange );
		};

		Player.prototype.clear = function() {
		  clearTimeout( this.timeout );
		};

		Player.prototype.pause = function() {
		  if ( this.state == 'playing' ) {
		    this.state = 'paused';
		    this.clear();
		  }
		};

		Player.prototype.unpause = function() {
		  // re-start play if paused
		  if ( this.state == 'paused' ) {
		    this.play();
		  }
		};

		// pause if page visibility is hidden, unpause if visible
		Player.prototype.visibilityChange = function() {
		  var isPageHidden = document.hidden;
		  this[ isPageHidden ? 'pause' : 'unpause' ]();
		};

		Player.prototype.visibilityPlay = function() {
		  this.play();
		  document.removeEventListener( 'visibilitychange', this.onVisibilityPlay );
		};

		// -------------------------- Flickity -------------------------- //

		utils.extend( Flickity.defaults, {
		  pauseAutoPlayOnHover: true,
		} );

		Flickity.createMethods.push('_createPlayer');
		var proto = Flickity.prototype;

		proto._createPlayer = function() {
		  this.player = new Player( this );

		  this.on( 'activate', this.activatePlayer );
		  this.on( 'uiChange', this.stopPlayer );
		  this.on( 'pointerDown', this.stopPlayer );
		  this.on( 'deactivate', this.deactivatePlayer );
		};

		proto.activatePlayer = function() {
		  if ( !this.options.autoPlay ) {
		    return;
		  }
		  this.player.play();
		  this.element.addEventListener( 'mouseenter', this );
		};

		// Player API, don't hate the ... thanks I know where the door is

		proto.playPlayer = function() {
		  this.player.play();
		};

		proto.stopPlayer = function() {
		  this.player.stop();
		};

		proto.pausePlayer = function() {
		  this.player.pause();
		};

		proto.unpausePlayer = function() {
		  this.player.unpause();
		};

		proto.deactivatePlayer = function() {
		  this.player.stop();
		  this.element.removeEventListener( 'mouseenter', this );
		};

		// ----- mouseenter/leave ----- //

		// pause auto-play on hover
		proto.onmouseenter = function() {
		  if ( !this.options.pauseAutoPlayOnHover ) {
		    return;
		  }
		  this.player.pause();
		  this.element.addEventListener( 'mouseleave', this );
		};

		// resume auto-play on hover off
		proto.onmouseleave = function() {
		  this.player.unpause();
		  this.element.removeEventListener( 'mouseleave', this );
		};

		// -----  ----- //

		Flickity.Player = Player;

		return Flickity;

		} ) ); 
	} (player));
	return player.exports;
}

var addRemoveCell = {exports: {}};

var hasRequiredAddRemoveCell;

function requireAddRemoveCell () {
	if (hasRequiredAddRemoveCell) return addRemoveCell.exports;
	hasRequiredAddRemoveCell = 1;
	(function (module) {
		// add, remove cell
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireFlickity(),
		        requireUtils()
		    );
		  } else {
		    // browser global
		    factory(
		        window,
		        window.Flickity,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, Flickity, utils ) {

		// append cells to a document fragment
		function getCellsFragment( cells ) {
		  var fragment = document.createDocumentFragment();
		  cells.forEach( function( cell ) {
		    fragment.appendChild( cell.element );
		  } );
		  return fragment;
		}

		// -------------------------- add/remove cell prototype -------------------------- //

		var proto = Flickity.prototype;

		/**
		 * Insert, prepend, or append cells
		 * @param {[Element, Array, NodeList]} elems - Elements to insert
		 * @param {Integer} index - Zero-based number to insert
		 */
		proto.insert = function( elems, index ) {
		  var cells = this._makeCells( elems );
		  if ( !cells || !cells.length ) {
		    return;
		  }
		  var len = this.cells.length;
		  // default to append
		  index = index === undefined ? len : index;
		  // add cells with document fragment
		  var fragment = getCellsFragment( cells );
		  // append to slider
		  var isAppend = index == len;
		  if ( isAppend ) {
		    this.slider.appendChild( fragment );
		  } else {
		    var insertCellElement = this.cells[ index ].element;
		    this.slider.insertBefore( fragment, insertCellElement );
		  }
		  // add to this.cells
		  if ( index === 0 ) {
		    // prepend, add to start
		    this.cells = cells.concat( this.cells );
		  } else if ( isAppend ) {
		    // append, add to end
		    this.cells = this.cells.concat( cells );
		  } else {
		    // insert in this.cells
		    var endCells = this.cells.splice( index, len - index );
		    this.cells = this.cells.concat( cells ).concat( endCells );
		  }

		  this._sizeCells( cells );
		  this.cellChange( index, true );
		};

		proto.append = function( elems ) {
		  this.insert( elems, this.cells.length );
		};

		proto.prepend = function( elems ) {
		  this.insert( elems, 0 );
		};

		/**
		 * Remove cells
		 * @param {[Element, Array, NodeList]} elems - ELements to remove
		 */
		proto.remove = function( elems ) {
		  var cells = this.getCells( elems );
		  if ( !cells || !cells.length ) {
		    return;
		  }

		  var minCellIndex = this.cells.length - 1;
		  // remove cells from collection & DOM
		  cells.forEach( function( cell ) {
		    cell.remove();
		    var index = this.cells.indexOf( cell );
		    minCellIndex = Math.min( index, minCellIndex );
		    utils.removeFrom( this.cells, cell );
		  }, this );

		  this.cellChange( minCellIndex, true );
		};

		/**
		 * logic to be run after a cell's size changes
		 * @param {Element} elem - cell's element
		 */
		proto.cellSizeChange = function( elem ) {
		  var cell = this.getCell( elem );
		  if ( !cell ) {
		    return;
		  }
		  cell.getSize();

		  var index = this.cells.indexOf( cell );
		  this.cellChange( index );
		};

		/**
		 * logic any time a cell is changed: added, removed, or size changed
		 * @param {Integer} changedCellIndex - index of the changed cell, optional
		 * @param {Boolean} isPositioningSlider - Positions slider after selection
		 */
		proto.cellChange = function( changedCellIndex, isPositioningSlider ) {
		  var prevSelectedElem = this.selectedElement;
		  this._positionCells( changedCellIndex );
		  this._getWrapShiftCells();
		  this.setGallerySize();
		  // update selectedIndex
		  // try to maintain position & select previous selected element
		  var cell = this.getCell( prevSelectedElem );
		  if ( cell ) {
		    this.selectedIndex = this.getCellSlideIndex( cell );
		  }
		  this.selectedIndex = Math.min( this.slides.length - 1, this.selectedIndex );

		  this.emitEvent( 'cellChange', [ changedCellIndex ] );
		  // position slider
		  this.select( this.selectedIndex );
		  // do not position slider after lazy load
		  if ( isPositioningSlider ) {
		    this.positionSliderAtSelected();
		  }
		};

		// -----  ----- //

		return Flickity;

		} ) ); 
	} (addRemoveCell));
	return addRemoveCell.exports;
}

var lazyload = {exports: {}};

var hasRequiredLazyload;

function requireLazyload () {
	if (hasRequiredLazyload) return lazyload.exports;
	hasRequiredLazyload = 1;
	(function (module) {
		// lazyload
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        window,
		        requireFlickity(),
		        requireUtils()
		    );
		  } else {
		    // browser global
		    factory(
		        window,
		        window.Flickity,
		        window.fizzyUIUtils
		    );
		  }

		}( window, function factory( window, Flickity, utils ) {

		Flickity.createMethods.push('_createLazyload');
		var proto = Flickity.prototype;

		proto._createLazyload = function() {
		  this.on( 'select', this.lazyLoad );
		};

		proto.lazyLoad = function() {
		  var lazyLoad = this.options.lazyLoad;
		  if ( !lazyLoad ) {
		    return;
		  }
		  // get adjacent cells, use lazyLoad option for adjacent count
		  var adjCount = typeof lazyLoad == 'number' ? lazyLoad : 0;
		  var cellElems = this.getAdjacentCellElements( adjCount );
		  // get lazy images in those cells
		  var lazyImages = [];
		  cellElems.forEach( function( cellElem ) {
		    var lazyCellImages = getCellLazyImages( cellElem );
		    lazyImages = lazyImages.concat( lazyCellImages );
		  } );
		  // load lazy images
		  lazyImages.forEach( function( img ) {
		    new LazyLoader( img, this );
		  }, this );
		};

		function getCellLazyImages( cellElem ) {
		  // check if cell element is lazy image
		  if ( cellElem.nodeName == 'IMG' ) {
		    var lazyloadAttr = cellElem.getAttribute('data-flickity-lazyload');
		    var srcAttr = cellElem.getAttribute('data-flickity-lazyload-src');
		    var srcsetAttr = cellElem.getAttribute('data-flickity-lazyload-srcset');
		    if ( lazyloadAttr || srcAttr || srcsetAttr ) {
		      return [ cellElem ];
		    }
		  }
		  // select lazy images in cell
		  var lazySelector = 'img[data-flickity-lazyload], ' +
		    'img[data-flickity-lazyload-src], img[data-flickity-lazyload-srcset]';
		  var imgs = cellElem.querySelectorAll( lazySelector );
		  return utils.makeArray( imgs );
		}

		// -------------------------- LazyLoader -------------------------- //

		/**
		 * class to handle loading images
		 * @param {Image} img - Image element
		 * @param {Flickity} flickity - Flickity instance
		 */
		function LazyLoader( img, flickity ) {
		  this.img = img;
		  this.flickity = flickity;
		  this.load();
		}

		LazyLoader.prototype.handleEvent = utils.handleEvent;

		LazyLoader.prototype.load = function() {
		  this.img.addEventListener( 'load', this );
		  this.img.addEventListener( 'error', this );
		  // get src & srcset
		  var src = this.img.getAttribute('data-flickity-lazyload') ||
		    this.img.getAttribute('data-flickity-lazyload-src');
		  var srcset = this.img.getAttribute('data-flickity-lazyload-srcset');
		  // set src & serset
		  this.img.src = src;
		  if ( srcset ) {
		    this.img.setAttribute( 'srcset', srcset );
		  }
		  // remove attr
		  this.img.removeAttribute('data-flickity-lazyload');
		  this.img.removeAttribute('data-flickity-lazyload-src');
		  this.img.removeAttribute('data-flickity-lazyload-srcset');
		};

		LazyLoader.prototype.onload = function( event ) {
		  this.complete( event, 'flickity-lazyloaded' );
		};

		LazyLoader.prototype.onerror = function( event ) {
		  this.complete( event, 'flickity-lazyerror' );
		};

		LazyLoader.prototype.complete = function( event, className ) {
		  // unbind events
		  this.img.removeEventListener( 'load', this );
		  this.img.removeEventListener( 'error', this );

		  var cell = this.flickity.getParentCell( this.img );
		  var cellElem = cell && cell.element;
		  this.flickity.cellSizeChange( cellElem );

		  this.img.classList.add( className );
		  this.flickity.dispatchEvent( 'lazyLoad', event, cellElem );
		};

		// -----  ----- //

		Flickity.LazyLoader = LazyLoader;

		return Flickity;

		} ) ); 
	} (lazyload));
	return lazyload.exports;
}

/*!
 * Flickity v2.2.2
 * Touch, responsive, flickable carousels
 *
 * Licensed GPLv3 for open source use
 * or Flickity Commercial License for commercial use
 *
 * https://flickity.metafizzy.co
 * Copyright 2015-2021 Metafizzy
 */

var hasRequiredJs;

function requireJs () {
	if (hasRequiredJs) return js.exports;
	hasRequiredJs = 1;
	(function (module) {
		( function( window, factory ) {
		  // universal module definition
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		        requireFlickity(),
		        requireDrag(),
		        requirePrevNextButton(),
		        requirePageDots(),
		        requirePlayer(),
		        requireAddRemoveCell(),
		        requireLazyload()
		    );
		  }

		} )( window, function factory( Flickity ) {
		  return Flickity;
		} ); 
	} (js));
	return js.exports;
}

/*!
 * Flickity fullscreen v1.1.1
 * Enable fullscreen view for Flickity
 */

var hasRequiredFullscreen;

function requireFullscreen () {
	if (hasRequiredFullscreen) return fullscreen.exports;
	hasRequiredFullscreen = 1;
	(function (module) {
		/*jshint browser: true, undef: true, unused: true, strict: true*/

		( function( window, factory ) {
		  // universal module definition
		  /*jshint strict: false */ /*globals define, module, require */
		  if ( module.exports ) {
		    // CommonJS
		    module.exports = factory(
		      requireJs()
		    );
		  } else {
		    // browser global
		    factory(
		      window.Flickity
		    );
		  }

		}( window, function factory( Flickity ) {

		Flickity.createMethods.push('_createFullscreen');
		var proto = Flickity.prototype;


		proto._createFullscreen = function() {
		  this.isFullscreen = false;

		  if ( !this.options.fullscreen ) {
		    return;
		  }
		  // buttons
		  this.viewFullscreenButton = new FullscreenButton( 'view', this );
		  this.exitFullscreenButton = new FullscreenButton( 'exit', this );

		  this.on( 'activate', this._changeFullscreenActive );
		  this.on( 'deactivate', this._changeFullscreenActive );
		};

		// ----- activation ----- //

		proto._changeFullscreenActive = function() {
		  var childMethod = this.isActive ? 'appendChild' : 'removeChild';
		  this.element[ childMethod ]( this.viewFullscreenButton.element );
		  this.element[ childMethod ]( this.exitFullscreenButton.element );
		  // activate or deactivate buttons
		  var activeMethod = this.isActive ? 'activate' : 'deactivate';
		  this.viewFullscreenButton[ activeMethod ]();
		  this.exitFullscreenButton[ activeMethod ]();
		};

		// ----- view, exit, toggle ----- //

		proto.viewFullscreen = function() {
		  this._changeFullscreen( true );
		  this.focus();
		};

		proto.exitFullscreen = function() {
		  this._changeFullscreen( false );
		};

		proto._changeFullscreen = function( isView ) {
		  if ( this.isFullscreen == isView ) {
		    return;
		  }
		  this.isFullscreen = isView;
		  var classMethod = isView ? 'add' : 'remove';
		  document.documentElement.classList[ classMethod ]('is-flickity-fullscreen');
		  this.element.classList[ classMethod ]('is-fullscreen');
		  this.resize();
		  // HACK extra reposition on fullscreen for images
		  if ( this.isFullscreen ) {
		    this.reposition();
		  }
		  this.dispatchEvent( 'fullscreenChange', null, [ isView ] );
		};

		proto.toggleFullscreen = function() {
		  this._changeFullscreen( !this.isFullscreen );
		};

		// ----- setGallerySize ----- //

		// overwrite so fullscreen cells are full height
		var setGallerySize = proto.setGallerySize;
		proto.setGallerySize = function() {
		  if ( !this.options.setGallerySize ) {
		    return;
		  }
		  if ( this.isFullscreen ) {
		    // remove height style on fullscreen
		    this.viewport.style.height = '';
		  } else {
		    // otherwise, do normal
		    setGallerySize.call( this );
		  }
		};

		// ----- keyboard ----- //

		// ESC key closes full screen
		Flickity.keyboardHandlers[27] = function() {
		  this.exitFullscreen();
		};

		// ----- FullscreenButton ----- //

		function FullscreenButton( name, flickity ) {
		  this.name = name;
		  this.createButton();
		  this.createIcon();
		  // events
		  // trigger viewFullscreen or exitFullscreen on click
		  this.onClick = function() {
		    flickity[ name + 'Fullscreen' ]();
		  };
		  this.clickHandler = this.onClick.bind( this );
		}

		FullscreenButton.prototype.createButton = function() {
		  var element = this.element = document.createElement('button');
		  element.className = 'flickity-button flickity-fullscreen-button ' +
		    'flickity-fullscreen-button-' + this.name;
		  // prevent button from submitting form
		  element.setAttribute( 'type', 'button' );
		  // set label
		  var label = capitalize( this.name + ' full-screen' );
		  element.setAttribute( 'aria-label', label );
		  element.title = label;
		};

		function capitalize( text ) {
		  return text[0].toUpperCase() + text.slice(1);
		}

		var svgURI = 'http://www.w3.org/2000/svg';

		var pathDirections = {
		  view: 'M15,20,7,28h5v4H0V20H4v5l8-8Zm5-5,8-8v5h4V0H20V4h5l-8,8Z',
		  exit: 'M32,3l-7,7h5v4H18V2h4V7l7-7ZM3,32l7-7v5h4V18H2v4H7L0,29Z',
		};

		FullscreenButton.prototype.createIcon = function() {
		  var svg = document.createElementNS( svgURI, 'svg');
		  svg.setAttribute( 'class', 'flickity-button-icon' );
		  svg.setAttribute( 'viewBox', '0 0 32 32' );
		  // path & direction
		  var path = document.createElementNS( svgURI, 'path');
		  var direction = pathDirections[ this.name ];
		  path.setAttribute( 'd', direction );
		  // put it together
		  svg.appendChild( path );
		  this.element.appendChild( svg );
		};

		FullscreenButton.prototype.activate = function() {
		  this.element.addEventListener( 'click', this.clickHandler );
		};

		FullscreenButton.prototype.deactivate = function() {
		  this.element.removeEventListener( 'click', this.clickHandler );
		};

		Flickity.FullscreenButton = FullscreenButton;

		// ----- fin ----- //

		return Flickity;

		})); 
	} (fullscreen));
	return fullscreen.exports;
}

var fullscreenExports = requireFullscreen();
var Flickity = /*@__PURE__*/getDefaultExportFromCjs(fullscreenExports);

/**
 * BasicSlider component.
 * @desc Internal component to utilise a {@link FlickityInstance} slider.
 * @internal
 * @constructor
 */
function BasicSlider({
  elementType = 'div',
  options,
  className,
  flickityRef,
  onReady = () => {},
  testId,
  children,
  sliderCounter = false,
  ...otherProps
}) {
  const CustomElement = `${elementType}`;
  const flkty = useRef(null);
  const [ready, setReady] = useState(false);
  const [isAlreadyReady, setIsAlreadyReady] = useState(false);
  const [flickityNode, setFlickityNode] = useState(null);
  /**
   * Initialize selected index and draggable and reload cells.
   */
  useEffect(() => {
    const {
      draggable,
      initialIndex
    } = options ?? {};
    if (ready && !isAlreadyReady) {
      setIsAlreadyReady(true);
      const isActive = flkty.current?.isActive;
      if (flkty.current) {
        flkty.current?.deactivate();
        flkty.current.selectedIndex = initialIndex || 0;
        if (draggable === undefined) {
          // @ts-ignore @FIXME: Check if we can omit this by not using ReactNode.
          flkty.current.options.draggable = children ? children.length > 1 : false;
        } else {
          if (flkty.current.options) {
            flkty.current.options.draggable = draggable;
          }
        }
      }
      if (isActive) {
        flkty.current?.activate();
      }
      if (options?.imagesLoaded && flickityNode) {
        imagesloaded(flickityNode, () => {
          flkty.current?.reloadCells();
        });
      }
    } else if (ready && isAlreadyReady) {
      onReady(flkty?.current);
    } else {
      flkty.current?.reloadCells();
    }
    return () => {};
  }, [options, children, flickityNode, ready, isAlreadyReady, onReady]);
  /**
   * Add children to flickity slider element via React Portal and set ready state.
   */
  const renderPortal = useCallback(() => {
    if (flickityNode) {
      const sliderNode = flickityNode.querySelector('.flickity-slider');
      // @TODO: Cast to node as we know that it cannot be null.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const element = /*#__PURE__*/createPortal(children, sliderNode);
      const setFlickityReady = () => {
        if (!ready) {
          const changeToReady = () => setReady(true);
          if (options?.imagesLoaded) {
            imagesloaded(flickityNode, changeToReady);
          } else {
            changeToReady();
          }
        }
        return null;
      };
      setTimeout(setFlickityReady, 0);
      return element;
    }
    return null;
  }, [flickityNode, children, ready, options?.imagesLoaded]);
  /**
   * Create the FlickityInstance object and save underlying node element to a React state.
   * Using callback refs, @see https://reactjs.org/docs/refs-and-the-dom.html#callback-refs .
   */
  const flickityWrapperRef = useCallback(node => {
    if (!flkty.current) {
      flkty.current = new Flickity(node, options);
      if (flickityRef) {
        flickityRef(flkty.current);
      }
      setFlickityNode(node);
    }
    return null;
  }, [options, flickityRef, setFlickityNode]);
  return /*#__PURE__*/React__default.createElement(CustomElement, {
    className: className,
    ref: flickityWrapperRef,
    "data-testid": testId,
    "data-slider-counter": sliderCounter,
    ...otherProps
  }, renderPortal());
}
const FLICKITY_BUTTONS_CLASS = 'flickity-prev-next-button';
const FLICKITY_DOTS_CLASS = 'flickity-page-dots';
const FLICKITY_RESIZE_CLASS = 'flickity-resize';
/**
 * Simple hook for handling {@link BasicSlider} side effects.
 * Handle the following side effects in different components in one place:
 * - Handle toggling slider controls.
 * - Dynamic children changes.
 * - Initial index selection.
 * - Managing slide height for different side effects inside slider content (e.g. font-loading glitches).
 * - Handling of reloaded browser tabs where the slider remain uninitialized.
 */
const useBasicSlider = (flickityRef, elements, {
  defaultSelected,
  hasDynamicElements = true,
  initialIndex,
  initialResizeThreshold = 0,
  selected
}) => {
  /**
   * Handle {@link BasicSlider} controls.
   */
  const toggleSliderControls = useCallback(() => {
    if (flickityRef) {
      const size = flickityRef?.size?.width;
      const {
        slideableWidth
      } = flickityRef;
      const slideable = (slideableWidth ?? 0) > (size ?? 0);
      const pageDotsHTMLCollection = flickityRef.element?.getElementsByClassName(FLICKITY_DOTS_CLASS);
      const flickityButtonsHTMLCollection = flickityRef.element?.getElementsByClassName(FLICKITY_BUTTONS_CLASS);
      /**
       * This fixes a bug where FlickityInstance is unable to reassign the draggable
       * behavior when resizing from a big to small viewport when `groupCells`
       * or `contain` options are set. The fix only kicks in when the `draggable`
       * option in FlickityInstance was set.
       * @see https://flickity.metafizzy.co/options.html#draggable
       * @see https://github.com/metafizzy/flickity/issues/960
       */
      if (flickityRef.options?.draggable) {
        // eslint-disable-next-line no-param-reassign
        flickityRef.isDraggable = slideable;
        flickityRef.element?.classList.toggle('is-draggable', slideable);
        flickityRef.element?.classList.toggle('is-slideable', slideable);
      }
      Array.from(pageDotsHTMLCollection ?? []).forEach(pageDots => {
        pageDots.classList.toggle('is-slideable', slideable);
      });
      Array.from(flickityButtonsHTMLCollection ?? []).forEach(btn => {
        btn.classList.toggle('is-slideable', slideable);
      });
    }
  }, [flickityRef]);
  /**
   * Handle {@link BasicSlider} side effects.
   */
  useEffect(() => {
    const instance = flickityRef;
    if (instance) {
      const resizeFn = instance.resize.bind(instance);
      instance.resize = () => {
        instance.element?.classList.remove(FLICKITY_RESIZE_CLASS);
        resizeFn();
        requestAnimationFrame(() => {
          instance.element?.classList.add(FLICKITY_RESIZE_CLASS);
          /**
           * Always keep the selected items visible.
           */
          if (selected !== null && selected !== undefined) {
            instance.selectCell(selected, false, true);
          }
        });
      };
      /**
       * Check if we have an initial value to instantly show this slide.
       */
      if (initialIndex !== null && initialIndex !== undefined) {
        /**
         * We select the cell here as we deal with `groupCell`.
         * @see https://flickity.metafizzy.co/api.html#selectcell
         */
        instance.selectCell(initialIndex, false, true);
      }
      const onReady = () => {
        debounce(instance.resize, initialResizeThreshold)();
        /**
         * Fixes incorrect height calculation.
         * Trigger resize after all fonts are loaded.
         */
        document?.fonts?.ready?.then(() => {
          debounce(instance.resize, initialResizeThreshold)();
        });
        /**
         * Fixes nasty layout bug for some journeys.
         * The {@link initialResizeThreshold} must be increased for this to work.
         * @see https://jira.platform.vwfs.io/browse/BRON-7534
         */
        const timeout = setTimeout(() => {
          resizeFn();
        }, initialResizeThreshold + 50);
        return () => {
          timeout && clearTimeout(timeout);
        };
      };
      instance.on?.('select', toggleSliderControls);
      instance.on?.('ready', onReady);
      return () => {
        instance.off?.('select', toggleSliderControls);
        instance.off?.('ready', onReady);
      };
    }
    return () => {};
  }, [flickityRef, initialIndex, initialResizeThreshold, selected, toggleSliderControls]);
  /**
   * Handle {@link BasicSlider} when children change dynamically.
   */
  useEffect(() => {
    const instance = flickityRef;
    if (instance && elements && hasDynamicElements) {
      /**
       * Resize FlickityInstance and recalculate the position of all children.
       * @see https://flickity.metafizzy.co/api.html#reloadcells
       * @see https://flickity.metafizzy.co/api.html#resize
       */
      instance.reloadCells();
      instance.resize();
    }
  }, [flickityRef, elements, defaultSelected, selected, hasDynamicElements]);
  /**
   * Resize/reposition the slider when a page gets visible.
   * Resize the slider when returning to a reloaded hidden page
   * to avoid rendering bugs.
   */
  useEffect(() => {
    const instance = flickityRef;
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (instance) {
          setTimeout(() => instance?.resize(), 100, 10);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [flickityRef]);
};

/**
 * The CardsSlider provides a space-saving way for adding an arbitrary
 * amount of cards. If the amount of cards exceeds the horizontal space,
 * a carousel slider is automatically created. This gives the user
 * the possibility to reach all available cards by sliding horizontally.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-cards-slider.html
 *
 * @constructor
 */
function CardsSlider({
  className,
  cellAlign = 'left',
  groupCells = true,
  children,
  testId,
  ...otherProps
}) {
  const initialResizeThreshold = 300;
  const [flickityRef, setFlickityRef] = useState(null);
  useBasicSlider(flickityRef, children, {
    initialResizeThreshold
  });
  const flickityOptions = {
    cellSelector: '.c-cards-slider__item',
    pageDots: true,
    prevNextButtons: true,
    groupCells,
    cellAlign,
    contain: false,
    freeScroll: true
  };
  const classNameList = classNames('c-cards-slider-container', className).trim();
  const flickityClassNameList = classNames('c-cards-slider').trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(BasicSlider, {
    elementType: "div",
    className: flickityClassNameList,
    flickityRef: c => setFlickityRef(c),
    options: flickityOptions,
    onReady: c => c?.element?.parentElement?.classList.add('is-initialized')
  }, children));
}
CardsSlider.displayName = 'CardsSlider';
/**
 * The CardsSliderItem component is internally used to wrap
 * the item content and display it inside the {@link CardsSlider}.
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {React.ReactNode} [children] - The content to display inside the {@link CardsSliderItem} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link CardsSliderItem} component.
 * @constructor
 */
function CardsSliderItem({
  className,
  children,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-cards-slider__item', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-cards-slider__card-wrapper"
  }, children));
}
CardsSliderItem.displayName = 'CardsSlider.Item';
CardsSlider.Item = CardsSliderItem;

/**
 * Use the Carousel component to create a slideshow that a user can interact with.
 * This slideshow can contain a series of {@link CarouselImage}.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-carousel--carousel.html
 *
 * @constructor
 */
function Carousel({
  autoPlay = false,
  buttonsOnHover,
  buttonsVisible,
  children,
  className,
  counterEnforced,
  fullscreen = false,
  itemAspectRatio,
  lazyLoad = false,
  objectFitContain,
  wrapAround = false,
  testId,
  ...otherProps
}) {
  const defaultAutoPlayInterval = 5000;
  const initialResizeThreshold = 300;
  const [flickityRef, setFlickityRef] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useBasicSlider(flickityRef, children, {
    initialResizeThreshold
  });
  /**
   * Handles {@link isFullscreen} when entering or existing fullscreen mode.
   */
  useEffect(() => {
    if (flickityRef) {
      flickityRef.on?.('fullscreenChange', setIsFullscreen);
    }
  }, [flickityRef]);
  /**
   * Add `[data-slider-counter]` attribute when {@link BasicSlider} is mounted
   * and {@link counterEnforced} is set.
   * Add `[data-carousel-fullscreen]` attribute when {@link BasicSlider} is mounted
   * and {@link fullscreen} is set.
   */
  useEffect(() => {
    if (flickityRef && counterEnforced) {
      flickityRef.element?.setAttribute('data-slider-counter', `${counterEnforced}`);
    }
    if (flickityRef && fullscreen) {
      flickityRef.element?.setAttribute('data-carousel-fullscreen', `${fullscreen}`);
    }
  }, [flickityRef, counterEnforced, fullscreen]);
  /**
   * Add `[data-testid]` attribute when {@link BasicSlider} is mounted
   * and {@link testId} is set.
   */
  useEffect(() => {
    if (flickityRef && testId) {
      flickityRef.element?.setAttribute('data-testid', testId);
    }
  }, [flickityRef, testId]);
  const flickityOptions = {
    autoPlay: autoPlay === true ? defaultAutoPlayInterval : autoPlay,
    cellSelector: '.c-carousel__item',
    fullscreen,
    imagesLoaded: true,
    lazyLoad,
    wrapAround
  };
  const classNameList = classNames('c-carousel', {
    'c-carousel--buttons-visible': buttonsVisible,
    'c-carousel--buttons-on-hover': buttonsOnHover
  }, className).trim();
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(BasicSlider, {
    className: classNameList,
    flickityRef: c => setFlickityRef(c),
    options: flickityOptions,
    ...otherProps
  }, React__default.Children.map(children, child => {
    if (/*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/React__default.cloneElement(child, {
        /**
         * Set {@link imgSizes} to `100vw` when entering fullscreen mode.
         * @see https://jira.platform.vwfs.io/browse/BRON-6972
         */
        imgSizes: isFullscreen ? '100vw' : child?.props.imgSizes,
        itemAspectRatio,
        lazyLoad,
        objectFitContain
      });
    }
  })));
}
Carousel.displayName = 'Carousel';
/**
 * The CarouselImage component is used internally to wrap the images
 * and display them within the {@link Carousel}.
 *
 * Note: {@link itemAspectRatio}, {@link lazyLoad} and {@link objectFitContain}
 * are set by parent {@link Carousel}.
 *
 * @param {string} [altText] - The `[alt]` attribute on the image element.
 * @param {string} [className] - Additional CSS classes on the image element.
 * @param {string} [imgSizes] - The `[sizes]` attribute on the image element.
 * @param {string} [imgSrc] - The `[src]` attribute on the image element.
 * @param {string} [imgSrcSet] - The `[srcset]` attribute on the image element.
 * @param {Carousel.itemAspectRatio} [itemAspectRatio] - {@link Carousel.itemAspectRatio}.
 * @param {Carousel.lazyLoad} [lazyLoad] - {@link Carousel.lazyLoad}.
 * @param {Carousel.objectFitContain} [objectFitContain] - {@link Carousel.objectFitContain}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The CarouselImage component.
 * @constructor
 */
function CarouselImage({
  altText,
  className,
  imgSizes,
  imgSrc,
  imgSrcSet,
  itemAspectRatio,
  lazyLoad,
  objectFitContain,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-carousel__item', {
    'o-ratio': itemAspectRatio,
    [`o-ratio--${itemAspectRatio}`]: itemAspectRatio
  }).trim();
  const imgClassNameList = classNames('c-carousel__image', {
    'u-object-fit-contain': objectFitContain,
    'o-ratio__content': itemAspectRatio
  }, className).trim();
  /**
   * Handles the use of specific attributes for Flickity or
   * native HTML attributes depending on {@link lazyLoad}.
   */
  const imgProps = {
    [`${lazyLoad ? 'data-flickity-lazyload-src' : 'src'}`]: imgSrc,
    [`${lazyLoad ? 'data-flickity-lazyload-srcset' : 'srcSet'}`]: imgSrcSet
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("img", {
    className: imgClassNameList,
    sizes: imgSizes,
    alt: altText,
    ...imgProps
  }));
}
CarouselImage.displayName = 'Carousel.Image';
Carousel.Image = CarouselImage;

/**
 * Bronson Checkbox component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-checkbox.html
 *
 * @constructor
 */
function Checkbox({
  children,
  className,
  error,
  id,
  required,
  requiredText,
  testId,
  ...otherProps
}) {
  const labelClassNameList = classNames('c-checkbox', className).trim();
  const inputClassNameList = classNames('c-checkbox__input').trim();
  /**
   * If {@link required} is set, render a `required` flag according {@link requiredText}.
   */
  function renderIfRequired(required, requiredText) {
    return required ? /*#__PURE__*/React__default.createElement("span", {
      "aria-hidden": "true"
    }, requiredText) : null;
  }
  return /*#__PURE__*/React__default.createElement("label", {
    className: labelClassNameList,
    htmlFor: id
  }, /*#__PURE__*/React__default.createElement("input", {
    id: id,
    className: inputClassNameList,
    type: "checkbox",
    "aria-invalid": error ? true : undefined,
    required: required ? true : undefined,
    "data-testid": testId,
    ...otherProps
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-checkbox__label"
  }, children, renderIfRequired(required, requiredText)));
}

/*
 * Helper constants and functions.
 */
// Make it easier for ourselves by putting some values in objects.
// In TypeScript, these would be enums.
const Keys = {
  Backspace: 'Backspace',
  Clear: 'Clear',
  Down: 'ArrowDown',
  End: 'End',
  Enter: 'Enter',
  Escape: 'Escape',
  Home: 'Home',
  Left: 'ArrowLeft',
  PageDown: 'PageDown',
  // @TODO: Focus up 10 options (or to first option).
  PageUp: 'PageUp',
  // @TODO: Focus down 10 options (or last option).
  Right: 'ArrowRight',
  Space: ' ',
  Tab: 'Tab',
  Up: 'ArrowUp'
};
const MenuActions = {
  Close: 0,
  CloseSelect: 1,
  First: 2,
  Last: 3,
  Next: 4,
  Open: 5,
  Previous: 6,
  Select: 7,
  Space: 8,
  Next10: 9,
  Previous10: 10,
  Type: 11
};
/**
 * Return Combobox action from key press.
 * @param {string|event} event - Key value from event object.
 * @param {boolean} menuOpen - If opening menu should be triggered.
 * @returns {number} - Action to be triggered.
 */
function getActionFromKey(event, menuOpen) {
  const {
    key,
    altKey,
    ctrlKey,
    metaKey
  } = event;
  // Handle opening when closed.
  if (!menuOpen && (key === Keys.Down || key === Keys.Enter || key === Keys.Space)) {
    return MenuActions.Open;
  }
  // Handle keys when open.
  if (key === Keys.Down) {
    return MenuActions.Next;
  }
  if (key === Keys.Up) {
    return MenuActions.Previous;
  }
  if (key === Keys.Home) {
    return MenuActions.First;
  }
  if (key === Keys.End) {
    return MenuActions.Last;
  }
  if (key === Keys.Escape) {
    return MenuActions.Close;
  }
  if (key === Keys.Enter) {
    return MenuActions.CloseSelect;
  }
  if (key === Keys.Space) {
    return MenuActions.Select;
  }
  if (key === Keys.PageUp) {
    return MenuActions.Previous10;
  }
  if (key === Keys.PageDown) {
    return MenuActions.Next10;
  }
  if (key === 'Backspace' || key === 'Clear' || key.length === 1 && key !== Keys.Space && !altKey && !ctrlKey && !metaKey) {
    return MenuActions.Type;
  }
  return Infinity;
}
/**
 * Get valid index based on action, current and max index.
 * If the index exceeds the available length, e.g. 18/20 from is selected
 * and PAGEDOWN is pressed, the max (20) gets returned.
 * @param {number} current - Current index.
 * @param {number} length - Maximum available index.
 * @param {number} action - MenuAction enum.
 * @returns
 */
function getUpdatedIndex(current, length, action) {
  const isLast = current === length - 1;
  switch (action) {
    case MenuActions.First:
      return 0;
    case MenuActions.Last:
      return length - 1;
    case MenuActions.Previous:
      return Math.max(current === 0 ? length - 1 : 0, current - 1);
    case MenuActions.Previous10:
      return Math.max(0, current - 10);
    case MenuActions.Next:
      return Math.min(isLast ? 0 : length, current + 1);
    case MenuActions.Next10:
      return Math.min(length, current + 10);
    default:
      return current;
  }
}

/**
 * The ComboboxContext holds the state of the current component
 * including selected values/options, open state, multiple state
 * and current focused options.
 * @type {React.Context<{}>}
 */
const ComboboxContext = /*#__PURE__*/React.createContext([]);
/**
 * Allowed reducer actions on the state.
 */
const comboboxReducerActions = {
  toggleOption: 'TOGGLE_OPTION',
  setOption: 'SET_OPTION',
  setFilterQuery: 'SET_FILTER_QUERY',
  openMenu: 'OPEN_MENU',
  closeMenu: 'CLOSE_MENU',
  toggleMenu: 'TOGGLE_MENU',
  focusOption: 'FOCUS_OPTION',
  focusOptionByAction: 'FOCUS_OPTION_BY_ACTION',
  focusOptionByLabel: 'FOCUS_OPTION_BY_TYPE',
  focusPrevOption: 'FOCUS_PREV_OPTION',
  focusNextOption: 'FOCUS_NEXT_OPTION',
  blurOption: 'BLUR_OPTION',
  setTouched: 'SET_TOUCHED'
};
/**
 * Sort the value array like the options array.
 * @param {ComboboxOption[]} values
 * @param {ComboboxOption[]} options
 * @returns {*}
 */
const getSortedValues = (values, options) => {
  if (!Array.isArray(options)) {
    return values;
  }
  /**
   * Sort user values according to options.
   */
  return values.sort((a, b) => options.findIndex(o => a.key === o.key) - options.findIndex(o => b.key === o.key));
};
/**
 * Our state update logic written as reducer pattern.
 * @param {ComboboxState} state - The state to update.
 * @param {ComboboxStateAction} action - Allowed actions types and payload.
 * @returns {ComboboxState} - Returns a new state object.
 */
function comboboxReducer(state, action) {
  switch (action.type) {
    case comboboxReducerActions.setOption:
      {
        /**
         * State update logic to set/activate an option:
         * 1. Check if multiple options are allowed.
         * 2. Check the value is present in state.
         * 3. Return the current state if so.
         * 4. Merge it with the current state if absent.
         * 5. Otherwise, return the current payload/value as the new selected for single mode.
         * @type {ComboboxOption[]|*[]}
         */
        // eslint-disable-next-line no-nested-ternary
        const nextState = state.isMulti // [1]
        ? state.value.find(({
          key
        }) => key === action.payload?.key) // [2]
        ? state.value // [3]
        : [...state.value, ...[action.payload]] // [4]
        : [action.payload]; // [5]
        return {
          ...state,
          value: nextState,
          filterQuery: '',
          touched: true
        };
      }
    case comboboxReducerActions.toggleOption:
      {
        /**
         * State update logic for toggling an option:
         * 1. Check if multiple options are allowed.
         * 2. Check the value is present in state.
         * 3. Exclude the current value if so.
         * 4. Merge it with the current state if absent.
         * 5. Otherwise, return the current payload/value as the new selected for single mode.
         * @type {ComboboxOption[]|*[]}
         */
        // eslint-disable-next-line no-nested-ternary
        const nextState = state.isMulti // [1]
        ? state.value.find(({
          key
        }) => key === action.payload?.key) // [2]
        ? state.value.filter(({
          key
        }) => key !== action.payload?.key) // [3]
        : [...state.value, ...[action.payload]] // [4]
        : [action.payload]; // [5]
        return {
          ...state,
          touched: true,
          value: nextState
        };
      }
    case comboboxReducerActions.openMenu:
      {
        const firstValue = getSortedValues(state.value, state.options)?.[0];
        /**
         * If a value was selected before restore the focus on this value.
         */
        if (firstValue && !state.isOpen) {
          const nextKey = state.options.find(o => o.key === firstValue?.key);
          const nextIndex = state.options.findIndex(o => o.key === firstValue.key);
          if (nextKey && nextIndex) {
            return {
              ...state,
              isOpen: true,
              touched: true,
              focusedOption: nextKey,
              currentIndex: nextIndex
            };
          }
        }
        return {
          ...state,
          isOpen: true,
          touched: true
        };
      }
    case comboboxReducerActions.closeMenu:
      {
        return {
          ...state,
          isOpen: false,
          filterQuery: ''
        };
      }
    case comboboxReducerActions.toggleMenu:
      {
        const firstValue = getSortedValues(state.value, state.options)?.[0];
        /**
         * If a value was selected before restore the focus on this value.
         */
        if (firstValue && !state.isOpen) {
          const nextKey = state.options.find(o => o.key === firstValue?.key);
          const nextIndex = state.options.findIndex(o => o.key === firstValue.key);
          if (nextKey && nextIndex) {
            return {
              ...state,
              isOpen: !state.isOpen,
              touched: true,
              focusedOption: nextKey,
              currentIndex: nextIndex
            };
          }
        }
        return {
          ...state,
          isOpen: !state.isOpen,
          touched: true
        };
      }
    case comboboxReducerActions.focusOption:
      {
        const nextKey = state.options.find(({
          key
        }) => action.payload?.key === key);
        const nextIndex = state.options.findIndex(({
          key
        }) => action.payload?.key === key);
        return {
          ...state,
          focusedOption: nextKey,
          currentIndex: nextIndex
        };
      }
    case comboboxReducerActions.focusOptionByLabel:
      {
        const nextKey = state.options.find(({
          value
        }) => value.toLowerCase().startsWith(action.payload?.label));
        const nextIndex = state.options.findIndex(({
          key
        }) => nextKey?.key === key);
        /**
         * If current key could not be found yielding an index of -1,
         * return the current state.
         */
        if (nextIndex < 0) {
          return state;
        }
        return {
          ...state,
          focusedOption: nextKey,
          currentIndex: nextIndex
        };
      }
    case comboboxReducerActions.focusOptionByAction:
      {
        const visibleOptions = state.options.filter(({
          value
        }) => value?.toLowerCase().includes(state.filterQuery?.toLowerCase() ?? ''));
        /**
         * Resolve the next/prev option index with handling disable states.
         * @param {number} currentIndex
         * @param {number} length
         * @param {number} currentAction
         * @returns {number|number|*|number}
         */
        const resolveIndex = (currentIndex, length, currentAction) => {
          const nextIndex = currentIndex === null ? 0 : getUpdatedIndex(currentIndex, length, currentAction);
          const nextKey = visibleOptions[nextIndex];
          /**
           * If a search yields only one disabled element return it.
           */
          if (nextKey?.disabled && length <= 1) {
            return nextIndex;
          }
          if (nextKey?.disabled) {
            /**
             * If the currentIndex and nextIndex are equal, increment it manually to prevent an infinite loop.
             */
            return resolveIndex(currentIndex === 0 && nextIndex === 0 ? nextIndex + 1 : nextIndex, length, currentAction);
          }
          return nextIndex;
        };
        const nextIndex = resolveIndex(state.currentIndex, visibleOptions.length, action.payload?.action);
        const nextKey = visibleOptions[nextIndex];
        return {
          ...state,
          focusedOption: nextKey,
          currentIndex: nextIndex
        };
      }
    case comboboxReducerActions.focusPrevOption:
      {
        const currentIndex = state.options.findIndex(({
          key
        }) => state.focusedOption?.key === key) ?? 0;
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        const prevOption = state.options[prevIndex];
        return {
          ...state,
          focusedOption: prevOption,
          currentIndex: prevIndex
        };
      }
    case comboboxReducerActions.focusNextOption:
      {
        const currentIndex = state.options.findIndex(({
          key
        }) => state.focusedOption?.key === key) ?? 0;
        const nextIndex = currentIndex < state.options.length - 1 ? currentIndex + 1 : currentIndex;
        const nextOption = state.options[nextIndex];
        return {
          ...state,
          focusedOption: nextOption,
          currentIndex: nextIndex
        };
      }
    case comboboxReducerActions.blurOption:
      {
        return {
          ...state,
          focusedOption: undefined,
          currentIndex: null
        };
      }
    case comboboxReducerActions.setFilterQuery:
      {
        return {
          ...state,
          currentIndex: null,
          filterQuery: action.payload,
          /**
           * Check if any option matches the filter payload.
           */
          hasOptions: state.options.some(({
            value
          }) => value?.toLowerCase().includes(action.payload?.toLowerCase()))
        };
      }
    case comboboxReducerActions.setTouched:
      {
        return {
          ...state,
          touched: true
        };
      }
    default:
      {
        throw new Error(`UNHANDLED ACTION TYPE: ${action.type}`);
      }
  }
}
/**
 * Convenience helper to create a context provider.
 * @param children - The component or children to wrap in the context.
 * @param initialValue - Initial state.
 * @returns {JSX.Element} - Returns a context {@link ComboboxProvider}.
 * @constructor
 */
function ComboboxProvider({
  children,
  initialValue
}) {
  const [state, dispatch] = React.useReducer(comboboxReducer, initialValue);
  const value = React.useMemo(() => [state, dispatch], [state]);
  return /*#__PURE__*/React.createElement(ComboboxContext.Provider, {
    value: value
  }, children);
}
/**
 * State update hook.
 * @returns {{}}
 */
function useCombobox() {
  const context = React.useContext(ComboboxContext);
  /**
   * Throw an exception if the hook is used outside a {@link ComboboxContext.Provider}.
   */
  if (context === undefined) {
    throw new Error('useCombobox must be used within a ComboboxProvider');
  }
  return context;
}

/**
 * @TODO: Add proper Typescript typings.
 * @type {React.Context<{}>}
 */
const FormFieldContext = /*#__PURE__*/React__default.createContext({});

/**
 * Bronson Input component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-input.html
 * @constructor
 */
const Input = /*#__PURE__*/forwardRef(({
  addonClassName,
  addonClickHandler,
  addonElement = 'span',
  addonIcon,
  addonSpinner,
  addonText,
  addonType,
  autosuggest,
  className,
  defaultValue,
  divProps,
  error,
  ghost,
  id,
  modifierStates,
  noBackground,
  onChange,
  reversed,
  stateIcon,
  success,
  type,
  testId,
  ...otherProps
}, ref) => {
  const CustomElement = `${addonElement}`;
  /**
   * Track internal state via the wrapping {@link FormFieldContext}.
   * @type {object}
   */
  const context = React__default.useContext(FormFieldContext);
  /**
   * Hook to set active/inactive states on the {@link Input}.
   */
  useEffect(() => {
    if (context?.setElementActive) {
      context.setElementActive(!!defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);
  const divClassNameList = classNames('c-input', {
    [`c-input--${type} `]: type,
    // Main class modifier convenience prop extension.
    'c-input--ghost': ghost,
    // Convenience prop from Bronson variants.
    'c-input--reversed ': reversed,
    // Convenience prop from Bronson variants.
    'c-input--autosuggest': autosuggest
  }, className).trim();
  const inputClassNameList = classNames('c-input__input', {
    'is-success': success,
    [`${modifierStates} `]: modifierStates
  }).trim();
  function renderIfStateIcon() {
    if (stateIcon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: "c-input__state-icon",
        role: undefined,
        viaCss: true
      });
    }
    return null;
  }
  const spanClassNameList = classNames('c-input__addon', {
    'c-input__addon--no-background': noBackground // Convenience prop from Bronson variants.
  }, addonClassName).trim();
  function renderIfAddon() {
    if (addonIcon || addonSpinner || addonText) {
      const addon = addonSpinner ? addonSpinner : addonText;
      return /*#__PURE__*/React__default.createElement(CustomElement, {
        className: spanClassNameList,
        /**
         * Render the element type if set, but always set it for `button`.
         */
        type: addonType ?? addonElement === 'button' ? addonElement : undefined,
        "aria-label": addonIcon || addonSpinner ? addonText : undefined,
        onClick: addonElement === 'button' ? addonClickHandler : null
      }, addonIcon ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Icon, {
        name: addonIcon,
        role: undefined
      }))) : addon);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList,
    ...divProps
  }, /*#__PURE__*/React__default.createElement("input", {
    defaultValue: defaultValue,
    id: id,
    name: id,
    className: inputClassNameList,
    type: `${type || 'text'}`,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `error-${id}` : undefined,
    "data-testid": testId,
    ref: ref,
    onChange: event => [onChange && onChange(event), context.setElementActive && context.setElementActive(!!event.target.value)],
    ...otherProps
  }), renderIfStateIcon(), renderIfAddon());
});

/**
 * Check if one option is of type {@link ComboboxGroupOption}.
 * @param {ComboboxGroupOption | ComboboxOption} option
 * @returns {boolean}
 */
const isGroupedOption = option => !!option.label;
/**
 * Check if the passed options have the {@link ComboboxGroupOption} type signature.
 * @param {ComboboxOptionsType} options
 * @returns {boolean}
 */
const hasGroupOptions = options => options.every(isGroupedOption);
/**
 * Filter options if their value matches the {@link filterQuery}.
 * @param {ComboboxOptionsType} options
 * @param {string} filterQuery
 * @returns {ComboboxOptionsType}
 */
const filterOptions = (options = [], filterQuery = '') => {
  if (hasGroupOptions(options)) {
    return options.map(groupOption => ({
      label: groupOption.label,
      options: groupOption.options.filter(option => option.value.toLowerCase().includes(filterQuery?.toLowerCase()))
    }));
  } else {
    return options.filter(option => option.value.toLowerCase().includes(filterQuery?.toLowerCase()));
  }
};
/**
 * Flatten grouped options into a one-dimensional array to make it conform to the internal option type signature.
 * @param {ComboboxOptionsType} options
 * @returns {ComboboxOption[]}
 */
const flattenGroupOptions = options => options.reduce((prev, curr) => {
  if (curr?.options) {
    return [...prev, ...curr.options];
  } else {
    prev.push(curr);
    return prev;
  }
}, []);
/**
 * Render the Combobox items depending on the signature of the {@link ComboboxOptionsType}.
 * When the options are of type {@link ComboboxGroupOption}, the items are put in a labeled optgroup.
 * @param {ComboboxOptionsType} options
 * @returns {React.ReactElement[]}
 */
function renderComboboxItems(options = []) {
  return options.map((item, i) => isGroupedOption(item) ? (/*#__PURE__*/React__default.createElement(ComboboxGroup, {
    label: item.label,
    key: `option-group-${i}`
  }, item.options.map(({
    key,
    value,
    disabled
  }, j) => (/*#__PURE__*/React__default.createElement(Combobox.Item, {
    element: "li",
    key: `option-${i}-${j}`,
    optionKey: key,
    optionValue: value,
    disabled: disabled
  }, value))))) : (/*#__PURE__*/React__default.createElement(Combobox.Item, {
    key: `option-${i}`,
    optionKey: item.key,
    optionValue: item.value,
    disabled: item.disabled
  }, item.value)));
}
/**
 * Render the Combobox items depending on the signature of the {@link ComboboxOptionsType}.
 * When the options are of type {@link ComboboxGroupOption}, the items are put in a labeled optgroup.
 * @DEPRECATED Remove in v11.
 */
function renderLegacyComboboxItems(options = [], children) {
  return options.map((item, i) => {
    const legacyChildrenArray = React__default.Children.toArray(children).filter(isValidElement);
    return isGroupedOption(item) ? (/*#__PURE__*/React__default.createElement(ComboboxGroup, {
      label: item.label,
      key: `option-group-${i}`
    }, item.options.map(({
      key
    }) => {
      return legacyChildrenArray.find(({
        props
      }) => props.optionKey === key);
    }))) : legacyChildrenArray.find(({
      props
    }) => props.optionKey === item.key);
  });
}
/**
 * The Combobox component.
 * @desc A Combobox is a select that enables the user to select one or more items from a list.
 * Compared to the native select the Combobox offers custom functionality and styling for options.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-combobox.html
 *
 * @constructor
 */
function Combobox({
  children,
  defaultState,
  open,
  options,
  ...internalComboboxArgs
}) {
  if (options && children) {
    console.error('Combobox: You cannot use `options` and `children` together. Please use either the new `options` prop (recommended) pass in the `Combobox.Item` as children (deprecated).');
    return null;
  }
  /**
   * Modern way of rendering Combobox items via {@link options}.
   */
  if (options && !children) {
    const flatOptions = flattenGroupOptions(options);
    /**
     * Merge the internal default state with the passed {@link defaultState} and options.
     * @type {ComboboxState}
     */
    const initialState = {
      // Internal default state
      ...{
        options: [],
        value: [],
        currentIndex: null,
        filterQuery: ''
      },
      ...defaultState,
      ...{
        options: flatOptions
      },
      ...{
        defaultOptions: options
      },
      isOpen: open,
      isMulti: internalComboboxArgs.multiple,
      hasOptions: !!flatOptions?.length
    };
    return /*#__PURE__*/React__default.createElement(ComboboxProvider, {
      initialValue: initialState
    }, /*#__PURE__*/React__default.createElement(ComboboxInternal, {
      ...internalComboboxArgs
    }));
  } else if (!options && children) {
    /**
     * Extract the current options from passed children.
     * @type {ComboboxOption[]}
     * @DEPRECATED Remove in v11.
     */
    const optionsFromChildren = useMemo(() => {
      const childrenArray = Children.toArray(children);
      return childrenArray.map(({
        props: {
          optionValue,
          optionKey,
          disabled
        }
      }) => ({
        value: optionValue,
        key: optionKey,
        ...(disabled && {
          disabled
        })
      }));
    }, [children]);
    /**
     * Merge the internal default state with the passed {@link defaultState} and options.
     * @type {ComboboxState}
     */
    const initialState = {
      // Internal default state
      ...{
        options: [],
        value: [],
        currentIndex: null,
        filterQuery: ''
      },
      ...defaultState,
      ...{
        options: optionsFromChildren
      },
      ...{
        defaultOptions: optionsFromChildren
      },
      isOpen: open,
      isMulti: internalComboboxArgs.multiple,
      hasOptions: !!optionsFromChildren?.length
    };
    return /*#__PURE__*/React__default.createElement(ComboboxProvider, {
      initialValue: initialState
    }, /*#__PURE__*/React__default.createElement(ComboboxInternal, {
      ...internalComboboxArgs,
      legacy: true
    }, children));
  }
  return null;
}
Combobox.displayName = 'Combobox';
/**
 * The internal {@link ComboboxInternal} core implementation.
 *
 * @internal
 * @type {Partial<Combobox>} - The actual props that are passed to the component.
 * @return {JSX.Element} - The internal {@link ComboboxInternal} component.
 * @constructor
 */
function ComboboxInternal({
  ariaLabelledBy,
  className,
  disabled,
  /** @DEPRECATED Remove in v11. */
  children,
  /** @DEPRECATED Remove in v11. */
  legacy,
  id,
  multiple = false,
  name,
  noOptionsMessage,
  onChange = () => {},
  onKeyDown = () => {},
  onListClose = () => {},
  onListOpen = () => {},
  placeholder,
  readOnly,
  searchable = false,
  searchInputLabel = 'Search list …',
  searchInputPlaceholder = 'Search list …',
  /**
   * The valueFormatter is opinionated according to the implementation in Bronson.
   * It can be customized as need be.
   */
  valueFormatter = values => values.length > 1 ? `${values.length} Options` : values[0]?.value,
  testId,
  ...otherProps
}) {
  /**
   * Use the context provider via hook in the internal Combobox.
   */
  const [state, dispatch] = useCombobox();
  /**
   * Tracking the select ref.
   * @type {React.MutableRefObject<null>}
   */
  const selectRef = useRef(null);
  /**
   * Ref for the input.
   * @type {React.MutableRefObject<null>}
   */
  const inputRef = useRef(null);
  /**
   * Ref for the searchable input.
   * @type {React.MutableRefObject<null>}
   */
  const searchInputRef = useRef(null);
  /**
   * Memoize the {@link state?.touched} to avoid rerender.
   */
  const touched = useMemo(() => state?.touched, [state?.touched]);
  /**
   * Set the focus on the search input. Use a slight delay to prevent animation hiccups.
   * @type {(function(): void)|*}
   */
  const focusSearchInput = useCallback(() => {
    setTimeout(() => {
      searchInputRef?.current?.focus();
    }, 50);
  }, []);
  /**
   * Restore the Combobox focus state when closing/select-closing the menu.
   * Use a slight delay to prevent animation hiccups.
   * @type {(function(): void)|*}
   */
  const focusCombobox = useCallback(() => {
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 50);
  }, []);
  /**
   * Call the callback only if the input was clicked or updated.
   * Do not include {@link touched} in the effect’s dependencies as we do not want to render on its changes.
   */
  useEffect(() => {
    if (touched) {
      onChange(state?.value ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, state?.value]);
  /**
   * Handle clicks outside the {@link Combobox}.
   */
  useEffect(() => {
    const outsideClickHandler = event => {
      if (state?.isOpen && !selectRef?.current?.contains(event?.target)) {
        dispatch?.({
          type: comboboxReducerActions.closeMenu
        });
        dispatch?.({
          type: comboboxReducerActions.setFilterQuery,
          payload: ''
        });
        focusCombobox();
        onListClose();
      }
    };
    document.addEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
    // Cleanup the hook.
    return () => document.removeEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
  }, [dispatch, focusCombobox, onListClose, state?.isOpen]);
  /**
   * The component’s main element `classList`.
   * @type {string}
   */
  const classNameList = classNames('c-combobox__wrapper', className).trim();
  const valuePresentationClassNameList = classNames('c-combobox__value-presentation__text', {
    'has-placeholder': !state?.value?.length
  }).trim();
  /**
   * The component’s main element sensible default attributes.
   * @type {{"aria-labelledby": {ariaLabelledBy}, "aria-controls": string, tabIndex: string|number, "aria-activedescendant": (string|string)}}
   */
  const comboboxAttributes = {
    'aria-activedescendant': state?.focusedOption?.key ? `${state?.focusedOption?.key}-option` : '',
    'aria-controls': `${id}-listbox`,
    'aria-labelledby': ariaLabelledBy,
    tabIndex: disabled ? undefined : 0
  };
  /**
   * Handle clicks on the {@link Combobox}.
   * @type {(function(*): void)|*}
   */
  const onClickHandler = useCallback(event => {
    /**
     * Only call the click handler when we really hit the {@link Combobox}.
     */
    if (event?.target === inputRef?.current) {
      /**
       * Toggle the menu/dropdown visibility.
       */
      dispatch?.({
        type: comboboxReducerActions.toggleMenu
      });
      /**
       * If the menu is open, call the respective callback,
       * reset the filter query and manually reset the uncontrolled search input.
       * If it is closed set the focus to the search input if applicable.
       */
      if (state?.isOpen) {
        dispatch?.({
          type: comboboxReducerActions.setFilterQuery,
          payload: ''
        });
        focusCombobox();
        onListClose();
      } else {
        focusSearchInput();
        onListOpen();
      }
    }
  }, [dispatch, focusCombobox, focusSearchInput, onListClose, onListOpen, state?.isOpen]);
  /**
   * Handle keyboard events on the {@link Combobox}.
   * @param {KeyboardEvent} event - The currently observed event.
   */
  // eslint-disable-next-line consistent-return
  const onKeydownHandler = event => {
    if (disabled || readOnly) return false;
    const action = getActionFromKey(event, state?.isOpen);
    if (typeof onKeyDown === 'function') {
      onKeyDown(event);
      if (event.defaultPrevented) {
        return false;
      }
    }
    /**
     * Handle keyboard interactions.
     *
     * Handle focus navigation of list items (arrow keys, home/end and pageUp/pageDown).
     */
    if (action === MenuActions.Next || action === MenuActions.Next10 || action === MenuActions.Last || action === MenuActions.First || action === MenuActions.Previous || action === MenuActions.Previous10) {
      event.preventDefault();
      if (state?.isOpen) {
        dispatch?.({
          type: comboboxReducerActions.focusOptionByAction,
          payload: {
            action
          }
        });
      } else {
        dispatch?.({
          type: comboboxReducerActions.openMenu
        });
        onListOpen();
      }
      /**
       * Handle list item selection (space).
       */
    } else if (action === MenuActions.Select) {
      event.preventDefault();
      /**
       * Close menu in non-multiple mode after option selection.
       */
      if (!multiple || !state?.focusedOption) {
        dispatch?.({
          type: comboboxReducerActions.closeMenu
        });
        dispatch?.({
          type: comboboxReducerActions.setFilterQuery,
          payload: ''
        });
        onListClose();
        focusCombobox();
      }
      /**
       * Toggle the current focused option on select.
       */
      if (state?.focusedOption) {
        dispatch?.({
          type: comboboxReducerActions.toggleOption,
          payload: state?.focusedOption
        });
      }
      /**
       * Handle list item selection and close (enter).
       */
    } else if (action === MenuActions.CloseSelect) {
      event.preventDefault();
      if (state?.focusedOption) {
        dispatch?.({
          type: comboboxReducerActions.toggleOption,
          payload: state?.focusedOption
        });
      }
      dispatch?.({
        type: comboboxReducerActions.closeMenu
      });
      dispatch?.({
        type: comboboxReducerActions.setFilterQuery,
        payload: ''
      });
      focusCombobox();
      /**
       * Handle list close.
       */
    } else if (action === MenuActions.Close) {
      event.preventDefault();
      dispatch?.({
        type: comboboxReducerActions.closeMenu
      });
      dispatch?.({
        type: comboboxReducerActions.setFilterQuery,
        payload: ''
      });
      focusCombobox();
      /**
       * Handle list open. If the menu is not open, activate the respective option,
       * open the menu and manually focus the uncontrolled search input.
       */
    } else if (action === MenuActions.Open) {
      if (!state?.isOpen) {
        dispatch?.({
          type: comboboxReducerActions.focusOptionByAction,
          payload: {
            action
          }
        });
      }
      dispatch?.({
        type: comboboxReducerActions.openMenu
      });
      focusSearchInput();
      /**
       * Handle simple label select when not in {@link searchable} mode,
       * which is handled via {@link onSearchHandler}.
       */
    } else if (action === MenuActions.Type && !searchable) {
      event.preventDefault();
      event.stopPropagation();
      dispatch?.({
        type: comboboxReducerActions.focusOptionByLabel,
        payload: {
          label: event.key.toLowerCase()
        }
      });
      if (!state?.isOpen) {
        dispatch?.({
          type: comboboxReducerActions.openMenu
        });
        onListOpen();
      }
    }
  };
  /**
   * Handle search input.
   * @type {(function(*): void)|*}
   */
  const onSearchHandler = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    dispatch?.({
      type: comboboxReducerActions.setFilterQuery,
      payload: event.target.value
    });
  }, [dispatch]);
  /**
   * Reset the search when clicking the pseudo close button.
   * @param event
   */
  const onSearchResetHandler = event => {
    const eventTarget = event?.target;
    event.preventDefault();
    event.stopPropagation();
    if (!eventTarget?.value) {
      dispatch?.({
        type: comboboxReducerActions.setFilterQuery,
        payload: ''
      });
    }
  };
  const listboxClassNames = classNames('c-combobox__listbox', {
    'has-no-options': !state?.hasOptions
  });
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-name": name,
    ref: selectRef,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("input", {
    type: "hidden",
    name: "combobox-values"
  }), /*#__PURE__*/React__default.createElement("div", {
    "aria-haspopup": "listbox",
    "aria-expanded": state?.isOpen ?? false,
    "aria-owns": `${id}-listbox`,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    className: "c-combobox",
    /**
     * @TODO: We need to add this here to avoid warnings for missing `[aria-controls]` and `[aria-expanded]`
     *        because we do not include them as our Combobox does not support searchable functionality yet.
     */
    /* eslint-disable-next-line jsx-a11y/role-has-required-aria-props */
    role: "combobox",
    onClick: onClickHandler,
    onKeyDown: onKeydownHandler,
    onFocus: event => {
      if (event?.target === inputRef?.current && state?.focusedOption) {
        dispatch?.({
          type: comboboxReducerActions.blurOption
        });
      }
    },
    ...comboboxAttributes,
    ref: inputRef
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-combobox__value-presentation"
  }, /*#__PURE__*/React__default.createElement("span", {
    id: `${id}-values`,
    className: valuePresentationClassNameList
  }, valueFormatter(state?.value ?? []) || placeholder)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-combobox__listbox-container"
  }, searchable && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-combobox__search-input"
  }, /*#__PURE__*/React__default.createElement(Input, {
    "aria-autocomplete": "list",
    "aria-controls": `${id}-listbox`,
    "aria-activedescendant": "",
    "aria-label": searchInputLabel,
    placeholder: searchInputPlaceholder,
    type: "search",
    onChange: onSearchHandler,
    onInput: onSearchResetHandler,
    value: state?.filterQuery,
    autoComplete: "off",
    ref: searchInputRef
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: listboxClassNames,
    role: "listbox",
    "aria-multiselectable": multiple,
    id: `${id}-listbox`,
    tabIndex: 0,
    "data-combobox-no-options-message": noOptionsMessage
  }, legacy ? searchable ? /** @DEPRECATED Remove in v11. */
  renderLegacyComboboxItems(filterOptions(state?.defaultOptions, state?.filterQuery), children) : renderLegacyComboboxItems(state?.defaultOptions, children) : searchable ? renderComboboxItems(filterOptions(state?.defaultOptions, state?.filterQuery)) : renderComboboxItems(state?.defaultOptions)))));
}
/**
 * The ComboboxItem component.
 * @desc Used as direct child for the {@link Combobox}.
 * @constructor
 */
function ComboboxItem({
  className,
  children,
  disabled,
  element = 'div',
  onClick,
  optionKey,
  optionValue,
  testId,
  ...otherProps
}) {
  const ref = useRef(null);
  const [state, dispatch] = useCombobox();
  const isActive = useMemo(() => state?.value?.find(value => value?.key === optionKey), [state, optionKey]);
  const isCurrent = useMemo(() => state?.focusedOption?.key === optionKey, [state, optionKey]);
  const classNameList = classNames('c-combobox__item', {
    'is-focused': isActive || isCurrent
  }, className).trim();
  /**
   * This assigns the `element` prop to a variable which is in PascalCase,
   * because custom component tags names must start with
   * an uppercase letter as lowercase tags are reserved for HTML.
   * @see https://reactjs.org/docs/jsx-in-depth.html#html-tags-vs.-react-components
   */
  // @ts-ignore
  const CustomElement = typeof element === 'string' ? `${element}` : element;
  /**
   * Handles clicks on the options. Toggle the option’s selected state?.
   * Calls {@link onClick} callback.
   */
  const onClickHandler = () => {
    dispatch?.({
      type: comboboxReducerActions.toggleOption,
      payload: {
        key: optionKey,
        value: optionValue
      }
    });
    if (state?.isMulti) {
      dispatch?.({
        type: comboboxReducerActions.focusOption,
        payload: {
          key: optionKey
        }
      });
    } else {
      dispatch?.({
        type: comboboxReducerActions.closeMenu
      });
    }
    if (typeof onClick === 'function') {
      onClick(state?.value);
    }
  };
  /**
   * Hook to handle `scrollIntoView` of current focused options.
   */
  useEffect(() => {
    if (isCurrent) {
      ref?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [isCurrent]);
  return /*#__PURE__*/React__default.createElement(CustomElement, {
    role: "option",
    ref: ref,
    id: `${optionKey}-option`,
    className: classNameList,
    "data-value": optionValue,
    "data-key": optionKey,
    "data-testid": testId,
    "aria-selected": !!isActive,
    "aria-disabled": disabled,
    onClick: onClickHandler,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-combobox__item__text"
  }, children));
}
ComboboxItem.displayName = 'Combobox.Item';
Combobox.Item = ComboboxItem;
function ComboboxGroup({
  label,
  children
}) {
  const optGroupId = `combobox-optgroup-${uuidv4('')}`;
  return /*#__PURE__*/React__default.createElement("ul", {
    className: "c-combobox__optgroup",
    role: "group",
    "aria-labelledby": optGroupId
  }, /*#__PURE__*/React__default.createElement("li", {
    className: "c-combobox__optgroup-label",
    role: "presentation",
    id: optGroupId
  }, label), children);
}
ComboboxGroup.displayName = 'Combobox.Group';
Combobox.Group = ComboboxGroup;

/* eslint-disable jsx-a11y/aria-role */
/**
 * Bronson PriceBox component.
 * @see https://bronson.vwfs.tools/bluelabel/components/detail/bronson-price-box.html
 * @desc Please note: In order to announce the discount value, discount price
 *       and actual price to assistive technology, apply both `[aria-label]` and
 *       `[role=text]` to those elements. This ensures accessible content without
 *       relying on the visual appearance.
 * @constructor
 */
function PriceBox({
  standalone,
  header,
  discountValue,
  ariaLabelDiscountValue,
  discountOldPrice,
  ariaLabelDiscountOldPrice,
  price,
  ariaLabelPrice,
  textAbovePrice,
  textBelowPrice,
  additionalInfo,
  testId,
  ...otherProps
}) {
  const CustomTag = `${standalone ? 'article' : 'div'}`;
  const CustomTagHeader = `${standalone ? 'header' : 'div'}`;
  const classNameList = classNames('c-price-box ', {
    'c-price-box--integrated ': !standalone
  }).trim();
  const tagClassNameList = classNames('c-tag ', 'c-tag--negative ', 'c-price-box__discount__value ').trim();
  function renderHeader() {
    if (header) {
      return /*#__PURE__*/React__default.createElement(CustomTagHeader, {
        className: "c-price-box__header"
      }, header);
    }
    return null;
  }
  function renderDiscountValue() {
    if (discountValue) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: tagClassNameList
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "c-tag__label",
        "aria-label": ariaLabelDiscountValue,
        role: "text"
      }, discountValue));
    }
    return null;
  }
  function renderDiscountOldPrice() {
    if (discountOldPrice) {
      return /*#__PURE__*/React__default.createElement("s", {
        className: "c-price-box__discount__old-price",
        "aria-label": ariaLabelDiscountOldPrice,
        role: "text"
      }, discountOldPrice);
    }
    return null;
  }
  function renderDiscount() {
    if (discountValue || discountOldPrice) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-price-box__discount"
      }, renderDiscountValue(), renderDiscountOldPrice());
    }
    return null;
  }
  function renderPrice() {
    return /*#__PURE__*/React__default.createElement("span", {
      className: "c-price-box__price",
      "aria-label": ariaLabelPrice,
      role: "text"
    }, price);
  }
  function renderTextAbovePrice() {
    if (textAbovePrice) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-price-box__text"
      }, textAbovePrice);
    }
    return null;
  }
  function renderTextBelowPrice() {
    if (textBelowPrice) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-price-box__text"
      }, textBelowPrice);
    }
    return null;
  }
  function renderAdditionalInfo() {
    if (additionalInfo) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-price-box__additional-info"
      }, /*#__PURE__*/React__default.createElement("p", {
        className: "c-price-box__additional-info__text"
      }, additionalInfo));
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement(CustomTag, {
    ...otherProps,
    "data-testid": testId,
    className: classNameList
  }, renderHeader(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-price-box__body"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-price-box__body__content"
  }, renderTextAbovePrice(), renderDiscount(), renderPrice(), renderTextBelowPrice()), renderAdditionalInfo()));
}

/**
 * Simple hook for utilizing `window.matchMedia`.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 *
 * @param {string} query - A mediaQueryString
 * @returns {boolean} - Returns a boolean indicating if the media matches.
 */
const useMatchMedia = query => {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener ? media.addEventListener('change', listener) : media.addListener(listener);
    /**
     * Cleanup function.
     */
    return () => media.removeEventListener ? media.removeEventListener('change', listener) : media.removeListener(listener);
  }, [matches, query]);
  return matches;
};

/**
 * Asynchronously observe resizes of a target reference element.
 * Please note that IE11 and other legacy browser do not implement the ResizeObserver API
 * and therefore need to polyfill the missing functionality.
 * @example
 *    const myTarget = useRef(null)
 *
 *    export function Example({}) {
 *      const myTarget = useRef(null);
 *      useResizeObserver({
 *        ref: myTarget,
 *        onResizeHandler: (entry) => {
 *          // Do your stuff on every observed resize here.
 *        },
 *      });
 *      return (
 *        <div ref={myTarget}>👋</div>
 *      );
 *    };
 */
function useResizeObserver({
  ref,
  onResizeHandler
}) {
  useEffect(() => {
    const {
      current
    } = ref;
    /**
     * If we have an {@link HTMLElement} to observe and a valid callback.
     */
    if (current && onResizeHandler) {
      const resizeObserver = new ResizeObserver(entries => {
        onResizeHandler(entries);
      });
      resizeObserver.observe(current);
      return () => resizeObserver.unobserve(current);
    }
    return () => {};
  }, [onResizeHandler, ref]);
}

/**
 * Get text content from node.
 *
 * @param {React.ReactNode} node - A React element.
 * @return {string} - The text content.
 *
 * @example
 *     getTextContent(<Component/>)
 *     getTextContent([<Component/>, <Component/>, ...])
 *     getTextContent('Lorem ipsum dolor ...')
 *     getTextContent(99.99)
 */
function getTextContent(node) {
  const type = typeof node;
  if (type === 'string' || type === 'number') {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }
  if (type === 'object' && node) {
    return getTextContent(node?.props?.children);
  }
  return '';
}

/**
 * Bronson CompareAndSelect component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-comapre-and-select.html
 * @constructor
 */
const CompareAndSelect = ({
  buttonVariant = false,
  children,
  defaultSelected,
  deselectable,
  inputGroup,
  onChange,
  preserveHeight,
  radioButtonSection,
  sliderCounter,
  stacked,
  testId,
  ...otherProps
}) => {
  const [flickityRef, setFlickityRef] = useState(null);
  const [selected, setSelected] = useState(defaultSelected);
  const initialResizeThreshold = 250;
  const compareSelectAccordionBreakpointCustomProp = '--bron-compare-select-accordion-breakpoint';
  const [breakpoint, setBreakpoint] = useState(null);
  const matchBreakpoint = useMatchMedia(`(min-width: ${breakpoint})`);
  const stackedElementRef = useRef(null);
  const elementRef = useRef(null);
  /**
   * Add the ability to reset the {@link defaultSelected} prop via side effect.
   * Only allow resetting when external deselection is turned on via {@link deselectable}.
   */
  useEffect(() => {
    if (deselectable) {
      setSelected(defaultSelected);
    }
  }, [defaultSelected, deselectable]);
  /**
   * Handles {@link flickityRef} and {@link stackedElementRef} depending on the
   * current breakpoint when {@link stacked}.
   * Get the current breakpoint for {@link compareSelectAccordionBreakpointCustomProp}
   * and set it as {@link breakpoint}.
   */
  useEffect(() => {
    const refElement = matchBreakpoint ? flickityRef?.element : stackedElementRef?.current;
    /**
     * Cleanup function to prevent the internal state of the component
     * from being updated when it is unmounted.
     */
    const timer = setTimeout(() => {
      if (refElement && !breakpoint) {
        const currentBreakpoint = getComputedStyle(refElement).getPropertyValue(compareSelectAccordionBreakpointCustomProp);
        setBreakpoint(currentBreakpoint);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [flickityRef, stackedElementRef, breakpoint, matchBreakpoint]);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && deselectable && radioButtonSection) {
      throw new Error('A radio button group cannot be deselectable.');
    }
  }, [deselectable, radioButtonSection]);
  /**
   * Establish equal heights for items.
   * @type {(function(): void)|*}
   */
  const handleEqualHeights = useCallback(() => {
    if (!preserveHeight) {
      return;
    }
    setTimeout(() => {
      const headers = Array.from(elementRef?.current?.querySelectorAll('.c-compare-and-select__title') ?? []) ?? [];
      const subheaders = Array.from(elementRef?.current?.querySelectorAll('.c-compare-and-select__pricing') ?? []) ?? [];
      headers?.forEach(h => h?.style.setProperty('--bron-compare-and-select-min-height', 'initial'));
      subheaders?.forEach(h => h?.style.setProperty('--bron-compare-and-select-min-height', 'initial'));
      const [maxHeightTitle] = headers?.map(h => h?.clientHeight).sort((a, b) => a > b ? -1 : 1) ?? [];
      const [maxHeightSubTitles] = subheaders?.map(h => h?.clientHeight).sort((a, b) => a > b ? -1 : 1) ?? [];
      if (maxHeightTitle) {
        headers?.forEach(h => h?.style.setProperty('--bron-compare-and-select-min-height', `${maxHeightTitle}px`));
      }
      if (maxHeightSubTitles) {
        subheaders?.forEach(h => h?.style.setProperty('--bron-compare-and-select-min-height', `${maxHeightSubTitles}px`));
      }
      flickityRef && flickityRef.resize();
    }, 100);
  }, [flickityRef, preserveHeight]);
  useEffect(() => {
    handleEqualHeights();
  }, [elementRef, handleEqualHeights]);
  /**
   * Track resizing on the {@link elementRef} and call the equal height callback.
   */
  useResizeObserver({
    ref: elementRef,
    onResizeHandler: () => {
      handleEqualHeights();
    }
  });
  const updateSelected = newSelected => {
    if (newSelected === selected) {
      if (deselectable && !radioButtonSection) {
        // eslint-disable-next-line no-param-reassign
        newSelected = undefined;
      }
    }
    setSelected(newSelected);
    if (onChange) {
      onChange(newSelected);
    }
    flickityRef && flickityRef.resize();
  };
  useBasicSlider(flickityRef, children, {
    initialIndex: defaultSelected,
    selected,
    initialResizeThreshold
  });
  const flickityOptions = {
    cellSelector: '.c-compare-and-select__item',
    cellAlign: 'center',
    groupCells: '100%',
    initialIndex: defaultSelected
  };
  const flickityClassNameList = classNames('c-compare-and-select-container__inner', 'is-initialized', {
    'c-compare-and-select--stacked': stacked
  }).trim();
  /* Use 'CompareAndSelect.Item' component. */
  const compareAndSelectItems = () => React__default.Children.map(children, (child, index) => {
    if (!child) {
      return null;
    }
    return /*#__PURE__*/React__default.cloneElement(child, {
      selected: selected === index,
      updateSelected,
      index,
      buttonVariant,
      radioButtonSection,
      inputGroup,
      stacked,
      matchBreakpoint
    });
  });
  function renderStacked() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-compare-and-select-container__inner  c-compare-and-select--stacked",
      ref: stackedElementRef
    }, compareAndSelectItems());
  }
  function renderSlider() {
    return /*#__PURE__*/React__default.createElement(BasicSlider, {
      elementType: "div",
      className: flickityClassNameList,
      flickityRef: c => setFlickityRef(c),
      options: flickityOptions,
      sliderCounter: sliderCounter
    }, compareAndSelectItems());
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: "c-compare-and-select-container",
    ref: elementRef
  },
  /**
   * Mounts {@link BasicSlider} only if the media query
   * in {@link matchBreakpoint} matches when {@link stacked}.
   */
  stacked && !matchBreakpoint ? renderStacked() : renderSlider());
};
/**
 * Bronson CompareAndSelectItem component (nested).
 * Render a single {@link CompareAndSelect} children item.
 * @internal
 * @constructor
 */
function CompareAndSelectItem({
  ariaLabelDiscountOldPrice,
  ariaLabelDiscountValue,
  ariaLabelPrice,
  buttonLabel,
  buttonVariant,
  checkbox,
  children: itemChildren,
  className,
  description,
  descriptionClassName,
  descriptionInfoIcon,
  descriptionLinkHref,
  descriptionLinkLabel,
  descriptionTitle,
  detailsButtonLabel,
  disableButton,
  discountOldPrice,
  discountValue,
  flexibleButton,
  icon,
  index,
  indicatorTitle,
  indicatorTitleAttr = undefined,
  infoIcon,
  inputAttributes,
  inputGroup,
  label,
  legal,
  legalTitle,
  matchBreakpoint,
  price,
  pricingAdditionalInfo,
  pricingHeader,
  radioButtonSection,
  radioDescription,
  radioInfoIcon,
  radioValue,
  selected,
  stacked,
  title,
  titleAttr = undefined,
  truncateTitle,
  updateSelected,
  testId,
  ...otherProps
}) {
  const priceValue = price;
  const priceHeader = pricingHeader ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, pricingHeader, infoIcon)) : null;
  const CSSOverrides = {
    '--transition-duration': 0,
    '--transition-property': 'none'
  };
  const itemClassNameList = classNames('c-compare-and-select__item', className).trim();
  const inputClassNameList = classNames('c-compare-and-select__hidden-input', {
    'c-radio__input': radioButtonSection
  }).trim();
  const divClassNameList = classNames('c-compare-and-select', {
    'c-compare-and-select--selected': buttonVariant && selected,
    'c-radio__label-container': radioButtonSection
  }).trim();
  const iClassNameList = classNames({
    'c-compare-and-select__icon': icon
  }).trim();
  const descriptionClassNameList = classNames('c-compare-and-select__description', descriptionClassName).trim();
  const titleTextClassNameList = classNames('c-compare-and-select__title-text', {
    'u-text-truncate': truncateTitle
  }).trim();
  const renderCompareAndSelectButton = () => (/*#__PURE__*/React__default.createElement(Button, {
    onClick: () => updateSelected?.(index),
    className: "c-compare-and-select__button",
    type: "button",
    disabled: disableButton
  }, buttonLabel));
  function renderUnlessButtonVariant() {
    if (!buttonVariant) {
      return /*#__PURE__*/React__default.createElement("input", {
        className: inputClassNameList,
        type: "radio",
        name: inputGroup,
        id: `${inputGroup}-${index}`,
        checked: selected,
        readOnly: true,
        ...inputAttributes
      });
    }
    return null;
  }
  function renderIfLabel() {
    return label && /*#__PURE__*/React__default.createElement("div", {
      className: "c-compare-and-select__label"
    }, label);
  }
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon
      });
    }
    return null;
  }
  function renderIfValue() {
    if (priceValue) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-compare-and-select__pricing"
      }, /*#__PURE__*/React__default.createElement(PriceBox, {
        header: priceHeader,
        ariaLabelDiscountValue: ariaLabelDiscountValue,
        discountValue: discountValue,
        ariaLabelDiscountOldPrice: ariaLabelDiscountOldPrice,
        discountOldPrice: discountOldPrice,
        ariaLabelPrice: ariaLabelPrice,
        price: priceValue,
        additionalInfo: pricingAdditionalInfo
      }));
    }
    return null;
  }
  function renderIfDetails() {
    if (stacked && itemChildren) {
      return /*#__PURE__*/React__default.createElement("details", {
        className: "c-compare-and-select__details",
        open: matchBreakpoint ? true : undefined
      }, /*#__PURE__*/React__default.createElement("summary", {
        className: "c-compare-and-select__summary"
      }, /*#__PURE__*/React__default.createElement(Button, {
        link: true,
        small: true,
        simple: true,
        icon: "semantic-expand",
        element: "span"
      }, detailsButtonLabel)), renderIfChildren());
    }
    return null;
  }
  function renderIfChildren() {
    if (itemChildren) {
      return /*#__PURE__*/React__default.createElement("ul", {
        className: "c-compare-and-select__list c-icon-list c-icon-list--small"
      }, itemChildren /* Use 'SelectAndCompare.CompareAndSelect.IconListItem' component. */);
    }
    return null;
  }
  function renderIfLinkInDescription() {
    if (descriptionLinkHref && descriptionLinkLabel) {
      return /*#__PURE__*/React__default.createElement("a", {
        href: descriptionLinkHref
      }, descriptionLinkLabel);
    }
    return null;
  }
  function renderIfDescription() {
    if (description) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: descriptionClassNameList
      }, descriptionTitle && (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("strong", null, descriptionTitle), ":", ' ')), description, renderIfLinkInDescription(), descriptionInfoIcon);
    }
    return null;
  }
  function renderIfButton() {
    if (!flexibleButton && buttonLabel) {
      return renderCompareAndSelectButton();
    }
    return null;
  }
  function renderIfFlexibleButton() {
    if (flexibleButton && buttonLabel) {
      return renderCompareAndSelectButton();
    }
    return null;
  }
  function renderIfCheckbox() {
    if (checkbox) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-compare-and-select__checkbox"
      }, checkbox);
    }
    return null;
  }
  function renderIfLegal() {
    if (legal) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: "c-compare-and-select__legal"
      }, legalTitle && (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("strong", null, legalTitle, ":"), ' ')), legal);
    }
    return null;
  }
  function renderIfRadioButtonSection() {
    if (radioButtonSection) {
      return (
        /*#__PURE__*/
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        React__default.createElement("label", {
          className: "c-radio c-compare-and-select__radio",
          htmlFor: `${inputGroup}-${index}`,
          onClick: e => e.target.className.search('info-icon') === -1 && updateSelected?.(index)
        }, /*#__PURE__*/React__default.createElement("span", {
          className: "c-radio__label c-compare-and-select__radio-label"
        }, radioValue, radioInfoIcon, /*#__PURE__*/React__default.createElement("span", {
          className: "c-compare-and-select__radio-description"
        }, radioDescription)))
      );
    }
    return null;
  }
  function renderIfIndicatorTitle() {
    if (indicatorTitle) {
      return /*#__PURE__*/React__default.createElement("h3", {
        className: "c-compare-and-select__title-indicator-text",
        title: indicatorTitleAttr ?? indicatorTitle
      }, indicatorTitle);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    className: itemClassNameList,
    "data-testid": testId
  }, renderUnlessButtonVariant(), /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList,
    style: CSSOverrides
  }, renderIfLabel(), /*#__PURE__*/React__default.createElement("header", {
    className: "c-compare-and-select__title"
  }, renderIfIcon(), renderIfIndicatorTitle(), /*#__PURE__*/React__default.createElement("h4", {
    className: titleTextClassNameList,
    title: titleAttr ?? getTextContent(title)
  }, title)), renderIfValue() /* PriceBox */, renderIfFlexibleButton() /* Flexible Button */, stacked ? renderIfDetails() /* Stacked */ : renderIfChildren() /* IconList */, renderIfDescription(), /*#__PURE__*/React__default.createElement("footer", {
    className: "c-compare-and-select__footer"
  }, renderIfButton(), renderIfCheckbox(), renderIfLegal(), renderIfRadioButtonSection())));
}
CompareAndSelect.displayName = 'CompareAndSelect.Item';
CompareAndSelect.Item = CompareAndSelectItem;
/**
 * Bronson IconListItem component (nested).
 * Render a decorative icon for the icon list.
 * @internal
 * @constructor
 */
function IconListItem({
  icon,
  iconDescription,
  children,
  ...otherProps
}) {
  const iClassNameList = classNames('c-icon-list__icon').trim();
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: iClassNameList,
    name: icon
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "u-visually-hidden"
  }, `${iconDescription}: `), children);
}
IconListItem.displayName = 'CompareAndSelect.IconListItem';
CompareAndSelect.IconListItem = IconListItem;

/**
 * The ConsumptionGraphic component is a visual representation of energy classes and consumption.
 * It displays {@link EfficiencyTag} in a standardized format.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-consumption-graphic.html
 * @constructor
 */
function ConsumptionGraphic({
  activeRatings,
  caption,
  children,
  className,
  labels,
  scope = 'eu',
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-consumption-graphic', className).trim();
  function renderIfCaption() {
    if (caption) {
      return /*#__PURE__*/React__default.createElement("figcaption", {
        className: "c-consumption-graphic__caption u-visually-hidden"
      }, caption);
    }
    return null;
  }
  function renderIfLabels() {
    if (labels) {
      {
        return labels?.map((label, i) => (/*#__PURE__*/React__default.createElement("span", {
          className: "c-consumption-graphic__column-label",
          key: `column-label-${i + 1}`
        }, label)));
      }
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("figure", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, renderIfCaption(), /*#__PURE__*/React__default.createElement("span", {
    className: "c-consumption-graphic__graphic",
    "aria-hidden": "true",
    "data-efficiency-tag-scope": scope
  }, React__default.Children.map(children, efficiencyTag => {
    /**
     * Get an array of indices for any occurrence of the
     * efficiencyTag’s rating in {@link activeRatings}.
     * Filter out any undefined values.
     */
    const columns = activeRatings?.map((rating, i) => {
      if (rating === efficiencyTag?.props?.rating) {
        return i + 1;
      }
    }).filter(i => !!i) ?? [];
    if (! /*#__PURE__*/React__default.isValidElement(efficiencyTag)) {
      return null;
    }
    /**
     * Multiple ratings do not have a column number.
     * For singular ratings we assume the first entry in {@link columns}.
     */
    const colNumber = columns.length > 1 ? undefined : columns[0];
    return /*#__PURE__*/React__default.createElement(ConsumptionGraphicItem, {
      column: colNumber
    }, /*#__PURE__*/React__default.cloneElement(efficiencyTag, {
      scope
    }), columns.map(index => /*#__PURE__*/React__default.cloneElement(efficiencyTag, {
      pointer: true,
      key: `rating-pointer-${index}`
    })));
  }), renderIfLabels()));
}
function ConsumptionGraphicItem({
  children,
  className,
  column,
  ...otherProps
}) {
  const classNameList = classNames('c-consumption-graphic__item', className).trim();
  return /*#__PURE__*/React__default.createElement("span", {
    className: classNameList,
    "data-consumption-graphic-pointer-column": column,
    ...otherProps
  }, children);
}

/*
 * Bronson ContextBar component.
 */
const ContextBar = ({
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isContextBar = true,
  testId,
  ...otherProps
}) => {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-context-bar"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-context-bar__panel",
    "aria-hidden": "true"
  }, /*#__PURE__*/React__default.createElement("nav", {
    ...otherProps,
    "data-testid": testId,
    className: "c-context-bar__navs",
    "aria-label": "primary"
  }, children /* Use 'ContextBar.SiteNav' component. */)));
};
/*
 * Bronson SiteNav component (nested).
 */
function SiteNav$1({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    className: "c-context-bar__nav"
  }, children /* Use 'ContextBar.SiteNav.Item' component. */);
}
/*
 * Bronson SiteNavItem component (nested).
 */
const SiteNavItem$1 = ({
  noLink,
  title,
  label,
  children
}) => {
  const spanClassNameList = classNames({
    'c-context-bar__title': title,
    'c-context-bar__nav-label': !title
  }).trim();
  function renderIfNoLink() {
    if (noLink) {
      return /*#__PURE__*/React__default.createElement("li", null, /*#__PURE__*/React__default.createElement("span", {
        className: spanClassNameList
      }, label));
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderIfNoLink(), children /* Use 'ContextBar.SiteNav.Item.ContextItem' component. */);
};
/*
 * Bronson ContextItem component (nested).
 */
function ContextItem({
  contextBarIcon,
  href,
  iconLabel,
  linkProps,
  normalLink,
  onClick,
  ...otherProps
}) {
  const iClassNameList = classNames('c-context-bar__icon').trim();
  function renderIfIconLabel() {
    if (iconLabel) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-context-bar__icon-label"
      }, iconLabel);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(ReactLink, {
    className: "c-context-bar__link",
    onClick: onClick,
    href: href,
    normalLink: normalLink,
    ...linkProps
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: iClassNameList,
    name: contextBarIcon
  }), renderIfIconLabel()));
}
ContextItem.displayName = 'ContextBar.SiteNav.Item.ContextItem';
SiteNavItem$1.ContextItem = ContextItem;
SiteNavItem$1.displayName = 'ContextBar.SiteNav.Item';
SiteNav$1.Item = SiteNavItem$1;
SiteNav$1.displayName = 'ContextBar.SiteNav';
ContextBar.SiteNav = SiteNav$1;

/**
 * Bronson ContextNotification component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-context-notification.html
 *
 * @constructor
 */
function ContextNotification({
  children,
  testId,
  ...otherProps
}) {
  const divClassName = classNames('js-context-notification' // Used in Bronson CSS.
  ).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: divClassName,
    "data-testid": testId,
    ...otherProps
  }, React__default.Children.map(children, child => /*#__PURE__*/React__default.cloneElement(child, {
    context: true
  })));
}

/* eslint-disable react-hooks/exhaustive-deps */
// TODO: check deps
/**
 * Default options for the internal range slider.
 * @type {Partial<nouislider.Options>}
 */
const noUiSliderDefaultOpts = {
  cssPrefix: 'c-custom-range-slider',
  cssClasses: {
    active: '--active',
    background: '--background',
    draggable: '--draggable',
    horizontal: '--horizontal',
    vertical: '--vertical',
    ltr: '--left-to-right',
    rtl: '--right-to-left',
    target: '__target',
    base: '__base',
    origin: '__origin',
    handle: '__thumb',
    handleLower: '__thumb--lower',
    handleUpper: '__thumb--upper',
    touchArea: '__touch-area',
    connects: '__connect-container',
    connect: '__connect',
    drag: '--drag',
    tap: '--tap',
    tooltip: '__tooltip-wrapper',
    pips: '__pips',
    pipsHorizontal: '__pips--horizontal',
    pipsVertical: '__pips--vertical',
    marker: '__marker',
    markerHorizontal: '__marker--horizontal',
    markerVertical: '__marker--vertical',
    markerNormal: '__marker--normal',
    markerLarge: '__marker--large',
    markerSub: '__marker--sub',
    textDirectionLtr: '--text-direction-ltr',
    textDirectionRtl: '--text-direction-rtl',
    value: '__value',
    valueHorizontal: '__value--horizontal',
    valueVertical: '__value--vertical',
    valueNormal: '__value--normal',
    valueLarge: '__value--large',
    valueSub: '__value--sub'
  }
};
/**
 * Checks whether a number is in range of a min and max range.
 * If {@link max} is not set the upper-bound of the range will be defined by {@link min}.
 * The match is including the min/max boundary values.
 *
 * @param {number} num - The number to check.
 * @param {number} min - The lower-bound of the range or the upper-bound if {@link max} is not defined.
 * @param {number|null|undefined} [max] - Optional upper-bound of the range.
 * @returns {boolean} - Whether {@link num} in inside the range.
 * @example
 *      inRange(1, 0, 10) => true
 *      inRange(1, 10) => true
 *      inRange(100, 10, 99) => false
 *      inRange(100, 10) => false
 *      inRange(10, 10) => true
 */
const inRange = (num, min, max) => {
  return max ? num >= min && num <= max : num <= min;
};
function CustomRangeSlider({
  className,
  connect,
  disabled,
  noUiSliderOpts,
  onChange,
  onSet,
  pips,
  range,
  sliderInstanceRef,
  step,
  testId,
  tooltipPosition,
  tooltips,
  value,
  ...otherProps
}) {
  const sliderRef = useRef(null);
  const sliderInstance = useRef(null);
  const noUiSliderConfig = {
    ...noUiSliderDefaultOpts,
    ...noUiSliderOpts
  };
  useEffect(() => {
    sliderInit();
    return () => sliderCleanup();
  }, []);
  const internalValue = React__default.useRef(null);
  internalValue.current = value;
  useEffect(() => {
    const currentValue = sliderInstance?.current?.get();
    const didValueChange = Array.isArray(value) && Array.isArray(currentValue) ? value.length === currentValue.length && value.every(i => currentValue.includes(i)) : currentValue === value;
    if (!didValueChange && range && value) {
      sliderInstance?.current?.updateOptions({
        range
      }, false);
      sliderInstance?.current?.set(value);
    }
  }, [value, range]);
  const {
    min,
    max
  } = range;
  useEffect(() => {
    removePipsClickHandlers();
    cleanUpPipsUpdateOnChange();
    sliderInstance.current?.updateOptions({
      range,
      step,
      // @ts-ignore @TODO: Check if we can import Pips.Range type here.
      pips
    }, true);
    if (pips) {
      addPipsClickHandlers();
      setUpPipsUpdateOnChange();
    }
  }, [min, max, step, pips?.mode, pips?.density, pips?.format?.to]);
  const componentClasses = classNames(
  // Prevent rendering the default class twice.
  noUiSliderConfig.cssPrefix === noUiSliderDefaultOpts.cssPrefix ? noUiSliderDefaultOpts.cssPrefix : '', className).trim();
  const componentDataAttributes = {
    'data-custom-range-slider-tooltip-position': tooltipPosition ?? (tooltips && !tooltipPosition ? 'top' : undefined),
    'data-custom-range-slider-pips': !!pips,
    'data-custom-range-slider-tooltips': tooltips ? true : null
  };
  const tooltipClassName = `${noUiSliderConfig.cssPrefix}__tooltip`;
  const tooltipArrowClassName = `${noUiSliderConfig.cssPrefix}__arrow`;
  const markerSelector = `.${noUiSliderConfig.cssPrefix}__marker`;
  const markerValueSelector = `.${noUiSliderConfig.cssPrefix}__value`;
  const wrapTooltip = customTooltips => {
    /**
     * Define how to handle value formatting.
     * @param {boolean | PartialFormatter} v
     * @returns {((v: (string | number)) => string | number) | ((v: (string | number)) => string | number) | ((value: number) => (string | number))}
     */
    const tooltipFormatter = v => {
      if (typeof v === 'boolean') {
        return v => v;
      }
      if (typeof v === 'object' && typeof v.to === 'function') {
        return v.to;
      }
      return v => v;
    };
    /**
     * Change teh default markup of the tooltip to be wrapped in a `div`.
     * @param {boolean | PartialFormatter} v
     * @returns {{to: (v: number) => string}}
     */
    const formatTooltipMarkup = v => {
      const formatter = tooltipFormatter(v);
      return {
        to: v => `<div class="${tooltipClassName}">${formatter(v)}</div><div class="${tooltipArrowClassName}"> </div>`
      };
    };
    if (Array.isArray(customTooltips)) {
      return customTooltips.map(formatTooltipMarkup);
    } else {
      return formatTooltipMarkup(customTooltips);
    }
  };
  function sliderInit() {
    if (sliderRef.current) {
      sliderInstance.current = nouislider.create(sliderRef.current, Object.assign(noUiSliderConfig, {
        connect: connect || [true, false],
        start: value,
        range,
        step,
        pips,
        tooltips: tooltips && wrapTooltip(tooltips)
      }));
    }
    if (pips) {
      addPipsClickHandlers();
      setUpPipsUpdateOnChange();
    }
    if (onChange) {
      getSliderValues('update', onChange);
    }
    if (onSet) {
      getSliderValues('set', onSet);
    }
    sliderInstanceRef?.(sliderInstance);
  }
  const getSliderValues = (event, callback) => {
    sliderInstance.current?.on(event, values => {
      /**
       * Compare current value against initial value.
       * 1. Check if `values` and `internalValue.current` is an array.
       * 2. For multiple handle controls compare array equality.
       * 3. For single handle control compare single value equality.
       */
      const hasChanged = Array.isArray(values) && Array.isArray(internalValue?.current) ? values.length === internalValue?.current?.length && !values.every(i => internalValue?.current?.includes(parseFloat(`${i}`))) :
      // @ts-ignore @FIXME: Argument of type 'number | any[] | null | undefined' is not assignable to parameter of type 'string'. Type 'undefined' is not assignable to type 'string'.
      parseFloat(values) !== parseFloat(internalValue?.current);
      if (hasChanged) {
        callback(values.length > 1 ? values : values[0]);
      }
    });
  };
  function sliderCleanup() {
    removePipsClickHandlers();
    sliderInstance.current?.destroy();
  }
  const updateSliderValue = event => {
    const eventTarget = event?.target;
    const currentValue = eventTarget?.hasAttribute('data-value') ? eventTarget?.getAttribute('data-value') : eventTarget?.nextElementSibling?.getAttribute('data-value');
    if (currentValue) {
      sliderInstance.current?.set(currentValue);
    }
  };
  function addPipsClickHandlers() {
    if (sliderRef.current) {
      sliderRef.current.querySelectorAll(`${markerSelector}, ${markerValueSelector}`).forEach(node => node.addEventListener('click', updateSliderValue));
    }
  }
  function removePipsClickHandlers() {
    if (sliderRef.current) {
      sliderRef.current.querySelectorAll(`${markerSelector}, ${markerValueSelector}`).forEach(node => node.removeEventListener('click', updateSliderValue));
    }
  }
  function setUpPipsUpdateOnChange() {
    if (sliderRef.current) {
      const markers = Array.from(sliderRef.current.querySelectorAll(markerSelector));
      sliderInstance.current?.on('update.pipsUpdate', currentValue => {
        /**
         * Get the first and last (min and max) values:
         * 1. Parse all values to a float.
         * 2. Sort all floats ascending from min to max.
         * 3. Filter out the first and last item (min/max), where `i === 0` is the first item,
         *    and `i === arr.length - 1 && i !== 0` is the last one, excluding the first one.
         */
        const [_min, _max] = currentValue.map(val => parseFloat(`${val}`)).sort((a, b) => a >= b ? a : b).filter((val, i, arr) => i === 0 || i === arr.length - 1 && i !== 0);
        /**
         * Iterate over the marker and set the related CSS classes.
         */
        markers.forEach(marker => {
          const markerAttr = marker?.nextElementSibling?.getAttribute('data-value') ?? '';
          const markerValue = parseFloat(markerAttr);
          marker?.classList.remove('in-range', 'is-active');
          if (inRange(markerValue, _min, _max)) {
            marker?.classList.add(_min === markerValue ? 'is-active' : 'in-range');
          }
        });
      });
    }
  }
  function cleanUpPipsUpdateOnChange() {
    sliderInstance.current?.off('update.pipsUpdate');
  }
  return /*#__PURE__*/React__default.createElement("div", {
    "data-testid": testId,
    ref: sliderRef,
    className: componentClasses,
    "aria-disabled": disabled,
    // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
    inert: disabled ? '' : null,
    ...componentDataAttributes,
    ...otherProps
  });
}

/**
 * Bronson CustomProgress component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-custom-progress.html
 *
 * @constructor
 */
function CustomProgress({
  className,
  connect,
  detailsLeft,
  detailsRight,
  range,
  steps,
  theme,
  testId,
  ...otherProps
}) {
  const [rangeSliderRef, setRangeSliderRef] = useState(null);
  const [tooltipRefs, setTooltipRefs] = useState([]);
  const classNameList = classNames('c-custom-progress', className).trim();
  useEffect(() => {
    if (rangeSliderRef?.current && tooltipRefs.length < 1 && steps) {
      const thumbs = Array.from(rangeSliderRef?.current?.target?.querySelectorAll('.c-custom-progress__thumb')) ?? [];
      const triggerTargets = Array.from(rangeSliderRef?.current?.target?.querySelectorAll('.c-custom-progress__connect')) ?? [];
      /**
       * Iterate of the thumbs and inject an icon where configured.
       */
      thumbs.forEach((thumb, i) => {
        const iconName = steps?.[i].icon;
        if (iconName) {
          thumb?.insertAdjacentHTML('beforeend', `<i class="c-custom-progress__thumb__icon c-icon c-icon--[${iconName}]" aria-hidden="true" role="img"></i>`);
        }
      });
      setTimeout(() => setTooltipRefs(thumbs.map((h, i) => (/*#__PURE__*/React__default.createElement(Tippy, {
        content: steps[i]?.tooltip?.content,
        key: `renderTooltips-${i}`,
        reference: h,
        trigger: steps[i]?.tooltip?.trigger ?? 'manual',
        triggerTarget: triggerTargets[i] ?? null,
        showOnCreate: steps[i]?.tooltip?.trigger !== 'mouseenter',
        appendTo: rangeSliderRef?.current?.target,
        placement: steps[i]?.tooltip?.placement ?? 'top',
        hideOnClick: false,
        maxWidth: "none",
        allowHTML: true,
        onCreate: () => {
          const isStatic = steps[i]?.tooltip?.trigger !== 'mouseenter';
          const placementDirection = steps[i]?.tooltip?.placement ?? 'top';
          /**
           * Add positional classes to the thumb and trigger element.
           */
          thumbs[i]?.classList.add(`c-custom-progress__tooltip-placement--${placementDirection}`);
          triggerTargets[i]?.classList.add(`c-custom-progress__tooltip-placement--${placementDirection}`);
          /**
           * Static and non-final thumbs get a special class.
           */
          if (isStatic && !steps[i]?.final) {
            thumbs[i]?.classList.add('c-custom-progress__step--main');
            triggerTargets[i]?.classList.add('c-custom-progress__step--main');
          }
        },
        popperOptions: {
          modifiers: [{
            name: 'flip',
            options: {
              enable: false,
              enabled: false,
              flipVariations: false,
              /**
               * This disables the flip behavior.
               * @see https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements
               */
              fallbackPlacements: []
            }
          }]
        }
      })))), 150);
    }
  }, [rangeSliderRef, steps, tooltipRefs]);
  const customRangeSliderValue = steps?.map(s => s.value);
  const renderSlider = () => (/*#__PURE__*/React__default.createElement(CustomRangeSlider, {
    className: "c-custom-progress__visualization",
    sliderInstanceRef: ref => setRangeSliderRef(ref),
    range: range,
    value: customRangeSliderValue,
    connect: connect,
    disabled: true,
    "aria-hidden": "true",
    tabIndex: -1,
    noUiSliderOpts: {
      cssPrefix: 'c-custom-progress',
      keyboardSupport: false
    },
    "data-custom-progress-theme": theme
  }));
  const renderDescription = () => (/*#__PURE__*/React__default.createElement("div", {
    className: "c-custom-progress__description"
  }, detailsLeft && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-custom-progress__description__item c-custom-progress__description__item--start"
  }, detailsLeft)), detailsRight && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-custom-progress__description__item c-custom-progress__description__item--end"
  }, detailsRight))));
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, tooltipRefs, renderSlider(), renderDescription());
}

/**
 * Bronson DataOverview component.
 */
function DataOverview({
  buttonLabel,
  buttonProps,
  children,
  className,
  headerLanguage = 'de',
  icon,
  title,
  titleLevel = 4,
  testId,
  ...otherProps
}) {
  const HeadingElement = `h${titleLevel}`;
  const articleClassNameList = classNames('c-data-overview ', className).trim();
  function renderIfDataOverviewIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-data-overview__icon"
      }, /*#__PURE__*/React__default.createElement(Icon, {
        name: icon
      }));
    }
    return null;
  }
  function renderIfDataOverviewButton() {
    if (buttonLabel) {
      return /*#__PURE__*/React__default.createElement(Button, {
        element: "a",
        link: true,
        small: true,
        simple: true,
        className: "c-data-overview__button",
        iconViaCss: true,
        ...buttonProps
      }, buttonLabel);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("article", {
    ...otherProps,
    "data-testid": testId,
    className: articleClassNameList
  }, /*#__PURE__*/React__default.createElement("header", {
    className: "c-data-overview__header",
    lang: headerLanguage
  }, renderIfDataOverviewIcon(), /*#__PURE__*/React__default.createElement(HeadingElement, {
    className: "c-data-overview__title"
  }, title), renderIfDataOverviewButton()), /*#__PURE__*/React__default.createElement("div", {
    className: "c-data-overview__body"
  }, children));
}

/**
 * Additional inline styles for a disabled DatePicker button.
 * @type {{pointerEvents: string}}
 */
const disabledButtonStyle = {
  pointerEvents: 'none'
};
/**
 * Regex to check against the native dateString format.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
 * @type {RegExp}
 */
const NATIVE_INPUT_DATE_REGEX = /\d{4}-\d{2}-\d{2}/;
/**
 * Pads one-digit date numbers with a leading zero.
 * @param {number} d - The date number to pad with a leading zero.
 * @return {string} - Returns of the passed date number.
 * @example
 *    padDate(1) → '01'
 */
const padDate = d => `${d < 10 ? d.toString().padStart(2, '0') : d}`;
/**
 * Extract a separator from a given dateFormat.
 * @param {string} dateFormat - The given dateFormat to extract the separator.
 * @return {string} - The date separator, usually a one-char string.
 * @example
 *    getSeparator('Y-m-d') → '-'
 */
const getSeparator = dateFormat => dateFormat.substring(1, 2);
/**
 * Create an {@link IMask.MaskedDate.pattern} input pattern.
 * @param {string} dateFormat - The given dateFormat string.
 * @return {IMask.MaskedDate.pattern} - Return a masked date pattern.
 *                                      Note the backticks: They are part of it!.
 * @example
 *    createPattern('Y-m-d') → 'Y-`m-`d'
 */
const createPattern = dateFormat => {
  const separator = getSeparator(dateFormat);
  return dateFormat.replaceAll(separator, `${separator}\``);
};
/**
 * Given a dateFormat string return an object with dateFormat as keys and their index
 * by splitting it via separator.
 * @param dateFormat
 * @return {{[string]: number}}
 * @example
 *    getDateTokenPosition('Y-m-d') → {'Y': 0, 'm': 1, 'd': 2}
 */
const getDateTokenPosition = dateFormat => {
  const separator = getSeparator(dateFormat);
  return dateFormat.split(separator).reduce((prev, curr, idx) => ({
    ...prev,
    [curr]: idx
  }), {});
};
/**
 * Get an object of placeholder characters mapping on the dateFormat.
 * @param {string} dateFormat - The given dateFormat.
 * @param {string} [placeholder] - The given placeholder.
 * @return {{yearPlaceholderChar, dayPlaceholderChar, monthPlaceholderChar}|{yearPlaceholderChar: string, dayPlaceholderChar: string, monthPlaceholderChar: string}}
 * @example
 *    getPlaceholderChars('Y/m/d', 'a/b/c') →
 *    { yearPlaceholderChar: 'a', monthPlaceholderChar: 'b', dayPlaceholderChar: 'c' }
 */
const getPlaceholderChars = (dateFormat, placeholder) => {
  const separator = getSeparator(dateFormat);
  const {
    Y: yearPos,
    m: monthPos,
    d: dayPos
  } = getDateTokenPosition(dateFormat);
  if (placeholder) {
    const splittedPlaceholder = placeholder.split(separator);
    return {
      yearPlaceholderChar: splittedPlaceholder[yearPos],
      monthPlaceholderChar: splittedPlaceholder[monthPos],
      dayPlaceholderChar: splittedPlaceholder[dayPos]
    };
  }
  return {
    yearPlaceholderChar: 'Y',
    monthPlaceholderChar: 'm',
    dayPlaceholderChar: 'd'
  };
};
/**
 * The heart of date parsing. Is used by {@link Flatpickr} and also {@link IMask}
 * to parse the date input from a string to a valid date. The function has to make
 * assumptions whether the current input device is a plain `input[type=text]` or
 * a native `input[type=date]`.
 * If the passed date cannot be parsed directly, it is not in a valid native HTML5
 * date format `yyyy-mm-dd`. In such a case it is parsed accordingly the supplied {@link dateFormat}.
 *
 * @param {string} separator - The current date separator.
 * @param {number} yearPos - The position of the year in the date array.
 * @param {number} monthPos - The position of the month in the date array.
 * @param {number} dayPos - The position of the day in the date array.
 * @param {boolean} isNativeInput - If the input is a native or non-native input.
 * @param {string} [dateFormat] - The passed dateFormat.
 * @return {(function(*=): (string|null|Date))|*} - Returns a partially-applied function to parse dates.
 * @example
 *    parseDate('/', 0, 1, 2)('2000/01/01') → '2000-01-01'
 */
const parseDate = (separator, yearPos, monthPos, dayPos, isNativeInput, dateFormat) => date => {
  /**
   * Check if we are trying to parse the placeholder
   * and bail out if this is the case.
   */
  if (date.search(/[a-zA-Z]/g) >= 0) {
    return '';
  }
  /**
   * Check if the date can be split via the {@link separator}
   * and bail out if this is not the case.
   */
  if (!isNativeInput && !date.includes(separator)) {
    return '';
  }
  /**
   * Split the date into an array of its parts (year, month, day).
   * @type {*}
   */
  const yearMonthDay = date?.split(separator);
  /**
   * Check if the passed date has the native HTML5 date format.
   * @type {boolean}
   */
  const hasNativeDateFormat = NATIVE_INPUT_DATE_REGEX.test(date);
  /**
   * Constructed string that matches the date time string (a simplification of the ISO 8601 calendar date extended format). We are explicitly appending the daytime as 'T00:00:00' to have the same behavior as
   * constructing dates with individual date and time component values, e.g. new Date(2020, 3, 1).
   * @see https://developer.mozilla.org/en-US/docs/web/javascript/reference/global_objects/date/parse#date_time_string_format
   * @type {string}
   */
  const adaptiveDateString = `${yearMonthDay[yearPos]}-${yearMonthDay[monthPos]}-${yearMonthDay[dayPos]}T00:00:00`;
  /**
   * Check if the date origins from a non-native datepicker.
   */
  if (!isNativeInput) {
    const parsedDate = new Date(adaptiveDateString);
    if (isValidDateObject(parsedDate)) {
      return parsedDate;
    }
    // eslint-disable-next-line no-console
    console.error(`Bronson-React Picker: Your date (${date}) is invalid. Please check that it matches the dateFormat (${dateFormat}).`);
    return null;
  }
  /**
   * The date origins from a native  HTML5 datepicker `input[type=date]`.
   * If the date matches the hasNativeDateFormat (usually this happens on the first render)
   * we directly parse it, otherwise we use the {@link adaptiveDateString} to get
   * the date out of it.
   * @type {*|string}
   */
  const nativeDateString = hasNativeDateFormat ? date : adaptiveDateString;
  const parsedDate = new Date(nativeDateString);
  return isValidDateObject(parsedDate) ? parsedDate : null;
};
/**
 * Given a dateFormat, the partially applied function returns
 * an {@link IMask} date to string formatter function.
 * Only used when {@link maskedInput} is set to `true`.
 * @see https://imask.js.org/api/#maskedformat
 *
 * @param {string} dateFormat - The currently used {@link dateFormat}.
 * @return {function(*): string} - {@link IMask} date to string formatter function.
 * @example
 *    formatDate('Y-m-d')(1-12-2000) → '2000-12-01'
 */
const formatDate = dateFormat => date => {
  const day = padDate(date.getDate());
  const month = padDate(date.getMonth() + 1);
  const year = date.getFullYear();
  const {
    Y: yearPos,
    m: monthPos,
    d: dayPos
  } = getDateTokenPosition(dateFormat);
  const separator = getSeparator(dateFormat);
  const newDate = [];
  newDate[yearPos] = year;
  newDate[monthPos] = month;
  newDate[dayPos] = day;
  return newDate.join(separator);
};
/**
 * Set the correct locale for the calendar.
 * @param {('pl'|'de'|'it'|'cs'|'es'|'fr'|'nl'|'pt'|'ja'|'sk')} locale - The locale to set.
 * @return {CustomLocale|null}
 * @example
 *    setLocale('dk') → null
 */
const setLocale = locale => {
  switch (locale) {
    case 'pl':
      return pl.pl;
    case 'de':
      return de.de;
    case 'it':
      return it.it;
    case 'cs':
      return cs.cs;
    case 'es':
      return es.es;
    case 'fr':
      return fr.fr;
    case 'nl':
      return nl.nl;
    case 'pt':
      return pt.pt;
    case 'ja':
      return ja.ja;
    case 'sk':
      return sk.sk;
    default:
      return null;
  }
};
/**
 * Checks if the given variable is a valide date.
 * @param {*} v - The variable to check.
 * @return {boolean}
 * @example
 *    isValidDate(new Date('2000-01-01')) → true
 *    isValidDate(new Date('21')) → false
 *    isValidDate('2000-01-01') → false
 */
const isValidDateObject = v => v instanceof Date && !Number.isNaN(v.getDate());
/**
 * Naive check if the date matches the passed {@link dateFormat}.
 * @param {string} date
 * @param {string} dateFormat
 * @returns {boolean}
 * @example
 *    matchesDateFormat('2000-01-01', 'd.m.Y') → false
 *    matchesDateFormat('2000-01-01', 'Y.m.d') → false
 *    matchesDateFormat('2000-01-01', 'Y-m-d') → true
 */
const matchesDateFormat = (date, dateFormat) => {
  /**
   * If no date was specified or if it contains letters (re iMask chars)
   * the date does not match.
   */
  if (!date || date.search(/[a-zA-Z]/g) >= 0) {
    return false;
  }
  // @ts-ignore @TODO: Fix to comply with expected type.
  const dateObj = globalThis?.flatpickr?.parseDate(date, dateFormat);
  const day = padDate(dateObj.getDate());
  const month = padDate(dateObj.getMonth() + 1);
  const year = dateObj.getFullYear().toString();
  const {
    Y: yearPos,
    m: monthPos,
    d: dayPos
  } = getDateTokenPosition(dateFormat);
  const separator = getSeparator(dateFormat);
  if (!date.includes(separator)) {
    return false;
  }
  const splitDate = date.split(separator);
  const hasDay = dayPos ? splitDate[dayPos] === day : true;
  const hasMonth = monthPos ? splitDate[monthPos] === month : true;
  const hasYear = yearPos ? splitDate[yearPos] === year : true;
  return hasDay && hasMonth && hasYear;
};
/**
 * Bronson Picker component.
 * Generic date and time picker component
 * to enable {@link DatePicker} and {@link TimePicker}.
 * @internal
 * @constructor
 */
function Picker({
  className,
  componentClassName,
  dateFormat = 'Y-m-d',
  disabled = false,
  disableDays = [],
  error = false,
  flatpickrOptions = {},
  flatpickrProps,
  id,
  inputProps,
  locale = 'de',
  maskedInput = true,
  mode = 'single',
  monthSelectorDropdown = false,
  name,
  noBackground = true,
  onBlur,
  onChange,
  onError,
  pickerClassName,
  placeholder,
  value: customValue,
  weekNumbers,
  testId,
  required
}) {
  /**
   * Track internal state via the wrapping {@link FormFieldContext}.
   * @type {object}
   */
  const context = React__default.useContext(FormFieldContext);
  /**
   * Hook to set active/inactive state on the Picker.
   */
  useEffect(() => {
    if (context?.setElementActive) {
      context.setElementActive(!!customValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customValue]);
  /**
   * Ref to the React flatpickr node.
   * @type {React.MutableRefObject<null>}
   */
  const flatpickrRef = useRef(null);
  /**
   * Ref to the masked input field.
   * Only utilized when {@link maskedInput} is set to `true`.
   * @type {React.MutableRefObject<null>}
   */
  const maskRef = useRef(null);
  /**
   * Track the internal date state.
   */
  const [date, setDate] = useState(customValue ?? '');
  /**
   * Track error states.
   */
  const [hasError, setHasError] = useState(false);
  /**
   * Track whether native/non-native input is used.
   * Is set via the {@link flatpickrRef}.
   */
  const [isMobileInput, setIsMobileInput] = useState(false);
  /**
   * Track the current {@link IMask.MaskedPattern.lazy} state.
   * @see https://imask.js.org/api/#maskedpatternlazy
   */
  const [isLazy, setIsLazy] = useState(true);
  /**
   * Memoize the instance’s {@link dateFormat} separator string, e.g. '-' or '/' etc..
   * @type {string}
   */
  const separator = useMemo(() => getSeparator(dateFormat), [dateFormat]);
  /**
   * Memoize the instance’s date part positions and extract them into variables.
   * @type {object}
   */
  const {
    Y: yearPos,
    m: monthPos,
    d: dayPos
  } = useMemo(() => getDateTokenPosition(dateFormat), [dateFormat]);
  /**
   * Memoize the instance’s date part placeholder and extract them into variables.
   * @type {object}
   */
  const {
    yearPlaceholderChar,
    monthPlaceholderChar,
    dayPlaceholderChar
  } = useMemo(() => getPlaceholderChars(dateFormat, placeholder), [dateFormat, placeholder]);
  /**
   * Memoize the instance’s date parser.
   */
  const mParseDate = useMemo(() => parseDate(separator, yearPos, monthPos, dayPos, isMobileInput, dateFormat), [separator, yearPos, monthPos, dayPos, isMobileInput, dateFormat]);
  /**
   * Memoize the instance’s date formatter.
   * @type {function(*): string}
   */
  const mFormatDate = useMemo(() => formatDate(dateFormat), [dateFormat]);
  /**
   * Set the current internal date state via a side effect.
   */
  useEffect(() => {
    setDate(customValue ?? '');
  }, [customValue]);
  /**
   * Set the current internal native/non-native input mode via a side effect.
   */
  useEffect(() => {
    setIsMobileInput(flatpickrRef?.current?.flatpickr?.isMobile);
  }, [flatpickrRef]);
  /**
   * Fixes a bug where the mobile input won’t reflect the state changes.
   * We just fix this for `[disabled]` here.
   * @see https://github.com/haoxins/react-flatpickr/issues/27
   * @see https://github.com/haoxins/react-flatpickr/issues/125
   */
  useEffect(() => {
    if (isMobileInput) {
      disabled ? flatpickrRef?.current?.flatpickr?.mobileInput?.setAttribute('disabled', '') : flatpickrRef?.current?.flatpickr?.mobileInput?.removeAttribute('disabled');
    }
  }, [disabled, isMobileInput]);
  const componentPickerWrapperClass = classNames('c-input', className).trim();
  const componentPickerClass = classNames('c-input__input', pickerClassName).trim();
  const componentPickerButtonClass = classNames('c-input__addon', {
    'c-input__addon--no-background': noBackground
  }).trim();
  const componentPickerButtonIconClass = classNames('c-icon', `c-icon--[semantic-${flatpickrOptions?.enableTime ? 'clock' : 'calendar'}]`).trim();
  /**
   * Internal onValue function to handle date changes
   * and update the internal state.
   */
  const onValue = value => {
    /**
     * Set the lazy state to false and show the IMask if applicable.
     * Only used when {@link maskedInput} is set to `true`.
     */
    setIsLazy(false);
    /**
     * If we have a value we set the internal {@link date} state to it and call any external `onChange` function
     */
    if (value) {
      setDate(value);
    }
    /**
     * Synchronize the current {@link IMask.MaskedDate.value} from the input view.
     * Only used when {@link maskedInput} is set to `true`.
     * @see https://imask.js.org/api/#inputmaskupdatevalue
     */
    if (maskRef?.current?.maskRef?.updateValue) {
      maskRef?.current.maskRef.updateValue();
    }
    return value;
  };
  /**
   * Display a masked input via {@link IMask} when {@link maskedInput} is set to `true`
   * otherwise use a simple input element. For {@link TimePicker} we always disable inputMasks.
   * @return {JSX.Element}
   */
  const renderInput = () => maskedInput && mode === 'single' && !flatpickrOptions?.enableTime ? (/*#__PURE__*/React__default.createElement(IMaskInput, {
    className: componentPickerClass,
    "aria-invalid": error ? true : undefined,
    name: name,
    ref: maskRef,
    "data-input": true,
    "data-testid": testId,
    mask: Date,
    overwrite: true,
    pattern: createPattern(dateFormat),
    format: mFormatDate,
    // @ts-ignore @FIXME: Consolidate to `(value: string) => Date`.
    parse: mParseDate,
    blocks: {
      d: {
        mask: IMask.MaskedRange,
        placeholderChar: dayPlaceholderChar,
        from: 1,
        to: 31,
        maxLength: 2
      },
      m: {
        mask: IMask.MaskedRange,
        placeholderChar: monthPlaceholderChar,
        from: 1,
        to: 12,
        maxLength: 2
      },
      Y: {
        mask: IMask.MaskedRange,
        placeholderChar: yearPlaceholderChar,
        from: 1,
        to: 2999
      }
    },
    autofix: false,
    lazy: isLazy,
    disabled: disabled,
    id: id,
    required: required,
    ...inputProps
  })) : (
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  React__default.createElement("input", {
    "data-testid": testId,
    "aria-invalid": error ? true : undefined,
    name: name,
    className: componentPickerClass,
    placeholder: placeholder,
    disabled: disabled,
    "data-input": true,
    id: id,
    required: required,
    ...inputProps
  }));
  /**
   * Render the DatePicker calendar icon.
   * @return {JSX.Element}
   */
  const renderIcon = () => {
    return /*#__PURE__*/React__default.createElement(Icon, {
      className: componentPickerButtonIconClass,
      viaCss: true
    });
  };
  /**
   * Render the DatePicker button.
   * @return {JSX.Element}
   */
  const renderButton = () => {
    return /*#__PURE__*/React__default.createElement("button", {
      className: componentPickerButtonClass,
      type: "button",
      tabIndex: -1,
      "aria-label": "toggle datepicker",
      "data-toggle": true,
      style: disabled ? disabledButtonStyle : undefined
    }, renderIcon());
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: componentClassName
  }, /*#__PURE__*/React__default.createElement(Flatpickr, {
    className: componentPickerWrapperClass,
    ref: flatpickrRef,
    ...flatpickrProps,
    value: date,
    /**
     * Hook
     * @see node_modules/flatpickr/dist/typings.d.ts
     */
    onChange: (dates, currentDateString) => {
      if (!hasError) {
        const dateString = onValue(currentDateString);
        if (dateString) {
          onChange?.(dateString);
        }
        /**
         * Handle active/inactive state on the Picker.
         */
        context?.setElementActive?.(!!dateString);
      }
    },
    /**
     * The `onBlur` hook gets the current value from the input,
     * as {@link Flatpickr} does not support `blur` hooks itself.
     */
    onBlur: event => {
      if (!hasError) {
        /**
         * Prevent blurs when the calendar popup is still open.
         */
        if (!flatpickrRef?.current?.flatpickr?.isOpen) {
          const inputValue = event.target.value;
          const dateString = onValue(inputValue);
          /**
           * Check if the input is a native (mobile) HTML5 input.
           * If so, we need to convert the W3C standard date format
           * to the one that was passed for {@link dateFormat}.
           */
          if (isMobileInput) {
            const nativeDate = new Date(dateString);
            if (isValidDateObject(nativeDate)) {
              /**
               * Format from `YYYY-MM-DD` to {@link dateFormat}.
               */
              const formattedDate = flatpickrRef?.current?.flatpickr?.formatDate(nativeDate, dateFormat);
              onBlur?.(formattedDate);
            } else {
              onError?.(`Bronson-React Picker: Native date picker provided an invalid date (${dateString})`);
            }
          } else {
            onBlur?.(dateString);
          }
          setIsLazy(true);
        }
      }
    },
    onValueUpdate: () => {
      if (maskRef?.current?.maskRef?.updateValue) {
        maskRef?.current.maskRef.updateValue();
      }
    },
    onClick: () => setIsLazy(false),
    options: {
      /**
       * Callback for {@link Flatpickr} internal error handler.
       * @param {Error} err - Internal Flatpickr error.
       */
      errorHandler: err => {
        /**
         * Check if the error has a message.
         * Set the Picker {@link hasError} state to `true`. This prevents
         * other callback from fire due to the async value reset further down.
         * Only recover for date inputs but not when {@link flatpickrOptions?.enableTime}
         * is set.
         */
        if (err?.message && !flatpickrOptions?.enableTime) {
          /**
           * Prevent Flatpickr from throwing an error when the initial mask is displayed.
           */
          if (maskRef.current?.maskRef?.masked?.value?.includes(yearPlaceholderChar) && maskRef.current?.maskRef?.masked?.value?.includes(monthPlaceholderChar) && maskRef.current?.maskRef?.masked?.value?.includes(dayPlaceholderChar)) {
            setHasError(true);
            setTimeout(() => {
              setHasError(false);
            }, 0);
            if (!hasError) {
              onChange?.('');
            }
            return;
          }
          /**
           * Call the {@link onError} callback.
           */
          onError?.(err?.message);
          /**
           * IF a Flatpickr instance is present, try to recover the last valid value.
           */
          if (flatpickrRef?.current?.flatpickr) {
            setHasError(true);
            setTimeout(() => {
              /**
               * Only recover valid values, otherwise just ignore.
               */
              if (matchesDateFormat(date, dateFormat)) {
                if (maskRef.current?.maskRef?.updateValue) {
                  maskRef.current.maskRef.updateValue();
                }
                flatpickrRef?.current?.flatpickr?.setDate(date);
              } else if (mode === 'range' || mode === 'multiple') {
                flatpickrRef?.current?.flatpickr?.setDate(date);
              }
              setHasError(false);
            }, 0);
          }
        }
      },
      wrap: true,
      allowInput: true,
      mode,
      locale: setLocale(locale),
      /**
       * Disable custom date parsing for {@link TimePicker}.
       * @param dateStr
       * @return {null|string|Date}
       */
      parseDate: flatpickrOptions?.enableTime ? null : dateStr => mParseDate(dateStr),
      dateFormat,
      disable: disableDays,
      nextArrow: '<i class="c-icon c-icon--[semantic-forward]" aria-hidden="true" role="img"></i>',
      prevArrow: '<i class="c-icon c-icon--[semantic-back]" aria-hidden="true" role="img"></i>',
      monthSelectorType: monthSelectorDropdown ? 'dropdown' : 'static',
      weekNumbers,
      ...flatpickrOptions
    }
  }, renderInput(), renderButton()));
}

/**
 * Bronson DatePicker component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-datepicker.html
 *
 * @constructor
 */
function DatePicker({
  className,
  dateFormat = 'Y-m-d',
  datePickerClassName,
  disabled = false,
  disableDays = [],
  error = false,
  flatpickrOptions = {},
  flatpickrProps,
  locale,
  maskedInput = true,
  maxDate,
  minDate,
  mode = 'single',
  monthSelectorDropdown = false,
  name,
  noBackground = true,
  onBlur,
  onChange,
  onError,
  placeholder,
  required,
  value,
  weekNumbers,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement(Picker, {
    "aria-invalid": error ? true : undefined,
    className: className,
    componentClassName: "c-datepicker",
    dateFormat: dateFormat,
    disabled: disabled,
    disableDays: disableDays,
    error: error,
    flatpickrOptions: {
      ...flatpickrOptions,
      ...{
        minDate,
        maxDate
      }
    },
    flatpickrProps: flatpickrProps,
    locale: locale,
    maskedInput: maskedInput,
    mode: mode,
    monthSelectorDropdown: monthSelectorDropdown,
    name: name,
    noBackground: noBackground,
    onBlur: onBlur,
    onChange: onChange,
    onError: onError,
    pickerClassName: datePickerClassName,
    placeholder: placeholder,
    required: required,
    value: value,
    weekNumbers: weekNumbers,
    testId: testId,
    ...otherProps
  });
}

/**
 * The DescriptionList component is our implementation of the
 * [HTML Description List element `<dl>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dl)
 * and is offered in different responsive layout variants.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-description-list.html
 * @constructor
 */
function DescriptionList({
  children,
  className,
  grid,
  horizontal,
  shortTerms,
  split,
  table,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-description-list', {
    // Convenience props from Bronson variants.
    'c-description-list--grid': grid,
    'c-description-list--horizontal': horizontal,
    'c-description-list--terms-short': shortTerms,
    'c-description-list--split': split,
    'c-description-list--table': table
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("dl", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, children);
}
DescriptionList.displayName = 'DescriptionList';
/**
 * Internal DescriptionListGroup container.
 * @internal
 * @returns {JSX.Element} - The DescriptionListGroup element.
 * @constructor
 */
function DescriptionListGroup({
  children,
  className,
  id,
  termClassName,
  termText,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-description-list__group', className).trim();
  const dtClassNameList = classNames('c-description-list__term', termClassName).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("dt", {
    className: dtClassNameList,
    id: id
  }, termText), children);
}
DescriptionListGroup.displayName = 'DescriptionList.Group';
DescriptionList.Group = DescriptionListGroup;
/**
 * Internal DescriptionListDetail container.
 * @internal
 * @constructor
 */
function DescriptionListDetail({
  action,
  children,
  className,
  small
}) {
  const ddClassNameList = classNames('c-description-list__detail', className).trim();
  return /*#__PURE__*/React__default.createElement("dd", {
    className: ddClassNameList
  }, children && (/*#__PURE__*/React__default.createElement("span", {
    className: "c-description-list__text"
  }, small ? (/*#__PURE__*/React__default.createElement("span", {
    className: "c-description-list__small-print"
  }, children)) : React__default.Children.map(children, child => {
    /**
     * Add CSS class `.c-description-list__small-print` conditionally,
     * only if the child is of type HTMLTimeElement.
     */
    if (/*#__PURE__*/isValidElement(child)) {
      if (child?.type === 'time') {
        const timeProps = child?.props;
        return /*#__PURE__*/React__default.cloneElement(/*#__PURE__*/React__default.createElement("time", {
          className: "c-description-list__small-print",
          dateTime: timeProps?.dateTime
        }, timeProps?.children));
      } else {
        return child;
      }
    } else {
      return child;
    }
  }))), action && /*#__PURE__*/React__default.createElement("div", {
    className: "c-description-list__action"
  }, action));
}
DescriptionListDetail.displayName = 'DescriptionList.Detail';
DescriptionList.Detail = DescriptionListDetail;

/* eslint-disable react-hooks/exhaustive-deps */
// TODO: check deps
const AccordionContext = /*#__PURE__*/React__default.createContext(null);
/*
 * Bronson DescriptionListAccordion component.
 */
function DescriptionListAccordion({
  // defaultOpen,
  children,
  className,
  headerSplit,
  multipleOpen,
  totalLabel,
  totalLabelNote,
  totalValue,
  withBackground,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState([]);
  const contextValue = {
    // the value of "open" state
    isOpen,
    // callback function to toggle the open states
    toggleOpen: ref => toggleOpen(ref),
    // callback function to update the open states
    updateOpen: (ref, refToBeOpen) => updateOpen(ref, refToBeOpen),
    headerSplit
  };
  function isOpen(ref) {
    return open.indexOf(ref) >= 0;
  }
  /*
   * Updates the open state for a given item (ref) with a target condition (refToBeOpen bool).
   * Subsequently updates other states accordingly.
   */
  function updateOpen(ref, refToBeOpen) {
    if (multipleOpen) {
      const index = open.indexOf(ref);
      if (refToBeOpen) {
        // should be open
        if (index < 0) {
          // not yet in open list, add
          setOpen(open.concat(open, [ref]));
        }
        // should not be open
      } else if (index >= 0) {
        // present in list, remove
        setOpen(open.filter(value => value !== ref));
      }
    } else if (refToBeOpen) {
      // set as only open
      setOpen([ref]);
    } else {
      // set none to be open
      setOpen([]);
    }
  }
  /*
   * Change open state for the given item (ref) without the need to know the current state.
   */
  function toggleOpen(ref) {
    updateOpen(ref, !(open.indexOf(ref) >= 0));
  }
  const divClassNameList = classNames('c-accordion', 'c-description-list-accordion__accordion', {
    // Main class modifier convenience prop extension.
    'c-accordion--bg-panel ': withBackground,
    // Convenience prop from Bronson variants.
    'c-accordion--header-split': headerSplit
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("section", {
    className: "c-description-list-accordion"
  }, /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList,
    "data-accordion-multiple": !multipleOpen && 'true'
  }, /*#__PURE__*/React__default.createElement(AccordionContext.Provider, {
    value: contextValue
  }, children)), /*#__PURE__*/React__default.createElement("dl", {
    className: "c-description-list-accordion__item  c-description-list-accordion__item--total"
  }, /*#__PURE__*/React__default.createElement("dt", {
    className: "c-description-list-accordion__item__label"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-description-list-accordion__item__label-text"
  }, totalLabel), /*#__PURE__*/React__default.createElement("div", {
    className: "c-description-list-accordion__item__label-note"
  }, totalLabelNote)), /*#__PURE__*/React__default.createElement("dd", {
    className: "c-description-list-accordion__item__value"
  }, totalValue)));
}
DescriptionListAccordion.displayName = 'DescriptionListAccordion';
/**
 * Bronson AccordionItem component.
 * @internal
 */
function DescriptionListAccordionItem({
  children,
  className,
  defaultOpen,
  icon,
  nonCollapsable,
  subtitle,
  title,
  titleSplit,
  tooltip,
  testId,
  ...otherProps
}) {
  const itemRef = useRef(uuidv4());
  const context = React__default.useContext(AccordionContext);
  useEffect(() => {
    context?.updateOpen(itemRef.current, !!defaultOpen);
  }, [defaultOpen]);
  const childDivClassNameList = classNames('c-accordion__header', 'c-description-list-accordion__header', {
    'is-active': context?.isOpen(itemRef.current)
  }, className).trim();
  /**
   * Needed e.g. because IconList within the panel would result in icons staying on screen even when collapsed.
   * This is handled by Bronson JS otherwise.
   */
  const panelStyle = {
    display: context?.isOpen(itemRef.current) ? 'inherit' : 'none'
  };
  const defaultCursor = nonCollapsable ? {
    cursor: 'default'
  } : {};
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: childDivClassNameList,
    onClick: () => {
      return !nonCollapsable && context?.toggleOpen(itemRef.current);
    },
    "data-testid": testId,
    style: defaultCursor,
    ...otherProps
  }, !nonCollapsable && /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-icon"
  }), /*#__PURE__*/React__default.createElement("h3", {
    className: "c-accordion__title"
  }, nonCollapsable ? (/*#__PURE__*/React__default.createElement("div", {
    className: "c-accordion__title-label",
    style: defaultCursor
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text c-description-list-accordion__title-label"
  }, title, icon && (/*#__PURE__*/React__default.createElement(InfoIcon, {
    className: "c-accordion__title-info-icon",
    element: "button",
    icon: icon
  }, tooltip))), context?.headerSplit && (/*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text c-description-list-accordion__title-value"
  }, titleSplit)))) : (/*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label",
    "aria-expanded": context?.isOpen(itemRef.current)
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text c-description-list-accordion__title-label"
  }, title, icon && (/*#__PURE__*/React__default.createElement(InfoIcon, {
    className: "c-accordion__title-info-icon",
    element: "button",
    icon: icon
  }, tooltip))), context?.headerSplit && (/*#__PURE__*/React__default.createElement("span", {
    className: "c-accordion__title-label-text c-description-list-accordion__title-value"
  }, titleSplit))))), subtitle && /*#__PURE__*/React__default.createElement("p", {
    className: "c-accordion__subtitle"
  }, subtitle)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-accordion__panel",
    "aria-hidden": !context?.isOpen(itemRef.current),
    style: panelStyle
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-accordion__content c-description-list-accordion__content"
  }, children)));
}
DescriptionListAccordionItem.displayName = 'DescriptionListAccordion.Item';
DescriptionListAccordion.Item = DescriptionListAccordionItem;
/*
 * Bronson DescriptionItem component.
 */
function DescriptionItem({
  label,
  labelNote,
  children
}) {
  return /*#__PURE__*/React__default.createElement("dl", {
    className: "c-description-list-accordion__item"
  }, /*#__PURE__*/React__default.createElement("dt", {
    className: "c-description-list-accordion__item__label"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-description-list-accordion__item__label-text"
  }, label), labelNote && /*#__PURE__*/React__default.createElement("div", {
    className: "c-description-list-accordion__item__label-note"
  }, labelNote)), /*#__PURE__*/React__default.createElement("dd", {
    className: "c-description-list-accordion__item__value"
  }, children));
}
DescriptionItem.displayName = 'DescriptionListAccordion.DL';
DescriptionListAccordion.DL = DescriptionItem;

/**
 * Bronson DisplayCard component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-display-card.html
 * @constructor
 */
function DisplayCard({
  children,
  className,
  cover,
  element = 'article',
  footerContent,
  fullLink,
  gradient,
  heading,
  headingLevel = 4,
  imageAlt,
  imageProps,
  imageSrc,
  inverted,
  wideText,
  testId,
  ...otherProps
}) {
  const CustomTagDisplayCardElement = `${element}`;
  const customTagClassNameList = classNames('c-display-card', {
    'c-display-card--full-link': fullLink,
    'c-display-card--inverted': inverted,
    'c-display-card--overlay': gradient
  }, className).trim();
  const containerClassNameList = classNames('c-display-card__container', {
    'c-display-card__container--cover': cover,
    'c-display-card__container--grid': wideText
  }, className).trim();
  function renderIfImage() {
    if (imageSrc || imageProps) {
      const imageClassName = classNames('c-display-card__image', imageProps?.className);
      return /*#__PURE__*/React__default.createElement("img", {
        src: imageSrc,
        alt: imageAlt,
        ...imageProps,
        className: imageClassName
      });
    }
    return null;
  }
  function renderIfHeading() {
    if (heading) {
      const CustomDisplayCardHeadingLevel = `h${headingLevel}`;
      return /*#__PURE__*/React__default.createElement(CustomDisplayCardHeadingLevel, {
        className: "c-display-card__title"
      }, heading);
    }
    return null;
  }
  function renderIfFooter() {
    checkFooterContent(footerContent);
    if (footerContent) {
      return /*#__PURE__*/React__default.createElement("footer", {
        className: "c-display-card__footer"
      }, footerContent);
    }
    return null;
  }
  function renderIfBody() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-display-card__body"
      }, children);
    }
    return null;
  }
  /**
   * Check if {@link footerContent} contains at least one child with
   * `c-display-card__link` className when {@link fullLink} is set to `true`.
   */
  function checkFooterContent(footerContent) {
    /**
     * Display a warning only when:
     *  1. {@link fullLink} is set to `true`.
     *  2. Only for children that pass {@link React.isValidElement}.
     *  3. If no child contains `.c-display-card__link` in its `className`.
     */
    if (fullLink && !React__default.Children.toArray(footerContent ?? [])?.filter(React__default.isValidElement)
    /* eslint-disable  @typescript-eslint/no-explicit-any */?.some(child => {
      return child?.props?.className?.includes('c-display-card__link');
    })) {
      console.warn('Bronson-React DisplayCard with `fullLink` expects `footerContent` to have one interactive element with an additional `className` of `.c-display-card__link`.');
    }
  }
  return /*#__PURE__*/React__default.createElement(CustomTagDisplayCardElement, {
    ...otherProps,
    "data-testid": testId,
    className: customTagClassNameList
  }, /*#__PURE__*/React__default.createElement("div", {
    className: containerClassNameList
  }, renderIfImage(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-display-card__content"
  }, renderIfHeading(), renderIfBody(), renderIfFooter())));
}

const DROPDOWN_CONTAINER_ID = uuidv4('bron-dropdown-');
/**
 * A CSS selector list to query only focusable elements.
 * @see https://html.spec.whatwg.org/multipage/interaction.html#focusable
 * @type {string}
 */
const focusableSelectors = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
/**
 * Bronson DropdownContainer component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-dropdown-container.html
 *
 * @constructor
 */
function DropdownContainer({
  children,
  className,
  defaultContainerOffset,
  direction = 'bottom',
  flip = true,
  onToggle,
  open = false,
  showArrow = true,
  trigger,
  testId,
  ...otherProps
}) {
  const [visible, setVisible] = useState(open);
  const [containerOffset, setContainerOffset] = useState(null);
  const arrowRef = useRef(null);
  const referenceRef = useRef(null);
  const popperRef = useRef(null);
  const classNameList = classNames('c-dropdown-container', className).trim();
  if (!trigger) {
    throw new TypeError('DropdownContainer must specify a valid `trigger`, e.g. `<Button>Open</Button>`');
  }
  useEffect(() => {
    /**
     * Cleanup function to prevent the internal state of the component
     * from being updated when it is unmounted.
     */
    const timer = setTimeout(() => {
      if (referenceRef?.current) {
        setContainerOffset(parseInt(getComputedStyle(referenceRef?.current).getPropertyValue('--js-dropdown-container-offset'), 10));
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [containerOffset]);
  const {
    styles,
    attributes
  } = usePopper(referenceRef.current, popperRef.current, {
    placement: `${direction}-start`,
    modifiers: [{
      name: 'preventOverflow',
      options: {
        boundary: document.body
      }
    }, {
      name: 'offset',
      options: {
        /**
         * Fine-tuning the position here.
         * @see https://popper.js.org/docs/v2/modifiers/offset/
         */
        offset: [0, defaultContainerOffset ?? containerOffset]
      }
    }, ...(showArrow ? [{
      name: 'arrow',
      options: {
        element: arrowRef?.current
      }
    }] : []), {
      name: 'flip',
      options: {
        /**
         * This disables the flip behavior.
         * @see https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements
         */
        ...(!flip && {
          fallbackPlacements: [direction]
        })
      }
    }]
  });
  const containerStyle = {
    ...styles.popper
  };
  /**
   * Handle clicks outside the DropdownContainer.
   */
  useEffect(() => {
    const outsideClickHandler = event => {
      const triggerButton = referenceRef?.current?.querySelector('button');
      const eventTarget = event?.target;
      /**
       * Only close the current dropdown on an outside click.
       */
      if (visible && !eventTarget.closest('.c-dropdown-container__panel') && !triggerButton?.contains(eventTarget)) {
        setVisible(false);
        onToggle?.(false);
      }
    };
    document.addEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
    /**
     * Cleanup the hook.
     */
    return () => document.removeEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
  }, [onToggle, visible]);
  /**
   * Handle `Esc` keypress.
   */
  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape') {
        const firstFocusable = referenceRef?.current?.querySelector(focusableSelectors);
        setVisible(false);
        onToggle?.(false);
        firstFocusable?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape, {
      capture: true
    });
    return () => document.removeEventListener('keydown', handleEscape, {
      capture: true
    });
  }, [onToggle, visible]);
  /**
   * Keep the focus inside.
   */
  useEffect(() => {
    const focusableEls = Array.from(popperRef?.current?.querySelectorAll(focusableSelectors) ?? []);
    const firstFocusableEl = focusableEls?.[0];
    const lastFocusableEl = focusableEls?.[focusableEls?.length - 1];
    const KEYCODE_TAB = 9;
    const instance = popperRef?.current;
    const handleFocusTrap = e => {
      const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;
      if (!isTabPressed) {
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl?.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastFocusableEl) {
        firstFocusableEl.focus();
        e.preventDefault();
      }
    };
    instance?.addEventListener('keydown', handleFocusTrap, {
      capture: true
    });
    return () => instance?.removeEventListener('keydown', handleFocusTrap, {
      capture: true
    });
  });
  function renderIfArrow() {
    if (showArrow) {
      return /*#__PURE__*/React__default.createElement("span", {
        ref: arrowRef,
        className: "c-dropdown-container__arrow",
        "aria-hidden": "true",
        style: styles.arrow
      });
    }
    return null;
  }
  function renderIfTrigger() {
    if (/*#__PURE__*/isValidElement(trigger)) {
      return /*#__PURE__*/React__default.cloneElement(trigger, {
        onClick: () => {
          setVisible(!visible);
          onToggle?.(!visible);
        },
        'aria-expanded': visible,
        'aria-controls': DROPDOWN_CONTAINER_ID,
        className: classNames(trigger.props.className, 'c-dropdown-container__button'),
        iconClassName: classNames(trigger.props.className, 'c-dropdown-container__icon'),
        type: 'button'
      });
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-test-id": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-dropdown-container__trigger-wrapper",
    ref: referenceRef
  }, renderIfTrigger()), /*#__PURE__*/React__default.createElement("div", {
    id: DROPDOWN_CONTAINER_ID,
    ref: popperRef,
    className: "c-dropdown-container__panel",
    style: containerStyle,
    "aria-hidden": !visible,
    ...attributes.popper
  }, renderIfArrow(), children));
}
DropdownContainer.displayName = 'DropdownContainer';
function DropdownItem$1({
  className,
  children
}) {
  const classNameList = classNames('c-dropdown-container__item', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList
  }, children);
}
DropdownItem$1.displayName = 'DropdownContainer.Item';
DropdownContainer.Item = DropdownItem$1;
function DropdownDivider({
  className
}) {
  const classNameList = classNames('c-dropdown-container__divider', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "aria-hidden": "true"
  });
}
DropdownDivider.displayName = 'DropdownContainer.Divider';
DropdownContainer.Divider = DropdownDivider;

/**
 * Normalize non `de` ratings to comply with the new German legislation.
 */
const normalizeRatingClasses = (ratingClass, scope = 'eu') => {
  if (scope === 'eu') {
    return ratingClass;
  } else if (scope === 'de') {
    if (ratingClass === 'a+++' || ratingClass === 'a++' || ratingClass === 'a+') {
      return 'a';
    } else {
      return ratingClass;
    }
  }
};
/**
 * Bronson Efficiency Tag component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-efficiency-tag.html
 *
 * @constructor
 */
function EfficiencyTag({
  className,
  label,
  pointer,
  rating,
  scope = 'eu',
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-efficiency-tag', {
    'c-efficiency-tag--pointer': pointer
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("span", {
    className: classNameList,
    "data-efficiency-tag": pointer || label ? null : normalizeRatingClasses(rating, scope),
    "data-efficiency-tag-scope": pointer || label ? null : scope,
    "data-testid": testId,
    ...otherProps
  }, label ?? rating?.toUpperCase());
}

/*
 * Bronson ErrorMessage component.
 */
function ErrorMessage({
  id,
  children,
  testId,
  ...otherProps
}) {
  /**
   * Throw a console warning when `id` prop is not used.
   */
  if (!id) {
    console.warn('Warning: The `id` property is required for accessibility reasons to assign the element that returns an error message.');
  }
  return /*#__PURE__*/React__default.createElement("p", {
    className: "c-error-message",
    id: id ? `error-${id}` : undefined,
    "data-testid": testId,
    ...otherProps
  }, children);
}

const STATE_ID = 'expandable-content';
/*
 * Bronson ExpandableContent component.
 */
function ExpandableContent({
  children,
  className,
  defaultOpen = false,
  lazyRender = false,
  noBackground,
  noPageWrap = false,
  pageWrapClassName,
  triggerLabel,
  triggerLabelClose,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState(defaultOpen);
  const {
    isRendered,
    setRendered
  } = useLazyRender(lazyRender);
  const updateOpen = useCallback(newOpen => {
    setOpen(newOpen);
    if (newOpen) {
      setRendered(STATE_ID);
    }
  }, [setRendered]);
  const toggleOpen = () => {
    updateOpen(!open);
  };
  const divClassNameList = classNames('c-expandable-content', 'js-is-ready',
  // Used in Bronson CSS.
  {
    // Main class modifier convenience prop extension.
    'c-expandable-content--no-background': noBackground,
    // Convenience prop from Bronson variants.
    'is-closed-within': !open
  }, className).trim();
  const divClassNameList2 = noPageWrap ? '' : classNames('o-page-wrap', pageWrapClassName).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList2
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-expandable-content__trigger c-expandable-content__trigger-open",
    "aria-expanded": open,
    onClick: toggleOpen,
    type: "button"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-expandable-content__trigger-text c-expandable-content__trigger-text-open"
  }, triggerLabel), /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-expandable-content__icon",
    viaCss: true
  })), isRendered(STATE_ID) && (
  /*#__PURE__*/
  /**
   * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
   * @see https://github.com/facebook/react/pull/24730
   * @TODO: Remove once React supports direct `[inert]` usage.
   * @see BRON-11871
   */
  // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
  React__default.createElement("div", {
    className: "c-expandable-content__collapse",
    "aria-hidden": !open,
    inert: open ? null : ''
  }, /*#__PURE__*/React__default.createElement("section", {
    className: "c-expandable-content__content"
  }, children), /*#__PURE__*/React__default.createElement("button", {
    className: "c-expandable-content__trigger c-expandable-content__trigger-close",
    "aria-expanded": open,
    onClick: toggleOpen,
    type: "button"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-expandable-content__trigger-text c-expandable-content__trigger-text-close"
  }, triggerLabelClose), /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-expandable-content__icon",
    viaCss: true
  }))))));
}

/**
 * The FavoriteButton component is a toggle button with pressed states ('on', 'off').
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-favorite-button.html
 * @usage
 * - active: initial pressed/active state.
 * - reversed: reverse order of label and icon.
 * - icon only: label is hidden.
 */
function FavoriteButton({
  active,
  className,
  iconOnly,
  labelOff,
  labelOn,
  onPressedChange,
  reversed,
  testId,
  ...otherProps
}) {
  /**
   * Set the internal state based on {@link active}.
   * @type {boolean}
   */
  const externalPressed = active ?? false;
  /**
   * Track the active state.
   */
  const [isActive, setIsActive] = useState(externalPressed);
  useEffect(() => {
    setIsActive(externalPressed);
  }, [externalPressed]);
  function togglePressed() {
    setIsActive(!isActive);
    onPressedChange?.(!isActive);
  }
  const buttonClassNameList = classNames('c-fav-button', {
    'c-fav-button--reversed': reversed,
    'c-fav-button--icon-only': iconOnly,
    'is-active': isActive
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("button", {
    ...otherProps,
    className: buttonClassNameList,
    "data-testid": testId,
    onClick: () => togglePressed(),
    type: "button"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-fav-button__icon",
    "aria-hidden": "true",
    role: "presentation"
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-fav-button__label c-fav-button__label--on"
  }, labelOn), /*#__PURE__*/React__default.createElement("span", {
    className: "c-fav-button__label c-fav-button__label--off"
  }, labelOff));
}

/**
 * Asynchronously observe intersections of a target reference element.
 * Please note that IE11 and other legacy browser do not implement the IntersectionObserver API
 * and therefore need to polyfill the missing functionality.
 * @example
 *    const myTarget = useRef(null)
 *
 *    export function Example({}) {
 *      const myTarget = useRef(null);
 *      useIntersectionObserver({
 *        ref: sectionNavRef,
 *        target: myTarget,
 *        onIntersectionHandler: ScrollObserverCore.observeEntries,
 *        opts: {
 *          intersectionObserverOpts: { rootMargin: '0px 0px -100%' },
 *          classes: ScrollObserverCore.classes,
 *        },
 *      })
 *      return (
 *        <div ref={myTarget}>👋</div>
 *      );
 *    };
 */
function useIntersectionObserver({
  ref,
  target,
  onIntersectionHandler = () => () => {},
  opts,
  onMatch = () => {}
}) {
  useEffect(() => {
    const {
      current
    } = ref ?? {};
    const currentTarget = Array.isArray(target) ? target.filter(t => t?.current) : target?.current;
    /**
     * If we have an {@link HTMLElement} to observe.
     */
    if (current && currentTarget) {
      const intersectionObserver = new IntersectionObserver(entries => {
        if (onIntersectionHandler) {
          onIntersectionHandler?.(current, opts, onMatch)(entries);
        }
      }, opts?.intersectionObserverOpts);
      /**
       * Handle a collection of targets.
       */
      if (Array.isArray(currentTarget)) {
        currentTarget.forEach(t => {
          if (t?.current) {
            intersectionObserver.observe(t.current);
          }
        });
      } else if (currentTarget) {
        intersectionObserver.observe(currentTarget);
      }
      return () => {
        if (Array.isArray(currentTarget)) {
          return currentTarget.forEach(t => {
            if (t?.current) {
              intersectionObserver.unobserve(t.current);
            }
          });
        } else if (currentTarget) {
          return intersectionObserver.unobserve(currentTarget);
        }
      };
    }
    return () => {};
  }, [onIntersectionHandler, onMatch, opts, ref, target]);
}

const SCROLL_OBSERVER_SKIP_OBSERVE_ATTR = 'data-scroll-observer-skip';
/**
 * The ScrollObserverCore handles all internal class/intersection calculations.
 * @type {ScrollObserverCore}
 */
const ScrollObserverCore = Object.seal({
  ___uuid: uuidv4(),
  lastScrollTop: null,
  mostVisibleEntry: null,
  classes: {
    inViewportClass: 'has-target-inside-view',
    outsideViewportClass: 'has-target-outside-view',
    aboveEnterViewportClass: 'has-target-enter-above-view',
    aboveLeaveViewportClass: 'has-target-leave-above-view',
    belowEnterViewportClass: 'has-target-enter-below-view',
    belowLeaveViewportClass: 'has-target-leave-below-view',
    mostVisibleViewportAttribute: 'data-target-most-visible-view'
  },
  scrollObserverOptions: {
    allowMultiple: false,
    /**
     * Setting proper defaults if data attributes are nullish.
     * @see https://w3c.github.io/IntersectionObserver/#dictdef-intersectionobserverinit
     */
    root: null,
    rootMargin: '0px',
    threshold: 0
  },
  observeEntries(element, opts, onMatch) {
    return entries => {
      entries.forEach(entry => {
        const {
          boundingClientRect: {
            top
          },
          intersectionRect: {
            height
          }
        } = entry;
        entry.target.__intersectionHeight = height;
        if (!element.hasAttribute(SCROLL_OBSERVER_SKIP_OBSERVE_ATTR)) {
          if (entry.isIntersecting) {
            element.classList.remove(opts.classes.outsideViewportClass);
            element.classList.add(opts.classes.inViewportClass);
            if (ScrollObserverCore.lastScrollTop ?? top < 0) {
              element.classList.add(opts.classes.belowEnterViewportClass);
              element.classList.remove(opts.classes.belowLeaveViewportClass);
              element.classList.remove(opts.classes.aboveEnterViewportClass);
            } else {
              element.classList.add(opts.classes.aboveEnterViewportClass);
              element.classList.remove(opts.classes.aboveLeaveViewportClass);
              element.classList.remove(opts.classes.belowEnterViewportClass);
            }
          } else {
            element.classList.add(opts.classes.outsideViewportClass);
            element.classList.remove(opts.classes.inViewportClass);
            if (ScrollObserverCore.lastScrollTop ?? top < 0) {
              element.classList.add(opts.classes.aboveLeaveViewportClass);
              element.classList.remove(opts.classes.aboveEnterViewportClass);
              element.classList.remove(opts.classes.belowEnterViewportClass);
            } else {
              element.classList.add(opts.classes.belowLeaveViewportClass);
              element.classList.remove(opts.classes.belowEnterViewportClass);
              element.classList.remove(opts.classes.aboveEnterViewportClass);
            }
          }
        }
        ScrollObserverCore.lastScrollTop = top;
      });
      if (opts.intersectionObserverOpts.allowMultiple) {
        ScrollObserverCore.mostVisibleEntry = entries.reduce((prev, curr) => {
          // @ts-expect-error TS(2531): Object is possibly 'null'.
          // eslint-disable-next-line no-underscore-dangle
          return curr.target.__intersectionHeight > (prev?.target?.__intersectionHeight ?? 0) ? curr : prev;
        }, null);
        if (ScrollObserverCore.mostVisibleEntry) {
          element.setAttribute(ScrollObserverCore.classes.mostVisibleViewportAttribute, `#${ScrollObserverCore.mostVisibleEntry?.target?.id}`);
        }
      }
      if (!element.hasAttribute(SCROLL_OBSERVER_SKIP_OBSERVE_ATTR)) {
        onMatch({
          intersectionObserverEntries: entries,
          mostVisibleEntry: ScrollObserverCore.mostVisibleEntry
        });
      }
    };
  }
});

/**
 * Bronson FloatingBar component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-floating-bar.html
 *
 * @constructor
 */
function FloatingBar({
  children,
  className,
  target,
  testId,
  ...otherProps
}) {
  const floatingBarRef = useRef(null);
  /**
   * Track resizing on the FloatingBar ref and get current offset height.
   */
  useResizeObserver({
    ref: floatingBarRef,
    onResizeHandler: () => {
      const elementOffsetHeight = floatingBarRef?.current?.offsetHeight ?? '';
      document.documentElement.style.setProperty('--bron-sticky-offset', `${elementOffsetHeight}px`);
    }
  });
  /**
   * Track the target container.
   */
  useIntersectionObserver({
    ref: floatingBarRef,
    target,
    onIntersectionHandler: ScrollObserverCore.observeEntries,
    opts: {
      intersectionObserverOpts: {
        rootMargin: '0px',
        threshold: [0, 1]
      },
      classes: ScrollObserverCore.classes
    }
  });
  const aClass = classNames('c-floating-bar', className).trim();
  /**
   * Handle scroll to the desired element.
   * @param {HTMLElement} scrollToRef- The element to scroll to.
   */
  const scrollTo = scrollToRef => scrollToRef?.current && window.scrollTo(0, scrollToRef.current.offsetTop);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("a", {
    ref: floatingBarRef,
    "data-testid": testId,
    className: aClass,
    "data-scroll": true,
    onClick: () => scrollTo(target),
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-floating-bar__inner"
  }, children)));
}

/**
 * Bronson FloatingButton component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-floating-button--default.html
 *
 * @constructor
 *
 * @DEPRECATED: Component will be removed in v12.0.0. Will be replaced by {@link ActionButtonIncognito} which can be placed inside a {@link ActionButtonsContainer}.
 */
function FloatingButton({
  ariaExpanded = false,
  controls,
  disabled,
  icon,
  label,
  testId,
  ...otherProps
}) {
  const [area, setArea] = useState(null);
  const ref = useRef(null);
  const floatGrid = useFloatGrid(area);
  const iClassNameList = classNames('c-floating-button__icon').trim();
  useEffect(() => {
    if (!area) {
      setTimeout(() => {
        // Get custom prop from CSS.
        setArea(getFloatGridCustomProp(ref?.current));
      }, 0);
    }
  }, [ref, area]);
  const comp = /*#__PURE__*/React__default.createElement("div", {
    className: "c-floating-button",
    ref: ref,
    ...otherProps,
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-btn c-floating-button__button",
    title: label,
    "aria-expanded": ariaExpanded,
    "aria-controls": controls,
    disabled: disabled,
    type: "button"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: iClassNameList,
    name: icon
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-floating-button__label"
  }, label)));
  return area && floatGrid ? /*#__PURE__*/createPortal(comp, floatGrid) : comp;
}

/**
 * Bronson FoldOut component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-fold-out.html
 * @constructor
 */
function FoldOut({
  children,
  defaultOpen = false,
  isOpen: isControlledOpen,
  fullToggle,
  labelHidden,
  labelUnhidden,
  productInfoSummary,
  showFooterTrigger,
  testId,
  onToggle,
  ...otherProps
}) {
  const [buttonId] = useState(uuidv4());
  const [targetId] = useState(uuidv4());
  const isControlled = isControlledOpen !== undefined;
  const [isOpen, setIsOpen] = useState(isControlledOpen || defaultOpen);
  useEffect(() => {
    if (isControlled) {
      setIsOpen(isControlledOpen);
    }
  }, [isControlledOpen]);
  const handleToggle = () => {
    const newState = !isOpen;
    if (!isControlled) setIsOpen(newState);
    onToggle?.(newState);
  };
  const ref = useRef(null);
  const divClassNameList = classNames('c-fold-out', {
    'c-fold-out--full-toggle': fullToggle
  }).trim();
  function renderHeader() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-fold-out__header"
    }, productInfoSummary);
  }
  function renderContent() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-fold-out__content",
      ref: ref,
      id: targetId
    }, children);
  }
  function renderTrigger(shortId) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-fold-out__trigger"
    }, /*#__PURE__*/React__default.createElement(Toggle$1, {
      className: "c-fold-out__trigger__button",
      iconClassName: "c-icon c-fold-out__trigger-icon",
      id: `${buttonId}-${shortId}`,
      targetId: targetId,
      /**
       * The {@link Toggle} object takes the invert state.
       */
      hidden: !isOpen,
      labelHidden: labelHidden,
      labelUnhidden: labelUnhidden,
      targetRef: ref,
      /**
       * Pass in {@link Button.propTypes}.
       */
      iconViaCss: true,
      link: true,
      small: true,
      simple: true,
      /**
       * Update the {@link FoldOut} state.
       */
      onClick: handleToggle
    }));
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderHeader(), renderTrigger('header'), renderContent(), showFooterTrigger && renderTrigger('footer'));
}

/**
 * Bronson PageWrap component.
 * @see https://bronson.vwfs.tools/default/docs/objects/wrapper.html
 * @constructor
 */
function PageWrap({
  children,
  size,
  ...otherProps
}) {
  const pageWrapClassName = classNames('o-page-wrap', {
    [`o-page-wrap--${size}`]: size
  }).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    className: pageWrapClassName
  }, children);
}

/*
 * Bronson Paragraph component.
 */
function Paragraph({
  children,
  className,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("p", {
    ...otherProps,
    "data-testid": testId,
    className: className
  }, children);
}

/**
 * Bronson Footer component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-footer--footer-full.html
 * @constructor
 */
function Footer({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("footer", {
    className: "c-footer",
    "data-testid": testId,
    ...otherProps
  }, children);
}
Footer.displayName = 'Footer';
/**
 * FooterModuleBreadcrumbs
 * @constructor
 */
function FooterModuleBreadcrumbs({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer__module c-footer__module--breadcrumb",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(PageWrap, null, /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer-breadcrumb"
  }, children)));
}
FooterModuleBreadcrumbs.displayName = 'Footer.ModuleBreadcrumbs';
Footer.ModuleBreadcrumbs = FooterModuleBreadcrumbs;
/**
 * FooterModuleNavColumns
 * @constructor
 */
function FooterModuleNavColumns({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer__module c-footer__module--nav",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(PageWrap, null, /*#__PURE__*/React__default.createElement("nav", {
    className: "c-footer-nav"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer-nav__columns"
  }, children))));
}
FooterModuleNavColumns.displayName = 'Footer.ModuleNavColumns';
Footer.ModuleNavColumns = FooterModuleNavColumns;
/**
 * Bronson FooterNavColumn component (nested).
 * @constructor
 */
function FooterNavColumn({
  children,
  title,
  testId
}) {
  /**
   * Add state handling for footer accordion.
   */
  const [active, setActive] = useState(false);
  /**
   * Allocate unique identifiers for accessibility reasons.
   * @type {string|*}
   */
  const buttonUUID = useMemo(() => uuidv4('bron-footer-button-'), []);
  const panelUUID = useMemo(() => uuidv4('bron-footer-panel-'), []);
  const columnClassName = classNames('c-footer-nav__column', 'js-footer-accordion',
  // Used in Bronson CSS.
  'js-is-ready' // Used in Bronson CSS.
  );
  const titleClassName = classNames('c-footer-nav__title', {
    'is-active': active
  });
  const panelClassName = classNames('c-footer-nav__list', 'js-is-ready',
  // Used in Bronson CSS.
  {
    'is-closed': !active
  });
  return /*#__PURE__*/React__default.createElement("div", {
    className: columnClassName,
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement("div", {
    className: titleClassName,
    onClick: () => setActive(!active)
  }, /*#__PURE__*/React__default.createElement("h4", {
    className: "c-footer-nav__title-label is-heading"
  }, title), /*#__PURE__*/React__default.createElement("button", {
    className: "c-footer-nav__title-label is-button",
    type: "button",
    "aria-expanded": active,
    "aria-controls": panelUUID,
    id: buttonUUID
  }, title)), /*#__PURE__*/React__default.createElement("ul", {
    id: panelUUID,
    "aria-labelledby": buttonUUID,
    role: "region",
    className: panelClassName
  }, children));
}
FooterNavColumn.displayName = 'Footer.NavColumn';
Footer.NavColumn = FooterNavColumn;
/**
 * Bronson FooterNavColumnItem component (nested).
 * @constructor
 */
function FooterNavColumnItem({
  children,
  href,
  linkProps,
  normalLink,
  onClick,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-footer-nav__item",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(ReactLink, {
    className: "c-footer-nav__link",
    onClick: onClick,
    href: href,
    normalLink: normalLink,
    ...linkProps
  }, children));
}
FooterNavColumn.Item = FooterNavColumnItem;
Footer.NavColumn.Item = FooterNavColumn.Item;
/**
 * FooterModuleMetaNav
 * @constructor
 */
function FooterModuleMetaNav({
  children,
  copyrightSign = '©',
  copyrightText,
  socialItems,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer__module c-footer__module--meta-nav",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "o-page-wrap"
  }, /*#__PURE__*/React__default.createElement("nav", {
    className: "c-footer-meta-bar"
  }, /*#__PURE__*/React__default.createElement("ul", {
    className: "c-footer-meta-nav"
  }, children), socialItems && (/*#__PURE__*/React__default.createElement("ul", {
    className: "c-footer-social-media"
  }, socialItems.map(({
    href,
    icon,
    label,
    ...linkAttrs
  }, i) => {
    const iconClassNames = classNames('c-footer-social-media__icon');
    return /*#__PURE__*/React__default.createElement("li", {
      className: "c-footer-social-media__item",
      key: i
    }, /*#__PURE__*/React__default.createElement(ReactLink, {
      href: href,
      title: label,
      className: "c-footer-social-media__link",
      ...linkAttrs
    }, /*#__PURE__*/React__default.createElement(Icon, {
      className: iconClassNames,
      name: icon
    })));
  }))), /*#__PURE__*/React__default.createElement("span", {
    className: "c-footer-copyright"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-footer-copyright__sign"
  }, copyrightSign), /*#__PURE__*/React__default.createElement("span", {
    className: "c-footer-copyright__text"
  }, copyrightText)))));
}
FooterModuleMetaNav.displayName = 'Footer.ModuleMetaNav';
Footer.ModuleMetaNav = FooterModuleMetaNav;
/**
 * Bronson FooterModuleMetaNavItem component (nested).
 * @constructor
 */
function FooterModuleMetaNavItem({
  children,
  href,
  linkProps,
  normalLink,
  onClick,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps,
    className: "c-footer-meta-nav__item",
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement(ReactLink, {
    className: "c-footer-meta-nav__link",
    onClick: onClick,
    href: href,
    normalLink: normalLink,
    ...linkProps
  }, children));
}
FooterModuleMetaNavItem.displayName = 'Footer.ModuleMetaNav.Item';
FooterModuleMetaNav.Item = FooterModuleMetaNavItem;
Footer.ModuleMetaNav.Item = FooterModuleMetaNavItem;
/**
 * FooterModuleFootnotes
 * @constructor
 */
function FooterModuleFootnotes({
  children,
  footerText,
  testId,
  ...otherProps
}) {
  function renderFooterText() {
    if (footerText) {
      /**
       * If {@link footerText} is a string, just return it as a single child.
       */
      if (typeof footerText === 'string') {
        return /*#__PURE__*/React__default.createElement(Paragraph, null, footerText);
      }
      return footerText;
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer__module c-footer__module--footnotes",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "o-page-wrap"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-footer-footnotes"
  }, renderFooterText(), children)));
}
FooterModuleFootnotes.displayName = 'Footer.ModuleFootnotes';
Footer.ModuleFootnotes = FooterModuleFootnotes;

/*
 * Bronson Footnote component.
 */
function Footnote({
  children,
  numbered,
  testId,
  ...otherProps
}) {
  const CustomTag = `${numbered ? 'ol' : 'ul'}`;
  return /*#__PURE__*/React__default.createElement(CustomTag, {
    ...otherProps,
    "data-testid": testId,
    className: "c-footnotes"
  }, children);
}
Footnote.displayName = 'Footnote';
/*
 * Bronson FootnoteItem component (nested).
 */
function FootnoteItem({
  bullet,
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps,
    "data-footnote-bullet": bullet
  }, children);
}
FootnoteItem.displayName = 'Footnote.Item';
Footnote.Item = FootnoteItem;

const FormContext = /*#__PURE__*/React__default.createContext({});

/**
 * The Form component is used to collect, validate, and submit the user input.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-form.html
 * @constructor
 */
function Form({
  children,
  className,
  floatingLabel,
  testId,
  ...otherProps
}) {
  const contextValue = {
    floatingLabel
  };
  const classNameList = classNames({
    'is-js-on': floatingLabel // Used in Bronson CSS.
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("form", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(FormContext.Provider, {
    value: contextValue
  }, children));
}

/**
 * The InlineGroup arranges items with defined horizontal spacing in a row.
 * If the items do not fit into one row, they will wrap to the next row with defined vertical spacing.
 * @see https://bronson.vwfs.tools/default/docs/objects/inline-group.html
 * @constructor
 */
function InlineGroup({
  align = 'top',
  children,
  className,
  grid,
  small,
  stretch,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('o-inline-group', className, {
    'o-inline-group--grid': grid,
    // Convenience prop
    'o-inline-group--small': small,
    // Convenience prop
    'o-inline-group--stretch': stretch,
    // Convenience prop
    /**
     * Since the default for {@link align} is `top` no modifier class is needed.
     * Only applicable when {@link align} is set to either `bottom` or `center`.
     */
    [`o-inline-group--${align} `]: align === 'bottom' || align === 'center' ? align : null // Convenience prop
  }).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    ...otherProps,
    "data-testid": testId
  }, children);
}
InlineGroup.displayName = 'InlineGroup';
/**
 * Internal item for the {@link InlineGroup} component.
 * @internal
 * @constructor
 */
function InlineGroupItem({
  children
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "o-inline-group__item"
  }, children);
}
InlineGroupItem.displayName = 'InlineGroup.Item';
InlineGroupItem.Item = InlineGroupItem;

/**
 * Values for {@link FormField.propTypes.type} prop.
 */
const typeValues = ['input', 'autosuggest', 'textarea', 'select', 'checkbox', 'other', 'group'];
/**
 * If {@link required} is set, render a `required` flag according {@link requiredText}.
 */
function renderIfRequired(required, requiredText) {
  return required ? /*#__PURE__*/React__default.createElement("span", {
    "aria-hidden": "true"
  }, requiredText) : null;
}
/**
 * Bronson FormField component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-form-field.html
 * @constructor
 */
function FormField({
  children,
  className,
  errorMessage,
  extraContent,
  hint,
  infoIcon,
  inline,
  labelElement = 'label',
  labelForId,
  labelId,
  labelText,
  noFloatingLabel,
  required,
  requiredText,
  staticLabel,
  type,
  testId,
  ...otherProps
}) {
  const context = React__default.useContext(FormContext);
  const [elementActive, setElementActive] = useState(false);
  const contextValue = {
    errorMessage,
    setElementActive: active => setElementActive(active)
  };
  const formFloatingLabel = context && context.floatingLabel;
  useEffect(() => {
    if (!type) {
      const valueHint = typeValues.join(', ');
      if (formFloatingLabel) {
        // eslint-disable-next-line no-console
        console.error(`Warning: The prop \`type\` is required in \`<FormField>\` (possible values: ${valueHint}) because floating labels are enabled in \`<Form>\` element.`);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`The prop \`type\` should be added to \`<FormField>\` (possible values: ${valueHint}). This warning has no negative effect because floating labels are not enabled in the \`<Form>\` element.`);
      }
    }
  });
  const standardLabel =
  // <Form floatingLabel> prop not set
  !formFloatingLabel ||
  // no floating when explicitly set
  noFloatingLabel ||
  // downstream element is active (e.g. contains text, triggered via onChange in element on callback)
  elementActive ||
  // no floating if not in [input, textarea]
  type !== 'input' && type !== 'textarea' ||
  // No floating with {@link InfoIcon}.
  infoIcon;
  const divClassNameList = classNames('c-form-field', {
    'c-form-field--no-floating': noFloatingLabel,
    'c-form-field--static': staticLabel,
    'c-form-field--textarea': type === 'textarea',
    'is-active': formFloatingLabel && standardLabel && !staticLabel,
    'c-form-field--inline': inline
  }, className).trim();
  const CustomTagLabelElement = `${labelElement}`;
  function renderIfLabel() {
    if (labelText) {
      return /*#__PURE__*/React__default.createElement(CustomTagLabelElement, {
        id: labelId,
        htmlFor: labelElement === 'label' && labelForId ? labelForId : undefined,
        className: "c-form-field__label"
      }, labelText, renderIfRequired(required, requiredText), renderIfInfoIcon());
    }
    return null;
  }
  function renderIfInfoIcon() {
    return infoIcon ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, infoIcon) : null;
  }
  function renderIfErrorMessage() {
    return errorMessage ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, errorMessage) : null;
  }
  function renderIfExtraContent() {
    if (extraContent) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-form-field__extra"
      }, extraContent);
    }
    return null;
  }
  function renderIfHint() {
    if (hint) {
      /**
       * @type {string | React.ReactNode}
       * Type string can be used for text only.
       * Type React.ReactNode can be used for links (`<a href='#'>`) or to include button with {@link onClick} functionality.
       */
      return /*#__PURE__*/React__default.createElement("p", {
        className: "c-form-field__hint",
        id: `hint-${labelForId}`,
        "data-testid": testId && `hint-${testId}`
      }, hint);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderIfLabel(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-form-field__box"
  }, /*#__PURE__*/React__default.createElement(FormFieldContext.Provider, {
    value: contextValue
  }, children), renderIfErrorMessage(), renderIfExtraContent()), renderIfHint());
}
function FormFieldRadioButtonGroup({
  /** Custom props */
  disabled,
  /** _InternalFormFieldGroupProps */
  grid,
  small,
  /** FormField Props */
  children,
  extraContent,
  errorMessage,
  hint,
  infoIcon,
  labelId,
  labelText,
  onClick,
  required,
  requiredText,
  testId,
  ...otherProps
}) {
  function renderIfInfoIcon() {
    return infoIcon ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, infoIcon) : null;
  }
  function renderIfErrorMessage() {
    return errorMessage ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, errorMessage) : null;
  }
  function renderIfExtraContent() {
    if (extraContent) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-form-field__extra"
      }, extraContent);
    }
    return null;
  }
  function renderIfHint() {
    if (hint) {
      /**
       * If the {@link onClick} is set we use an {@link HTMLAnchorElement} as hint,
       * otherwise we fall back to {@link HTMLParagraphElement}.
       * @type {string}
       */
      const HintElement = onClick ? 'a' : 'p';
      return /*#__PURE__*/React__default.createElement(HintElement, {
        className: "c-form-field__hint",
        id: `hint-${labelId ?? ''}`,
        "data-testid": testId && `hint-${testId}`,
        onClick: onClick
      }, hint);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("fieldset", {
    ...otherProps,
    "data-testid": testId,
    className: "c-form-field c-form-field--static",
    disabled: disabled ? disabled : undefined,
    "aria-required": required ? 'true' : undefined,
    "aria-invalid": errorMessage ? 'true' : undefined
  }, /*#__PURE__*/React__default.createElement("legend", {
    id: labelId,
    className: "c-form-field__label"
  }, labelText, renderIfRequired(required, requiredText), renderIfInfoIcon()), /*#__PURE__*/React__default.createElement("div", {
    className: "c-form-field__box"
  }, /*#__PURE__*/React__default.createElement(_InternalFormFieldGroup, {
    grid: grid,
    small: small
  }, children), renderIfErrorMessage(), renderIfExtraContent()), renderIfHint());
}
function FormFieldCheckboxGroup({
  /** Custom props */
  disabled,
  /** _InternalFormFieldGroupProps */
  grid,
  small,
  /** FormField Props */
  children,
  extraContent,
  errorMessage,
  hint,
  infoIcon,
  labelId,
  labelText,
  onClick,
  required,
  requiredText,
  testId,
  ...otherProps
}) {
  function renderIfInfoIcon() {
    return infoIcon ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, infoIcon) : null;
  }
  function renderIfErrorMessage() {
    return errorMessage ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, errorMessage) : null;
  }
  function renderIfExtraContent() {
    if (extraContent) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-form-field__extra"
      }, extraContent);
    }
    return null;
  }
  function renderIfHint() {
    if (hint) {
      /**
       * If the {@link onClick} is set we use an {@link HTMLAnchorElement} as hint,
       * otherwise we fall back to {@link HTMLParagraphElement}.
       * @type {string}
       */
      const HintElement = onClick ? 'a' : 'p';
      return /*#__PURE__*/React__default.createElement(HintElement, {
        className: "c-form-field__hint",
        id: `hint-${labelId ?? ''}`,
        "data-testid": testId && `hint-${testId}`,
        onClick: onClick
      }, hint);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("fieldset", {
    ...otherProps,
    "data-testid": testId,
    className: "c-form-field c-form-field--static",
    disabled: disabled ? disabled : undefined
  }, /*#__PURE__*/React__default.createElement("legend", {
    id: labelId,
    className: "c-form-field__label"
  }, labelText, renderIfRequired(required, requiredText), renderIfInfoIcon()), /*#__PURE__*/React__default.createElement("div", {
    className: "c-form-field__box"
  }, /*#__PURE__*/React__default.createElement(_InternalFormFieldGroup, {
    grid: grid,
    small: small
  }, children), renderIfErrorMessage(), renderIfExtraContent()), renderIfHint());
}
/*
 * Internal Bronson RadioButtonGroup component.
 */
function _InternalFormFieldGroup({
  children,
  grid,
  small,
  ...otherProps
}) {
  function renderChildren() {
    return React__default.Children.map(children, child => {
      if (!child) {
        return null;
      }
      return /*#__PURE__*/React__default.createElement(InlineGroupItem, {
        ...otherProps
      }, child);
    });
  }
  return /*#__PURE__*/React__default.createElement(InlineGroup, {
    grid: grid,
    small: small
  }, renderChildren());
}

function FormFieldLabel({
  children,
  className,
  for: htmlFor,
  notion = false,
  testId
}) {
  const componentLabelClass = classNames('c-form-field__label', className).trim();
  return /*#__PURE__*/React__default.createElement("label", {
    "data-testid": testId,
    htmlFor: htmlFor,
    className: componentLabelClass
  }, children, notion && /*#__PURE__*/React__default.createElement("span", {
    className: "c-form-field__notion"
  }, "*"));
}

/*
 * Bronson FormHeading component.
 */
function FormHeading({
  children,
  className,
  description,
  icon,
  testId,
  ...otherProps
}) {
  const legendClassNameList = classNames('c-form-heading', className).trim();
  const iClassNameList = classNames('c-form-heading__icon').trim();
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon
      });
    }
    return null;
  }
  function renderIfText() {
    if (description) {
      return /*#__PURE__*/React__default.createElement("small", {
        className: "c-form-heading__text"
      }, description);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("legend", {
    ...otherProps,
    "data-testid": testId,
    className: legendClassNameList
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-form-heading__title"
  }, renderIfIcon(), children), renderIfText());
}

/**
 * Bronson FormSection component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-form-section.html
 * @constructor
 */
function FormSection({
  children,
  className,
  defaultOpen = false,
  disabled,
  error,
  linkLabel,
  noTruncate,
  onChange,
  onClick,
  onLinkClick,
  subtitle,
  success,
  title,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef(null);
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);
  function toggleOpen() {
    setOpen(!open);
    if (onChange) {
      onChange(!open);
    }
  }
  const divClassNameList = classNames('c-form-section', {
    'is-open': open,
    // Convenience prop from Bronson variants.
    'c-form-section--error': error,
    'c-form-section--success': success
  }, className).trim();
  const sectionHeaderClassNameList = classNames('c-form-section__header', {
    'c-form-section__header--no-truncate': noTruncate
  }, className).trim();
  const sectionPanelClassNameList = classNames('c-form-section__panel', {
    'is-collapsed': !open && !expanded
  }, {
    'is-expanded': open && expanded
  }, className).trim();
  function renderHeaderContent() {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("span", {
      className: "c-form-section__title"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "c-form-section__title-text",
      role: "heading"
    }, title), renderIfFormSectionShowLink()), renderIfFormSectionSubtitle());
  }
  function renderIfFormSectionShowLink() {
    if (linkLabel) {
      return /*#__PURE__*/React__default.createElement(Button, {
        element: "span",
        className: "c-form-section__title-link",
        link: true,
        small: true,
        onClick: onLinkClick
      }, linkLabel);
    }
    return null;
  }
  function renderIfFormSectionSubtitle() {
    if (subtitle) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-form-section__subtitle"
      }, subtitle);
    }
    return null;
  }
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    React__default.createElement("section", {
      ...otherProps,
      "data-testid": testId,
      className: divClassNameList,
      "aria-disabled": disabled,
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: disabled ? '' : null
    }, /*#__PURE__*/React__default.createElement("button", {
      className: sectionHeaderClassNameList,
      "aria-expanded": open,
      onClick: () => !disabled && [toggleOpen(), onClick && onClick()],
      type: "button"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "c-form-section__header-content"
    }, renderHeaderContent()), /*#__PURE__*/React__default.createElement("span", {
      className: "c-form-section__header-icon"
    })), /*#__PURE__*/React__default.createElement("div", {
      className: sectionPanelClassNameList,
      "aria-hidden": !open,
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: open ? null : '',
      ref: panelRef,
      onTransitionEnd: event => {
        if (event.target === panelRef?.current) {
          if (open) {
            setExpanded(true);
          } else {
            setExpanded(false);
          }
        }
      }
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "c-form-section__content"
    }, children)))
  );
}

/**
 * Bronson FormSectionGroup component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-form-section-group.html
 * @constructor
 */
function FormSectionGroup({
  children,
  className,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames('c-form-section-group', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, children);
}

/**
 * Bronson Header component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-header.html
 * @constructor
 */
function Header({
  children,
  className,
  logo,
  notificationCenter,
  staticHeader,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-header', {
    'c-header--static': staticHeader // Convenience prop from Bronson variants.
  }, className).trim();
  function renderIfNotificationCenter() {
    if (notificationCenter) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-header__notification-center"
      }, notificationCenter);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("header", {
    className: "c-header__wrapper",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classNameList
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-header__content-wrapper"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-header__logo"
  }, logo), React__default.Children.map(children, child => {
    if (/*#__PURE__*/isValidElement(child)) {
      return child.props.isContextBar ? child : /*#__PURE__*/React__default.createElement("div", {
        className: "c-header__nav"
      }, child);
    }
    return null;
  }), renderIfNotificationCenter())));
}

/*
 * Bronson Heading component.
 */
function Heading({
  level,
  children,
  testId,
  ...otherProps
}) {
  const CustomTagHeadingsLevel = `h${level}`;
  return /*#__PURE__*/React__default.createElement(CustomTagHeadingsLevel, {
    ...otherProps,
    "data-testid": testId
  }, children);
}

const BRONSON_CDN_BASE_URL = 'https://cdn.bronson.vwfs.io';
/**
 * Hook used to get the Bronson CDN path.
 * Determined from env variables, either
 * - REACT_APP_BRONSON_BRAND and REACT_APP_BRONSON_VERSION
 * - REACT_APP_WEBSITE_BRONSON_THEME and REACT_APP_WEBSITE_BRONSON_VERSION
 * - REACT_APP_WEBSITE_THEME
 *
 * @returns object with properties
 * - `cdn` - path to Bronson CDN
 * - `theme` - Bronson theme, e.g. 'audi'
 * - `version` - Bronson version, e.g. 7.30.0
 */
const useBronson = () => {
  const bronson = {
    theme: null,
    version: null,
    cdn: null
  };
  if (process.env.REACT_APP_BRONSON_BRAND && process.env.REACT_APP_BRONSON_VERSION) {
    // See 'gondor' projects
    bronson.theme = process.env.REACT_APP_BRONSON_BRAND;
    bronson.version = process.env.REACT_APP_BRONSON_VERSION;
  } else if (process.env.REACT_APP_WEBSITE_BRONSON_THEME && process.env.REACT_APP_WEBSITE_BRONSON_VERSION) {
    bronson.theme = process.env.REACT_APP_WEBSITE_BRONSON_THEME;
    bronson.version = process.env.REACT_APP_WEBSITE_BRONSON_VERSION;
  } else if (process.env.REACT_APP_WEBSITE_THEME) {
    // e.g. cc-nl-frontend with 'vw6/v/1.12.0'
    // eslint-disable-next-line prefer-regex-literals
    const splitted = process.env.REACT_APP_WEBSITE_THEME.split(new RegExp('(.+)/v/(\\d+\\.\\d+\\.\\d+)'));
    if (splitted.length >= 2) {
      // eslint-disable-next-line prefer-destructuring
      bronson.theme = splitted[1];
      // eslint-disable-next-line prefer-destructuring
      bronson.version = splitted[2];
    }
  }
  if (bronson.theme && bronson.version) {
    bronson.cdn = `${BRONSON_CDN_BASE_URL}/${bronson.theme}/v/${bronson.version}`;
  }
  return bronson;
};

var VideoPauseState;
(function (VideoPauseState) {
  VideoPauseState[VideoPauseState["INITIAL"] = -1] = "INITIAL";
  VideoPauseState[VideoPauseState["USER_FORCED"] = 0] = "USER_FORCED";
  VideoPauseState[VideoPauseState["VISIBILITY_CHANGE_FORCED"] = 1] = "VISIBILITY_CHANGE_FORCED";
  VideoPauseState[VideoPauseState["REDUCED_MOTION_FORCED"] = 2] = "REDUCED_MOTION_FORCED";
})(VideoPauseState || (VideoPauseState = {}));
/**
 * Possible connection type values.
 * @type {Readonly<{"slow-2g": number, "2g": number, "3g": number, "4g": number}>}
 */
const EffectiveConnectionTypeEnum = Object.freeze({
  'slow-2g': 0,
  '2g': 1,
  '3g': 2,
  '4g': 3
});
/**
 * Compare the current and the preferred downlink.
 * @return {boolean} - If the current connection matches at least the preferred one.
 */
function checkConnectivity(connection, autoplayMinimumConnection) {
  /**
   * If no preferred connection type was passed we assume a great connection type (4G).
   * @type {string}
   */
  const preferredConnectionType = autoplayMinimumConnection ?? '4g';
  const currentDownlink = EffectiveConnectionTypeEnum[connection?.effectiveType];
  const minDownlink = EffectiveConnectionTypeEnum[preferredConnectionType];
  return currentDownlink < minDownlink;
}
/**
 * Bronson Video component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-video.html
 */
const Video = /*#__PURE__*/React__default.forwardRef(({
  alt,
  autoPlay,
  autoplayMinimumConnection,
  captions,
  captionsLabel,
  captionsLanguage,
  captionsSrc,
  controls,
  fullscreen,
  iconUrl,
  id,
  initOnLoad = true,
  loop,
  muted,
  onEnded,
  onEnterFullscreen,
  onExitFullscreen,
  onPause,
  onPlay,
  onPlaying,
  poster,
  selfHosted,
  service,
  src,
  testId,
  ...otherProps
}, ref) => {
  const {
    cdn: bronsonCDN
  } = useBronson();
  const [pauseState, setPauseState] = useState(VideoPauseState.INITIAL);
  const [insufficientConnection, setInSufficientConnection] = useState(false);
  const videoRef = useRef(null);
  const plyrInstance = useRef(null);
  /**
   * Get the current network information. Only supported via Chromium for now.
   */
  // @ts-ignore @TODO: Check if a custom type could help here.
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  /**
   * Get the `prefers-reduced-motion` CSS media query setting.
   */
  const reduceMotionMediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = reduceMotionMediaQueryList?.matches;
  /**
   * Handle play-state of Video depending on if the current page is visible.
   * If the user paused the video via {@link pauseState} or
   * the video has not been played yet, do not play it when the
   * page visibility changes to unhidden/visible.
   *
   * Always pause the video when switching to another page tab.
   * Otherwise, resume playing the video if it has `autoplay` set when:
   *  - the user did not pause it when leaving the active tab,
   *  - and the user has not set a reduced motion setting,
   *  - and the connection is sufficient.
   */
  const handleVisibilityChange = useCallback(() => {
    if (plyrInstance?.current && videoRef?.current) {
      if (document.hidden) {
        if (pauseState !== VideoPauseState.USER_FORCED) {
          plyrInstance?.current?.pause();
        }
      } else if (
      /**
       * If `autoplay` was set but not restriction on connection bandwidth go ahead,
       * otherwise we have an `autoplay` and need to check if the connectivity is still sufficient.
       */
      (autoPlay && !autoplayMinimumConnection || autoplayMinimumConnection && !insufficientConnection) && !prefersReducedMotion && pauseState === VideoPauseState.VISIBILITY_CHANGE_FORCED) {
        plyrInstance?.current?.play();
      } else {
        if (pauseState === VideoPauseState.USER_FORCED) {
          plyrInstance?.current?.pause();
        }
      }
    }
  }, [autoPlay, pauseState, plyrInstance, videoRef, prefersReducedMotion, autoplayMinimumConnection, insufficientConnection]);
  /**
   * Check the page visibility and stop/resume the video on `visibilitychange` event.
   */
  document.addEventListener('visibilitychange', handleVisibilityChange);
  /**
   * Add a listener for {@link MediaQueryListEvent} to handle media reduced motion media changes.
   */
  reduceMotionMediaQueryList.addEventListener('change', () => {
    if (reduceMotionMediaQueryList?.matches) {
      plyrInstance?.current?.stop();
    }
  });
  useEffect(() => {
    if (autoplayMinimumConnection && !prefersReducedMotion) {
      /**
       * Check if the current connection is insufficient and pause the video.
       * Otherwise, check if an autoplay was preferred and play it.
       */
      setInSufficientConnection(connection ? checkConnectivity(connection, autoplayMinimumConnection) : false);
    }
  }, [autoplayMinimumConnection, connection, prefersReducedMotion]);
  /**
   * NOTE: Refs and imperative handling are here intended as API for media playback is forwarded to parent
   * state resides within Plyr player and is not mapped to another state object copy.
   */
  useImperativeHandle(ref, () => ({
    // @ts-ignore @TODO: Fix when all browsers return a Promise.
    play: () => {
      plyrInstance?.current?.play();
    },
    pause: () => {
      plyrInstance?.current?.pause();
    },
    stop: () => {
      plyrInstance?.current?.stop();
    },
    fullscreen: () => {
      plyrInstance?.current?.fullscreen.enter();
    }
  }));
  useEffect(() => {
    /**
     * Video will be initialized when videoRef found and prop 'initOnLoad' is true (default).
     * Video initialization can be prevented, e.g. for cookie consent reasons, by setting prop 'initOnLoad' to false.
     */
    if (!(videoRef.current && initOnLoad)) return undefined;
    const plyrConfig = {
      debug: false,
      autoPlay: !insufficientConnection && autoPlay ? true : null,
      invertTime: false,
      controls: controls === false ? null : ['play', 'play-large', 'progress', 'current-time', 'mute', 'volume', 'captions', 'fullscreen'],
      fullscreen: fullscreen ?? {
        fallback: true
      },
      youtube: {
        noCookie: true
      },
      urls: {
        youtube: {
          /**
           * Disable YouTube API (for title/aspect ratio calculation)
           * calls through third-party Noembed service.
           * @see https://noembed.com/
           */
          api: ''
        }
      },
      /**
       * Make Plyr respect `[muted]`.
       * @see https://github.com/sampotts/plyr/issues/1495#issuecomment-659735226
       * @type {{enabled: boolean}}
       */
      muted,
      storage: muted ? {
        enabled: false
      } : {},
      volume: muted ? 0 : null,
      iconUrl: iconUrl || (bronsonCDN ? `${bronsonCDN}/svg/plyr-sprite.svg` : null)
    };
    if (!plyrInstance.current) {
      plyrInstance.current = new Plyr(videoRef.current, plyrConfig);
      plyrInstance?.current?.on?.('playing', event => onPlaying?.(event.detail.plyr));
      plyrInstance?.current?.on?.('play', event => onPlay?.(event.detail.plyr));
      plyrInstance?.current?.on?.('pause', event => {
        if (document.hidden && pauseState === VideoPauseState.INITIAL) {
          setPauseState(VideoPauseState.VISIBILITY_CHANGE_FORCED);
        } else {
          setPauseState(VideoPauseState.USER_FORCED);
        }
        onPause?.(event.detail.plyr);
      });
      plyrInstance?.current?.on?.('ended', event => onEnded?.(event.detail.plyr));
      plyrInstance?.current?.on?.('enterfullscreen', event => onEnterFullscreen?.(event.detail.plyr));
      plyrInstance?.current?.on?.('exitfullscreen', event => onExitFullscreen?.(event.detail.plyr));
    }
    const instance = plyrInstance.current;
    return () => {
      instance?.destroy?.();
    };
  }, [videoRef, autoPlay, onPlaying, onPlay, onPause, onEnded, onEnterFullscreen, onExitFullscreen, iconUrl, initOnLoad, bronsonCDN, muted, controls, fullscreen]);
  /**
   * Assert media sources for the `<video>` element.
   * @returns {unknown[]|null}
   */
  const videoSources = () => {
    if (Array.isArray(src)) {
      return src.map(({
        path,
        type
      }) => /*#__PURE__*/React__default.createElement("source", {
        key: `${type}${path}`,
        type: `video/${type}`,
        src: path
      }));
    }
    return null;
  };
  const renderTrack = () => captions && /*#__PURE__*/React__default.createElement("track", {
    kind: "captions",
    label: captionsLabel,
    src: captionsSrc,
    srcLang: captionsLanguage,
    default: true
  });
  /**
   * NOTE: Need to wrap a `<div>` around as Plyr is manipulating complete DOM and React is otherwise not able to unmount.
   */
  return /*#__PURE__*/React__default.createElement("div", null, selfHosted ? (/*#__PURE__*/React__default.createElement("video", {
    ref: videoRef,
    poster: poster,
    controls: controls,
    autoPlay: !insufficientConnection && autoPlay ? true : undefined,
    loop: loop,
    muted: true,
    "data-testid": testId,
    ...otherProps
  }, videoSources(), renderTrack(), alt)) : (/*#__PURE__*/React__default.createElement("div", {
    ref: videoRef,
    "data-plyr-provider": service,
    "data-plyr-embed-id": id,
    "data-testid": testId,
    ...otherProps
  })));
});

/**
 * Bronson PlayButton component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-play-button.html
 * @constructor
 */
function PlayButton({
  className,
  iconClassName,
  label,
  testId,
  ...otherProps
}) {
  const buttonClassNameList = classNames('c-play-button', className).trim();
  const iconClassNameList = classNames('c-play-button__icon', iconClassName).trim();
  return /*#__PURE__*/React__default.createElement("button", {
    className: buttonClassNameList,
    type: "button",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: iconClassNameList,
    name: "play",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-play-button__label"
  }, label));
}

/**
 * Bronson HeroTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-hero-teaser.html
 * @constructor
 */
function HeroTeaser({
  auto,
  bar,
  bottomCenter,
  bottomLeft,
  bottomRight,
  buttonIsHiddenOnLargeScreen,
  buttonProps,
  buttonText,
  centerLeft,
  centerRight,
  children,
  className,
  imgProps,
  inverted,
  layer,
  layerWidth,
  noImage,
  onButtonClick,
  pretitle,
  shallow,
  sub,
  title,
  topCenter,
  topLeft,
  topRight,
  video,
  testId,
  ...otherProps
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  /**
   * Handle custom {@link PlayButton} interactions.
   * @type {(function(): (*|undefined))|*}
   */
  const onPlayButtonClick = useCallback(() => {
    if (videoRef?.current) {
      return isPlaying ? videoRef?.current?.pause() : videoRef?.current?.play();
    }
    return () => {};
  }, [isPlaying, videoRef]);
  /**
   * Handle `onPlay`, `onStop` and `onPause` events on the {@link Video} instance.
   * We cannot pass `setIsPlaying` directly into the video handlers as that
   * resolves in re-renders as the {@link Video} component does not memoize the invocations.
   * @type {(function(*): void)|*}
   */
  const onVideoToggle = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plyr => {
    setIsPlaying(!plyr?.media?.paused);
  }, [setIsPlaying]);
  const divClassNameList = classNames('c-hero-teaser', {
    // Main class modifier convenience prop extension.
    'c-hero-teaser--top-left': topLeft,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--top-center': topCenter,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--top-right': topRight,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--center-left': centerLeft,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--center-right': centerRight,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--bottom-center': bottomCenter,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--bottom-left': bottomLeft,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--bottom-right': bottomRight,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--shallow': shallow,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--auto': auto,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--inverted': inverted,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--video': video && noImage,
    // Convenience prop from Bronson variants.
    'c-hero-teaser--layer': layer // Convenience prop from Bronson variants.
  }, className).trim();
  function renderHeroTeaserPretitle() {
    if (pretitle) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-hero-teaser__pretitle"
      }, /*#__PURE__*/React__default.createElement("p", null, pretitle));
    }
    return null;
  }
  function renderHeroTeaserSub() {
    if (sub) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-hero-teaser__subtitle"
      }, /*#__PURE__*/React__default.createElement("p", null, sub));
    }
    return null;
  }
  function renderHeroTeaserText() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-hero-teaser__text"
      }, /*#__PURE__*/React__default.createElement("p", null, children));
    }
    return null;
  }
  const buttonWrapperClassNameList = classNames('c-hero-teaser__button', {
    'c-hero-teaser__button--hidden-on-large-screen': buttonIsHiddenOnLargeScreen
  }).trim();
  function renderHeroTeaserHasButton() {
    if (buttonText) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: buttonWrapperClassNameList
      }, /*#__PURE__*/React__default.createElement("button", {
        className: "c-btn",
        onClick: onButtonClick,
        type: "button",
        ...buttonProps
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "c-btn__text"
      }, buttonText)));
    }
    return null;
  }
  const layerClassNameList = classNames('c-hero-teaser__layer').trim();
  function renderHeroTeaserHasLayer() {
    if (layer) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: layerClassNameList,
        "data-hero-teaser-layer-size": layerWidth ?? null
      }, layer);
    }
    return null;
  }
  function renderHeroTeaserHasBar() {
    if (bar) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-hero-teaser__bar"
      }, /*#__PURE__*/React__default.createElement("div", {
        className: "o-page-wrap"
      }, bar));
    }
    return null;
  }
  function renderHeroTeaserImage() {
    /**
     * Handles fallback if {@link imgProps.alt} is not set.
     */
    const imgAttrs = {
      ...imgProps,
      ...{
        alt: imgProps?.alt ?? getTextContent(title) ?? ''
      }
    };
    // eslint-disable-next-line jsx-a11y/alt-text
    return /*#__PURE__*/React__default.createElement("img", {
      className: "c-hero-teaser__image",
      ...imgAttrs
    });
  }
  function renderHeroTeaserTitle() {
    if (title) {
      const renderTitleChildren = t => {
        /**
         * If the title was an array, map over it
         * and wrap every item in `.c-hero-teaser__title-row`.
         */
        if (Array.isArray(t)) {
          return t.map((row, i) => (/*#__PURE__*/React__default.createElement("span", {
            className: "c-hero-teaser__title-row",
            key: `c-hero-teaser__title-row-${i}`
          }, row)));
        }
        /**
         * If the title is a string or a valid React element,
         * just return it as a single child.
         */
        if (typeof t === 'string' || /*#__PURE__*/isValidElement(t)) {
          return /*#__PURE__*/React__default.createElement("span", {
            className: "c-hero-teaser__title-row"
          }, t);
        }
        return null;
      };
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-hero-teaser__title"
      }, /*#__PURE__*/React__default.createElement("h1", null, renderTitleChildren(title)));
    }
    return null;
  }
  function renderHeroTeaserHeadingElement() {
    // Hide the heading element if no content is present. Bronson template: 'hero-teaser-hide-header'.
    return (pretitle || sub || title || children) && (/*#__PURE__*/React__default.createElement("header", {
      className: "c-hero-teaser__heading"
    }, renderHeroTeaserPretitle(), renderHeroTeaserTitle(), renderHeroTeaserSub(), renderHeroTeaserText()));
  }
  function renderBackgroundVideo() {
    return video ? (/*#__PURE__*/React__default.createElement("div", {
      className: "c-hero-teaser__background-video"
    }, /*#__PURE__*/React__default.createElement(Video, {
      ref: videoRef,
      ...video,
      onPlay: onVideoToggle,
      onPause: onVideoToggle,
      "aria-hidden": "true"
    }), /*#__PURE__*/React__default.createElement(PlayButton, {
      className: classNames('c-hero-teaser__background-video-control', {
        'is-paused': !isPlaying
      }),
      iconClassName: "c-hero-teaser__background-video-control-icon",
      label: "Start or stop the video",
      onClick: onPlayButtonClick
    }))) : (/*#__PURE__*/React__default.createElement("div", {
      className: "c-hero-teaser__image"
    }));
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, noImage || video ? renderBackgroundVideo() : renderHeroTeaserImage(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-hero-teaser__content"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-hero-teaser__content-inner"
  }, renderHeroTeaserHeadingElement(), renderHeroTeaserHasButton(), renderHeroTeaserHasLayer())), renderHeroTeaserHasBar());
}

/**
 * The Hint component is used for triggering all kinds of tiny user interactions
 * like showing a tooltip or linking to a footnote.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-hint.html
 * @constructor
 */
function Hint({
  className,
  children,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-hint', className).trim();
  return /*#__PURE__*/React__default.createElement("sup", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, children);
}

/*
 * Bronson Hr component.
 */
function Hr({
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("hr", {
    ...otherProps,
    "data-testid": testId
  });
}

/*
 * Bronson IconList component.
 */
const IconList = ({
  children,
  className,
  testId,
  ...otherProps
}) => {
  const ulClassNameList = classNames('c-icon-list', className).trim();
  return /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    "data-testid": testId,
    className: ulClassNameList
  }, children);
};
IconList.displayName = 'IconList';
/*
 * Bronson Item component (nested).
 */
function Item$3({
  children,
  icon,
  iconClassName,
  iconMeaning,
  success,
  ...otherProps
}) {
  const iClassNameList = classNames('c-icon-list__icon', {
    'u-text-success': success
  }, iconClassName).trim();
  function renderIfItemIconMeaning() {
    if (iconMeaning) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "u-visually-hidden"
      }, iconMeaning);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: iClassNameList,
    name: icon
  }), renderIfItemIconMeaning(), children);
}
Item$3.displayName = 'IconList.Item';
IconList.Item = Item$3;

/**
 * Indicator Dot
 * @desc Visual indicator to communicate a status.
 * It includes default statuses like `default`, `info`, `success`, `warning`, `error` and a `unread` status, e.g. for messages.
 * @constructor
 */
function IndicatorDot({
  ariaLabelStatus,
  className,
  label,
  status,
  tooltip = null,
  testId
}) {
  const indicatorDotClassNameList = classNames({
    'c-indicator-dot': true,
    [`c-indicator-dot--${status}`]: status
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("span", {
    className: indicatorDotClassNameList,
    "data-testid": testId
  }, tooltip ?? /*#__PURE__*/React__default.createElement("span", {
    className: "c-indicator-dot__dot",
    "aria-label": ariaLabelStatus
  }), label && /*#__PURE__*/React__default.createElement("span", {
    className: "c-indicator-dot__label"
  }, label));
}

var TableButtonSorting;
(function (TableButtonSorting) {
  TableButtonSorting["ASC"] = "asc";
  TableButtonSorting["DESC"] = "desc";
})(TableButtonSorting || (TableButtonSorting = {}));
function TableButton({
  children,
  className,
  icon,
  sort,
  testId,
  ...otherProps
}) {
  const sortAsc = sort === TableButtonSorting.ASC;
  const sortDesc = sort === TableButtonSorting.DESC;
  const componentListClass = classNames({
    'c-table__btn': true,
    'c-table__btn-sort': sortAsc || sortDesc,
    'is-sort-asc': sortAsc,
    'is-sort-desc': sortDesc
  }, className).trim();
  const sortProps = sort ? {
    'data-sort-order': sortAsc ? 'asc' : 'desc',
    iconViaCss: true
  } : {};
  const nonSortProps = sort ? {} : {
    secondary: !!icon,
    small: !!icon,
    icon
  };
  return /*#__PURE__*/React__default.createElement(Button, {
    className: componentListClass,
    ...sortProps,
    ...nonSortProps,
    type: "button",
    "data-testid": testId,
    ...otherProps
  }, children);
}

/**
 * Default localizations for selected locales, which can be passed as locale prop.
 * Note: A localization object can be passed instead with the localization prop.
 */
const localizationDefault = {
  de: {
    'month.01.name': 'Januar',
    'month.01.name.short': 'jan',
    'month.02.name': 'Februar',
    'month.02.name.short': 'feb',
    'month.03.name': 'März',
    'month.03.name.short': 'mär',
    'month.04.name': 'April',
    'month.04.name.short': 'apr',
    'month.05.name': 'Mai',
    'month.05.name.short': 'mai',
    'month.06.name': 'Juni',
    'month.06.name.short': 'jun',
    'month.07.name': 'Juli',
    'month.07.name.short': 'jul',
    'month.08.name': 'August',
    'month.08.name.short': 'aug',
    'month.09.name': 'September',
    'month.09.name.short': 'sep',
    'month.10.name': 'Oktober',
    'month.10.name.short': 'okt',
    'month.11.name': 'November',
    'month.11.name.short': 'nov',
    'month.12.name': 'Dezember',
    'month.12.name.short': 'dez'
  }
};
const InboxContext = /*#__PURE__*/React__default.createContext({});
/**
 * Bronson Inbox component.
 * @see https://bronson.vwfs.tools/bluelabel/components/detail/bronson-inbox.html
 * @constructor
 */
const Inbox = ({
  children,
  columns = ['colDate', 'colDownload', 'colSubject', 'colDelete'],
  dateFormatter,
  deleteButtonLabel = 'Delete',
  deleteShowLabel = false,
  downloadButtonLabel = 'Download',
  downloadShowLabel = false,
  filterElements,
  filterTitle,
  locale = 'de',
  localization,
  sortingOrder = 'ASC',
  tableColumnClassNames,
  tableHeader,
  tableHeaderClassNames,
  testId,
  ...otherProps
}) => {
  const [ascSorting, setAscSorting] = useState(sortingOrder === 'ASC');
  const contextValue = {
    localization: localization ?? localizationDefault[locale],
    tableHeader,
    tableHeaderClassNames,
    tableColumnClassNames,
    columns,
    dateFormatter,
    downloadButtonLabel,
    downloadShowLabel,
    deleteButtonLabel,
    deleteShowLabel,
    ascSorting,
    toggleSorting: () => setAscSorting(s => !s)
  };
  const sortedSections = useMemo(() => {
    // @ts-ignore @FIXME: Fix sort args.
    const sections = React__default.Children.toArray(children).sort(({
      props: a
    }, {
      props: b
    }) => {
      const numA = Number(a.year);
      const numB = Number(b.year);
      if (ascSorting) {
        return numA - numB;
      }
      return numB - numA;
    });
    return sections.map((section, i) => {
      if (i === 0) {
        return /*#__PURE__*/React__default.cloneElement(section, {
          showTableHeader: true,
          showYear: false
        });
      }
      return /*#__PURE__*/React__default.cloneElement(section, {
        showTableHeader: false,
        showYear: true
      });
    });
  }, [children, ascSorting]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, filterTitle || filterElements ? (/*#__PURE__*/React__default.createElement("div", {
    className: "c-inbox-filter"
  }, filterTitle && /*#__PURE__*/React__default.createElement("h3", {
    className: "c-inbox-filter__title"
  }, filterTitle), filterElements && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-inbox-filter__body"
  }, /*#__PURE__*/React__default.createElement(Form, {
    floatingLabel: true
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "o-inline-group o-inline-group--small o-inline-group--stretch"
  }, filterElements)))))) : (/*#__PURE__*/React__default.createElement(React__default.Fragment, null)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-inbox-table",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(InboxContext.Provider, {
    value: contextValue
  }, sortedSections /* Use 'Inbox.Section' component. */)));
};
Inbox.displayName = 'Inbox';
Inbox.defaultProps = {
  sortingOrder: 'ASC'
};
/**
 * Bronson FilterElement component (nested).
 * @constructor
 */
function FilterElement({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: "o-inline-group__item"
  }, children);
}
FilterElement.displayName = 'Inbox.FilterElement';
Inbox.FilterElement = FilterElement;
// TODO: move showYear to Inbox
// TODO: rename showTableHeader to first and use only internally
/**
 * Bronson Section component (nested).
 * @constructor
 */
function Section({
  children,
  showTableHeader,
  showYear,
  year,
  testId,
  ...otherProps
}) {
  const context = React__default.useContext(InboxContext);
  const thClassNames = (colName = '') => classNames('c-inbox-table__th', context.tableHeaderClassNames?.[colName]).trim();
  const renderTh = (item, index) => {
    if (context.columns?.[index] === 'colDate') {
      return /*#__PURE__*/React__default.createElement("th", {
        scope: "col",
        key: index,
        className: thClassNames(context.columns[index])
      }, /*#__PURE__*/React__default.createElement(TableButton, {
        sort: context.ascSorting ? TableButtonSorting.ASC : TableButtonSorting.DESC,
        onClick: context.toggleSorting
      }, item));
    }
    return /*#__PURE__*/React__default.createElement("th", {
      scope: "col",
      key: index,
      className: thClassNames(context.columns?.[index])
    }, item);
  };
  function renderIfShowYear() {
    if (showYear) {
      return /*#__PURE__*/React__default.createElement("h4", {
        className: "c-inbox-table__subheading"
      }, year);
    }
    return null;
  }
  function renderIfShowTableHeader() {
    if (showTableHeader && context.tableHeader) {
      return /*#__PURE__*/React__default.createElement("thead", null, /*#__PURE__*/React__default.createElement("tr", null, context.tableHeader.map(renderTh)));
    }
    return null;
  }
  /**
   * sorts the children according to sorting prop (@link context.ascSorting)
   */
  const sortedMessages = useMemo(() => {
    // @ts-ignore @FIXME: Fix sort args.
    return React__default.Children.toArray(children).sort(({
      props: a
    }, {
      props: b
    }) => {
      // eslint-disable-next-line no-bitwise
      const numA = a.year * 10000 + a.month * 100 + (a.day >> 0);
      // eslint-disable-next-line no-bitwise
      const numB = b.year * 10000 + b.month * 100 + (b.day >> 0);
      if (context.ascSorting) {
        return numA - numB;
      }
      return numB - numA;
    });
  }, [children, context.ascSorting]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderIfShowYear(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-table-wrapper",
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("table", null, renderIfShowTableHeader(), /*#__PURE__*/React__default.createElement("tbody", {
    className: "c-inbox-table__body"
  }, /* Use 'Inbox.Message' component. */sortedMessages))));
}
Section.displayName = 'Inbox.Section';
Inbox.Section = Section;
/**
 * Bronson Message component (nested).
 * @constructor
 */
function Message({
  children,
  day,
  download,
  extraInfo,
  messageKey,
  month,
  onDeleteClick,
  onDownloadClick,
  onItemClick,
  read,
  to,
  tooltipContent = 'Unread item',
  year,
  testId,
  ...otherProps
}) {
  const context = React__default.useContext(InboxContext);
  const tdClassNames = (classes, colName) => classNames('c-inbox-table__td', classes, context.tableColumnClassNames && context.tableColumnClassNames[colName]).trim();
  const restProps = {
    ...otherProps
  };
  context.columns?.forEach(col => delete restProps[col]);
  const monthKey = `month.${month}.name`;
  const monthKeyShort = `month.${month}.name`;
  const monthName = context.localization ? context.localization[monthKey] : null;
  const monthNameShort = context.localization ? context.localization[monthKeyShort] : null;
  const dateFormatter = context.dateFormatter || (dayName => `${dayName}. ${monthName}`);
  const trClassNameList = classNames({
    'c-inbox-table__tr ': true,
    'is-unread ': !read
  }).trim();
  function renderIfDownload() {
    if (download) {
      return /*#__PURE__*/React__default.createElement(Button, {
        element: "a",
        icon: "semantic-download",
        secondary: true,
        hiddenLabel: !context.downloadShowLabel,
        className: "c-table__btn",
        onClick: onDownloadClick,
        title: context.downloadButtonLabel
      }, context.downloadButtonLabel);
    }
    return null;
  }
  function renderIfExtraInfo() {
    if (extraInfo) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: "c-inbox-table__extra-info"
      }, extraInfo);
    }
    return null;
  }
  function renderUnlessDownload() {
    if (!download) {
      return /*#__PURE__*/React__default.createElement(Button, {
        icon: "semantic-delete",
        secondary: true,
        className: "c-table__btn",
        hiddenLabel: !context.deleteShowLabel,
        onClick: onDeleteClick,
        title: context.deleteButtonLabel
      }, context.deleteButtonLabel);
    }
    return null;
  }
  const renderColDate = colName => (/*#__PURE__*/React__default.createElement("td", {
    className: tdClassNames('c-inbox-table__td--date', colName),
    key: `${messageKey}--date`
  }, /*#__PURE__*/React__default.createElement("time", {
    className: "c-inbox-table__date",
    "data-inbox-day": day,
    "data-inbox-month": monthNameShort || month,
    dateTime: `${year}-${month}-${day}`,
    title: `${day}. ${monthName || month} ${year}`
  }, dateFormatter(day, month, year))));
  const renderColDownload = colName => (/*#__PURE__*/React__default.createElement("td", {
    className: tdClassNames('c-inbox-table__td--action', colName),
    key: `${messageKey}--download`
  }, renderIfDownload()));
  const renderColDelete = colName => (/*#__PURE__*/React__default.createElement("td", {
    className: tdClassNames('c-inbox-table__td--action', colName),
    key: `${messageKey}--delete`
  }, renderUnlessDownload()));
  const renderColSubject = colName => (/*#__PURE__*/React__default.createElement("td", {
    className: tdClassNames('c-inbox-table__td--subject', colName),
    key: `${messageKey}--subject`
  }, renderIfExtraInfo(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-inbox-table__td__subject"
  }, !read && (/*#__PURE__*/React__default.createElement(IndicatorDot, {
    className: "c-inbox-table__status-indicator",
    status: "unread",
    tooltip: /*#__PURE__*/React__default.createElement(Tooltip, {
      className: "c-indicator-dot__dot",
      content: tooltipContent,
      "aria-label": tooltipContent
    }, ' ')
  })), /*#__PURE__*/React__default.createElement(ReactLink, {
    onClick: onItemClick,
    to: to || '#',
    className: "c-inbox-table__link"
  }, children))));
  return /*#__PURE__*/React__default.createElement("tr", {
    ...restProps,
    "data-testid": testId,
    className: trClassNameList
  }, context.columns?.map(colName => {
    switch (colName) {
      case 'colDate':
        return renderColDate(colName);
      case 'colDownload':
        return renderColDownload(colName);
      case 'colSubject':
        return renderColSubject(colName);
      case 'colDelete':
        return renderColDelete(colName);
      default:
        // @ts-ignore @FIXME: Refactor component to reuse header message via context.columns and TableHeader.
        return otherProps[colName] ? (/*#__PURE__*/React__default.createElement("td", {
          className: tdClassNames('u-hide@s', colName),
          key: `${messageKey}--${colName}`
        }, otherProps[colName])) : null;
    }
  }));
}
Message.displayName = 'Inbox.Message';
Inbox.Message = Message;

/*
 * Bronson InlineEditable component.
 */
const InlineEditable = ({
  className,
  defaultActive,
  editLabel = 'Edit value',
  errorMessage,
  onReset,
  onSubmit,
  resetLabel = 'Reset value',
  submitLabel = 'Submit value',
  submittedValue,
  valid,
  value,
  ...otherProps
}) => {
  /**
   * Get ref for input element.
   */
  const inputRef = useRef(null);
  /**
   * Manage active state to show editable mode.
   */
  const [isActive, setIsActive] = useState(defaultActive);
  /**
   * Set the {isActive} state to true and memoize the function.
   * @type {(function(): void)|*}
   */
  const activate = useCallback(() => {
    setIsActive(true);
    inputRef?.current?.focus();
  }, []);
  /**
   * Set the {isActive} state to false and memoize the function.
   * @type {(function(): void)|*}
   */
  const deactivate = useCallback(() => {
    setIsActive(false);
    inputRef?.current?.blur();
  }, []);
  /**
   * The InlineEditable class names.
   * @type {string}
   */
  const inlineEditableClassNameList = classNames('c-inline-editable', className).trim();
  /**
   * Edit Button
   * @type {JSX.Element}
   */
  const editButton = /*#__PURE__*/React__default.createElement(Button, {
    link: true,
    simple: true,
    small: true,
    hiddenLabel: true,
    ariaLabel: editLabel,
    className: "c-inline-editable__btn",
    type: "button",
    icon: "semantic-edit",
    "data-tr-component": "Button",
    "data-tr-function": "Edit inline value",
    onClick: activate
  });
  const submitButton = /*#__PURE__*/React__default.createElement(Button, {
    hiddenLabel: true,
    className: "c-inline-editable__submit",
    type: "submit",
    icon: "semantic-checkmark",
    "data-tr-component": "Button",
    ariaLabel: submitLabel,
    "data-tr-function": "Submit inline value",
    disabled: value === submittedValue || !valid,
    onClick: deactivate
  });
  const resetButton = /*#__PURE__*/React__default.createElement(Button, {
    secondary: true,
    hiddenLabel: true,
    type: "reset",
    ariaLabel: resetLabel,
    className: "c-inline-editable__cancel",
    icon: "semantic-crossed-out",
    "data-tr-component": "Button",
    "data-tr-function": "Reset inline value",
    onClick: deactivate
  });
  return /*#__PURE__*/React__default.createElement("form", {
    className: inlineEditableClassNameList,
    onReset: onReset,
    onSubmit: onSubmit,
    "aria-hidden": isActive ? 'false' : 'true'
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-inline-editable__wrap"
  }, /*#__PURE__*/React__default.createElement(Input, {
    modifierStates: "c-inline-editable__input",
    addonClassName: "c-inline-editable__input-addon c-input__addon--no-background",
    onClick: activate,
    size: 1,
    divProps: {
      'data-inline-editable-size': value ?? 1
    },
    ...otherProps,
    value: value,
    ref: inputRef
  }), editButton, submitButton, resetButton), valid ? null : errorMessage);
};

var awesomplete = {exports: {}};

/**
 * Simple, lightweight, usable local autocomplete library for modern browsers
 * Because there weren’t enough autocomplete scripts in the world? Because I’m completely insane and have NIH syndrome? Probably both. :P
 * @author Lea Verou http://leaverou.github.io/awesomplete
 * MIT license
 */

var hasRequiredAwesomplete;

function requireAwesomplete () {
	if (hasRequiredAwesomplete) return awesomplete.exports;
	hasRequiredAwesomplete = 1;
	(function (module) {
		(function () {

		var _ = function (input, o) {
			var me = this;

			// Keep track of number of instances for unique IDs
			_.count = (_.count || 0) + 1;
			this.count = _.count;

			// Setup

			this.isOpened = false;

			this.input = $(input);
			this.input.setAttribute("autocomplete", "off");
			this.input.setAttribute("aria-autocomplete", "list");
			this.input.setAttribute("aria-expanded", "false");
			this.input.setAttribute("aria-controls", "awesomplete_list_" + this.count);
			this.input.setAttribute("aria-owns", "awesomplete_list_" + this.count);
			this.input.setAttribute("role", "combobox");

			// store constructor options in case we need to distinguish
			// between default and customized behavior later on
			this.options = o = o || {};

			configure(this, {
				minChars: 2,
				maxItems: 10,
				autoFirst: false,
				data: _.DATA,
				filter: _.FILTER_CONTAINS,
				sort: o.sort === false ? false : _.SORT_BYLENGTH,
				container: _.CONTAINER,
				item: _.ITEM,
				replace: _.REPLACE,
				tabSelect: false,
				listLabel: "Results List",
				statusNoResults: "No results found",
				statusXResults: "{0} results found", // uses index placeholder {0}
				statusTypeXChar: "Type {0} or more characters for results"
			}, o);

			this.index = -1;

			// Create necessary elements

			this.container = this.container(input);

			this.ul = $.create("ul", {
				hidden: "hidden",
				role: "listbox",
				id: "awesomplete_list_" + this.count,
				inside: this.container,
				"aria-label": this.listLabel
			});

			this.status = $.create("span", {
				className: "visually-hidden",
				role: "status",
				"aria-live": "assertive",
				"aria-atomic": true,
				inside: this.container,
				textContent: ""  // live region should start empty. Only when the text is changed it will be read by the screen reader.
			});

			// Bind events

			this._events = {
				input: {
					"input": this.evaluate.bind(this),
					"blur": this.close.bind(this, { reason: "blur" }),
					"keydown": function(evt) {
						var c = evt.keyCode;

						// If the dropdown `ul` is in view, then act on keydown for the following keys:
						// Enter / Esc / Up / Down
						if(me.opened) {
							if (c === 13 && me.selected) { // Enter
								evt.preventDefault();
								me.select(undefined, undefined, evt);
							}
							else if (c === 9 && me.selected && me.tabSelect) {
								evt.preventDefault();
								me.select(undefined, undefined, evt);
							}
							else if (c === 27) { // Esc
								me.close({ reason: "esc" });
							}
							else if (c === 38 || c === 40) { // Down/Up arrow
								evt.preventDefault();
								me[c === 38? "previous" : "next"]();
							}
						}
					}
				},
				form: {
					"submit": this.close.bind(this, { reason: "submit" })
				},
				ul: {
					// Prevent the default mousedowm, which ensures the input is not blurred.
					// The actual selection will happen on click. This also ensures dragging the
					// cursor away from the list item will cancel the selection
					"mousedown": function(evt) {
						evt.preventDefault();
					},
					// The click event is fired even if the corresponding mousedown event has called preventDefault
					"click": function(evt) {
						var li = evt.target;

						if (li !== this) {

							while (li && !/li/i.test(li.nodeName)) {
								li = li.parentNode;
							}

							if (li && evt.button === 0) {  // Only select on left click
								evt.preventDefault();
								me.select(li, evt.target, evt);
							}
						}
					}
				}
			};

			$.bind(this.input, this._events.input);
			$.bind(this.input.form, this._events.form);
			$.bind(this.ul, this._events.ul);

			if (this.input.hasAttribute("list")) {
				this.list = "#" + this.input.getAttribute("list");
				this.input.removeAttribute("list");
			}
			else {
				this.list = this.input.getAttribute("data-list") || o.list || [];
			}

			_.all.push(this);
		};

		_.prototype = {
			set list(list) {
				if (Array.isArray(list)) {
					this._list = list;
				}
				else if (typeof list === "string" && list.indexOf(",") > -1) {
						this._list = list.split(/\s*,\s*/);
				}
				else { // Element or CSS selector
					list = $(list);

					if (list && list.children) {
						var items = [];
						slice.apply(list.children).forEach(function (el) {
							if (!el.disabled) {
								var text = el.textContent.trim();
								var value = el.value || text;
								var label = el.label || text;
								if (value !== "") {
									items.push({ label: label, value: value });
								}
							}
						});
						this._list = items;
					}
				}

				if (document.activeElement === this.input) {
					this.evaluate();
				}
			},

			get selected() {
				return this.index > -1;
			},

			get opened() {
				return this.isOpened;
			},

			close: function (o) {
				if (!this.opened) {
					return;
				}

				this.input.setAttribute("aria-expanded", "false");
				this.ul.setAttribute("hidden", "");
				this.isOpened = false;
				this.index = -1;

				this.status.setAttribute("hidden", "");
				this.input.setAttribute("aria-activedescendant", "");

				$.fire(this.input, "awesomplete-close", o || {});
			},

			open: function () {
				this.input.setAttribute("aria-expanded", "true");
				this.ul.removeAttribute("hidden");
				this.isOpened = true;

				this.status.removeAttribute("hidden");

				if (this.autoFirst && this.index === -1) {
					this.goto(0);
				}

				$.fire(this.input, "awesomplete-open");
			},

			destroy: function() {
				//remove events from the input and its form
				$.unbind(this.input, this._events.input);
				$.unbind(this.input.form, this._events.form);

				// cleanup container if it was created by Awesomplete but leave it alone otherwise
				if (!this.options.container) {
					//move the input out of the awesomplete container and remove the container and its children
					var parentNode = this.container.parentNode;

					parentNode.insertBefore(this.input, this.container);
					parentNode.removeChild(this.container);
				}

				// remove autocomplete and aria attributes
				this.input.removeAttribute("autocomplete");
				this.input.removeAttribute("aria-autocomplete");
				this.input.removeAttribute("aria-expanded");
				this.input.removeAttribute("aria-controls");
				this.input.removeAttribute("aria-owns");		
				this.input.removeAttribute("role");

				//remove this awesomeplete instance from the global array of instances
				var indexOfAwesomplete = _.all.indexOf(this);

				if (indexOfAwesomplete !== -1) {
					_.all.splice(indexOfAwesomplete, 1);
				}
			},

			next: function () {
				var count = this.ul.children.length;
				this.goto(this.index < count - 1 ? this.index + 1 : (count ? 0 : -1) );
			},

			previous: function () {
				var count = this.ul.children.length;
				var pos = this.index - 1;

				this.goto(this.selected && pos !== -1 ? pos : count - 1);
			},

			// Should not be used, highlights specific item without any checks!
			goto: function (i) {
				var lis = this.ul.children;

				if (this.selected) {
					lis[this.index].setAttribute("aria-selected", "false");
				}

				this.index = i;

				if (i > -1 && lis.length > 0) {
					lis[i].setAttribute("aria-selected", "true");

					// fix: Turned off this status update.
					//		Screen readers Voiceover and Talkback won't read this status change.
					//		Narrator and NVDA do, but they already tell: 'X of Y (selected)'
					// this.status.textContent = lis[i].textContent + ", list item " + (i + 1) + " of " + lis.length;

					this.input.setAttribute("aria-activedescendant", this.ul.id + "_item_" + this.index);

					// scroll to highlighted element in case parent's height is fixed
					this.ul.scrollTop = lis[i].offsetTop - this.ul.clientHeight + lis[i].clientHeight;

					$.fire(this.input, "awesomplete-highlight", {
						text: this.suggestions[this.index]
					});
				}
			},

			select: function (selected, origin, originalEvent) {
				if (selected) {
					this.index = $.siblingIndex(selected);
				} else {
					selected = this.ul.children[this.index];
				}

				if (selected) {
					var suggestion = this.suggestions[this.index];

					var allowed = $.fire(this.input, "awesomplete-select", {
						text: suggestion,
						origin: origin || selected,
						originalEvent: originalEvent
					});

					if (allowed) {
						this.replace(suggestion);
						this.close({ reason: "select" });
						$.fire(this.input, "awesomplete-selectcomplete", {
							text: suggestion,
							originalEvent: originalEvent
						});
					}
				}
			},

			evaluate: function() {
				var me = this;
				var value = this.input.value;

				if (value.length >= this.minChars && this._list && this._list.length > 0) {
					this.index = -1;
					// Populate list with options that match
					this.ul.innerHTML = "";

					this.suggestions = this._list
						.map(function(item) {
							return new Suggestion(me.data(item, value));
						})
						.filter(function(item) {
							return me.filter(item, value);
						});

					if (this.sort !== false) {
						this.suggestions = this.suggestions.sort(this.sort);
					}

					this.suggestions = this.suggestions.slice(0, this.maxItems);

					this.suggestions.forEach(function(text, index) {
							me.ul.appendChild(me.item(text, value, index));
						});

					if (this.ul.children.length === 0) {

						this.status.textContent = this.statusNoResults;

						this.close({ reason: "nomatches" });

					} else {
						this.input.setAttribute("aria-activedescendant", ""); // none of the list items currently has aria-selected="true"

						this.open();

						this.status.textContent = this.statusXResults.replaceAll('{0}', this.ul.children.length); // N results found;
					}
				}
				else {

					this.close({ reason: "nomatches" });

					if (this.minChar <= 1 || value.length >= this.minChars) {
					   this.status.textContent = this.statusNoResults;
					} else {
					   this.status.textContent = this.statusTypeXChar.replaceAll('{0}', this.minChars); // Type N or more characters for results
					}

				}
			}
		};

		// Static methods/properties

		_.all = [];

		_.FILTER_CONTAINS = function (text, input) {
			return RegExp($.regExpEscape(input.trim()), "i").test(text);
		};

		_.FILTER_STARTSWITH = function (text, input) {
			return RegExp("^" + $.regExpEscape(input.trim()), "i").test(text);
		};

		_.SORT_BYLENGTH = function (a, b) {
			if (a.length !== b.length) {
				return a.length - b.length;
			}

			return a < b? -1 : 1;
		};

		_.CONTAINER = function (input) {
			return $.create("div", {
				className: "awesomplete",
				around: input
			});
		};

		_.ITEM = function (text, input, item_id) {
			var html = input.trim() === "" ? text : text.replace(RegExp($.regExpEscape(input.trim()), "gi"), "<mark>$&</mark>");
			return $.create("li", {
				innerHTML: html,
				"role": "option",
				"aria-selected": "false",
				"tabindex": "-1", // for the Talkback screen reader
				"id": "awesomplete_list_" + this.count + "_item_" + item_id
			});
		};

		_.REPLACE = function (text) {
			this.input.value = text.value;
		};

		_.DATA = function (item/*, input*/) { return item; };

		// Private functions

		function Suggestion(data) {
			var o = Array.isArray(data)
			  ? { label: data[0], value: data[1] }
			  : typeof data === "object" && "label" in data && "value" in data ? data : { label: data, value: data };

			this.label = o.label || o.value;
			this.value = o.value;
		}
		Object.defineProperty(Suggestion.prototype = Object.create(String.prototype), "length", {
			get: function() { return this.label.length; }
		});
		Suggestion.prototype.toString = Suggestion.prototype.valueOf = function () {
			return "" + this.label;
		};

		function configure(instance, properties, o) {
			for (var i in properties) {
				var initial = properties[i],
					attrValue = instance.input.getAttribute("data-" + i.toLowerCase());

				if (typeof initial === "number") {
					instance[i] = parseInt(attrValue);
				}
				else if (initial === false) { // Boolean options must be false by default anyway
					instance[i] = attrValue !== null;
				}
				else if (initial instanceof Function) {
					instance[i] = null;
				}
				else {
					instance[i] = attrValue;
				}

				if (!instance[i] && instance[i] !== 0) {
					instance[i] = (i in o)? o[i] : initial;
				}
			}
		}

		// Helpers

		var slice = Array.prototype.slice;

		function $(expr, con) {
			return typeof expr === "string"? (con || document).querySelector(expr) : expr || null;
		}

		function $$(expr, con) {
			return slice.call((con || document).querySelectorAll(expr));
		}

		$.create = function(tag, o) {
			var element = document.createElement(tag);

			for (var i in o) {
				var val = o[i];

				if (i === "inside") {
					$(val).appendChild(element);
				}
				else if (i === "around") {
					var ref = $(val);
					ref.parentNode.insertBefore(element, ref);
					element.appendChild(ref);

					if (ref.getAttribute("autofocus") != null) {
						ref.focus();
					}
				}
				else if (i in element) {
					element[i] = val;
				}
				else {
					element.setAttribute(i, val);
				}
			}

			return element;
		};

		$.bind = function(element, o) {
			if (element) {
				for (var event in o) {
					var callback = o[event];

					event.split(/\s+/).forEach(function (event) {
						element.addEventListener(event, callback);
					});
				}
			}
		};

		$.unbind = function(element, o) {
			if (element) {
				for (var event in o) {
					var callback = o[event];

					event.split(/\s+/).forEach(function(event) {
						element.removeEventListener(event, callback);
					});
				}
			}
		};

		$.fire = function(target, type, properties) {
			var evt = document.createEvent("HTMLEvents");

			evt.initEvent(type, true, true );

			for (var j in properties) {
				evt[j] = properties[j];
			}

			return target.dispatchEvent(evt);
		};

		$.regExpEscape = function (s) {
			return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
		};

		$.siblingIndex = function (el) {
			/* eslint-disable no-cond-assign */
			for (var i = 0; el = el.previousElementSibling; i++);
			return i;
		};

		// Initialization

		function init() {
			$$("input.awesomplete").forEach(function (input) {
				new _(input);
			});
		}

		// Make sure to export Awesomplete on self when in a browser
		if (typeof self !== "undefined") {
			self.Awesomplete = _;
		}

		// Are we in a browser? Check for Document constructor
		if (typeof Document !== "undefined") {
			// DOM already loaded?
			if (document.readyState !== "loading") {
				init();
			}
			else {
				// Wait for it
				document.addEventListener("DOMContentLoaded", init);
			}
		}

		_.$ = $;
		_.$$ = $$;

		// Expose Awesomplete as a CJS module
		if (module.exports) {
			module.exports = _;
		}

		return _;

		}()); 
	} (awesomplete));
	return awesomplete.exports;
}

var awesompleteExports = requireAwesomplete();
var Awesomplete = /*@__PURE__*/getDefaultExportFromCjs(awesompleteExports);

function InputAutosuggest({
  awesompleteProps = {},
  list,
  listLabel = 'Suggestions',
  openOnFocus = false,
  showListLabel = false,
  ...otherProps
}) {
  const inputRef = useRef(null);
  const awesompleteRef = useRef(null);
  /**
   * Initialization of {@link Awesomplete} instance.
   */
  useEffect(() => {
    if (inputRef.current instanceof HTMLInputElement) {
      awesompleteRef.current = new Awesomplete(inputRef.current, {
        list: list,
        listLabel: listLabel,
        minChars: 1,
        maxItems: 10,
        sort: () => {}
      });
    }
  }, []);
  /**
   * Keeps track of {@link Awesomplete} attribute updates and updates its instance accordingly.
   */
  useEffect(() => {
    if (inputRef.current instanceof HTMLInputElement && awesompleteRef.current instanceof Awesomplete) {
      /**
       * When {@link openOnFocus} is set to true, {@link Awesomplete.minChars} is set to `0`
       * and the list of suggestions regenerated.
       */
      if (openOnFocus == true && !inputRef.current?.readOnly) {
        awesompleteProps.minChars = 0;
        inputRef.current?.addEventListener('focus', () => {
          awesompleteRef.current.evaluate();
        });
      }
      /**
       * Update of {@link Awesomplete} instance with respective attributes.
       */
      awesompleteProps.list = list;
      awesompleteProps.listLabel = listLabel;
      Object.assign(awesompleteRef.current, awesompleteProps);
      /**
       * If {@link showListLabel} is set to `true`, the CSS class `.show-label` is added to the `.awesomplete`
       * HTML element in order to visually show the {@link listLabel} inside the list of suggestions.
       */
      if (showListLabel) {
        inputRef?.current?.closest('.awesomplete')?.classList.add('show-label');
      }
    }
  }, [awesompleteProps, list, listLabel, openOnFocus, showListLabel]);
  return /*#__PURE__*/React__default.createElement(Input, {
    ref: inputRef,
    autosuggest: true,
    ...otherProps
  });
}

/**
 * Bronson InstructionStepper component.
 * @constructor
 */
function InstructionStepper({
  center,
  children,
  className,
  displayIcons = true,
  numbered,
  small,
  reversed,
  testId,
  vertical,
  ...otherProps
}) {
  const olClassNameList = classNames('c-instruction-stepper ', {
    'c-instruction-stepper--numbered ': numbered,
    // Convenience prop from Bronson variants.
    'c-instruction-stepper--vertical ': vertical,
    // Convenience prop from Bronson variants.
    'c-instruction-stepper--center ': center,
    // Convenience prop from Bronson variants.
    'c-instruction-stepper--small ': small,
    // Convenience prop from Bronson variants.
    'c-instruction-stepper--reversed ': reversed // Convenience prop from Bronson variants.
  }, className).trim();
  /**
   * Determine whether this stepper should display icons.
   * The `numbered` prop overrules the `displayIcons` prop to avoid number/icon collisions.
   * @type {boolean}
   */
  const displayIcon = !numbered && displayIcons;
  return /*#__PURE__*/React__default.createElement("ol", {
    ...otherProps,
    "data-testid": testId,
    className: olClassNameList
  }, React__default.Children.map(children, child => {
    if (/*#__PURE__*/React__default.isValidElement(child)) {
      return /*#__PURE__*/React__default.cloneElement(child, {
        displayIcon
      });
    }
  }) /* Use {@link InstructionStepperStep} component. */);
}
InstructionStepper.displayName = 'InstructionStepper';
/**
 * Bronson InstructionStepperStep component (nested).
 * @internal
 * @constructor
 */
function InstructionStepperStep({
  children,
  displayIcon,
  icon,
  state,
  title,
  ...otherProps
}) {
  const liClassNameList = classNames('c-instruction-stepper__step ', {
    [`${state} `]: state
  }).trim();
  function renderIfDisplayIcons() {
    if (displayIcon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        name: icon
      });
    }
    return null;
  }
  function renderIfDescription() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-instruction-stepper__description"
      }, children);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps,
    className: liClassNameList
  }, /*#__PURE__*/React__default.createElement("figure", {
    className: "c-instruction-stepper__item"
  }, renderIfDisplayIcons()), /*#__PURE__*/React__default.createElement("div", {
    className: "c-instruction-stepper__detail"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-instruction-stepper__title"
  }, title), renderIfDescription()));
}
InstructionStepperStep.displayName = 'InstructionStepper.Step';
InstructionStepper.Step = InstructionStepperStep;

/**
 * A generic container component to display content inside a layer.
 * The InteractionLayer also be configured to act as a modal and
 * suspend user interaction outside its own context.*
 * @constructor
 */
function InteractionLayer({
  button,
  buttonBarButtons,
  buttonInHeader,
  children,
  closeButtonLabel,
  closeOnBackdrop,
  id,
  isHidden,
  modal,
  size,
  onClose,
  onShow,
  testId,
  ...otherProps
}) {
  const interactionLayerElement = useRef(null);
  /**
   * Manage active state to show editable mode.
   */
  const [isVisible, setIsVisible] = useState(false);
  /**
   * Shows the {@link InteractionLayer} and calls {@link onShow} callback.
   * @type {function(): void}
   */
  const showInteractionLayer = useCallback(() => {
    setIsVisible(true);
    onShow?.();
  }, [setIsVisible, onShow]);
  /**
   * Set the {@see isVisible} state depending on the {@see isHidden} prop
   * and re-render is it gets changed.
   */
  useEffect(() => {
    setIsVisible(!isHidden);
    if (!isHidden) {
      showInteractionLayer();
    }
  }, [isHidden, isVisible, setIsVisible, showInteractionLayer]);
  /**
   * Hides the {@link InteractionLayer} and calls {@link onShow} callback.
   * @type {function(): void}
   */
  const hideInteractionLayer = useCallback(() => {
    setIsVisible(false);
    onClose?.();
  }, [setIsVisible, onClose]);
  /**
   * Handles backdrop clicks. Only enabled when {modal} and {closeOnBackdrop} are set to true.
   * Only calls the {hideInteractionLayer} function if the target element actually matches.
   * @type {(function(*): void)|*}
   */
  const handleBackdropClick = useCallback(event => {
    if (modal && closeOnBackdrop && event?.target === interactionLayerElement.current) hideInteractionLayer();
  }, [interactionLayerElement, hideInteractionLayer, modal, closeOnBackdrop]);
  /**
   * Additional button to render in and/or the header and footer section.
   * @type {JSX.Element}
   */
  const InteractionLayerButton = /*#__PURE__*/React__default.createElement(Button, {
    ...button
  });
  /**
   * The InteractionLayer close button.
   * @type {JSX.Element}
   */
  const InteractionLayerCloseButton = /*#__PURE__*/React__default.createElement("button", {
    className: "c-interaction-layer__close-btn",
    type: "button",
    "aria-label": closeButtonLabel,
    onClick: hideInteractionLayer,
    title: closeButtonLabel
  });
  const divClassNameList = classNames('c-interaction-layer__wrapper', {
    [`c-interaction-layer--${size}`]: size,
    'c-interaction-layer--modal': modal
  }).trim();
  /**
   * Conditionally display a sticky button bar.
   * @returns {JSX.Element}
   */
  const renderButtonBar = () => {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-interaction-layer__button-bar"
    }, /*#__PURE__*/React__default.createElement(ButtonContainer, {
      centerSmall: true,
      noWrap: true,
      reverse: true,
      stretch: true,
      verticalSmall: true
    }, buttonBarButtons));
  };
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React__default.createElement("div", {
      className: divClassNameList,
      "aria-hidden": !isVisible,
      id: id,
      ref: interactionLayerElement,
      onClick: handleBackdropClick,
      "data-testid": testId,
      ...otherProps
    }, /*#__PURE__*/React__default.createElement("div", {
      tabIndex: -1,
      onClick: hideInteractionLayer,
      role: "button",
      "aria-label": closeButtonLabel
    }), /*#__PURE__*/React__default.createElement("div", {
      className: "c-interaction-layer",
      role: "dialog"
    }, /*#__PURE__*/React__default.createElement("header", {
      className: "c-interaction-layer__header"
    }, buttonInHeader ? InteractionLayerButton : InteractionLayerCloseButton), /*#__PURE__*/React__default.createElement("div", {
      className: "c-interaction-layer__content",
      role: "document"
    }, children), button && /*#__PURE__*/React__default.createElement("div", {
      className: "c-interaction-layer__footer"
    }, InteractionLayerButton), buttonBarButtons && renderButtonBar()))
  );
}

/**
 * Bronson ItemTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-item-teaser.html
 * @constructor
 */
function ItemTeaser({
  alternative,
  block,
  blockSmallScreen,
  children,
  className,
  headingLevel = 5,
  icon,
  iconClassName,
  iconDescription,
  inline,
  inlineSmallScreen,
  linkButton,
  linkIcon = IconName.FORWARD,
  linkLabel,
  linkProps,
  row,
  small,
  title,
  testId,
  ...otherProps
}) {
  const articleClassNameList = classNames('c-item-teaser', {
    // Convenience props from Bronson variants.
    'c-item-teaser--small': small,
    'c-item-teaser--inline': inline,
    'c-item-teaser--alternative': alternative,
    'c-item-teaser--row': row,
    'c-item-teaser--block': block,
    'c-item-teaser--inline@small-screen': inlineSmallScreen,
    'c-item-teaser--block@small-screen': blockSmallScreen
  }, className).trim();
  const iClassNameList = classNames('c-item-teaser__icon', iconClassName).trim();
  function renderIfItemTeaserLinkLabel() {
    if (linkLabel) {
      const linkAttrs = linkButton ? {} : {
        link: true,
        simple: true,
        small: true
      };
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-item-teaser__link"
      }, /*#__PURE__*/React__default.createElement(Button, {
        icon: linkIcon === null ? undefined : linkIcon,
        ...linkAttrs,
        ...linkProps
      }, linkLabel));
    }
    return null;
  }
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon
      }), /*#__PURE__*/React__default.createElement("span", {
        className: "c-item-teaser__icon-description u-visually-hidden"
      }, iconDescription));
    }
    return null;
  }
  function renderIfTitle() {
    if (title) {
      return /*#__PURE__*/React__default.createElement(Heading, {
        level: headingLevel,
        className: "c-item-teaser__title"
      }, title);
    }
    return null;
  }
  function renderIfText() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-item-teaser__text"
      }, children);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("article", {
    ...otherProps,
    "data-testid": testId,
    className: articleClassNameList
  }, renderIfIcon(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-item-teaser__body"
  }, renderIfTitle(), renderIfText(), renderIfItemTeaserLinkLabel()));
}

/**
 * The LabeledContentSection is used to add a label to a section of content.
 * Please note that a frame around the content is added for VW6.
 * Note: Please do not mix the LabeledContentSection with the `.o-content-section` helper.
 * The spacing of `.o-content-section` is added to the LabeledContentSection by default.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-labeled-content-section.html
 * @constructor
 */
function LabeledContentSection({
  className,
  children,
  id,
  title,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-labeled-content-section', className).trim();
  /**
   * Internal identifier to improve a11y for the component.
   * @type {string}
   */
  const labeledContentSectionId = `c-labeled-content-section-${id ?? uuidv4()}`;
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-labeled-content-section__label"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-labeled-content-section__label-text",
    id: labeledContentSectionId
  }, title)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-labeled-content-section__content",
    "aria-labelledby": labeledContentSectionId
  }, children));
}

/**
 * Bronson LicensePlate component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-license-plate.html
 * @constructor
 */
function LicensePlate({
  className,
  country,
  countryCode,
  euStars,
  horizontalStripEu,
  horizontalStripJp,
  jp,
  jpPrefectureCode,
  jpRegistrationPrefix,
  jpVehicleCode,
  nl,
  registrationNumber,
  size,
  testId,
  ...otherProps
}) {
  const articleClassNameList = classNames('c-license-plate', {
    [`c-license-plate--${size}`]: size,
    // Convenience prop from Bronson variants.
    'c-license-plate--nl': nl,
    // Convenience prop from Bronson variants.
    'c-license-plate--jp': jp // Convenience prop from Bronson variants.
  }, className).trim();
  function renderIfLicensePlateEuStars() {
    if (euStars) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-license-plate__eu-stars"
      });
    }
    return null;
  }
  function renderIfLicensePlateHorizontalStripEu() {
    if (horizontalStripEu) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-license-plate__horizontal-strip c-license-plate__horizontal-strip--eu"
      }, renderIfLicensePlateEuStars(), /*#__PURE__*/React__default.createElement("abbr", {
        className: "c-license-plate__country-code",
        title: country
      }, countryCode));
    }
    return null;
  }
  function renderIfLicensePlateHorizontalStripJp() {
    if (horizontalStripJp) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-license-plate__horizontal-strip c-license-plate__horizontal-strip--jp"
      }, jpRegistrationPrefix);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("article", {
    ...otherProps,
    "data-testid": testId,
    className: articleClassNameList,
    "aria-label": "License Plate"
  }, renderIfLicensePlateHorizontalStripEu(), renderIfLicensePlateHorizontalStripJp(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-license-plate__registration-number",
    "aria-label": "Registration Number"
  }, jpRegistrationPrefix ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("span", {
    className: "c-license-plate__registration-top-bar"
  }, `${jpPrefectureCode} ${jpVehicleCode}`), /*#__PURE__*/React__default.createElement("span", {
    className: "c-license-plate__registration-bottom-bar"
  }, registrationNumber))) : (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("span", {
    className: "c-license-plate__registration-bottom-bar"
  }, registrationNumber)))));
}

/*
 * Bronson ListInline component.
 */
const ListInline = ({
  children,
  testId,
  ...otherProps
}) => {
  return /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    "data-testid": testId,
    className: "o-list-inline"
  }, children /* Use 'ListInline.Item' component. */);
};
ListInline.displayName = 'ListInline';
/*
 * Bronson Item component (nested).
 */
function Item$2({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, children);
}
Item$2.displayName = 'ListInline.Item';
ListInline.Item = Item$2;

/*
 * Bronson ListUi component.
 */
function ListUi({
  children,
  className,
  ordered,
  testId,
  ...otherProps
}) {
  // tag containing a variable or condition
  const CustomTagListUiElement = ordered ? 'ol' : 'ul';
  const ClassNameList = classNames('c-list-ui', {
    // main class modifier convenience prop extension
    'c-list-ui--ordered ': ordered // Convenience prop from Bronson variants.
  }, className).trim();
  return /*#__PURE__*/React__default.createElement(CustomTagListUiElement, {
    ...otherProps,
    "data-testid": testId,
    className: ClassNameList
  }, children);
}
ListUi.displayName = 'ListUi';
/*
 * Bronson ListUiItem component (nested).
 */
function ListUiItem({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps,
    className: "c-list-ui__item"
  }, children);
}
ListUiItem.displayName = 'ListUi.Item';
ListUi.Item = ListUiItem;

/**
 * Bronson Logo component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-logo.html
 * @constructor
 */
function Logo({
  align,
  alt,
  ariaLabel,
  className,
  custom,
  disableLink,
  height,
  heightSmall,
  onClick,
  src,
  srcSmall,
  title,
  testId,
  ...otherProps
}) {
  /**
   * Conditionally add {@link height}, {@link heightSmall} and/or {@link align} as custom props.
   * @type {React.CSSProperties}
   */
  const logoCustomProps = {
    ...(height && {
      '--logo-image-height': height
    }),
    ...(heightSmall && {
      '--logo-image-height-small': heightSmall
    }),
    ...(align && {
      '--logo-image-align': align
    })
  };
  const aClassNameList = classNames('c-logo', {
    'c-logo--has-image': src && !custom,
    'c-logo--custom': custom
  }, className).trim();
  /**
   * Merge the {@link logoCustomProps} into an existing style prop.
   */
  const logoStyles = {
    ...(otherProps?.style ?? {}),
    ...logoCustomProps
  };
  /**
   * Render the logo image(s).
   */
  function renderIfSrc() {
    if (src) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, srcSmall && /*#__PURE__*/React__default.createElement("img", {
        src: srcSmall,
        alt: alt || '',
        className: "c-logo__image-small"
      }), /*#__PURE__*/React__default.createElement("img", {
        src: src,
        alt: alt || '',
        className: "c-logo__image"
      }));
    } else {
      return null;
    }
  }
  /**
   * Conditionally render a `<span>` as Logo wrapper.
   * @type {string}
   */
  const LogoElement = disableLink ? 'span' : 'a';
  return /*#__PURE__*/React__default.createElement(LogoElement, {
    className: aClassNameList,
    /**
     * Display the `[title]` conditionally depending on
     * if {@link disableLink} is not set or {@link title} is set.
     */
    title: disableLink ? undefined : title,
    "data-testid": testId,
    "aria-label": src ? null : ariaLabel,
    role: disableLink ? undefined : 'link',
    onClick: disableLink ? undefined : onClick,
    ...otherProps,
    style: logoStyles
  }, renderIfSrc());
}

/**
 * The Bronson Logo Banner component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-logo-banner.html
 * @constructor
 */
function LogoBanner({
  backgroundColor,
  className,
  color,
  logoHeight,
  imageProps,
  linkProps,
  prefix,
  row,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-logo-banner', {
    'c-logo-banner--row': row
  }, className).trim();
  const style = {
    '--logo-banner-background-color': backgroundColor,
    '--logo-banner-color': color,
    '--logo-banner-logo-height': logoHeight
  };
  const renderPrefix = () => prefix ? /*#__PURE__*/React__default.createElement("span", {
    className: "c-logo-banner__prefix"
  }, prefix) : null;
  // eslint-disable-next-line jsx-a11y/alt-text
  const renderImage = () => imageProps ? /*#__PURE__*/React__default.createElement("img", {
    ...imageProps,
    className: "c-logo-banner__logo"
  }) : null;
  const renderChildren = () => (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, renderPrefix(), renderImage()));
  const renderContent = () => linkProps ? (/*#__PURE__*/React__default.createElement("a", {
    ...linkProps,
    className: "c-logo-banner__body"
  }, renderChildren())) : (/*#__PURE__*/React__default.createElement("span", {
    className: "c-logo-banner__body"
  }, renderChildren()));
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps,
    style: style
  }, renderContent());
}

/**
 * The MediaGrid component is a grid of {@link MediaGridItem}
 * which are links around images displayed in a grid.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-media-grid.html
 * @constructor
 */
function MediaGrid({
  aspectRatio,
  className,
  children,
  testId,
  ...otherProps
}) {
  const ulClassNameList = classNames('c-media-grid', className).trim();
  return /*#__PURE__*/React__default.createElement("ul", {
    className: ulClassNameList,
    ...otherProps,
    "data-testid": testId,
    "data-aspect-ratio": aspectRatio ?? null
  }, children);
}
MediaGrid.displayName = 'MediaGrid';
/**
 * The MediaGridItem component is internally used to wrap the item content
 * in a link and display it inside the {@link MediaGrid}.
 * @constructor
 */
function MediaGridItem({
  children,
  href,
  label,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-media-grid__item"
  }, /*#__PURE__*/React__default.createElement("a", {
    className: "c-media-grid__link",
    href: href,
    ...otherProps
  }, React__default.Children.map(children, child => /*#__PURE__*/React__default.cloneElement(child, {
    className: 'c-media-grid__image'
  })), label && /*#__PURE__*/React__default.createElement("span", {
    className: "c-media-grid__label"
  }, label)));
}
MediaGridItem.displayName = 'MediaGrid.Item';
MediaGrid.Item = MediaGridItem;

/**
 * Bronson MediaTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-media-teaser.html
 * @constructor
 */
function MediaTeaser({
  buttonLabel,
  buttonModifier,
  buttonProps,
  children,
  className,
  fullLink,
  fullLinkFooter,
  imgProps,
  linkButton,
  onButtonClick,
  pretitle,
  reversed,
  title,
  vertical,
  video,
  testId,
  ...otherProps
}) {
  const sectionClassNameList = classNames('c-media-teaser', {
    // Main class modifier convenience prop extension.
    'c-media-teaser--reversed': reversed,
    // Convenience prop from Bronson variants.
    'c-media-teaser--vertical': vertical,
    // Convenience prop from Bronson variants.
    'c-media-teaser--full-link': fullLink // Convenience prop from Bronson variants.
  }, className).trim();
  const btnClassNameList = classNames({
    'c-media-teaser__link': linkButton && !fullLink
  }, buttonModifier).trim();
  const renderIfFullLink = () => {
    if (fullLink) {
      return fullLinkFooter;
    }
    return null;
  };
  function renderIfButtonLabel() {
    if (buttonLabel) {
      return linkButton ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Button, {
        className: btnClassNameList,
        onClick: onButtonClick,
        element: "a",
        icon: "semantic-forward",
        link: true,
        small: true,
        ...buttonProps
      }, buttonLabel))) : (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Button, {
        className: btnClassNameList,
        onClick: onButtonClick,
        ...buttonProps
      }, buttonLabel)));
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("section", {
    ...otherProps,
    "data-testid": testId,
    className: sectionClassNameList
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-media-teaser__media"
  }, video || imgProps && /*#__PURE__*/React__default.createElement("img", {
    className: "c-media-teaser__image",
    alt: "",
    ...imgProps
  })), /*#__PURE__*/React__default.createElement("div", {
    className: "c-media-teaser__caption"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-media-teaser__pretitle"
  }, pretitle), /*#__PURE__*/React__default.createElement("h3", {
    className: "c-media-teaser__title"
  }, title), /*#__PURE__*/React__default.createElement("div", {
    className: "c-media-teaser__body"
  }, children), /*#__PURE__*/React__default.createElement("div", {
    className: "c-media-teaser__footer"
  }, renderIfFullLink(), renderIfButtonLabel())));
}

/**
 * Bronson Modal component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-modal--modal.html
 * @constructor
 */
function Modal({
  asPortal,
  buttonCancelLabel,
  buttonCancelText,
  buttonCloseLabel,
  buttonConfirmLabel,
  buttonConfirmText,
  buttonConfirmType,
  center,
  children,
  className,
  closeAny,
  confirmationDisabled,
  hideCloseButton,
  hideTitle,
  id,
  large,
  left,
  onCancel,
  onClickOutside,
  onClose,
  onConfirm,
  portalContainer,
  shown,
  status,
  stretch,
  title,
  testId,
  ...otherProps
}) {
  const MODAL_TITLE_ID = uuidv4('bron-modal-title-');
  const modalWrapper = useRef(null);
  const componentModalClass = classNames('c-modal', {
    'c-modal--left': left,
    'c-modal--center': center,
    'c-modal--large': large,
    'c-modal--stretch': stretch,
    [`c-modal--${status}`]: status
  }, className).trim();
  const componentModalTitleWrapperClass = classNames('c-modal__title__wrapper', {
    'u-visually-hidden': hideTitle
  }).trim();
  const onClick = e => {
    if (modalWrapper.current !== e.currentTarget || closeAny) {
      onClose?.();
    }
    e.stopPropagation();
  };
  function renderChildren() {
    if (typeof children === 'string') {
      return /*#__PURE__*/React__default.createElement(Paragraph, null, children);
    }
    return children;
  }
  function renderFooter() {
    /**
     * Conditionally render Modal footer.
     */
    if (!(buttonCancelText || buttonConfirmText)) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-modal__footer"
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "c-modal__actions"
    }, buttonCancelText && (/*#__PURE__*/React__default.createElement("div", {
      className: "c-modal__actions__item"
    }, /*#__PURE__*/React__default.createElement(Button, {
      type: "button",
      onClick: onCancel,
      secondary: true,
      "aria-label": buttonCancelLabel
    }, buttonCancelText))), buttonConfirmText && (/*#__PURE__*/React__default.createElement("div", {
      className: "c-modal__actions__item"
    }, /*#__PURE__*/React__default.createElement(Button, {
      type: buttonConfirmType,
      onClick: closeAny ? onClick : onConfirm,
      "aria-label": buttonConfirmLabel,
      disabled: confirmationDisabled
    }, buttonConfirmText)))));
  }
  function onKeyDown(e) {
    if (e.keyCode === 27) {
      onClose?.();
    }
  }
  if (shown) {
    document.addEventListener('keydown', onKeyDown, {
      capture: false
    });
  } else {
    document.removeEventListener('keydown', onKeyDown, {
      capture: false
    });
  }
  const modal =
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
  React__default.createElement("div", {
    "data-testid": testId,
    className: componentModalClass,
    "aria-hidden": !shown,
    id: id,
    onClick: closeAny ? onClick : onClickOutside,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-modal__overlay",
    tabIndex: -1
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "c-modal__wrapper",
    role: "dialog",
    "aria-labelledby": title ? MODAL_TITLE_ID : undefined,
    onClick: onClick,
    ref: modalWrapper
  }, /*#__PURE__*/React__default.createElement("header", {
    className: "c-modal__header"
  }, title && (/*#__PURE__*/React__default.createElement("div", {
    className: componentModalTitleWrapperClass
  }, status && /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-modal__status-icon",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement(Heading, {
    level: 2,
    className: "c-modal__title",
    id: MODAL_TITLE_ID
  }, title))), !hideCloseButton && (/*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "c-modal__close-btn",
    onClick: onClick,
    "aria-label": buttonCloseLabel,
    title: buttonCloseLabel
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "c-modal__content",
    role: "document"
  }, renderChildren()), renderFooter()));
  return asPortal ? /*#__PURE__*/ReactDOM.createPortal(modal, portalContainer || document.body) : modal;
}

/**
 * Bronson NotificationCenterItem component.
 * @constructor
 */
function NotificationCenterItem({
  children,
  close = true,
  closeLabel,
  datetime,
  markAsReadLabel,
  markAsUnreadLabel,
  onClick,
  onClose,
  onMarkReadUnread,
  showUnreadButton = true,
  statusReadLabel,
  timestamp,
  timestampText,
  title,
  unread,
  testId,
  ...otherProps
}) {
  const articleClassNameList = classNames('c-notification-center__item ', {
    'c-notification-center__item--read ': !unread
  }).trim();
  function renderIfTitle() {
    if (title) {
      return /*#__PURE__*/React__default.createElement("h3", {
        className: "c-notification-center__item__heading"
      }, /*#__PURE__*/React__default.createElement("a", {
        onClick: onClick,
        className: "c-notification-center__item__heading-link"
      }, title));
    }
    return null;
  }
  const handleOnCLose = event => {
    event.nativeEvent.stopImmediatePropagation();
    onClose?.();
  };
  /**
   * Renders an {@link IndicatorDot} to signalize read/unread state.
   * @return {JSX.Element}
   */
  const renderNotificationCenterIndicator = () => {
    if (unread) {
      const args = {
        className: 'c-notification-center__item__status',
        status: 'unread',
        tooltip: (/*#__PURE__*/React__default.createElement(Tooltip, {
          className: "c-indicator-dot__dot",
          content: statusReadLabel
        }, ' '))
      };
      return /*#__PURE__*/React__default.createElement(IndicatorDot, {
        ...args
      });
    }
    return null;
  };
  function renderIfClose() {
    if (close) {
      return /*#__PURE__*/React__default.createElement("button", {
        className: "c-notification-content-center__item__close",
        onClick: handleOnCLose,
        type: "button",
        title: closeLabel
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "c-notification-content-center__item__close-label"
      }, closeLabel));
    }
    return null;
  }
  function renderIfText() {
    if (children) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-notification-center__item__body"
      }, children);
    }
    return null;
  }
  function renderIfTimestamp() {
    if (timestamp) {
      return /*#__PURE__*/React__default.createElement("time", {
        dateTime: datetime,
        className: "c-notification-center__item__timestamp"
      }, timestampText);
    }
    return null;
  }
  function renderIfShowUnreadButton() {
    if (showUnreadButton) {
      return /*#__PURE__*/React__default.createElement(Button, {
        small: true,
        link: true,
        className: "c-notification-center__item__mark-read-toggle",
        onClick: onMarkReadUnread
      }, unread ? markAsReadLabel : markAsUnreadLabel);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("article", {
    ...otherProps,
    "data-testid": testId,
    className: articleClassNameList
  }, renderNotificationCenterIndicator(), renderIfTitle(), renderIfClose(), renderIfText(), renderIfTimestamp(), renderIfShowUnreadButton());
}
NotificationCenterItem.displayName = 'NotificationCenter.Item';

const notClosed = item => !item.closed;
const mouseEvent = 'click';
/**
 * Bronson NotificationCenter component.
 * @constructor
 */
function NotificationCenter({
  children,
  closeButtonLabel = 'CLOSE',
  defaultOpen = false,
  notifications,
  panelHeading,
  testId,
  toggleLabel,
  ...otherProps
}) {
  const ncPanelRef = useRef(null);
  const [open, setOpen] = useState(defaultOpen);
  const [itemsToShow, setItemsToShow] = useState([]);
  useEffect(() => {
    setItemsToShow(notifications?.filter(notClosed) ?? []);
  }, [notifications]);
  const countUnread = itemsToShow && itemsToShow.filter(item => !item.read).length;
  useEffect(() => {
    const handleClick = event => {
      if (ncPanelRef.current?.contains(event.target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener(mouseEvent, handleClick, {
      capture: true
    });
    return () => {
      document.removeEventListener(mouseEvent, handleClick, {
        capture: true
      });
    };
  }, []);
  const onToggleOpen = event => {
    event.nativeEvent.stopImmediatePropagation();
    setOpen(newOpen => !newOpen);
  };
  const updateItem = (item, items) => items.map(i => i.id === item.id ? {
    ...item
  } : i);
  const notificationCenterClassNameList = classNames('c-notification-center', {
    'is-active': open
  }).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: notificationCenterClassNameList
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: onToggleOpen,
    className: "c-notification-center__toggle",
    type: "button",
    "aria-expanded": open
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-notification-center__toggle__label"
  }, toggleLabel), countUnread > 0 && (/*#__PURE__*/React__default.createElement(Badge, {
    modifier: "notification",
    wrapperClass: "c-notification-center__toggle__badge"
  }, countUnread))), itemsToShow?.length > 0 && (/*#__PURE__*/React__default.createElement("section", {
    className: "c-notification-center__panel",
    "aria-hidden": !open,
    ref: ncPanelRef
  }, /*#__PURE__*/React__default.createElement("header", {
    className: "c-notification-center__panel__header"
  }, /*#__PURE__*/React__default.createElement("h2", {
    className: "c-notification-center__panel__heading"
  }, panelHeading), /*#__PURE__*/React__default.createElement(Button, {
    onClick: onToggleOpen,
    className: "c-notification-center__panel__close",
    link: true,
    small: true,
    simple: true,
    iconViaCss: true
  }, closeButtonLabel)), itemsToShow.map(n => {
    const notificationCenterItem = children?.(n);
    /**
     * Overload NotificationCenterItemProps with custom callbacks due to
     * ITS implementation of the callbacks in first place. :(
     */
    const notificationCenterItemProps = notificationCenterItem?.props;
    if (/*#__PURE__*/isValidElement(notificationCenterItem)) {
      return /*#__PURE__*/React__default.cloneElement(notificationCenterItem, {
        key: n.id,
        unread: !n.read,
        title: n.title,
        onClose: () => {
          notificationCenterItemProps?.onClose?.(n);
          // eslint-disable-next-line no-param-reassign
          n.closed = true;
          setItemsToShow(items => items.filter(notClosed));
        },
        onClick: () => {
          notificationCenterItemProps?.onClick?.(n);
          setOpen(false);
        },
        onMarkReadUnread: () => {
          notificationCenterItemProps?.onMarkReadUnread?.(n);
          // eslint-disable-next-line no-param-reassign
          n.read = !n.read;
          setItemsToShow(items => updateItem(n, items));
        }
      });
    }
    return null;
  }))));
}
NotificationCenter.Item = NotificationCenterItem;

/**
 * Bronson NotificationsGroup component.
 * Groups all related {@link Notification}.
 * @constructor
 */
function NotificationsGroup({
  children,
  testId,
  ...otherProps
}) {
  const groupClassName = classNames('c-notifications-group', 'js-is-ready',
  // Used in Bronson CSS.
  'is-stacked').trim();
  return /*#__PURE__*/React__default.createElement("section", {
    className: groupClassName,
    "data-testid": testId,
    ...otherProps
  }, children);
}
/**
 * Bronson Notification component (nested).
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-notification.html
 */
function Notification({
  buttons = null,
  children,
  className,
  closeLabel = 'Close Notification',
  context,
  onCloseClick,
  showCloseButton = true,
  status,
  text,
  title,
  visible,
  testId,
  ...otherProps
}) {
  const containerClassName = classNames('c-notification__container', 'js-is-ready' // Used in Bronson CSS.
  ).trim();
  const divClassName = classNames('c-notification', {
    'c-notification--context': context,
    [`c-notification--${status}`]: status
  }, className).trim();
  function renderButtons() {
    if (buttons) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-notification__buttons"
      }, buttons);
    }
    return null;
  }
  function renderCloseButton() {
    if (showCloseButton) {
      return /*#__PURE__*/React__default.createElement("button", {
        className: "c-notification__close",
        title: closeLabel,
        type: "button",
        onClick: onCloseClick
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "u-visually-hidden"
      }, closeLabel));
    }
    return null;
  }
  function renderIfStatus() {
    return status ? /*#__PURE__*/React__default.createElement(Icon, {
      className: "c-notification__status-icon",
      viaCss: true
    }) : null;
  }
  function renderIfTitle() {
    return title ? /*#__PURE__*/React__default.createElement("h3", {
      className: "c-notification__title"
    }, title) : null;
  }
  function renderText() {
    /**
     * If {@link text} is set, render it as content for `.c-notification__text`
     * and render {@link children} as sibling elements of `.c-notification__text`.
     * Else render out {@link children} as content for `.c-notification__text` only.
     * @DEPRECATED: Replace with `<div className="c-notification__text">{children}</div>` in v11.
     */
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
      className: "c-notification__text"
    }, text || children), text ? children : '');
  }
  return visible ? (/*#__PURE__*/React__default.createElement("div", {
    className: containerClassName,
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement("div", {
    className: divClassName,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-notification__outer-wrap"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-notification__inner-wrap"
  }, renderIfStatus(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-notification__content"
  }, renderIfTitle(), renderText(), renderButtons()), renderCloseButton()))))) : null;
}

/**
 * The OrderedList component is an ordinary `<ol>` element,
 * used for normal numeration lists in editorial content.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-ordered-list.html
 * @constructor
 */
const OrderedList = ({
  children,
  nestedCounter,
  testId,
  ...otherProps
}) => {
  const olClassNameList = classNames({
    'c-ol': !nestedCounter,
    'c-list-nested-counter': nestedCounter
  }).trim();
  return /*#__PURE__*/React__default.createElement("ol", {
    ...otherProps,
    "data-testid": testId,
    className: olClassNameList
  }, children);
};
OrderedList.displayName = 'OrderedList';
/**
 * The Item component (nested) is internally used to wrap
 * the item content and display it inside the {@link OrderedList}.
 */
function Item$1({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, children);
}
Item$1.displayName = 'OrderedList.Item';
OrderedList.Item = Item$1;

/*
 * Bronson Pagination component.
 */
function Pagination({
  children,
  nextDisabled,
  nextText,
  onNextClick,
  onPrevClick,
  prevDisabled,
  prevText,
  title,
  testId,
  ...otherProps
}) {
  const aClassNameList = classNames({
    'c-pagination__link ': true,
    'c-pagination__link--stepper-prev ': true,
    'is-disabled ': prevDisabled
  }).trim();
  const aClassNameList2 = classNames({
    'c-pagination__link ': true,
    'c-pagination__link--stepper-next ': true,
    'is-disabled ': nextDisabled
  }).trim();
  function renderIfNotPrevDisabled() {
    return prevDisabled ? null : (/*#__PURE__*/React__default.createElement("a", {
      href: "#",
      className: aClassNameList,
      title: prevText,
      onClick: onPrevClick
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, prevText)));
  }
  function renderIfPrevDisabled() {
    return prevDisabled ? (/*#__PURE__*/React__default.createElement("span", {
      className: aClassNameList,
      title: prevText
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, prevText))) : null;
  }
  function renderIfNotNextDisabled() {
    return nextDisabled ? null : (/*#__PURE__*/React__default.createElement("a", {
      href: "#",
      onClick: onNextClick,
      className: aClassNameList2,
      title: nextText
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, nextText)));
  }
  function renderIfNextDisabled() {
    return nextDisabled ? (/*#__PURE__*/React__default.createElement("span", {
      className: aClassNameList2,
      title: nextText
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, nextText))) : null;
  }
  return /*#__PURE__*/React__default.createElement("nav", {
    ...otherProps,
    "data-testid": testId,
    className: "c-pagination",
    "aria-label": title
  }, /*#__PURE__*/React__default.createElement("ul", {
    className: "c-pagination__list"
  }, /*#__PURE__*/React__default.createElement("li", {
    className: "c-pagination__item"
  }, renderIfNotPrevDisabled(), renderIfPrevDisabled()), children /* Use 'Pagination.Item' component. */, /*#__PURE__*/React__default.createElement("li", {
    className: "c-pagination__item"
  }, renderIfNotNextDisabled(), renderIfNextDisabled())));
}
Pagination.displayName = 'Pagination';
/*
 * Bronson List component (nested).
 */
function PaginationItem({
  active,
  children,
  disabled,
  onClick,
  ...otherProps
}) {
  const aClassNameList = classNames({
    'c-pagination__link ': true,
    'is-active ': active,
    'is-disabled ': disabled
  }).trim();
  function renderIfNotActiveAndNotDisabled() {
    return !active && !disabled ? (/*#__PURE__*/React__default.createElement("a", {
      href: "#",
      onClick: onClick,
      className: aClassNameList
    }, children)) : null;
  }
  function renderIfActiveOrDisabled() {
    return active || disabled ? (/*#__PURE__*/React__default.createElement("span", {
      className: aClassNameList,
      "aria-current": active ? 'page' : false
    }, children)) : null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps,
    className: "c-pagination__item"
  }, renderIfNotActiveAndNotDisabled(), renderIfActiveOrDisabled());
}
PaginationItem.displayName = 'Pagination.Item';
Pagination.Item = PaginationItem;

/*
 * Bronson Progress component.
 */
function Progress({
  className,
  error,
  success,
  value,
  testId,
  ...otherProps
}) {
  const progressClassNameList = classNames('c-progress', {
    // main class modifier convenience prop extension
    'is-success ': success,
    // Convenience prop from Bronson variants.
    'is-error ': error // Convenience prop from Bronson variants.
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("progress", {
    ...otherProps,
    "data-testid": testId,
    className: progressClassNameList,
    max: "100",
    value: value
  });
}

/**
 * Bronson ProductCard component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-card.html
 * @constructor
 */
function ProductCard({
  buttons,
  carousel,
  defaultExpanded,
  disabled,
  empty,
  expandable,
  expandableContent,
  expandableLinkLabel,
  expandableText,
  expandableTitle,
  expandableTriggerLabel,
  favoriteButton,
  footnotes,
  onClick,
  meta,
  priceBox,
  productSubtitle,
  productTitle,
  progressLabel,
  progressValue,
  status,
  tag,
  tagList,
  tagListTitle,
  text,
  testId,
  ...otherProps
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const classNameList = classNames('c-product-card', {
    'c-product-card--empty': empty,
    // Convenience prop from Bronson variants.
    'c-product-card--inactive': disabled // Convenience prop from Bronson variants.
  }).trim();
  function renderIfHeader() {
    if (favoriteButton || tag || tagList) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__header"
      }, renderIfFeatures(), renderIfTagList());
    }
    return null;
  }
  function renderIfFeatures() {
    if (favoriteButton || tag) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__features"
      }, renderIfFavoriteButton(), renderIfTag());
    }
    return null;
  }
  function renderIfFavoriteButton() {
    if (favoriteButton) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__features__item c-product-card__features__item--fav-button"
      }, favoriteButton);
    }
    return null;
  }
  function renderIfTag() {
    if (tag) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__features__item c-product-card__features__item--tag"
      }, tag);
    }
    return null;
  }
  function renderIfTagList() {
    if (tagList) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__tag-list"
      }, tagListTitle && /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__tag-list__title"
      }, tagListTitle), tagList);
    }
    return null;
  }
  function renderIfSubtitle() {
    if (productSubtitle) {
      return /*#__PURE__*/React__default.createElement("h5", {
        className: "c-product-card__subtitle"
      }, productSubtitle);
    }
    return null;
  }
  function renderIfProgress() {
    if (progressValue) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__progress__wrapper"
      }, /*#__PURE__*/React__default.createElement(Progress, {
        className: "c-product-card__progress",
        value: progressValue
      }), progressLabel && /*#__PURE__*/React__default.createElement("p", {
        className: "c-product-card__progress__label"
      }, progressLabel));
    }
    return null;
  }
  function renderIfBody() {
    if (text) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__body"
      }, typeof text === 'string' ? /*#__PURE__*/React__default.createElement(Paragraph, null, text) : text);
    }
    return null;
  }
  function renderIfTitle() {
    if (expandableTitle) {
      return /*#__PURE__*/React__default.createElement("h5", {
        className: "c-product-card__expandable-panel__title"
      }, expandableTitle);
    }
    return null;
  }
  function renderIfText() {
    if (expandableText) {
      return /*#__PURE__*/React__default.createElement("p", {
        className: "c-product-card__expandable-panel__text"
      }, expandableText);
    }
    return null;
  }
  function renderIfExpandable() {
    if (expandable) {
      const expandablePanelId = uuidv4('bron-expandable-panel-');
      const expandableClassNameList = classNames('c-product-card__expandable', {
        'is-open': expanded
      }).trim();
      const expandableTriggerClassNameList = classNames('js-card-expandable__trigger' // Used in Bronson CSS.
      ).trim();
      return /*#__PURE__*/React__default.createElement("div", {
        className: expandableClassNameList
      }, /*#__PURE__*/React__default.createElement(Button, {
        link: true,
        small: true,
        simple: true,
        className: expandableTriggerClassNameList,
        icon: "semantic-expand",
        "aria-controls": expandablePanelId,
        "aria-expanded": expanded,
        onClick: () => setExpanded(prevExpanded => !prevExpanded),
        type: "button"
      }, expandableTriggerLabel), /*#__PURE__*/React__default.createElement("div", {
        id: expandablePanelId,
        className: "c-product-card__expandable-panel",
        "aria-hidden": !expanded,
        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
        inert: expanded ? null : ''
      }, renderIfTitle(), renderIfText(), expandableContent, expandableLinkLabel && (/*#__PURE__*/React__default.createElement(Button, {
        element: "a",
        icon: "semantic-forward",
        link: true,
        small: true,
        onClick: onClick
      }, expandableLinkLabel))));
    }
    return null;
  }
  function renderIfFootnotes() {
    if (footnotes) {
      return footnotes;
    }
    return null;
  }
  /**
   * Conditionally render a status message.
   * @returns {JSX.Element|null}
   */
  function renderIfStatus() {
    if (status) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__status"
      }, status);
    }
    return null;
  }
  function renderFallbackImage() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-product-card__media-fallback o-ratio o-ratio--16:9",
      "aria-hidden": "true"
    }, /*#__PURE__*/React__default.createElement(Icon, {
      className: "c-product-card__media-fallback__icon  o-ratio__content",
      name: "car"
    }));
  }
  function renderIfPricing() {
    if (priceBox || progressValue) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-card__inline c-product-card__pricing"
      }, priceBox, renderIfProgress());
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("article", {
    className: classNameList,
    "data-container-query": "product-card",
    "data-testid": testId,
    ...otherProps
  }, !empty && renderIfHeader(), !empty && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-product-card__inline c-product-card__meta"
  }, productTitle && /*#__PURE__*/React__default.createElement("h4", {
    className: "c-product-card__title"
  }, productTitle), renderIfSubtitle(), meta)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-card__inline c-product-card__media"
  }, empty ? renderFallbackImage() : disabled ? carousel : carousel), !empty && renderIfPricing(), renderIfBody(), renderIfStatus(), buttons && (/*#__PURE__*/React__default.createElement("div", {
    className: "c-product-card__buttons"
  }, buttons?.map((button, index) => {
    const cssClasses = classNames('c-product-card__buttons__item', button.props.className);
    const keyIdx = `card-button-${index}`;
    return /*#__PURE__*/React__default.cloneElement(button, {
      className: cssClasses,
      key: keyIdx
    });
  }))), renderIfExpandable(), renderIfFootnotes());
}

/**
 * Bronson ProductInfo component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-info.html
 * @constructor
 */
function ProductInfo({
  caption,
  children,
  className,
  media,
  title,
  titleLevel = 3,
  titleSuffix,
  titleTagline,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames('c-product-info', className).trim();
  function renderIfTitle() {
    const HeadingElement = `h${titleLevel}`;
    if (title) {
      return /*#__PURE__*/React__default.createElement("hgroup", {
        className: "c-product-info__header"
      }, /*#__PURE__*/React__default.createElement(HeadingElement, {
        className: "c-product-info__title"
      }, title, titleSuffix && /*#__PURE__*/React__default.createElement("span", {
        className: "u-heading-alternative"
      }, titleSuffix)), titleTagline && /*#__PURE__*/React__default.createElement("p", {
        className: "c-product-info__subtitle"
      }, titleTagline));
    }
    return null;
  }
  function renderIfMediaElement() {
    if (media) {
      /**
       * Check if the media element is an image and assign a proper class to it
       */
      if (media.type === 'img') {
        return /*#__PURE__*/React__default.cloneElement(media, {
          ...media.props,
          ...{
            className: classNames('c-product-info__image', media?.props?.className)
          }
        });
      }
      return media;
    }
    return null;
  }
  function renderIfCaption() {
    if (caption) {
      return /*#__PURE__*/React__default.createElement("figcaption", {
        className: "c-product-info__caption"
      }, caption);
    }
    return null;
  }
  function renderIfMedia() {
    if (media) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-product-info__media"
      }, /*#__PURE__*/React__default.createElement("figure", {
        className: "c-product-info__figure",
        role: "group"
      }, renderIfMediaElement(), renderIfCaption()));
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderIfTitle(), renderIfMedia(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-info__aside"
  }, children));
}

/*
 * Bronson ProductInfoSummary component.
 */
function ProductInfoSummary({
  className,
  compact,
  imgAlt,
  imgProps,
  imgSizes,
  imgSrc,
  imgSrcSet,
  infoLabel,
  infoValue,
  subtitle,
  title,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames({
    'c-product-info-summary ': true,
    // main class modifier convenience prop extension
    'c-product-info-summary--compact ': compact // Convenience prop from Bronson variants.
  }, className).trim();
  function renderIfProductInfoSummaryImg() {
    if (imgSrc || imgSrcSet) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-product-info-summary__img"
      }, /*#__PURE__*/React__default.createElement("img", {
        src: imgSrc,
        srcSet: imgSrcSet,
        sizes: imgSizes,
        alt: imgAlt,
        ...imgProps
      }));
    }
    return null;
  }
  function renderIfProductInfoSummarySubtitle() {
    if (subtitle) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-product-info-summary__subtitle"
      }, subtitle);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderIfProductInfoSummaryImg(), /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-info-summary__heading"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-info-summary__title"
  }, title), renderIfProductInfoSummarySubtitle()), /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-info-summary__info"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-info-summary__info-label"
  }, infoLabel), /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-info-summary__info-value"
  }, infoValue)));
}

function ProductSearch({
  children,
  sidebar,
  className,
  ...otherProps
}) {
  const sectionClassName = classNames('c-product-search', {
    'c-product-search--has-sidebar': sidebar
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("section", {
    className: sectionClassName,
    ...otherProps
  }, children);
}
ProductSearch.displayName = 'ProductSearch';
function ProductSearchLayoutHeader({
  children
}) {
  return /*#__PURE__*/React__default.createElement("header", {
    className: "c-product-search__header"
  }, children);
}
function ProductSearchLayoutSidebar({
  children
}) {
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search__sidebar"
  }, children));
}
function ProductSearchLayoutResults({
  children
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search__results"
  }, children);
}
function ProductSearchLayoutAside({
  children
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search__aside"
  }, children);
}
function ProductSearchLayoutFooter({
  children
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search__footer"
  }, children);
}
ProductSearchLayoutHeader.displayName = 'ProductSearch.LayoutHeader';
ProductSearch.LayoutHeader = ProductSearchLayoutHeader;
ProductSearchLayoutSidebar.displayName = 'ProductSearch.LayoutSidebar';
ProductSearch.LayoutSidebar = ProductSearchLayoutSidebar;
ProductSearchLayoutResults.displayName = 'ProductSearch.LayoutResults';
ProductSearch.LayoutResults = ProductSearchLayoutResults;
ProductSearchLayoutAside.displayName = 'ProductSearch.LayoutAside';
ProductSearch.LayoutAside = ProductSearchLayoutAside;
ProductSearchLayoutFooter.displayName = 'ProductSearch.LayoutFooter';
ProductSearch.LayoutFooter = ProductSearchLayoutFooter;

function ProductSearchFilter({
  className,
  children,
  ...otherProps
}) {
  const divClassName = classNames('c-product-search-filter', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    className: divClassName
  }, children);
}

/**
 * The SectionHeading component shows headings in a consistent manner.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-section-headings--section-headings.html
 */
function SectionHeading({
  alternative,
  center,
  children,
  className,
  icon,
  infoIcon,
  level,
  primary,
  smallSpacing,
  subtitle,
  superscript,
  titleSecondary,
  testId,
  ...otherProps
}) {
  const headerClassNameList = classNames('c-section-heading', {
    'c-section-heading--center ': center,
    'c-section-heading--primary ': primary,
    'c-section-heading--small-spacing ': smallSpacing,
    'c-section-heading--alternative ': alternative
  }, className).trim();
  // tag containing a variable or condition
  const CustomTagSectionHeadingsLevel = `h${level}`;
  const renderTitleIcon = () => {
    const iconClassNameList = classNames('c-section-heading__icon');
    return /*#__PURE__*/React__default.createElement(Icon, {
      className: iconClassNameList,
      name: icon
    });
  };
  function renderIfSectionHeadingsTitle() {
    if (children) {
      return /*#__PURE__*/React__default.createElement(CustomTagSectionHeadingsLevel, {
        className: "c-section-heading__title"
      }, icon && renderTitleIcon(), alternative && titleSecondary ? (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("span", {
        className: "c-section-heading__title-text"
      }, children), /*#__PURE__*/React__default.createElement("span", {
        className: "c-section-heading__title-text u-heading-alternative"
      }, titleSecondary, superscript && /*#__PURE__*/React__default.createElement("sup", null, superscript)))) : (/*#__PURE__*/React__default.createElement("span", {
        className: "c-section-heading__title-text"
      }, children, superscript && /*#__PURE__*/React__default.createElement("sup", null, superscript))), infoIcon);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("header", {
    className: headerClassNameList,
    "data-testid": testId,
    ...otherProps
  }, renderIfSectionHeadingsTitle(), subtitle && /*#__PURE__*/React__default.createElement("p", {
    className: "c-section-heading__subtitle"
  }, subtitle));
}

/**
 * The ProductSearchForm component is meant to be used as
 * an initial search method, that leads into a search results page.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-search-form.html
 * @constructor
 */
function ProductSearchForm({
  children,
  className,
  title,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-product-search-form', className).trim();
  return /*#__PURE__*/React__default.createElement("article", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(SectionHeading, {
    center: true,
    level: 2,
    className: "c-product-search-form__heading"
  }, title), children);
}
/**
 * The ProductSearchFormActionButtons component is internally used to wrap
 * the action buttons and display it inside the {@link ProductSearchForm}.
 * @constructor
 */
function ProductSearchFormActionButtons({
  className,
  filterButton,
  resetButton,
  submitButton,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-product-search-form__action-buttons', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, filterButton, /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-form__button-container"
  }, resetButton, submitButton));
}
ProductSearchFormActionButtons.displayName = 'ProductSearchForm.ActionButtons';
ProductSearchForm.ActionButtons = ProductSearchFormActionButtons;

/**
 * Bronson TagList component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tag-list.html
 * @constructor
 */
function TagList({
  children,
  className,
  testId,
  ...otherProps
}) {
  const ulClassName = classNames('c-tag-list', className).trim();
  function renderChildren() {
    return React__default.Children.map(children, child => {
      if (!child) {
        return null;
      }
      return /*#__PURE__*/React__default.createElement("li", null, child);
    });
  }
  return /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    "data-testid": testId,
    className: ulClassName
  }, renderChildren());
}

/**
 * The ProductSearchHeader component is used inside the {@link ProductSearch} to provide a list
 * of activated filters from the {@link ProductSearchFilter} component.
 * It also contains configurable action buttons for sorting and other functionalities.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-search-header.html
 * @constructor
 */
function ProductSearchHeader({
  actionsButtons,
  actionsSort,
  className,
  filterLabelId,
  filterLabelText,
  filterPlaceholderLabel,
  filterResetLabel,
  filterToggleLabel,
  hasFilterToggle,
  resetFilterHandler,
  sidebar,
  sticky,
  tags,
  title,
  titleLevel = 1,
  toggleFilterHandler,
  testId,
  ...otherProps
}) {
  const [area, setArea] = useState(null);
  const ref = useRef(null);
  const parentRef = useRef(null);
  const floatGrid = useFloatGrid(area);
  const divClassName = classNames('c-product-search-header', {
    'c-product-search-header--sticky': sticky
  }, className).trim();
  const productSearchHeaderFilterBarClassNameList = classNames('c-product-search-header__filter-bar', {
    'c-product-search-header__filter-bar--sidebar': sidebar
  }).trim();
  useEffect(() => {
    if (!area) {
      setTimeout(() => {
        // Get custom prop from CSS.
        setArea(getFloatGridCustomProp(ref?.current));
      }, 0);
    }
  }, [ref, area]);
  /**
   * Track the section container.
   */
  useIntersectionObserver({
    ref,
    target: parentRef,
    onIntersectionHandler: ScrollObserverCore.observeEntries,
    opts: {
      intersectionObserverOpts: {},
      classes: ScrollObserverCore.classes
    }
  });
  /**
   * The button to show/hide the {@link ProductSearchFilter} inside an {@link InteractionLayer}.
   */
  const ProductSearchHeaderFilterToggle = /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__filter-toggle"
  }, /*#__PURE__*/React__default.createElement(Button, {
    icon: "semantic-filter",
    iconReversed: true,
    type: "button",
    onClick: toggleFilterHandler
  }, filterToggleLabel));
  /**
   * Sticky/FloatGrid positioned {@link ProductSearchHeaderFilterToggle}.
   */
  const ProductSearchHeaderFilterBar = /*#__PURE__*/React__default.createElement("div", {
    className: productSearchHeaderFilterBarClassNameList,
    ref: ref
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__filter-bar__inner"
  }, title ? /*#__PURE__*/React__default.createElement("h4", {
    className: "c-product-search-header__filter-bar__title"
  }, title) : null, hasFilterToggle && ProductSearchHeaderFilterToggle));
  /**
   * Contains the active filter tags and an optional reset button.
   */
  const ProductSearchHeaderFilter = /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__filter"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-product-search-header__filter__label",
    id: filterLabelId
  }, filterLabelText), /*#__PURE__*/React__default.createElement(TagList, {
    className: "c-product-search-header__filter__tags",
    "aria-describedby": filterLabelId,
    role: "group"
  }, tags), /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__filter__reset"
  }, /*#__PURE__*/React__default.createElement(Button, {
    link: true,
    onClick: resetFilterHandler
  }, filterResetLabel)));
  const ProductSearchHeaderActions = /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__actions"
  }, actionsButtons?.map(actionButton => (/*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__actions__item",
    key: actionButton.key
  }, actionButton))), /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__actions__item  c-product-search-header__actions__item--wrap"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__sort"
  }, actionsSort)));
  return /*#__PURE__*/React__default.createElement("section", {
    ...otherProps,
    className: divClassName,
    ref: parentRef,
    "data-testid": testId
  }, sticky && (area && floatGrid ? /*#__PURE__*/createPortal(ProductSearchHeaderFilterBar, floatGrid) : ProductSearchHeaderFilterBar), (title || hasFilterToggle) && (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, title ? (/*#__PURE__*/React__default.createElement(SectionHeading, {
    className: "c-product-search-header__title",
    alternative: false,
    level: titleLevel
  }, title)) : (/*#__PURE__*/React__default.createElement("div", null)), hasFilterToggle && ProductSearchHeaderFilterToggle)), /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-search-header__body"
  }, !tags?.length && filterPlaceholderLabel, !!tags?.length && ProductSearchHeaderFilter, ProductSearchHeaderActions));
}

/**
 * Bronson ProductTable component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-table.html
 * @constructor
 */
function ProductTable({
  children,
  className,
  headingLevel = 3,
  info,
  summary,
  title,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-product-table', className).trim();
  const CustomProductTableHeadingLevel = `h${headingLevel}`;
  const productTableHeading = /*#__PURE__*/React__default.createElement(CustomProductTableHeadingLevel, {
    className: "c-product-table__title"
  }, title);
  const productTableSummary = /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-table__section  c-product-table__section--summary"
  }, summary);
  const productTableInfo = /*#__PURE__*/React__default.createElement("div", {
    className: "c-product-table__information"
  }, info);
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, title && productTableHeading, children, summary && productTableSummary, info && productTableInfo);
}
ProductTable.displayName = 'ProductTable';
/**
 * Bronson ProductTableSection (nested).
 * @internal
 * @constructor
 */
function ProductTableSection({
  children,
  className,
  innerHeadingLevel = 4,
  innerTitle
}) {
  const classNameList = classNames('c-product-table__section', className).trim();
  const CustomProductTableSectionHeadingLevel = `h${innerHeadingLevel}`;
  const productTableSectionHeading = /*#__PURE__*/React__default.createElement(CustomProductTableSectionHeadingLevel, {
    className: "c-product-table__inner-title"
  }, innerTitle);
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList
  }, innerTitle && productTableSectionHeading, children);
}
ProductTableSection.displayName = 'ProductTable.Section';
ProductTable.Section = ProductTableSection;
/**
 * Bronson ProductTableComment (nested).
 * @internal
 * @constructor
 */
function ProductTableComment({
  children,
  className,
  type
}) {
  const classNameList = classNames({
    // Convenience props from Bronson.
    'c-product-table__item-term-comment': type === 'term',
    'c-product-table__item-detail-comment': type === 'detail'
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("span", {
    className: classNameList
  }, children);
}
ProductTableComment.displayName = 'ProductTable.Comment';
ProductTable.Comment = ProductTableComment;

/**
 * Bronson RadioButton component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-radio-button.html
 * @constructor
 */
function RadioButton({
  children,
  className,
  id,
  name,
  testId,
  ...otherProps
}) {
  const labelClassNameList = classNames('c-radio', className).trim();
  return /*#__PURE__*/React__default.createElement("label", {
    className: labelClassNameList,
    htmlFor: id
  }, /*#__PURE__*/React__default.createElement("input", {
    id: id,
    className: "c-radio__input",
    type: "radio",
    name: `${name || 'radio-button'}`,
    "data-testid": testId,
    ...otherProps
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-radio__label"
  }, children));
}

/*
 * Bronson ResponsiveImage component.
 */
function ResponsiveImage({
  src,
  srcSet,
  sizes,
  alt,
  testId,
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("figure", {
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement("img", {
    src: src,
    srcSet: srcSet,
    sizes: sizes,
    alt: alt,
    ...otherProps
  }), children && /*#__PURE__*/React__default.createElement("figcaption", null, children));
}

/**
 * The Section Nav is an on-page navigation pattern to allow for quick navigation to certain
 * collated/grouped sections on the page. It also acts as an orientation pattern as it highlights
 * the currently most visible section inside its navigation list.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-section-nav.html
 * @constructor
 */
function SectionNav({
  button,
  className,
  children,
  defaultSectionLabel,
  /**
   * The overflowThreshold defines an additional amount of pixels
   * an element must pass before it is being considered to be
   * in the parent’s scroll overflow.
   */
  overflowThreshold = 10,
  /**
   * Defines the time to wait after scrolling the list. Sometimes it
   * could be beneficial if a larger time is set in order make
   * the scrolling happen more prominently.
   */
  autoScrollDelay = 250,
  scrollObserverTargetRef,
  scrollObserverSectionRefs,
  reveal,
  testId,
  ...otherProps
}) {
  const [isActiveItem, setIsActiveItem] = useState(null);
  const [hasOverFlowLeft, setHasOverFlowLeft] = useState(false);
  const [hasOverFlowRight, setHasOverFlowRight] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(null);
  const [label, setLabel] = useState(null);
  /**
   * Refs
   * @type {React.MutableRefObject<null>}
   */
  const sectionNavRef = useRef(null);
  const sectionNavContainerRef = useRef(null);
  const sectionNavListRef = useRef(null);
  const activeItemRef = useRef(null);
  const sectionNavClassNames = classNames('c-section-nav', {
    'c-section-nav--reveal': reveal
  }, className).trim();
  const sectionNavListParentClassNames = classNames('c-section-nav__scroll', {
    'has-overflow-left': hasOverFlowLeft,
    'has-overflow-right': hasOverFlowRight
  }).trim();
  const sectionNavContainerClassNames = classNames('c-section-nav__container', {
    'is-visible': isExpanded
  }).trim();
  /**
   * Hook to disable scrolling the page when the scroll container is expanded on small viewports.
   */
  useEffect(() => {
    document.body.classList.toggle('c-section-nav-prevent-scroll', isExpanded);
  }, [isExpanded]);
  /**
   * Checks if the link is in the scroll list overflow under the following conditions:
   * [1] If the link is completely/partially in overflow on the right-hand side.
   * [2] If the link is completely/partially in overflow on the right-hand side.
   * @param {HTMLLinkElement} link
   * @param {HTMLUListElement} list
   * @return {boolean}
   */
  const checkLinkOverflow = (link, list) => {
    if (link && list) {
      return link.offsetLeft - list.scrollLeft >= list.offsetWidth || link.offsetLeft <= list.scrollLeft /* [2] */ || link.offsetLeft + link.clientWidth >= list.offsetWidth; /* [2] */
    }
    return false;
  };
  /**
   * Checks if the scroll list has an overflow to either horizontal side.
   */
  const checkListOverflow = useCallback(element => {
    const {
      clientWidth,
      scrollLeft,
      scrollWidth
    } = element;
    /**
     * If the target has a scroll overflow on the left-hand side.
     */
    if (scrollLeft > overflowThreshold) {
      setHasOverFlowLeft(true);
    } else {
      setHasOverFlowLeft(false);
    }
    /**
     * If the target has a scroll overflow on the right-hand side.
     */
    if (scrollWidth - clientWidth > scrollLeft) {
      setHasOverFlowRight(true);
    } else {
      setHasOverFlowRight(false);
    }
  }, [overflowThreshold]);
  /**
   * Update scrollOffset.
   */
  useEffect(() => {
    if (sectionNavListRef?.current) {
      const offset = parseFloat(window.getComputedStyle(sectionNavListRef?.current, '::before')?.width ?? 0);
      checkListOverflow(sectionNavListRef?.current);
      if (offset >= 0) {
        setScrollOffset(offset);
      }
    }
  }, [checkListOverflow, sectionNavListRef]);
  /**
   * Autoscroll an active item into view.
   */
  useEffect(() => {
    /**
     * Check if the list item is visible inside the scrollable list.
     * @type {boolean}
     */
    const listItemInOverflow = checkLinkOverflow(activeItemRef?.current, sectionNavListRef?.current);
    /**
     * Only scroll an item into the view that is activated, and
     * we are not in a scroll movement to avoid scroll cancellation
     * in Chrome.
     */
    if (activeItemRef?.current && listItemInOverflow) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          sectionNavListRef?.current?.scrollBy({
            top: 0,
            left: (activeItemRef?.current?.offsetLeft ?? 0) - (sectionNavListRef?.current?.scrollLeft ?? 0) - (scrollOffset ?? 0),
            behavior: 'smooth'
          });
        });
      }, autoScrollDelay);
    }
  }, [isActiveItem, scrollOffset, activeItemRef, autoScrollDelay]);
  /**
   * Track the section container.
   */
  useIntersectionObserver({
    ref: sectionNavRef,
    target: scrollObserverTargetRef,
    onIntersectionHandler: ScrollObserverCore.observeEntries,
    opts: {
      intersectionObserverOpts: {
        rootMargin: '0px 0px -100%'
      },
      classes: ScrollObserverCore.classes
    }
  });
  /**
   * Track the sections.
   */
  useIntersectionObserver({
    ref: sectionNavListRef,
    target: scrollObserverSectionRefs,
    onIntersectionHandler: ScrollObserverCore.observeEntries,
    opts: {
      intersectionObserverOpts: {
        rootMargin: '-49.99% 0px -50%',
        allowMultiple: true
      },
      classes: ScrollObserverCore.classes
    },
    onMatch: ({
      mostVisibleEntry
    }) => {
      const id = mostVisibleEntry?.target?.getAttribute('id');
      const content = document?.querySelector(`[href="#${id}"]`)?.textContent;
      if (id && isActiveItem !== id) {
        setIsActiveItem(id);
      }
      if (content) {
        setLabel(content);
      }
    }
  });
  /**
   * Resize callback
   * @type {(function([*]): void)|*}
   */
  const onResizeHandler = useCallback(entries => {
    const [entry] = entries;
    const breakpoint = parseFloat(getComputedStyle(entry.target).getPropertyValue('--bron-section-nav-breakpoint'));
    if (window.innerWidth > breakpoint) {
      if (isExpanded) {
        setIsExpanded(false);
      }
      if (sectionNavListRef?.current) {
        checkListOverflow(sectionNavListRef?.current);
      }
    }
  }, [checkListOverflow, isExpanded]);
  /**
   * Track resizing on the section nav.
   */
  useResizeObserver({
    ref: sectionNavRef,
    onResizeHandler
  });
  /**
   * Handle clicks on the backdrop and close the mobile navigation.
   * @param {Event} event
   */
  const handleBackdropClick = useCallback(event => {
    if (isExpanded && event?.target?.matches(`#${sectionNavContainerRef?.current?.id}`)) {
      setIsExpanded(false);
    }
  }, [isExpanded]);
  /**
   * Handle click/touch events on the section nav links.
   * If the website has an anchor navigation and the section id does not match
   * we do not scroll the list to prevent scroll cancelling on Chrome.
   * @see https://bugs.chromium.org/p/chromium/issues/detail?id=1121151
   * @see https://bugs.chromium.org/p/chromium/issues/detail?id=1043933
   * @see https://bugs.chromium.org/p/chromium/issues/detail?id=833617
   */
  const handleLinkClick = useCallback((textContent, id) => {
    setIsAutoScrolling(true);
    if (textContent) {
      setTimeout(() => {
        setIsAutoScrolling(null);
        setIsActiveItem(id);
      }, autoScrollDelay * 2);
    }
    if (isExpanded) {
      setIsExpanded(false);
    }
  }, [autoScrollDelay, isExpanded, setIsExpanded, setIsAutoScrolling]);
  /**
   * Scrolls the list by a fixed amount in either left or right direction.
   * The scroll is approximately 25% of the scroll list’s width each time.
   * @param {number} [direction=1] - The number designating the scroll direction, where 1 is left and -1 is right.
   */
  const scrollList = useCallback((direction = 1) => {
    requestAnimationFrame(() => {
      sectionNavListRef?.current?.scrollBy({
        top: 0,
        left: -(sectionNavListRef?.current?.offsetWidth ?? 0 / Math.abs((children?.length ?? 1) * 0.25)) * direction,
        behavior: 'smooth'
      });
    });
  }, [sectionNavListRef, children]);
  function renderSectionNavButton() {
    if (/*#__PURE__*/React__default.isValidElement(button)) {
      return /*#__PURE__*/React__default.cloneElement(button, {
        className: 'c-section-nav__btn'
      });
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: sectionNavClassNames,
    ref: sectionNavRef,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-section-nav__header"
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-section-nav__section-label",
    type: "button",
    "aria-expanded": isExpanded,
    "aria-controls": "section-nav-container",
    onClick: () => {
      setIsExpanded(!isExpanded);
    }
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-section-nav__section-label__text"
  }, label ?? defaultSectionLabel)), renderSectionNavButton()), /*#__PURE__*/React__default.createElement("nav", {
    className: sectionNavContainerClassNames,
    id: "section-nav-container",
    "aria-label": "Table of contents",
    ref: sectionNavContainerRef,
    onClick: handleBackdropClick
  }, /*#__PURE__*/React__default.createElement("div", {
    className: sectionNavListParentClassNames
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-section-nav__scroll-button  c-section-nav__scroll-button--prev",
    type: "button",
    tabIndex: -1,
    "aria-label": "Scroll to the left",
    onClick: () => scrollList(1)
  }), /*#__PURE__*/React__default.createElement("ul", {
    className: "c-section-nav__list",
    ref: sectionNavListRef,
    onScroll: event => checkListOverflow(event?.target),
    [SCROLL_OBSERVER_SKIP_OBSERVE_ATTR]: isAutoScrolling
  }, React__default.Children.map(children, child => /*#__PURE__*/React__default.cloneElement(child, {
    onClick: event => {
      child?.props?.onClick(event);
      handleLinkClick(event, child?.props.id);
    },
    isActive: isActiveItem === child?.props.id,
    ref: child?.props.id === isActiveItem ? activeItemRef : null
  }))), /*#__PURE__*/React__default.createElement("button", {
    className: "c-section-nav__scroll-button  c-section-nav__scroll-button--next",
    type: "button",
    tabIndex: -1,
    "aria-label": "Scroll to the right",
    onClick: () => scrollList(-1)
  })), renderSectionNavButton()));
}
/**
 * Internal SectionNavItem component to render {@link SectionNav} items.
 *
 * @params {string} id
 * @params {boolean} isActive
 * @params {function} onClick
 * @params {string} anchor
 * @params {string} text
 * @params {string} className
 *
 */
const SectionNavItem = /*#__PURE__*/React__default.forwardRef(({
  className,
  id,
  children,
  isActive,
  anchor,
  text,
  onClick = () => {}
}, ref) => {
  const sectionNavLinkClassNames = classNames('c-section-nav__link', {
    'is-active': isActive
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-section-nav__item",
    ref: ref
  }, /*#__PURE__*/React__default.createElement("a", {
    className: sectionNavLinkClassNames,
    href: `#${anchor}`,
    "data-text": text,
    onClick: event => onClick(event?.target?.textContent, id)
  }, children));
});
SectionNav.Item = SectionNavItem;
SectionNav.Item.displayName = 'SectionNav.Item';

/**
 * Bronson Select component.
 *
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-select.html
 *
 * @constructor
 */
function Select({
  error,
  disabled,
  name,
  id,
  testId,
  children,
  className,
  success,
  ...otherProps
}) {
  const divClassNameList = classNames('c-input c-input--select', className).trim();
  const selectClassNameList = classNames('c-input__input', {
    'is-success': success
  }).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList
  }, /*#__PURE__*/React__default.createElement("select", {
    id: id,
    className: selectClassNameList,
    name: name,
    disabled: disabled,
    "aria-invalid": error ? true : undefined,
    "data-testid": testId,
    ...otherProps
  }, children));
}
Select.displayName = 'Select';
/**
 * Bronson SelectItem component (nested).
 *
 * @constructor
 */
function SelectItem({
  children,
  value,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("option", {
    ...otherProps,
    value: value
  }, children);
}
SelectItem.displayName = 'Select.Item';
Select.Item = SelectItem;

/**
 * Bronson ShowMore component.
 * ShowMore is an accessible content-reveal component. It consists of
 * a content body and a button that is displayed below. The internal state
 * is handled via the {@link Toggle} object.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-show-more.html
 * @constructor
 */
function ShowMore({
  children,
  className,
  defaultExpanded = false,
  labelCollapse,
  labelExpand,
  onChange,
  testId,
  ...otherProps
}) {
  const ref = useRef(null);
  const classNameList = classNames('c-show-more', className).trim();
  const buttonId = uuidv4();
  const targetId = uuidv4();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-show-more__body",
    id: targetId,
    "data-testid": testId,
    ref: ref,
    ...otherProps
  }, children), /*#__PURE__*/React__default.createElement(Toggle$1, {
    className: "c-show-more__button",
    iconClassName: "c-show-more__icon",
    id: buttonId,
    targetId: targetId,
    /**
     * The {@link Toggle} object takes the invert state.
     */
    defaultHidden: !defaultExpanded,
    labelHidden: labelExpand,
    labelUnhidden: labelCollapse,
    targetRef: ref,
    /**
     * Pass in {@link Button.propTypes}.
     */
    iconViaCss: true,
    link: true,
    round: true,
    secondary: true,
    small: true,
    /**
     * Register `onChange` callback.
     */
    onClick: onChange
  }));
}

/**
 * The SiteNavContext holds the open state of the menu
 * and callback functions to control the state.
 * @type {React.Context<{}>}
 */
const SiteNavContext = /*#__PURE__*/React__default.createContext({});
/**
 * Bronson SiteNav component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-site-nav.html
 * @constructor
 */
const SiteNav = ({
  children,
  className,
  closeMenuText,
  openMenuText,
  title = 'Show Navigation',
  testId,
  ...otherProps
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [open, setOpen] = useState(null);
  const contextValue = {
    /**
     * The value of open state.
     */
    open,
    /**
     * Callback function to update open state to a new ref object.
     */
    setOpen: ref => {
      ref === open ? setOpen(null) : setOpen(ref);
    },
    /**
     * Callback function to close the menu.
     */
    closeMenu: () => setMenuOpen(false)
  };
  useEffect(() => {
    const handleEscape = event => {
      switch (event.key) {
        case 'Escape':
          setOpen(null);
          break;
      }
    };
    document.addEventListener('keydown', handleEscape, {
      capture: true
    });
    return () => document.removeEventListener('keydown', handleEscape, {
      capture: true
    });
  }, []);
  const classNameList = classNames('c-site-nav', {
    'is-active': menuOpen
  }, className).trim();
  const panelClassNameList = classNames('c-site-nav__panel', {
    'is-collapsed': !menuOpen
  }).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-site-nav__toggle",
    type: "button",
    title: title,
    onClick: () => setMenuOpen(!menuOpen),
    "aria-expanded": menuOpen
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-site-nav__toggle-icon",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-site-nav__toggle-open-label"
  }, openMenuText || 'Menu'), /*#__PURE__*/React__default.createElement("span", {
    className: "c-site-nav__toggle-close-label"
  }, closeMenuText || 'Close Menu')), /*#__PURE__*/React__default.createElement("div", {
    className: panelClassNameList
  }, /*#__PURE__*/React__default.createElement("nav", {
    className: "c-site-nav__navs",
    "aria-label": "Main Navigation"
  }, /*#__PURE__*/React__default.createElement(SiteNavContext.Provider, {
    value: contextValue
  }, children))));
};
SiteNav.displayName = 'SiteNav';
/**
 * Bronson SiteNavArea component (nested).
 * @constructor
 */
function SiteNavArea({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("ul", {
    className: "c-site-nav__nav",
    ...otherProps
  }, children);
}
SiteNavArea.displayName = 'SiteNav.Area';
SiteNav.Area = SiteNavArea;
/**
 * Bronson SiteNavItem component (nested).
 * @constructor
 */
const SiteNavItem = ({
  active,
  children,
  className,
  dropdownTitle,
  icon,
  label,
  language,
  navClassName,
  onClick,
  reversed,
  topAtBreakpoint,
  testId,
  ...otherProps
}) => {
  const areaRef = useRef(null);
  const value = useContext(SiteNavContext);
  /**
   * Create a unique ID for dropdown button
   * and dropdown panel.
   */
  const id = uuidv4();
  /**
   * Handle clicks outside the {@link SiteNavArea}.
   */
  useEffect(() => {
    const outsideClickHandler = event => {
      const eventTarget = event?.target;
      /**
       * Only close the current dropdown when a SiteNavItem is expanded,
       * the event target is not a childElement of the {@link areaRef} and
       * it is not a child of another {@link areaRef} (e.g. multiple navigations)
       * which would also count as an outside click.
       */
      if (value?.open && !areaRef?.current?.parentElement?.contains(eventTarget) && !eventTarget?.closest('.c-site-nav__nav')) {
        value?.setOpen?.(null);
      }
    };
    document.addEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
    /**
     * Cleanup the hook.
     */
    return () => document.removeEventListener('mousedown', outsideClickHandler, {
      capture: true
    });
  }, [value]);
  const navClassNameList = classNames('c-site-nav__item', {
    'c-site-nav__item--top-at-breakpoint': topAtBreakpoint,
    'c-site-nav__item--language': language
  }, navClassName).trim();
  const classNameList = classNames('c-site-nav__link', {
    'is-active': active,
    'is-reversed': reversed
  }, className).trim();
  /**
   * If there is a {@link DropdownItem},
   * then a `<button>` is rendered, otherwise a `<a>`.
   */
  const TriggerElement = children ? 'button' : 'a';
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: "c-site-nav__link-icon",
        viaCss: true
      });
    }
    return null;
  }
  function renderIfDropdownTitle() {
    if (dropdownTitle) {
      return /*#__PURE__*/React__default.createElement("li", {
        className: "c-site-nav-dropdown__title"
      }, dropdownTitle);
    }
    return null;
  }
  function renderIfDropdownItems(open) {
    const dropdownClassNameList = classNames('c-site-nav-dropdown', {
      'is-collapsed': open !== areaRef
    }).trim();
    if (children) {
      return /*#__PURE__*/React__default.createElement("nav", {
        className: dropdownClassNameList,
        id: `${id}-dropdown-panel`,
        "aria-labelledby": `${id}-dropdown-button`
      }, /*#__PURE__*/React__default.createElement("ul", {
        className: "c-site-nav-dropdown__list"
      }, renderIfDropdownTitle(), children));
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    className: navClassNameList,
    ref: areaRef,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement(TriggerElement, {
    className: classNameList,
    id: children ? `${id}-dropdown-button` : undefined,
    type: children ? 'button' : undefined,
    "aria-current": active ? 'page' : undefined,
    "aria-controls": children ? `${id}-dropdown-panel` : undefined,
    "aria-expanded": children ? value.open === areaRef : undefined,
    "data-testid": testId,
    tabIndex: children ? undefined : 0,
    role: children ? undefined : 'link',
    /**
     * Map keyboard events to handle the state of the current SiteNavItem.
     */
    onKeyDown: event => {
      switch (event.key) {
        case ' ':
        case 'Enter':
          value.setOpen?.(areaRef);
          break;
        case 'Escape':
          value.setOpen?.(null);
          break;
      }
    },
    /**
     * Note: `onMouseDown` is applied before `onBlur`, other than `onClick`.
     * If the action is performed in `onClick`, `onBlur` could already be
     * performed, closing another menu. In case of e.g. VW6, closing
     * another menu could move content, so the actual click doesn’t
     * happen anymore, which is an undesired behavior. Test this with
     * VW6 menu and 2 items with subitems, open item 1 and click on
     * item 2 (which will move as item 1 submenu disappears just before).
     */
    onMouseDown: event => [onClick && [
    /**
     * If there is no {@link DropdownItem},
     * a direct action will be performed.
     */
    !children && [value.setOpen?.(null), value.closeMenu?.()],
    /**
     * Perform the click action.
     */
    onClick(event)],
    /**
     * If there is a {@link DropdownItem},
     * then open this item after `onClick`.
     */
    children && [value.setOpen?.(areaRef)]]
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-site-nav__link-label"
  }, label), renderIfIcon()), renderIfDropdownItems(value.open));
};
SiteNavItem.displayName = 'SiteNav.Item';
SiteNav.Item = SiteNavItem;
/**
 * Bronson DropdownItem component (nested).
 * @constructor
 */
function DropdownItem({
  active,
  children,
  onClick,
  testId,
  ...otherProps
}) {
  const value = useContext(SiteNavContext);
  const classNameList = classNames('c-site-nav-dropdown__link', {
    'is-active': active
  }).trim();
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-site-nav-dropdown__item",
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("a", {
    className: classNameList,
    "aria-current": active ? 'page' : undefined,
    "data-testid": testId,
    tabIndex: 0,
    role: "link" // Improving accessibility through semantic mapping.
    ,

    /**
     * Map keyboard events to close the open {@link SiteNavItem}.
     */
    onKeyDown: event => {
      switch (event.key) {
        case 'Escape':
          value.setOpen?.(null);
          break;
      }
    },
    onClick: event => onClick && [
    /**
     * Close the open {@link SiteNavItem} that
     * contains this DropdownItem.
     */
    value.setOpen?.(null),
    /**
     * Close the whole menu (if the menu is open,
     * e.g. VW6 or on small viewports).
     */
    value.closeMenu?.(),
    /**
     * Perform the click action.
     */
    onClick(event)]
  }, children));
}
DropdownItem.displayName = 'SiteNavItem.DropdownItem';
SiteNavItem.DropdownItem = DropdownItem;

/**
 * Bronson Spinner component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-spinner.html
 * @constructor
 */
function Spinner({
  busy = true,
  center,
  children,
  className,
  fullPage,
  section,
  small,
  testId,
  ...otherProps
}) {
  // hint: combination of text and section for spinner is not (yet) possible
  const hasText = !section && children;
  const divClassNameList = classNames('c-spinner', {
    // main class modifier convenience prop extension
    'c-spinner--small ': small,
    // Convenience prop from Bronson variants.
    'c-spinner--center ': center,
    // Convenience prop from Bronson variants.
    'c-spinner--full-page ': fullPage,
    // Convenience prop from Bronson variants.
    'c-spinner--text ': hasText,
    // Convenience prop from Bronson variants.
    'c-spinner--section ': section // Convenience prop from Bronson variants.
  }, className).trim();
  function renderLoader() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "c-spinner__loader"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-spinner__element"
    }));
  }
  function renderIfHasText() {
    if (hasText) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-spinner__content"
      }, renderLoader(), /*#__PURE__*/React__default.createElement("div", {
        className: "c-spinner__text"
      }, children));
    }
    return null;
  }
  function renderIfNotHasText() {
    if (!hasText) {
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children, renderLoader());
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList,
    "aria-live": "polite",
    "aria-busy": busy
  }, renderIfHasText(), renderIfNotHasText());
}

/**
 * Bronson StatusMessage component.
 * StatusMessage communicates a status with icon and text. It includes
 * default statuses like `info`, `success`, `warning` and `error`.
 * The icon is set automatically depending on the status.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-status-message.html
 * @constructor
 */
function StatusMessage({
  className,
  label,
  status = null,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-status-message', {
    [`c-status-message--${status} `]: status
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, label);
}

const defaultConfig = {
  isStickyClass: 'is-sticky',
  isTransitioningClass: 'is-sticky-transitioning'
};
/**
 * Check if an element is leaving the viewport bounds and apply
 * a CSS class to the {@link current} element.
 * @returns {boolean} - Return a boolean indicating if the {@link current} element is sticky.
 */
function checkIfElementIsSticky(current, config, onStickyHandler) {
  /**
   * We cannot process anything if the element and config is missing.
   */
  if (!(current && config)) {
    return false;
  }
  /**
   * If we are transitioning we skip checking for stickiness.
   */
  if (current.classList.contains(config.isTransitioningClass)) {
    return false;
  }
  /**
   * Checks which positioning mode the `position: sticky` adheres to.
   *
   * @param {string} bottom - The CSS bottom of the element.
   * @param {string} top - The CSS top of the element.
   * @returns {boolean} - Returns a boolean indicating if the element is in sticky position.
   */
  const isSticky = (bottom, top) => {
    const {
      top: offsetTop
    } = current.getBoundingClientRect();
    if (bottom === 'auto' && top.search('px')) {
      return offsetTop <= parseFloat(top);
    }
    return false;
  };
  const {
    bottom: cssBottom,
    top: cssTop
  } = window.getComputedStyle(current);
  const sticks = isSticky(cssBottom, cssTop);
  /**
   * Call callback with current state.
   */
  onStickyHandler?.(sticks);
  /**
   * Check if the element sticks.
   */
  if (sticks) {
    current.classList.add(config.isStickyClass);
  } else {
    current.classList.remove(config.isStickyClass);
  }
  return true;
}
/**
 * Checks whether an element is positioned via `sticky` in CSS.
 *
 * @param {React.MutableRefObject<null|HTMLElement>} ref – The reference to observe resizes on.
 * @param {{isStickyClass: string, isTransitioningClass: string}} [config] - A {@link useStickyOpts} config object.
 * @param {function} [onStickyHandler] - An optional callback to call on each check with the current state as argument.
 */
function useSticky({
  ref,
  config,
  onStickyHandler
}) {
  /**
   * Compares the element’s scroll top/bottom offset against its computed top/bottom value.
   */
  useEffect(() => {
    const {
      current
    } = ref;
    /**
     * Internal
     * @typedef useStickyOpts
     * @type {{isStickyClass: string, isTransitioningClass: string}}
     */
    const opts = {
      ...defaultConfig,
      ...config
    };
    /**
     * If we have an {@link HTMLElement} to observe.
     */
    if (current) {
      current.addEventListener('transitionend', () => {
        current.classList.remove(opts.isTransitioningClass);
      });
      current.addEventListener('transitionstart', () => {
        current.classList.add(opts.isTransitioningClass);
      });
      /**
       * Setup scroll listener to check whether the element is sticky.
       */
      window.addEventListener('scroll', () => requestAnimationFrame(() => checkIfElementIsSticky(current, opts, onStickyHandler)));
      /**
       * Cleanup event handler.
       */
      return () => window.removeEventListener('scroll', () => requestAnimationFrame(() => checkIfElementIsSticky(current, opts, onStickyHandler)));
    }
    return () => {};
  }, [config, onStickyHandler, ref]);
}

/**
 * Bronson StickyContainer component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-sticky-container.html
 * @constructor
 */
function StickyContainer({
  children,
  className,
  testId,
  ...otherProps
}) {
  const ref = useRef(null);
  const stickyClassNameList = classNames('c-sticky-container', className);
  /**
   * Apply sticky hook to enable CSS class when the element is changing its stickiness state.
   * The classes are applied directly via side effects in {@link useSticky}.
   */
  useSticky({
    ref
  });
  return /*#__PURE__*/React__default.createElement("div", {
    ref: ref,
    className: stickyClassNameList,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-sticky-container__inner"
  }, children));
}

/* eslint-disable jsx-a11y/no-redundant-roles */
const TableContext = /*#__PURE__*/createContext({});
const TheadOrTbodyContext = /*#__PURE__*/createContext({});
const trackingRowStyle = {
  position: 'absolute',
  width: '100%',
  height: '0',
  top: 0,
  left: 0
};
const trackingColumnStyle = {
  position: 'absolute',
  width: '0',
  height: '100%',
  left: 0,
  top: 0
};
/**
 * Bronson Table component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-table--table.html
 * @constructor
 */
const Table = ({
  auto,
  bordered,
  children,
  className,
  colored,
  dataControls,
  fixedColumns,
  highlight,
  layoutFixed,
  narrow,
  noHover,
  noScroll,
  responsive,
  responsiveColumnHeader,
  responsiveRowHeader,
  stickyColumn,
  stickyHead,
  tableHeader,
  verticalAlign,
  wide,
  testId,
  ...otherProps
}) => {
  /**
   * Throw a console warning when `responsive` prop is used.
   * @DEPRECATED Remove in v11.
   */
  if (responsive) {
    console.warn('Warning: The `responsive` prop is deprecated and will be removed from Bronson-React 11. Please use `responsiveColumnHeader` instead.');
  }
  const tableWrapperRef = useRef(null);
  /**
   * Implement the sticky table behavior by utilizing two tracking elements
   * that signals if the table has either an intersecting header row or first column.
   * This deviates from how Bronson implements this (by tracking individual cells).
   * It also does not implement calculating parent and grandparent node offsets.
   */
  const stickColumnRef = useRef(null);
  const stickRowRef = useRef(null);
  /**
   * The state is tracked for either direction individually.
   */
  const [hasStickyHead, setHasStickyHead] = useState(false);
  const [hasStickyColumn, setHasStickyColumn] = useState(false);
  const [hasStickyColumnOverflow, setHasStickyColumnOverflow] = useState(false);
  /**
   * Function to update the `--scroll-width` custom property
   * (and `.has-overflow-clip` class) on the document root.
   */
  function updateScrollWidth(tableWrapper, table) {
    if (!tableWrapper || !table) {
      return;
    }
    const tableWrapperWidth = tableWrapper.getBoundingClientRect().width;
    const tableWidth = table.getBoundingClientRect().width;
    /**
     * The `.has-scroll-overflow` class is used to fake-scroll the table via CSS positioning,
     * but only when it’s necessary (when table is wider than its wrapper).
     */
    setHasStickyColumnOverflow(tableWidth > tableWrapperWidth);
    // Remove `--scroll-width` custom property from document root to not affect the following assignment.
    document.documentElement.style.removeProperty('--scroll-width');
    // Remove `.has-overflow-clip` class from document root to not affect the following assignment.
    document.documentElement.classList.remove('has-overflow-clip');
    // Width of the scrollable area (including the over-scrolling tables).
    const {
      scrollWidth
    } = document.body;
    /**
     * Add {@link scrollWidth} as custom property to document root.
     * `--scroll-width` is used to set the width of the sticky body to avoid
     * too early or unnecessary horizontal page scrolling.
     */
    document.documentElement.style.setProperty('--scroll-width', `${scrollWidth}px`);
    /**
     * Add `.has-overflow-clip` class to the document root.
     * Together with CSS this is a workaround for a bug in Firefox,
     * where it calculates an incorrect total scroll width.
     */
    document.documentElement.classList.add('has-overflow-clip');
  }
  /**
   * Function to update transform values of root and tables based
   * on the scroll status. Negative `--scroll-offset` value will be used
   * to position tables in the opposite scrolling direction.
   */
  function updateScrollOffset() {
    document.documentElement.style.setProperty('--scroll-offset', `${window.scrollX}px`);
  }
  /**
   * Append 'minimum-scale=1' to the `[content]` attribute of `<meta name="viewport">` if it does not already exist.
   * This fixes a behavior in Chromium-based mobile browsers where `window.scrollX` is otherwise always `0`.
   * @see https://stackoverflow.com/a/59443204
   */
  function setViewportMinimumScale() {
    // Select the viewport meta tag from the document.
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    // If the viewport meta tag exists.
    if (viewportMeta) {
      const viewportContent = viewportMeta?.getAttribute('content');
      // If the `[content]` attribute does not include 'minimum-scale'.
      if (!viewportContent?.includes('minimum-scale')) {
        viewportMeta.setAttribute('content', `${viewportContent}, minimum-scale=1`);
      }
    } else {
      // If the viewport meta tag does not exist, create it.
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1, minimum-scale=1';
      document.head.appendChild(meta);
    }
  }
  if (stickyHead || stickyColumn) {
    useEffect(() => {
      let resizeObserver;
      setViewportMinimumScale();
      const table = tableWrapperRef?.current?.querySelector('table');
      if (table && tableWrapperRef?.current) {
        // Update scroll width when a sticky table changes dimensions.
        resizeObserver = new ResizeObserver(() => {
          updateScrollWidth(tableWrapperRef?.current, table);
        });
        // Observe table element.
        resizeObserver.observe(table);
      }
      // Add custom props on page load.
      updateScrollOffset();
      return () => {
        if (table) {
          resizeObserver.unobserve(table);
        }
      };
    }, []);
    /**
     * Track resizes on the window to adjust scroll width of `<html>` element.
     */
    useEffect(() => {
      // Update scroll width on resize.
      let resizeRAF;
      const resizeCallback = () => {
        cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(() => {
          const table = tableWrapperRef?.current?.querySelector('table');
          if (table && tableWrapperRef?.current) {
            updateScrollWidth(tableWrapperRef?.current, table);
          }
        });
      };
      window.addEventListener('resize', resizeCallback, {
        passive: true
      });
      return () => {
        window.removeEventListener('resize', resizeCallback);
      };
    }, []);
    /**
     * Update the scroll offset of the `<html>` element.
     */
    useEffect(() => {
      // Update scroll offset on scroll.
      let scrollRAF;
      const scrollCallback = () => {
        cancelAnimationFrame(scrollRAF);
        scrollRAF = requestAnimationFrame(updateScrollOffset);
      };
      window.addEventListener('scroll', scrollCallback, {
        passive: true
      });
      return () => {
        window.removeEventListener('scroll', scrollCallback);
      };
    }, []);
    /**
     * Track the header row tracking element.
     */
    useIntersectionObserver({
      ref: tableWrapperRef,
      target: stickRowRef,
      onIntersectionHandler: () => ([entry]) => setHasStickyHead(!entry?.isIntersecting),
      opts: {
        classes: ScrollObserverCore.classes,
        intersectionObserverOpts: {
          rootMargin: '0px',
          threshold: [0, 1]
        }
      }
    });
    /**
     * Track the column tracking element.
     */
    useIntersectionObserver({
      ref: tableWrapperRef,
      target: stickColumnRef,
      onIntersectionHandler: () => ([entry]) => {
        // Left position of the table wrapper.
        const tableWrapperLeft = tableWrapperRef?.current?.getBoundingClientRect().left ?? 0;
        const table = tableWrapperRef?.current?.querySelector('table');
        if (table && tableWrapperRef?.current) {
          // Left position of the cell.
          const cellLeftPos = entry.boundingClientRect.left;
          // If the table wrapper or table have a left margin, we need to calculate with it.
          const tableWrapperMarginLeft = parseFloat(window.getComputedStyle(tableWrapperRef?.current).marginLeft);
          const tableMarginLeft = parseFloat(window.getComputedStyle(table).marginLeft);
          const marginLeft = tableWrapperMarginLeft + tableMarginLeft;
          /**
           * Detect that the cell is sticky when its left boundary is less than `0px` from the left border of the viewport.
           * Detect that the cell is sticky when the table wrapper (+ table margin) is further left than the cell.
           * This is the case when the table does not stick to the left edge of the viewport, but to another container.
           */
          // eslint-disable-next-line no-negated-condition
          const isSticky = tableWrapperLeft + marginLeft - cellLeftPos !== 0 ? true : cellLeftPos < 0;
          setHasStickyColumn(isSticky);
        }
      },
      opts: {
        classes: ScrollObserverCore.classes,
        intersectionObserverOpts: {
          rootMargin: '0px',
          threshold: [0, 1]
        }
      }
    });
  }
  const classNameList = classNames('c-table-wrapper', {
    'c-table-wrapper--auto': auto,
    'c-table-wrapper--no-scroll': noScroll,
    'c-table--bordered': bordered,
    'c-table--colored': colored,
    'c-table--data-controls': dataControls,
    [`c-table--fixed-column-${fixedColumns}`]: fixedColumns,
    'c-table--highlight': highlight,
    'c-table--layout-fixed': layoutFixed,
    'c-table--narrow': narrow,
    'c-table--no-hover': noHover,
    'c-table--responsive': responsive /** @DEPRECATED Remove in v11. */,
    'c-table--responsive-column': responsiveColumnHeader,
    'c-table--responsive-row': responsiveRowHeader,
    'c-table--sticky-column': stickyColumn,
    'c-table--sticky-head': stickyHead,
    'c-table--wide': wide,
    'has-sticky-head': hasStickyHead,
    'has-scroll-overflow': hasStickyColumnOverflow,
    'has-sticky-column': hasStickyColumn
  }, className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    ref: tableWrapperRef
  }, /*#__PURE__*/React__default.createElement(TableContext.Provider, {
    value: {
      tableHeader,
      responsive,
      responsiveColumnHeader,
      responsiveRowHeader
    }
  }, /*#__PURE__*/React__default.createElement("table", {
    role: "table",
    "data-vertical-align": verticalAlign,
    "data-testid": testId,
    ...otherProps
  }, children)), stickyColumn && /*#__PURE__*/React__default.createElement("div", {
    ref: stickColumnRef,
    style: trackingColumnStyle,
    "aria-hidden": true
  }), stickyHead && /*#__PURE__*/React__default.createElement("div", {
    ref: stickRowRef,
    style: trackingRowStyle,
    "aria-hidden": true
  }));
};
Table.Caption = Caption;
Table.Thead = Thead;
Table.Tbody = Tbody;
Table.Tfoot = Tfoot;
Table.Tr = Tr;
Table.Th = Th;
Table.Td = Td;
Table.Subheading = Subheading;
/**
 * Table Caption component.
 * @constructor
 */
function Caption({
  children
}) {
  return /*#__PURE__*/React__default.createElement("caption", null, children);
}
/**
 * Table Head component.
 * @constructor
 */
function Thead({
  children,
  verticalAlign,
  testId
}) {
  return /*#__PURE__*/React__default.createElement(TheadOrTbodyContext.Provider, {
    value: {
      type: 'head'
    }
  }, /*#__PURE__*/React__default.createElement("thead", {
    role: "rowgroup",
    "data-vertical-align": verticalAlign,
    "data-testid": testId
  }, children));
}
/**
 * Table Body component.
 * @constructor
 */
function Tbody({
  children,
  verticalAlign,
  testId
}) {
  const tableContext = useContext(TableContext);
  return /*#__PURE__*/React__default.createElement(TheadOrTbodyContext.Provider, {
    value: {
      type: 'body'
    }
  }, /*#__PURE__*/React__default.createElement("tbody", {
    role: "rowgroup",
    "data-vertical-align": verticalAlign,
    "data-testid": testId
  }, React__default.Children.map(children, (child, index) =>
  /**
   * If {@link responsiveRowHeader} is set and the current index
   * matches an existing value in {@link tableHeader}, then {@link tableRowIndex}
   * is also passed to the child elements, which is needed as an identifier
   * for the current {@link Table.Tr}.
   */
  tableContext?.responsiveRowHeader && tableContext?.tableHeader?.[index] ? /*#__PURE__*/React__default.cloneElement(child, {
    tableRowIndex: index
  }) : child)));
}
/**
 * Table Footer component.
 * @constructor
 */
function Tfoot({
  children,
  verticalAlign,
  testId
}) {
  return /*#__PURE__*/React__default.createElement("tfoot", {
    role: "rowgroup",
    "data-vertical-align": verticalAlign,
    "data-testid": testId
  }, children);
}
/**
 * Table Row component.
 * @constructor
 */
function Tr({
  children,
  className,
  disabled,
  selected,
  status,
  tableRowIndex,
  verticalAlign,
  ...otherProps
}) {
  const tableContext = useContext(TableContext);
  const headOrBodyContext = useContext(TheadOrTbodyContext);
  const classNameList = classNames({
    // Convenience props.
    'is-disabled': disabled,
    'is-selected': selected,
    [`is-${status}`]: status
  }, className).trim();
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React__default.createElement("tr", {
      className: classNameList,
      role: "row",
      "aria-disabled": disabled ?? null,
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: disabled ? '' : null,
      "data-vertical-align": verticalAlign,
      ...otherProps
    }, headOrBodyContext?.type === 'body' && (tableContext?.responsive || tableContext?.responsiveColumnHeader || tableContext?.responsiveRowHeader) ? React__default.Children.map(children, (child, index) => {
      /**
       * Skip {@link Table.Th} and assume it is the first child.
       */
      if (index === 0) {
        return child;
      }
      /**
       * Handle {@link tableHeader} for {@link responsiveColumnHeader}.
       */
      const columnHeader = tableContext?.responsive || tableContext?.responsiveColumnHeader ? tableContext?.tableHeader?.[index - 1] : null;
      /**
       * Handle {@link tableHeader} for {@link responsiveRowHeader}.
       */
      const rowHeader = tableContext?.responsiveRowHeader ? tableContext?.tableHeader?.[tableRowIndex] : null;
      return /*#__PURE__*/React__default.cloneElement(child, {
        'data-columnheader': columnHeader,
        'data-rowheader': rowHeader
      });
    }) : children)
  );
}
/**
 * Table Header component.
 * @constructor
 */
function Th({
  children,
  className,
  disabled,
  status,
  verticalAlign,
  ...otherProps
}) {
  const headOrBodyContext = useContext(TheadOrTbodyContext);
  const classNameList = classNames({
    // Convenience props.
    'is-disabled': disabled,
    [`is-${status}`]: status
  }, className).trim();
  let scopeVal;
  let roleVal;
  if (headOrBodyContext?.type === 'head') {
    scopeVal = children ? 'col' : undefined;
    roleVal = 'columnheader';
  } else if (headOrBodyContext?.type === 'body') {
    scopeVal = children ? 'row' : undefined;
    roleVal = 'rowheader';
  }
  return /*#__PURE__*/React__default.createElement("th", {
    className: classNameList,
    scope: scopeVal,
    role: roleVal,
    "aria-disabled": disabled,
    "data-vertical-align": verticalAlign,
    ...otherProps
  }, children);
}
/**
 * Table Data Cell component.
 * @constructor
 */
function Td({
  children,
  className,
  disabled,
  status,
  verticalAlign,
  ...otherProps
}) {
  const classNameList = classNames('c-table__cell', {
    // Convenience props.
    'is-disabled': disabled,
    [`is-${status}`]: status
  }, className).trim();
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React__default.createElement("td", {
      className: classNameList,
      role: "cell",
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: disabled ? '' : null,
      "data-vertical-align": verticalAlign,
      ...otherProps
    }, children)
  );
}
/**
 * Table Subheading component.
 * @constructor
 */
function Subheading({
  children,
  className,
  headingLevel = 4,
  ...otherProps
}) {
  const classNameList = classNames('c-table__subheading', className).trim();
  const CustomTagHeadingLevel = `h${headingLevel}`;
  /**
   * The {@link id} is required for reasons of accessibility and serves as an
   * indicator for the associated content of the {@link Subheading}.
   */
  const {
    id,
    ...trProps
  } = otherProps;
  /**
   * Apart from Bronson, we have set the maximum value of `[colspan]` to `1000`,
   * because according to the HTML specs, a higher value is invalid.
   * @see https://html.spec.whatwg.org/multipage/tables.html#attr-tdth-colspan
   */
  return /*#__PURE__*/React__default.createElement("tr", {
    className: classNameList,
    role: "row",
    ...trProps
  }, /*#__PURE__*/React__default.createElement("th", {
    scope: "rowgroup",
    id: id,
    colSpan: 1000,
    role: "rowheader"
  }, /*#__PURE__*/React__default.createElement(CustomTagHeadingLevel, {
    className: "c-table__subheading__title"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-table__subheading__title__text"
  }, children))));
}

const TabsContext = /*#__PURE__*/React__default.createContext({});
/**
 * Bronson Tabs component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tabs.html
 * @constructor
 */
function Tabs({
  children,
  className,
  defaultSelected,
  onChangeSelected,
  selected,
  testId,
  ...otherProps
}) {
  /**
   * To be downward compatible we still use this internal state when `defaultSelected` is available.
   */
  const [currSelected, setCurrSelected] = useState(defaultSelected);
  /**
   * Change the `isSelected()` and `setSelected()` functions depending on the `defaultSelected` state.
   * This is due the fact that either `defaultSelected` or `['selected, 'onChangeSelected']` can be used.
   * @type {{setSelected: (function(*=): void), isSelected: (function(*): boolean), selected: string}}
   */
  const contextValue = useMemo(() => {
    return defaultSelected ? {
      defaultSelected,
      selected: currSelected,
      // query whether the tab id is selected
      isSelected: tabId => currSelected === tabId,
      // callback function to set the selected tab
      setSelected: tabId => setCurrSelected(tabId)
    } : {
      defaultSelected: selected,
      selected,
      // query whether the tab id is selected
      isSelected: tabId => selected === tabId,
      // callback function to set the selected tab
      setSelected: tabId => onChangeSelected?.(tabId)
    };
  }, [currSelected, defaultSelected, onChangeSelected, selected]);
  const classNameList = classNames('c-tabs', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    className: classNameList,
    "data-testid": testId
  }, /*#__PURE__*/React__default.createElement(TabsContext.Provider, {
    value: contextValue
  }, children));
}
Tabs.displayName = 'Tabs';
/**
 * Bronson TabsHeader component (nested).
 * @constructor
 */
function TabsHeader({
  children,
  className,
  icons,
  iconsOnly,
  stacked,
  testId,
  ...otherProps
}) {
  const initialResizeThreshold = 100;
  const {
    defaultSelected,
    selected
  } = React__default.useContext(TabsContext);
  const [flickityRef, setFlickityRef] = useState(null);
  /**
   * Set the initialIndex depending on the current select tab.
   * If the default and selected tab mismatch we assume the user has navigated themselves.
   * @type {number|undefined}
   */
  const initialIndex = defaultSelected === selected ? React__default.Children.toArray(children).findIndex(c => {
    if (/*#__PURE__*/isValidElement(c)) {
      return c.props.tabId === defaultSelected;
    }
    return false;
  }) : undefined;
  useBasicSlider(flickityRef, children, {
    hasDynamicElements: false,
    initialIndex,
    initialResizeThreshold
  });
  const tabsClassNameList = classNames('c-tabs__nav', {
    'c-tabs__nav--icons': icons,
    'c-tabs__nav--icons-stacked': stacked,
    'c-tabs__nav--icons-only': iconsOnly
  }, className).trim();
  const flickityOptions = {
    cellSelector: '.c-tabs__nav-item',
    contain: true,
    // Prevent excess scroll at beginning or end of the slider.
    pageDots: false,
    cellAlign: 'left',
    groupCells: true,
    freeScroll: true,
    prevNextButtons: true,
    initialIndex
  };
  return /*#__PURE__*/React__default.createElement(BasicSlider, {
    elementType: "div",
    className: tabsClassNameList,
    role: "tablist",
    flickityRef: c => setFlickityRef(c),
    options: flickityOptions,
    "data-testid": testId,
    ...otherProps
  }, children);
}
TabsHeader.displayName = 'Tabs.Header';
Tabs.Header = TabsHeader;
/**
 * Bronson TabsHeaderItem component (nested).
 * @constructor
 */
function TabsHeaderItem({
  children,
  className,
  disabled,
  icon,
  tabId,
  testId,
  ...otherProps
}) {
  const context = React__default.useContext(TabsContext);
  const buttonClassNameList = classNames('c-tabs__nav-link', {
    'is-disabled ': disabled,
    'is-active ': !disabled && context?.isSelected?.(tabId)
  }, className).trim();
  const iClassNameList = classNames('c-tabs__icon').trim();
  function renderIfTabsItemIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: iClassNameList,
        name: icon
      });
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    className: "c-tabs__nav-item",
    "data-testid": testId,
    role: "presentation"
  }, /*#__PURE__*/React__default.createElement("button", {
    id: tabId,
    className: buttonClassNameList,
    "aria-expanded": !disabled && context?.isSelected?.(tabId),
    "aria-selected": !disabled && context?.isSelected?.(tabId),
    disabled: disabled,
    role: "tab",
    tabIndex: !disabled && context?.isSelected?.(tabId) ? -1 : undefined,
    onClick: () => !disabled && context?.setSelected?.(tabId),
    type: "button"
  }, renderIfTabsItemIcon(), /*#__PURE__*/React__default.createElement("span", {
    className: "c-tabs__text"
  }, children)));
}
TabsHeaderItem.displayName = 'Tab.HeaderItem';
Tabs.HeaderItem = TabsHeaderItem;
/**
 * Bronson TabsContent component (nested).
 * @constructor
 */
function TabsContent({
  children,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    "data-testid": testId,
    ...otherProps,
    className: "c-tabs__content-wrapper"
  }, children);
}
TabsContent.displayName = 'Tabs.Content';
Tabs.Content = TabsContent;
/**
 * Bronson TabsContentItem component (nested).
 * @constructor
 */
function TabsContentItem({
  children,
  className,
  tabId,
  testId,
  ...otherProps
}) {
  const context = React__default.useContext(TabsContext);
  const classNameList = classNames('c-tabs__content', className).trim();
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React__default.createElement("div", {
      ...otherProps,
      className: classNameList,
      "aria-hidden": context?.isSelected?.(tabId) ? 'false' : 'true',
      "aria-labelledby": tabId,
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: context.isSelected(tabId) ? null : '',
      "data-testid": testId,
      role: "tabpanel"
    }, children)
  );
}
TabsContentItem.displayName = 'Tabs.ContentItem';
Tabs.ContentItem = TabsContentItem;

/**
 * Bronson Tag component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tag.html
 * @constructor
 */
function Tag({
  children,
  className,
  close,
  closeLabel = 'Close',
  error,
  info,
  negative,
  onCloseClick,
  positive,
  success,
  warning,
  testId,
  ...otherProps
}) {
  const spanClassNameList = classNames('c-tag', {
    // main class modifier convenience prop extension
    'c-tag--success': success,
    // Convenience prop from Bronson variants.
    'c-tag--warning': warning,
    // Convenience prop from Bronson variants.
    'c-tag--error': error,
    // Convenience prop from Bronson variants.
    'c-tag--info': info,
    // Convenience prop from Bronson variants.
    'c-tag--positive': positive,
    // Convenience prop from Bronson variants.
    'c-tag--negative': negative // Convenience prop from Bronson variants.
  }, className).trim();
  function renderIfClose() {
    if (close) {
      return /*#__PURE__*/React__default.createElement("button", {
        type: "button",
        className: "c-tag__close",
        title: closeLabel,
        "aria-label": closeLabel,
        onClick: onCloseClick
      });
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("span", {
    ...otherProps,
    "data-testid": testId,
    className: spanClassNameList
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-tag__label"
  }, children), renderIfClose());
}

/**
 * Bronson Textarea component.
 * Essentially, a `<textarea>` element uses the same markup as input.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-textarea.html
 * @constructor
 */
function Textarea({
  charCounter,
  children,
  counterHint,
  counterMessageFormatter,
  counterMessageFormatterDebounce = 500,
  enableMaxLength = true,
  error,
  id,
  maxCounter,
  modifierStates,
  onChange,
  success,
  testId,
  ...otherProps
}) {
  /**
   * Track the current character count.
   */
  const [currentCount, setCurrentCount] = useState(0);
  /**
   * Track the textarea hint message state.
   */
  const [hintMessage, setHintMessage] = useState(undefined);
  /**
   * Track the internal state via {@link FormFieldContext}.
   * @type {object}
   */
  const context = useContext(FormFieldContext);
  /**
   * Debounce the hint message for assistive technologies.
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const message = maxCounter ? counterMessageFormatter?.(maxCounter - currentCount) ?? `You have ${maxCounter - currentCount} characters remaining.` : undefined;
      setHintMessage(message);
    }, counterMessageFormatterDebounce);
    return () => clearTimeout(timeoutId);
  }, [currentCount, maxCounter, counterMessageFormatterDebounce]);
  /**
   * 1. Update the context state and mark the component as active.
   * 2. Set the {@link currentCount} based on the character length of the `<textarea>`.
   */
  useEffect(() => {
    if (context?.setElementActive) {
      context.setElementActive(!!children);
    }
    setCurrentCount(children?.length ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  const wrapperClassNameList = classNames('c-input', 'c-input--textarea', {
    'is-success': success
  }).trim();
  const textareaClassNameList = classNames('c-input__input', {
    'is-success': success,
    [`${modifierStates} `]: modifierStates
  }).trim();
  const counterClassNameList = classNames('c-input__counter', {
    'c-input__counter--alert': typeof maxCounter === 'number' ? currentCount >= maxCounter : false
  }).trim();
  /**
   * Conditionally render an `[aria-describedby]` attribute,
   * Mixin any global passed `aria-describedby` into the char hint attribute.
   */
  const describedByAttr = [charCounter && maxCounter ? `${id}-counter-hint-textarea-counter-error` : undefined, otherProps?.['aria-describedby'] ?? undefined].filter(label => !!label).join(' ').trim() || undefined;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: wrapperClassNameList
  }, /*#__PURE__*/React__default.createElement("textarea", {
    defaultValue: children,
    id: id,
    className: textareaClassNameList,
    "aria-invalid": error ? true : undefined,
    "data-testid": testId,
    onChange: event => {
      charCounter && setCurrentCount(event.target.value.length);
      return [onChange?.(event), context.setElementActive && context.setElementActive(!!event.target.value)];
    },
    ...(enableMaxLength && maxCounter ? {
      maxLength: maxCounter
    } : {}),
    ...otherProps,
    "aria-describedby": describedByAttr
  })), charCounter && maxCounter && (/*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: counterClassNameList,
    "aria-hidden": "true"
  }, currentCount, " / ", maxCounter), /*#__PURE__*/React__default.createElement("div", {
    className: "u-visually-hidden"
  }, /*#__PURE__*/React__default.createElement("div", {
    id: `${id}-counter-hint-textarea-counter-error`
  }, counterHint ?? `You can enter up to ${maxCounter} characters.`), /*#__PURE__*/React__default.createElement("div", {
    className: "c-input__counter",
    id: `${id}-counter`,
    "aria-live": "polite"
  }, hintMessage ?? counterMessageFormatter?.(maxCounter - currentCount) ?? `You have ${maxCounter - currentCount} characters remaining.`)))));
}

/**
 * Bronson TileSelect component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tile-select.html
 * @constructor
 */
function TileSelect({
  checkboxGroupName,
  checked,
  children,
  defaultChecked,
  disabled,
  error,
  footer,
  icon,
  id,
  inputType = 'checkbox',
  onChange,
  onClick,
  radioGroupName,
  title,
  value,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-tile-select', {
    'is-error': error
  }).trim();
  const iconClassNameList = classNames('c-tile-select__icon');
  /**
   * Set the `[name]` value depending on the {@link inputType}.
   * If the type is `radio`, then use `radioGroupName` to ensure that all
   * input elements have the same value to be included in the radio group.
   * In the case of `checkbox`, use the {@link checkboxGroupName} if set to
   * group the TileSelects, otherwise the {@link id} is used.
   * @type {string}
   */
  const inputName = inputType === 'radio' ? radioGroupName : checkboxGroupName ?? id;
  /**
   * Callback when user clicks on the {@link TileSelect} element.
   * @type {function}
   */
  const handleClick = e => {
    if (onClick) {
      onClick(e);
    }
  };
  /**
   * Callback when state of the {@link TileSelect} element has changed.
   * @type {function}
   */
  const handleChange = e => {
    if (onChange) {
      onChange(e);
    }
  };
  function renderIfFooter() {
    if (footer) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-tile-select__body-bottom"
      }, footer);
    }
    return null;
  }
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
    React__default.createElement("div", {
      ...otherProps,
      "data-testid": testId,
      className: classNameList,
      inert: disabled ? '' : null
    }, /*#__PURE__*/React__default.createElement("label", {
      className: "c-tile-select__label",
      htmlFor: id
    }, /*#__PURE__*/React__default.createElement("input", {
      className: "c-tile-select__input",
      type: inputType,
      id: id,
      value: value,
      name: inputName,
      checked: checked,
      defaultChecked: defaultChecked,
      disabled: disabled,
      "aria-disabled": disabled,
      onClick: handleClick,
      onChange: handleChange
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "c-tile-select__body"
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "c-tile-select__body-top"
    }, icon && /*#__PURE__*/React__default.createElement(Icon, {
      className: iconClassNameList,
      name: icon
    }), title && /*#__PURE__*/React__default.createElement("span", {
      className: "c-tile-select__title"
    }, title), children && /*#__PURE__*/React__default.createElement("span", {
      className: "c-tile-select__text"
    }, children)), renderIfFooter())))
  );
}

/**
 * Bronson TileSelectGroup component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-tile-select-group.html
 * @constructor
 */
function TileSelectGroup({
  children,
  className,
  layoutItemClassName,
  testId,
  ...otherProps
}) {
  function renderChildren() {
    return React__default.Children.map(children, child => {
      if (!child) {
        return null;
      }
      const layoutItemClassNameList = classNames('o-layout__item', layoutItemClassName).trim();
      return /*#__PURE__*/React__default.createElement("div", {
        className: layoutItemClassNameList
      }, child);
    });
  }
  const classNameList = classNames('o-layout', 'o-layout--equal-height', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: classNameList
  }, renderChildren());
}

/**
 * Bronson TimePicker component.
 * @see https://bronson.vwfs.tools/bluelabel/components/detail/bronson-timepicker.html
 * @constructor
 */
function TimePicker({
  className = '',
  dateFormat = 'H:i',
  disabled = false,
  error = false,
  flatpickrOptions,
  flatpickrProps,
  id,
  locale,
  maxTime,
  minTime,
  name,
  noBackground = true,
  onBlur,
  onChange,
  onError,
  placeholder,
  time24hr = false,
  timePickerClassName = '',
  value,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement(Picker, {
    "aria-invalid": error ? true : undefined,
    className: className,
    componentClassName: "c-timepicker",
    dateFormat: dateFormat,
    disabled: disabled,
    error: error,
    flatpickrOptions: {
      ...flatpickrOptions,
      enableTime: true,
      noCalendar: true,
      time_24hr: time24hr,
      maxTime,
      minTime
    },
    flatpickrProps: flatpickrProps,
    id: id,
    locale: locale,
    maskedInput: false,
    mode: "single",
    name: name,
    noBackground: noBackground,
    onBlur: onBlur,
    onChange: onChange,
    onError: onError,
    pickerClassName: timePickerClassName,
    placeholder: placeholder,
    value: value,
    testId: testId,
    ...otherProps
  });
}

/**
 * Hook to manage external {@link ToastNotification} selection.
 *
 * @param {boolean} [defaultOpen=true] - Hide/show the {@link ToastNotification}.
 * @returns {object}
 */
const useToastOpen = (defaultOpen = true) => {
  const [open, setOpen] = useState(defaultOpen);
  return {
    props: {
      open: !!open,
      onClose: useCallback(() => setOpen(false), [])
    },
    open,
    setOpen
  };
};
/**
 * Bronson ToastNotifications component.
 * Groups all related {@link ToastNotification}.
 * @constructor
 */
function ToastNotifications({
  children,
  description,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("aside", {
    className: "c-toast-notifications",
    "aria-label": description,
    "data-testid": testId,
    ...otherProps
  }, children);
}
/**
 * Bronson ToastNotification component (nested).
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-toast-notification.html
 * @constructor
 */
function ToastNotification({
  children,
  className,
  closeLabel = 'Close Toast Notification',
  error,
  info,
  onClose,
  open = true,
  success,
  warning,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-toast-notification', {
    // Convenience props from Bronson variants.
    'c-toast-notification--default': !(info || success || warning || error),
    'c-toast-notification--info': info,
    'c-toast-notification--success': success,
    'c-toast-notification--warning': warning,
    'c-toast-notification--error': error,
    'is-closed': !open
  }, className).trim();
  /**
   * Set the initial height to make CSS animations work properly.
   * @TODO: Should be refactored with the ReactTransitionGroup add-on, @see BRON-7532.
   */
  //   const toastRef = useRef(null)
  //   const [initialHeight, setInitialHeight] = useState(null)
  //   useEffect(() => {
  //     if (toastRef && toastRef.current) {
  //       const h = toastRef.current.clientHeight
  //       if (!!open && !initialHeight) {
  //         setInitialHeight(h)
  //       }
  //     }
  //   }, [open, initialHeight])
  return (
    /*#__PURE__*/
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React__default.createElement("article", {
      className: classNameList,
      "aria-live": "polite",
      "aria-hidden": !open,
      // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
      inert: open ? null : '',
      role: "status",
      "data-testid": testId,
      ...otherProps
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "c-toast-notification__content"
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "c-toast-notification__body"
    }, /*#__PURE__*/React__default.createElement("p", null, children)), /*#__PURE__*/React__default.createElement("button", {
      className: "c-toast-notification__close",
      title: closeLabel,
      type: "button",
      onClick: onClose
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, closeLabel))))
  );
}

/**
 * Bronson Toggle component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-toggle.html
 * @constructor
 */
function Toggle({
  children,
  className,
  id,
  inputType = 'checkbox',
  name = 'toggle',
  onChange,
  onOff,
  textOther,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames('c-toggle', {
    'c-toggle--on-off': onOff
  }, className).trim();
  function renderIfToggleOnOff() {
    if (onOff) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-toggle__text"
      }, textOther);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    className: divClassNameList
  }, /*#__PURE__*/React__default.createElement("label", {
    htmlFor: id
  }, renderIfToggleOnOff(), /*#__PURE__*/React__default.createElement("input", {
    ...otherProps,
    onChange: onChange,
    "data-testid": testId,
    className: "c-toggle__input",
    type: inputType,
    id: id,
    name: name
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-toggle__text"
  }, children)));
}

/**
 * The UnorderedList component is an ordinary `<ul>` element,
 * used for proper styling with custom bullet points.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-unordered-list.html
 * @constructor
 */
const UnorderedList = ({
  children,
  testId,
  ...otherProps
}) => {
  const ulClassNameList = classNames('c-ul').trim();
  return /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    "data-testid": testId,
    className: ulClassNameList
  }, children);
};
UnorderedList.displayName = 'UnorderedList';
/**
 * The Item component (nested) is internally used to wrap
 * the item content and display it inside the {@link UnorderedList}.
 */
function Item({
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    ...otherProps
  }, children);
}
Item.displayName = 'UnorderedList.Item';
UnorderedList.Item = Item;

/**
 * Bronson Upload component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-upload.html
 * @constructor
 */
function Upload({
  accept,
  buttonText,
  className,
  descriptionSupplementaryText,
  descriptionText,
  disabled,
  disabledSupplementaryText,
  disabledText,
  id,
  loading,
  mouseover,
  mouseoverText,
  multiple,
  name,
  onChange,
  processingText,
  testId,
  ...otherProps
}) {
  const [hasHover, setHasHover] = useState(false);
  const uploadElement = useRef(null);
  /**
   * Handle side-effects for Upload Mouseover variant.
   */
  useEffect(() => {
    const current = uploadElement?.current;
    /**
     * Add `.has-mouseover` class dynamically
     * when {@link mouseover} is enabled.
     */
    if (mouseover && current) {
      const handleMouseEnter = () => {
        setHasHover(true);
      };
      const handleMouseLeave = () => {
        setHasHover(false);
      };
      current.addEventListener('mouseenter', handleMouseEnter);
      current.addEventListener('mouseleave', handleMouseLeave);
      /**
       * Cleanup function.
       */
      return () => {
        current.removeEventListener('mouseenter', handleMouseEnter);
        current.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
    return () => {};
  }, [mouseover]);
  const labelClassNameList = classNames('c-upload', {
    // Convenience prop from Bronson variants.
    'has-mouseover': mouseover && hasHover,
    'is-loading': loading,
    'is-disabled': disabled
  }, className).trim();
  function renderIfUploadButtonText() {
    if (buttonText) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-upload__actions"
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "c-upload__button c-btn"
      }, /*#__PURE__*/React__default.createElement("span", null, buttonText)));
    }
    return null;
  }
  function renderIfUploadDisabledSupplementaryText() {
    if (disabledSupplementaryText) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-upload__disabled-supplementary-text"
      }, disabledSupplementaryText);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("label", {
    ...otherProps,
    "data-testid": testId,
    className: labelClassNameList,
    htmlFor: id,
    ref: uploadElement
  }, /*#__PURE__*/React__default.createElement("input", {
    className: "c-upload__input",
    type: "file",
    id: id,
    onChange: onChange,
    accept: accept,
    multiple: multiple,
    name: name,
    disabled: disabled
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__description"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-upload__icon",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__description-text"
  }, descriptionText), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__description-supplementary-text"
  }, descriptionSupplementaryText)), renderIfUploadButtonText(), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__mouseover"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-upload__mouseover-icon",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__mouseover-text"
  }, mouseoverText)), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__loader"
  }, /*#__PURE__*/React__default.createElement(Spinner, {
    center: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__loader-text"
  }, processingText || 'Processing...')), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__disabled"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-upload__disabled-icon",
    viaCss: true
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload__disabled-text"
  }, disabledText), renderIfUploadDisabledSupplementaryText()));
}

/**
 * Bronson UploadContainer component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-upload-container.html
 * @constructor
 */
function UploadContainer({
  children,
  upload,
  testId,
  ...otherProps
}) {
  return /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-container"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-container__field"
  }, upload), /*#__PURE__*/React__default.createElement("ul", {
    ...otherProps,
    "data-testid": testId,
    className: "c-upload-container__itemlist"
  }, children));
}
UploadContainer.displayName = 'UploadContainer';
/**
 * Bronson UploadContainerItem component (nested).
 * @constructor
 */
function UploadContainerItem({
  children
}) {
  return /*#__PURE__*/React__default.createElement("li", {
    className: "c-upload-container__item"
  }, children);
}
UploadContainerItem.displayName = 'UploadContainer.Item';
UploadContainer.Item = UploadContainerItem;

/**
 * Bronson UploadItem component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-upload-item.html
 * @constructor
 */
function UploadItem({
  className,
  error,
  fileName,
  fileSize,
  filesizeProgress,
  fileType,
  hideMainButton,
  hideSecondaryButton,
  icon,
  imgAlt,
  imgLinkHref,
  imgLinkTitle,
  imgProps,
  imgSrc,
  loading,
  mainButtonLink,
  mainButtonLinkExternal,
  mainButtonProps,
  message,
  onMainButtonClick,
  onSecondaryButtonClick,
  preview,
  progressValue,
  secondaryButtonLink,
  secondaryButtonLinkExternal,
  secondaryButtonProps,
  success,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames('c-upload-item', {
    // Convenience props from Bronson variants.
    'c-upload-item--preview': preview,
    'is-loading': loading
  }, className).trim();
  function renderIfIcon() {
    if (icon) {
      return /*#__PURE__*/React__default.createElement(Icon, {
        className: "c-upload-item__icon",
        name: icon,
        "data-filetype": fileType
      });
    }
    return null;
  }
  function renderIfImage() {
    if (imgSrc) {
      return /*#__PURE__*/React__default.createElement("div", {
        className: "c-upload-item__image-wrapper"
      }, /*#__PURE__*/React__default.createElement("a", {
        className: "c-upload-item__image-link",
        href: imgLinkHref,
        title: imgLinkTitle,
        target: "_blank",
        rel: "noopener noreferrer"
      }, /*#__PURE__*/React__default.createElement("img", {
        className: "c-upload-item__image",
        src: imgSrc,
        alt: imgAlt,
        ...imgProps
      })));
    }
    return null;
  }
  function renderIfMain() {
    if (hideMainButton) {
      return null;
    }
    const props = {
      className: 'c-upload-item__interaction-icon c-upload-item__interaction-icon--main',
      onClick: onMainButtonClick,
      ...mainButtonProps
    };
    return mainButtonLink ? (/*#__PURE__*/React__default.createElement(ReactLink, {
      normalLink: true,
      href: mainButtonLink,
      target: mainButtonLinkExternal ? '_blank' : undefined,
      rel: mainButtonLinkExternal ? 'noopener noreferrer' : undefined,
      ...props
    })) : (
    /*#__PURE__*/
    // @ts-ignore @TODO: Should be replaced by the Button component, @see BRON-11670.
    React__default.createElement("button", {
      type: "button",
      ...props
    }));
  }
  function renderIfSecondary() {
    if (hideSecondaryButton) {
      return null;
    }
    const props = {
      className: 'c-upload-item__interaction-icon c-upload-item__interaction-icon--secondary',
      onClick: onSecondaryButtonClick,
      ...secondaryButtonProps
    };
    return secondaryButtonLink ? (/*#__PURE__*/React__default.createElement(ReactLink, {
      normalLink: true,
      href: secondaryButtonLink,
      target: secondaryButtonLinkExternal ? '_blank' : undefined,
      rel: secondaryButtonLinkExternal ? 'noopener noreferrer' : undefined,
      ...props
    })) : (
    /*#__PURE__*/
    // @ts-ignore @TODO: Should be replaced by the Button component, @see BRON-11670.
    React__default.createElement("button", {
      type: "button",
      ...props
    }));
  }
  function renderIfUploadItemMessage() {
    if (message) {
      return /*#__PURE__*/React__default.createElement("span", {
        className: "c-upload-item__message"
      }, message);
    }
    return null;
  }
  return /*#__PURE__*/React__default.createElement("div", {
    ...otherProps,
    "data-testid": testId,
    className: divClassNameList
  }, renderIfIcon(), renderIfImage(), /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-item__content"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-item__title-row"
  }, /*#__PURE__*/React__default.createElement("p", {
    className: "c-upload-item__title"
  }, fileName), /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-item__interaction-icons"
  }, renderIfMain(), renderIfSecondary(), /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-upload-item__status-icon",
    viaCss: true
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "c-upload-item__progress"
  }, /*#__PURE__*/React__default.createElement(Progress, {
    value: progressValue,
    success: success,
    error: error
  })), /*#__PURE__*/React__default.createElement("p", {
    className: "c-upload-item__subtitle-row"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload-item__filesize-progress"
  }, filesizeProgress, " of "), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload-item__filesize"
  }, fileSize), /*#__PURE__*/React__default.createElement("span", {
    className: "c-upload-item__filesize-progress"
  }, " (", progressValue, "% uploaded)"), renderIfUploadItemMessage())));
}

/**
 * Bronson Wizard component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-wizard.html
 * @constructor
 */
function Wizard({
  children,
  className,
  collapseBreakpoint,
  collapsedCounter,
  collapsedText,
  collapsedTitle,
  defaultOpen = false,
  noInteraction,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState(defaultOpen);
  const navClassNameList = classNames('c-wizard', 'js-is-ready',
  // Used in Bronson CSS.
  {
    'c-wizard--no-interaction': noInteraction // Convenience prop from Bronson variants.
  }, className).trim();
  const olClassNameList = classNames('c-wizard__list', {
    'is-closed': !open
  }, className).trim();
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("nav", {
    className: navClassNameList,
    "data-wizard-collapse": collapseBreakpoint,
    "data-testid": testId,
    ...otherProps
  }, /*#__PURE__*/React__default.createElement("button", {
    className: "c-wizard__trigger",
    type: "button",
    "aria-expanded": open,
    title: collapsedTitle,
    onClick: () => setOpen(!open)
  }, /*#__PURE__*/React__default.createElement(Icon, {
    className: "c-wizard__trigger-icon",
    viaCss: true
  }), collapsedText && /*#__PURE__*/React__default.createElement("span", {
    className: "c-wizard__trigger-text"
  }, collapsedText), collapsedCounter && /*#__PURE__*/React__default.createElement("span", {
    className: "c-wizard__trigger-counter"
  }, collapsedCounter)), /*#__PURE__*/React__default.createElement("ol", {
    className: olClassNameList
  }, children)));
}
Wizard.displayName = 'Wizard';
/**
 * Bronson WizardItem component (nested).
 * @constructor
 */
function WizardItem({
  active,
  children,
  className,
  disabled,
  done,
  error,
  hiddenCurrentStepText = 'Current Step',
  noInteraction,
  onClick,
  testId,
  ...otherProps
}) {
  const liClassNameList = classNames('c-wizard__item', {
    'is-done': done,
    'is-active': active,
    'is-error': error,
    'is-disabled': disabled,
    'has-no-interaction': noInteraction
  }, className).trim();
  function renderIfStateActive() {
    return active ? /*#__PURE__*/React__default.createElement("span", {
      className: "u-visually-hidden"
    }, hiddenCurrentStepText, ":") : null;
  }
  return /*#__PURE__*/React__default.createElement("li", {
    "data-testid": testId,
    className: liClassNameList
  }, /*#__PURE__*/React__default.createElement("a", {
    className: "c-wizard__link",
    onClick: onClick,
    "aria-disabled": disabled,
    ...otherProps
  }, renderIfStateActive(), /*#__PURE__*/React__default.createElement("span", {
    className: "c-wizard__link-title"
  }, children)));
}
WizardItem.displayName = 'Wizard.Item';
Wizard.Item = WizardItem;

/**
 * Bronson ContentSection component.
 * @see https://bronson.vwfs.tools/default/docs/objects/content-section.html
 * @constructor
 */
function ContentSection({
  altBackground,
  children,
  className,
  pageWrap,
  pageWrapSize,
  testId
}) {
  function renderContent() {
    if (pageWrap || pageWrapSize) {
      return /*#__PURE__*/React__default.createElement(PageWrap, {
        size: pageWrapSize
      }, children);
    }
    return children;
  }
  const classNameList = classNames('o-content-section', className).trim();
  return /*#__PURE__*/React__default.createElement("section", {
    className: classNameList,
    "data-theme": altBackground ? 'alternative' : undefined,
    "data-testid": testId
  }, renderContent());
}
/**
 * Bronson ComponentWrapper component (nested).
 * @constructor
 */
function ComponentWrapper({
  children,
  className
}) {
  const classNameList = classNames('o-component-wrapper', className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList
  }, children);
}
ContentSection.ComponentWrapper = ComponentWrapper;

/**
 * Bronson Fieldset component.
 * The Fieldset object handles vertical spacing in form fields so the spacing
 * between individual form fields is consistent and can be controlled via CSS.
 * @see https://bronson.vwfs.tools/default/docs/objects/fieldset.html
 * @constructor
 */
function Fieldset({
  children,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('o-fieldset').trim();
  return /*#__PURE__*/React__default.createElement("fieldset", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, children);
}
Fieldset.displayName = 'Fieldset';
/**
 * Bronson FieldsetRow component (nested).
 * @constructor
 */
function FieldsetRow({
  children,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('o-fieldset__row').trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNameList,
    "data-testid": testId,
    ...otherProps
  }, children);
}
FieldsetRow.displayName = 'Fieldset.Row';
Fieldset.Row = FieldsetRow;

/**
 * Bronson Main component.
 * @see https://bronson.vwfs.tools/default/docs/objects/main-layout-containers.html
 * @constructor
 */
function Main({
  children,
  ...otherProps
}) {
  const mainClassName = classNames('o-main').trim();
  return /*#__PURE__*/React__default.createElement("main", {
    className: mainClassName,
    ...otherProps
  }, children);
}

/**
 * Bronson Wrapper component.
 * @TODO Should be discussed whether at all it is obsolete and can be removed.
 * @constructor
 */
function Wrapper({
  children,
  pageWrapper = false
}) {
  if (pageWrapper) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "o-main-container"
    }, children);
  }
  return /*#__PURE__*/React__default.createElement("main", null, children);
}
/**
 * Bronson Layout component.
 * @see https://bronson.vwfs.tools/default/docs/objects/layout.html
 * @constructor
 */
const Layout = props => {
  const componentListClass = classNames('o-layout', {
    'o-layout--middle': props.middle,
    'o-layout--equal-height': props.equalHeight,
    'o-layout--center': props.center,
    'o-layout--right': props.right,
    'o-layout--flush': props.flush,
    'o-layout--divider': props.divider
  }, props.className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: componentListClass
  }, props.children);
};
/**
 * Bronson LayoutItem component (nested).
 * @constructor
 */
function LayoutItem(props) {
  const componentListClass = classNames('o-layout__item', {
    [`u-${props.default}`]: props.default,
    [`u-${props.xs}@xs`]: props.xs,
    [`u-${props.s}@s`]: props.s,
    [`u-${props.m}@m`]: props.m,
    [`u-${props.l}@l`]: props.l,
    [`u-${props.xl}@xl`]: props.xl
  }, props.className).trim();
  return /*#__PURE__*/React__default.createElement("div", {
    className: componentListClass
  }, props.children);
}
LayoutItem.displayName = 'Layout.Item';
Layout.Item = LayoutItem;

export { Accordion, ActionButtonsContainer, BackToTop, Badge, Blockquote, Breadcrumb, Button, ButtonContainer, ButtonGroup, Callout, Card, CardsSlider, Carousel, Checkbox, Combobox, CompareAndSelect, ConsumptionGraphic, ContentSection, ContextBar, ContextNotification, CustomProgress, CustomRangeSlider, DataOverview, DatePicker, DescriptionList, DescriptionListAccordion, DisplayCard, DropdownContainer, EfficiencyTag, ErrorMessage, ExpandableContent, FavoriteButton, Fieldset, FloatingBar, FloatingButton, FoldOut, Footer, Footnote, Form, FormField, FormFieldCheckboxGroup, FormFieldLabel, FormFieldRadioButtonGroup, FormHeading, FormSection, FormSectionGroup, Header, Heading, HeroTeaser, Hint, Hr, Icon, IconList, IconName, IconSize, Inbox, IndicatorDot, InfoIcon, InlineEditable, InlineGroup, Input, InputAutosuggest, InstructionStepper, InteractionLayer, ItemTeaser, LabeledContentSection, Layout, LicensePlate, ListInline, ListUi, Logo, LogoBanner, Main, MediaGrid, MediaTeaser, Modal, Notification, NotificationCenter, NotificationsGroup, OrderedList, PageWrap, Pagination, Paragraph, PlayButton, PriceBox, ProductCard, ProductInfo, ProductInfoSummary, ProductSearch, ProductSearchFilter, ProductSearchForm, ProductSearchHeader, ProductTable, Progress, RadioButton, ResponsiveImage, SectionHeading, SectionNav, Select, ShowMore, SiteNav, Spinner, StatusMessage, StickyContainer, Table, TableButton, TableButtonSorting, Tabs, Tag, TagList, Textarea, TileSelect, TileSelectGroup, TimePicker, ToastNotification, ToastNotifications, Toggle, Toggle$1 as ToggleObject, Tooltip, UnorderedList, Upload, UploadContainer, UploadItem, Video, Wizard, Wrapper, useToastOpen };
//# sourceMappingURL=index.es.js.map
